import React, { useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import SubInfoText from "./components/SubInfoText";
import DOMPurify from "dompurify";
import { useSetRecoilState } from "recoil";
import {
  PdfDownloadStartAtom,
  PdfProgressAtom,
} from "../../../recoil/PdfDownloadAtom";

// Components
import ResultScoreTextWrap from "./components/ResultScoreTextWrap";
import BalanceOfPowerWings from "./components/BalanceOfPowerWings";

// Images
import { images } from "../../../assets/testPageImages";

// ===== chart =====
import * as d3 from "d3";
import { Line, Radar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  RadialLinearScale,
  PointElement,
  LineElement,
  ArcElement,
  LinearScale,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { ShowErrorMsg } from "../../../utils/ShowErrorMsg";
import { useRecoilState } from "recoil";

// Chart.js에 필요한 요소 등록
ChartJS.register(
  CategoryScale,
  RadialLinearScale,
  LinearScale,
  PointElement,
  ArcElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ChartDataLabels
);
// ===== chart =====

const Container = styled.div`
  position: absolute;
  top: -9999px;
  left: -9999px;
  background-color: #fff;

  * {
    word-break: keep-all;
  }
  & * {
    background-color: transparent;
  }
  .text-center {
    text-align: center;
  }
  .font-medium {
    font-weight: 500;
  }
  .font-semibold {
    font-weight: 600;
  }
  .green-bg-box {
    border-radius: 16px;
    padding: 30px;
    background-color: #d9eee6;
  }

  .main.cover img {
    width: 100%;
  }

  /* 페이지 공통 */
  .page-common {
    /* box-shadow: inset 0px 0px 0px 1px red; */
    overflow: hidden;
    position: relative;
    z-index: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 1920px;
    /* max-width: 100%; */
    height: 2717px;
    background-color: #fff;
  }

  /* 요약본 */
  .page-summary {
    .main {
      padding: 40px 0px;
    }
    .grid {
      display: grid;
    }
    .gap-8 {
      gap: 8px;
    }
    .col-2 {
      grid-template-columns: repeat(2, 1fr);
    }
    .mt-24 {
      margin-top: 24px;
    }

    .balance-of-power-img-wrap {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      .text-wrap {
        display: flex;
        flex-direction: column;
        gap: 48px;
        position: static;
        .text-item {
          position: static;
          &.item-1 .tit .number,
          &.item-8 .tit .number,
          &.item-9 .tit .number {
            background-color: #ff5580;
          }
          &.item-2 .tit .number,
          &.item-3 .tit .number,
          &.item-4 .tit .number {
            background-color: #ffc96f;
          }
          &.item-5 .tit .number,
          &.item-6 .tit .number,
          &.item-7 .tit .number {
            background-color: #0034b2;
          }
          .tit {
            display: flex;
            align-items: center;
            gap: 16px;
            font-size: 24px;
            font-weight: 700;
            margin-bottom: 12px;
            .number {
              display: flex;
              justify-content: center;
              align-items: center;
              width: 48px;
              height: 48px;
              border-radius: 50%;
              color: #fff;
            }
          }
        }
      }
      .after-wrap {
        &.num_1::after {
          top: 22.1%;
          right: 22.8%;
        }
        &.num_2::after {
          top: 40.8%;
          right: 13%;
        }
        &.num_3::after {
          top: 61.7%;
          right: 17.3%;
        }
        &.num_4::after {
          top: 76.5%;
          right: 32.7%;
        }
        &.num_5::after {
          top: 76.5%;
          left: 36.3%;
        }
        &.num_6::after {
          top: 61.7%;
          left: 18.3%;
        }
        &.num_7::after {
          top: 40.8%;
          left: 13.6%;
        }
        &.num_8::after {
          top: 22.1%;
          left: 24.1%;
        }
        &.num_9::after {
          top: 13.1%;
          left: 46.5%;
        }
      }
    }

    .result-score-title-wrap {
      margin-bottom: 8px;
    }
    .result-score {
      height: 54px;
      margin-bottom: 54px;
    }
    .result-score .line {
      height: 54px;
      &::after {
        display: block;
        clear: both;
        position: absolute;
        bottom: -30px;
        left: -18px;
        font-size: 24px;
      }
      &.percent-15::after {
        content: "15";
      }
      &.percent-25::after {
        content: "25";
      }
      &.percent-30::after {
        content: "30";
      }
      &.percent-35::after {
        content: "35";
      }
      &.percent-40::after {
        content: "40";
      }
      &.percent-50::after {
        content: "50";
      }
      &.percent-60::after {
        content: "60";
      }
      &.percent-70::after {
        content: "70";
      }
    }

    .balance-of-power-img {
      width: 835px;
    }

    .overview-balance-of-power-label {
      font-size: 24px;
      font-weight: 600;
      border-left: 2px solid #ddd;
      padding: 10px 0px;
    }

    .title-wrap {
      display: flex;
      align-items: center;
      .main-title {
        font-size: 112px;
        color: #007a10;
      }
      .name {
        line-height: 1em;
        font-size: 64px;
        font-weight: 700;
        border-left: 2px solid #007a10;
        padding-left: 50px;
        margin-left: 50px;
      }
    }
    .contents-wrap {
      margin-top: 30px;
      &.mt-60 {
        margin-top: 60px;
      }
      .heading-wrap {
        display: flex;
        align-items: center;
        border-top: 3px solid #007a10;
        border-bottom: 3px solid #007a10;
        margin-bottom: 30px;
        .name {
          width: fit-content;
          min-width: 300px;
          height: 100%;
          height: 80px;
          line-height: 80px;
          text-align: center;
          font-size: 40px;
          font-weight: 700;
          color: #fff;
          background-color: #007a10;
        }
        .sub-wrap {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          padding-left: 30px;
          &.align-right {
            justify-content: flex-end;
          }
          .tit {
            font-size: 30px;
          }
          .page {
            font-size: 24px;
          }
        }
      }
    }
    .score-list-container {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      .score-list-item {
        padding: 24px 40px;
        &:nth-of-type(1),
        &:nth-of-type(2),
        &:nth-of-type(3) {
          border-bottom: 1px solid #e4e4e4;
        }
        &:not(:nth-of-type(3n)) {
          border-right: 1px solid #e4e4e4;
        }
        &:nth-of-type(3n) {
          padding-right: 0px;
        }
        &:nth-of-type(1),
        &:nth-of-type(4) {
          padding-left: 0px;
        }
        .result-score-title {
          font-size: 30px;
        }
        .result-score-num {
          font-size: 30px;
          strong {
            font-size: 44px;
          }
        }
      }
      .result-score-wrap {
        &.type1 .result-score-item {
          .txt,
          .title {
            font-weight: 700;
            color: #0077ff;
          }
          .icon {
            background-image: url(${images.icon_result_score_01_active});
          }
        }
        &.type2 .result-score-item {
          .txt,
          .title {
            font-weight: 700;
            color: #fcc200;
          }
          .icon {
            background-image: url(${images.icon_result_score_02_active});
          }
        }
        &.type3 .result-score-item {
          .txt,
          .title {
            font-weight: 700;
            color: #ff5b36;
          }
          .icon {
            background-image: url(${images.icon_result_score_03_active});
          }
        }
        &.type4 .result-score-item {
          .txt,
          .title {
            font-weight: 700;
            color: #d7262e;
          }
          .icon {
            background-image: url(${images.icon_result_score_04_active});
          }
        }
      }
      .result-score {
        &::before,
        &::after {
          display: block;
          clear: both;
          position: absolute;
          bottom: -30px;
          font-size: 24px;
        }
        &::before {
          content: "0";
          left: 0px;
        }
        &::after {
          content: "100";
          right: 0px;
        }
      }
    }
    .chart-wrap {
      gap: 40px;
      grid-template-columns: repeat(2, 1fr);
      .default-title {
        font-size: 30px;
      }
      .step-item-list .border-dash {
        padding: 20px 0px;
      }
      .meta-q-chart {
        display: flex;
        justify-content: center;
        align-items: center;
        border: 3px solid #00754a;
        border-radius: 16px;
        padding: 20px;
        canvas {
          width: 600px !important;
          height: 600px !important;
        }
      }
    }
    .ego-result-chart-group {
      width: 100%;
    }
    .life-score-section {
      padding: 0px;
      &::before,
      &::after {
        content: none;
      }
    }
    .life-score-result-wrap {
      padding: 0px;
      & > .row-group {
        width: 100%;
      }
      .default-txt {
        font-size: 24px;
      }
      .title-wrap {
        padding-bottom: 40px;
        .score {
          font-size: 44px;
        }
      }
    }

    .result-score-wrap .result-score-item .txt {
      font-size: 26px !important;
    }
    .result-score-title {
      font-size: 30px;
    }
    .result-score-num {
      font-size: 30px;
      strong {
        font-size: 44px;
      }
    }
  }

  .page-5 {
    .step-item-sub-title,
    .tip-item {
      font-size: 28px;
    }
    .step-item-list .border-dash {
      padding: 26px 0px;
    }
  }
  .page-25 {
    .gap40 {
      gap: 20px;
    }
    .row-group {
      gap: 4px;
    }
    .half-div {
      width: calc(50% - 10px);
    }
    .default-title {
      font-size: 32px;
      margin-bottom: 8px;
    }
    .default-txt {
      font-size: 26px;
    }
    .default-txt-s {
      font-size: 26px;
    }
    .wing-type-section {
      margin-top: 20px;
    }
    .wing-type-group {
      padding: 20px 0px;
    }
  }
  .sub-info-text-wrap {
    display: flex;
    gap: 8px;
  }
  .sub-info-text {
    font-size: 27px;
    line-height: 1.55em;
    color: #9a9a9b;
  }

  /* 컨텐츠 공통 */
  .col-group {
    display: flex;
  }
  .row-group {
    display: flex;
    flex-flow: column;
  }
  .container {
    margin: 0 auto;
    width: 100%;
    padding: 9.375vw;
  }
  .yellow {
    color: #fee500;
  }
  .red {
    color: #d7262e;
  }
  .orange {
    color: #f27255;
  }
  .green {
    color: #007a10;
  }
  .blue {
    color: #0088ff;
  }
  .img-container {
    width: 100%;
    position: relative;
  }
  .img-container img {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    object-fit: cover;
    object-position: center;
  }
  .video-container {
    width: 100%;
    padding-top: 56.25%;
    position: relative;
  }
  .video-container iframe {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
  ::-webkit-scrollbar {
    width: 4px;
  }
  ::-webkit-scrollbar-track {
    background: #e4e4e4;
  }
  ::-webkit-scrollbar-thumb {
    background: #484848;
    border-radius: 4px;
  }
  .null-txt {
    font-size: 16px;
    font-weight: 300;
    text-align: center;
    color: #202020;
    padding: 120px 0;
    align-items: center;
    gap: 16px;
  }
  .null-txt .icon {
    font-size: 32px;
  }

  .relative {
    position: relative;
  }
  .sp-bt {
    justify-content: space-between;
  }
  .al-ce {
    align-items: center;
  }
  .gap4 {
    gap: 4px;
  }
  .gap8 {
    gap: 8px;
  }
  .gap10 {
    gap: 10px;
  }
  .gap16 {
    gap: 16px;
  }
  .gap24 {
    gap: 24px;
  }
  .gap28 {
    gap: 28px;
  }
  .gap32 {
    gap: 32px;
  }
  .gap40 {
    gap: 40px;
  }
  .gap48 {
    gap: 48px;
  }
  .gap56 {
    gap: 56px;
  }
  .half-div {
    width: calc(50% - 20px);
  }
  .green-tip-box.w-full-size {
    width: 100%;
  }

  /* 페이지 공통 */
  .main {
    position: relative;
    max-width: 1560px;
    width: 100%;
    height: 2716px;
    padding: 6.5625vw 0;
  }
  .main {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .page-num {
    font-size: 30px;
    font-weight: 500;
    color: #707070;
    position: absolute;
    right: -100px;
    top: 50px;
  }
  .main-title-wrap {
    margin-bottom: 64px;
    gap: 24px;
    align-items: center;
  }
  .main-title-wrap .num {
    font-size: 240px;
  }
  .main-title-group {
    gap: 16px;
    padding-bottom: 32px;
    position: relative;
  }
  .main-title-group::after {
    content: "";
    display: block;
    position: absolute;
    width: 80px;
    height: 4px;
    background: #000;
    left: 0;
    bottom: 0;
  }
  .main-title {
    font-size: 64px;
  }
  .main-sub-title {
    font-size: 32px;
    font-weight: 500;
    line-height: 1.25;
  }

  .section-wrap {
    gap: 96px;
  }
  .section-title-wrap {
    margin-bottom: 56px;
    gap: 32px;
    align-items: baseline;
  }
  .section-title-wrap .num {
    width: 70px;
    height: 70px;
    background: #007a10;
    text-align: center;
    line-height: 70px;
    font-size: 46px;
    font-weight: bold;
    color: #fff;
    border-top-right-radius: 24px;
    border-bottom-left-radius: 24px;
  }
  .section-title {
    font-size: 50px;
    line-height: 1.25;
  }
  .section-title-s {
    font-size: 38px;
  }

  .default-title {
    font-size: 40px;
    font-weight: bold;
  }
  .default-txt {
    font-size: 30px;
    font-weight: 500;
    line-height: 1.5;
  }
  .default-txt * {
    line-height: 1.5;
    display: inline;
  }
  .default-txt-s {
    font-size: 26px;
    font-weight: normal;
    line-height: 1.35;
  }
  .default-txt-l {
    font-size: 34px;
    font-weight: normal;
    line-height: 1.35;
  }
  .default-txt-bold {
    font-weight: bold;
  }

  /* cover */
  .main.cover {
    max-width: 100%;
  }
  .user-info-list {
    position: absolute;
    width: 700px;
    left: 260px;
    bottom: 427px;
    gap: 80px;
  }
  .user-info-item {
    padding-bottom: 20px;
    border-bottom: 1px solid #fff;
  }
  .user-info-item .item-title {
    width: 200px;
    font-size: 36px;
    font-weight: 600;
    color: #fff;
  }
  .user-info-item .item-txt {
    width: calc(100% - 174px);
    font-size: 36px;
    font-weight: normal;
    color: #fff;
  }

  /* 목차 */
  .list {
    max-width: 1560px;
    margin: 0 auto;
    padding: 180px 0;
    position: relative;
  }
  .list::after {
    content: "";
    position: absolute;
    display: block;
    width: 100%;
    height: 10px;
    background: #007a10;
    top: 70px;
    left: 0;
    right: 0;
  }
  .list-title {
    width: 100%;
    font-size: 120px;
    font-weight: bold;
  }
  .list-wrap {
    width: 100%;
    gap: 80px;
    padding-right: 64px;
  }
  .list-group-title .num {
    width: 64px;
    text-align: right;
    font-size: 36px;
    font-weight: bold;
    color: #007a10;
  }
  .list-group-title .txt {
    width: calc(100% - 64px);
    font-size: 36px;
    font-weight: bold;
    color: #007a10;
  }
  .list-sub-item .page {
    width: 64px;
    text-align: right;
    font-size: 30px;
    font-weight: bold;
    line-height: 1.5;
  }
  .list-sub-item .title {
    width: calc(100% - 64px);
    font-size: 30px;
    line-height: 1.5;
  }

  /* 융합역량지수-01-01-마인드 인사이트 소개 */
  .main.bg-bottom {
    position: relative;
  }
  .main.bg-bottom::after {
    content: "";
    display: block;
    position: absolute;
    width: 1920px;
    height: 940px;
    bottom: 0;
    background: #d9eee6;
    left: 50%;
    transform: translateX(-50%);
    z-index: -1;
  }
  .section-top {
    position: relative;
    padding: 40px 0;
    padding-left: 360px;
    background: #eeeeee;
    border-radius: 32px;
    margin-bottom: 90px;
  }
  .section-top .img {
    position: absolute;
    width: 232px;
    left: 45px;
    top: -27px;
  }
  .section-top .txt {
    font-size: 30px;
    line-height: 1.6;
  }

  .main .step-item-wrap > div {
    width: 100%;
  }
  .step-item {
    align-items: baseline;
  }
  .step-item.border {
    padding: 32px;
    background: #fff;
    border-radius: 16px;
    border: 3px solid #00754a;
    align-items: baseline;
  }
  .step-item-num {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    text-align: center;
    line-height: 64px;
    background: #cbe2da;
    font-size: 26px;
    font-weight: bold;
    color: #007a10;
  }
  .step-item-txt-wrap {
    width: calc(100% - 64px - 24px);
  }
  .step-item-title {
    font-size: 40px;
    font-weight: bold;
    color: #00754a;
    align-items: center;
  }
  .step-item-txt {
    font-size: 26px;
    font-weight: 500;
    line-height: 1.5;
  }
  .step-item-sub-title {
    font-size: 30px;
    font-weight: bold;
    line-height: 1.5;
  }

  /* 융합역량지수-02-01-감정상태 */
  .result-score-container {
    align-items: flex-start;
  }
  .result-score-container .result-score-wrap {
    width: calc(50% - 20px);
  }
  .result-score-wrap {
    width: 100%;
    border-radius: 16px;
    background: #fff;
  }
  .result-score-wrap.border {
    padding: 56px 40px;
    border: 3px solid #e4e4e4;
  }
  .result-score-txt-wrap {
    width: 100%;
  }
  .result-score-container .result-score-txt-wrap {
    width: calc(50% - 20px);
  }
  .result-score-title-wrap {
    margin-bottom: 32px;
    align-items: center;
  }
  .result-score-title {
    font-size: 40px;
    font-weight: bold;
  }
  .result-score-num {
    font-size: 40px;
    font-weight: 500;
  }
  .result-score-num strong {
    font-size: 64px;
  }
  .result-score {
    position: relative;
    width: 100%;
    height: 64px;
    border-radius: 32px;
    background: #e4e4e4;
    margin-bottom: 48px;
  }
  /* .result-score .line { position: absolute; width: 0; height: 64px; border-left: 2px dashed #202020; top: 50%; transform: translateY(-50%); } */
  .result-score .line {
    position: absolute;
    width: 0;
    height: 64px;
    border-left: 2px dashed #202020;
    top: 0;
  }
  .result-score-gauge-wrap {
    overflow: hidden;
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 99px;
    background-color: #e4e4e4;
  }
  .result-score-gauge-wrap .result-score-gauge {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    height: 100%;
    border-radius: 32px;
  }

  .result-score-item {
    align-items: center;
  }
  .result-score-item:not(:last-child) {
    border-bottom: 1px solid #e4e4e4;
    padding-bottom: 24px;
    margin-bottom: 24px;
  }
  .result-score-item .icon {
    width: 64px;
    height: 64px;
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
  }
  .result-score-item:nth-child(1) .icon {
    background-image: url(${images.icon_result_score_01});
  }
  .result-score-item:nth-child(2) .icon {
    background-image: url(${images.icon_result_score_02});
  }
  .result-score-item:nth-child(3) .icon {
    background-image: url(${images.icon_result_score_03});
  }
  .result-score-item:nth-child(4) .icon {
    background-image: url(${images.icon_result_score_04});
  }
  .result-score-item .txt-group {
    width: calc(100% - 64px - 24px);
  }
  .result-score-item .title {
    font-size: 24px;
    font-weight: 600;
  }
  .result-score-item .txt {
    font-size: 24px;
    line-height: 1.2;
  }

  .result-score-wrap.type1 .result-score-item:nth-child(1) .title {
    font-size: 26px;
  }
  .result-score-wrap.type1 .result-score-item:nth-child(1) .txt {
    font-size: 30px;
  }
  .result-score-wrap.type1 .result-score-num,
  .result-score-wrap.type1 .result-score-num *,
  .result-score-wrap.type1 .result-score-item.current-score .title,
  .result-score-wrap.type1 .result-score-item.current-score .txt {
    color: #0077ff;
  }
  .result-score-wrap.type1 .result-score-gauge {
    background: #0077ff;
  }
  .result-score-wrap.type1 .result-score-item:nth-child(1) .icon {
    background-image: url(${images.icon_result_score_01_active});
  }
  .result-score-wrap.type2 .result-score-item:nth-child(2) .title {
    font-size: 26px;
  }
  .result-score-wrap.type2 .result-score-item:nth-child(2) .txt {
    font-size: 30px;
  }
  .result-score-wrap.type2 .result-score-num,
  .result-score-wrap.type2 .result-score-num *,
  .result-score-wrap.type2 .result-score-item.current-score .title,
  .result-score-wrap.type2 .result-score-item.current-score .txt {
    color: #fcc200;
  }
  .result-score-wrap.type2 .result-score-gauge {
    background: #fcc200;
  }
  .result-score-wrap.type2 .result-score-item:nth-child(2) .icon {
    background-image: url(${images.icon_result_score_02_active});
  }
  .result-score-wrap.type3 .result-score-item:nth-child(3) .title {
    font-size: 26px;
  }
  .result-score-wrap.type3 .result-score-item:nth-child(3) .txt {
    font-size: 30px;
  }
  .result-score-wrap.type3 .result-score-num,
  .result-score-wrap.type3 .result-score-num *,
  .result-score-wrap.type3 .result-score-item.current-score .title,
  .result-score-wrap.type3 .result-score-item.current-score .txt {
    color: #ff5b36;
  }
  .result-score-wrap.type3 .result-score-gauge {
    background: #ff5b36;
  }
  .result-score-wrap.type3 .result-score-item:nth-child(3) .icon {
    background-image: url(${images.icon_result_score_03_active});
  }
  .result-score-wrap.type4 .result-score-item:nth-child(4) .title {
    font-size: 26px;
  }
  .result-score-wrap.type4 .result-score-item:nth-child(4) .txt {
    font-size: 30px;
  }
  .result-score-wrap.type4 .result-score-num,
  .result-score-wrap.type4 .result-score-num *,
  .result-score-wrap.type4 .result-score-item.current-score .title,
  .result-score-wrap.type4 .result-score-item.current-score .txt {
    color: #d7262e;
  }
  .result-score-wrap.type4 .result-score-gauge {
    background: #d7262e;
  }
  .result-score-wrap.type4 .result-score-item:nth-child(4) .icon {
    background-image: url(${images.icon_result_score_04_active});
  }

  .result-score-txt {
    padding: 40px 56px;
    padding-right: 205px;
    border-radius: 16px;
    background: #eee;
    font-size: 40px;
    font-weight: bold;
    line-height: 1.4;
    word-break: keep-all;
    position: relative;
  }
  .result-score-txt::after {
    content: "";
    display: block;
    position: absolute;
    width: 205px;
    height: 100%;
    background-repeat: no-repeat;
    background-size: contain;
    background-position: right bottom;
    bottom: 0;
    right: 0;
  }
  .result-score-txt-img {
    display: block;
    position: absolute;
    width: auto;
    height: 85%;
    bottom: 0;
    right: 0;
  }
  .result-score-txt.type1 .color {
    color: #0077ff;
  }
  .result-score-txt.type2 .color {
    color: #fcc200;
  }
  .result-score-txt.type3 .color {
    color: #ff5b36;
  }
  .result-score-txt.type4 .color {
    color: #d7262e;
  }

  .result-score-list {
    margin-top: 48px;
  }

  .green-border-box {
    width: 100%;
    padding: 40px;
    border-radius: 16px;
    border: 3px solid #00754a;
    background: #fff;
  }
  .green-border-box.bg-green {
    background-color: #d9eee6;
  }
  .red-border-box {
    width: 100%;
    padding: 40px;
    border-radius: 16px;
    border: 3px solid #d7262e;
    background: #fff;
  }

  .icon-arrow-wrap {
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: space-evenly;
    align-items: center;
  }
  .icon-arrow-both {
    position: relative;
    width: 64px;
    height: 40px;
  }
  .icon-arrow-both .icon-arrow-left {
    width: 40px;
    position: absolute;
    left: 0;
  }
  .icon-arrow-both .icon-arrow-right {
    width: 40px;
    position: absolute;
    right: 0;
  }

  /* 융합역량지수-02-02-감정상태 */
  .depression-1 {
    position: relative;
  }
  .depression-1 .img {
    position: absolute;
    width: 320px;
    height: 425px;
    bottom: 0;
    right: 0;
  }
  .depression-2 {
    position: relative;
    padding-left: 500px;
  }
  .depression-2 .img {
    position: absolute;
    width: 407px;
    height: 694px;
    top: 40px;
    left: 40px;
  }

  /* 융합역량지수-02-03-감정상태 */
  .step-item-list.border {
    padding: 0 32px;
    border: 3px solid #00754a;
    border-radius: 16px;
    background-color: #ffffff;
  }
  .page-19 .step-item-list.border,
  .page-20 .step-item-list.border {
    background-color: transparent;
  }
  .step-item-list .border-dash {
    padding: 32px 0;
    word-break: keep-all;
    border-radius: 0;
    align-items: baseline;
  }
  .step-item-list .border-dash:not(:last-child) {
    border-bottom: 2px dashed #007a10;
  }

  .tip-wrap > div {
    width: 100%;
    justify-content: space-between;
  }
  .tip-wrap .img-1 {
    max-width: 440px;
    margin: 0 auto;
    margin-bottom: -56px;
  }
  .tip-item-wrap {
    padding: 32px;
    border-radius: 16px;
    background: #d9eee6;
  }
  .tip-item-wrap .icon {
    width: 64px;
    height: 64px;
    text-align: center;
    line-height: 64px;
    border-radius: 50%;
    background: #155845;
    color: #fff;
    font-size: 26px;
    font-weight: bold;
  }
  .tip-item-group {
    width: calc(100% - 64px - 24px);
  }
  .tip-item {
    font-size: 30px;
    font-weight: bold;
    color: #155845;
    line-height: 1.5;
  }
  .tip-item:not(:last-child) {
    padding-bottom: 24px;
    border-bottom: 2px dashed #155845;
    margin-bottom: 24px;
    word-break: keep-all;
  }

  /* 융합역량지수-02-04-감정상태 */
  .relax-title-wrap {
    align-items: flex-end;
  }
  .relax-title-wrap .img {
    width: 200px;
    height: 200px;
  }
  .relax-title-group {
    width: calc(100% - 200px - 40px);
  }

  /* 융합역량지수-02-05-감정상태 */
  .anger-img {
    position: absolute;
    padding: 30px;
    right: 0;
    top: 20px;
    width: 575px;
    height: 505px;
    background: #fff;
    border-bottom-left-radius: 210px;
  }

  /* 융합역량지수-02-08-감정상태 */
  .compare-group {
    width: 100%;
  }
  .compare-title {
    position: relative;
    padding-left: 56px;
    height: 184px;
    display: flex;
    flex-flow: column;
    justify-content: center;
    border-radius: 16px;
    overflow: hidden;
  }
  .compare-title .img {
    position: absolute;
    width: auto;
    height: 184px;
    top: 0;
    right: 0;
  }

  .compare-group:nth-child(1) .compare-title {
    background: #cbe3ce;
  }
  .compare-group:nth-child(2) .compare-title {
    background: #d0ddda;
  }
  .compare-group:nth-child(2) .compare-title .default-title {
    color: #155845;
  }
  .compare-group:nth-child(1) .step-item-num {
    background: #cbe3ce;
    color: #007a10;
  }
  .compare-group:nth-child(2) .step-item-num {
    background: #d0ddda;
    color: #155845;
  }

  /* 융합역량지수-03-01-융합역량지능 */
  .meta-q-guide {
    width: 100%;
    border-radius: 16px;
    border: 3px solid #e4e4e4;
    background: #fff;
  }
  .meta-q-guide-item {
    width: 100%;
    padding: 24px 0;
    padding-left: 40px;
    position: relative;
  }
  .meta-q-guide-item:not(:last-child):after {
    content: "";
    display: block;
    position: absolute;
    width: 2px;
    height: calc(100% - 48px);
    background: #e4e4e4;
    right: 0;
    top: 24px;
  }
  .meta-q-guide-item .title {
    font-size: 24px;
    font-weight: 600;
  }
  .meta-q-guide-item .txt {
    font-size: 24px;
  }

  .meta-q-chart-wrap {
    display: flex;
    gap: 40px;
    margin-top: 16px;
  }
  .meta-q-chart-wrap .chart-num-wrap {
    display: flex;
    flex-direction: column;
    height: 480px;
    justify-content: space-between;
    width: 40px;
    align-items: flex-end;
  }
  .meta-q-chart-wrap .chart-num {
    font-size: 24px;
    font-weight: 600;
  }
  .meta-q-chart-wrap .chart-graph-wrap {
    display: flex;
    width: calc(100% - 80px);
    justify-content: space-evenly;
    position: relative;
  }
  .meta-q-chart-wrap .chart-graph-wrap::after {
    content: "";
    display: block;
    position: absolute;
    width: 100%;
    height: 3px;
    background: #e4e4e4;
    top: 480px;
  }
  .meta-q-chart-wrap .chart-graph-wrap::before {
    content: "";
    display: block;
    position: absolute;
    width: 100%;
    height: 0;
    border-top: 2px dashed #000;
    top: 240px;
    z-index: 1;
  }
  .meta-q-chart-wrap .chart-gauge {
    height: 480px;
    width: 96px;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    background: #f5f5f5;
    position: relative;
  }
  .meta-q-chart-wrap .chart-gauge-core {
    position: absolute;
    width: 100%;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    bottom: 0;
    left: 0;
    right: 0;
    background: #aaaaaa;
  }
  .meta-q-chart-wrap .chart-graph-item.red .chart-gauge-core {
    background: #d7262e;
  }
  .meta-q-chart-wrap .chart-graph-item.green .chart-gauge-core {
    background: #00754a;
  }
  .meta-q-chart-wrap .chart-gauge-num {
    position: absolute;
    font-size: 24px;
    font-weight: 600;
    left: 50%;
    transform: translate(-50%, -100%);
    top: -8px;
  }
  .meta-q-chart-wrap .chart-txt-group {
    margin-top: 24px;
    align-items: center;
  }
  .meta-q-chart-wrap .chart-txt-group .txt {
    font-size: 24px;
    font-weight: 600;
  }
  .meta-q-chart-wrap .chart-graph-item.red .chart-txt-group .txt {
    color: #d7262e;
  }
  .meta-q-chart-wrap .chart-graph-item.green .chart-txt-group .txt {
    color: #00754a;
  }
  .meta-q-icon {
    background-repeat: no-repeat;
    width: 56px;
    height: 56px;
    background-size: contain;
    background-position: center;
  }
  .meta-q-icon > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .meta-q-result-group > div {
    width: 100%;
  }
  .step-item-list.border.red {
    border: 3px solid #d7262e;
  }
  .step-item-list.red .border-dash {
    border-color: #d7262e;
  }
  .step-item-list.red .step-item-num {
    background: #f7d3d5;
    color: #d7262e;
  }

  /* 융합역량지수-03-02 강점/약점보완영역 */
  .default-title .icon {
    width: 56px;
  }

  /* 융합역량지수-03-03-융합역량지능 */
  .circle-chart {
    margin: 0;
    padding: 0;
  }
  .circle-chart .label {
    text-anchor: middle;
    width: 100%;
    font-size: 24px;
    font-weight: 500;
  }
  // .circle-chart svg { width:600px; height: 600px;}

  /* .circle-chart { width: 100%; height: 674px; }
.circle-chart .label { text-anchor: middle; width: 100%; font-size: 24px; font-weight: 500; } */

  /* 융합역량지수-04-01 에너지 역량 */
  .step-item-list.per-type > .col-group {
    justify-content: space-between;
    align-items: center;
  }
  .step-item-list.per-type .step-item {
    width: 542px;
  }
  .step-item-list.per-type .step-item:last-child .step-item-num {
    background: #d0ddda;
    color: #155845;
  }
  .step-item-list.per-type .step-item:last-child .green {
    color: #155845;
  }
  .step-item-list.per-type .icon-arrow {
    font-size: 36px;
    color: #aeaeae;
    transform: rotate(-90deg);
  }
  .default-txt.center {
    text-align: center;
  }

  /* 융합역량지수-04-02 에너지 역량 */
  .energy-result-wrap {
    display: flex;
    flex-flow: column;
    gap: 80px;
  }
  .energy-result-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .energy-result-top-title {
    display: flex;
    align-items: center;
    gap: 24px;
  }
  .energy-result-top-title .num {
    width: 64px;
    height: 64px;
    border: 3px solid #aaaaaa;
    background: #fff;
    text-align: center;
    line-height: 58px;
    font-size: 26px;
    font-weight: 500;
    color: #aaaaaa;
    border-radius: 50%;
  }
  .energy-result-top-title .title {
    font-size: 32px;
    font-weight: 600;
    color: #aaaaaa;
    text-transform: uppercase;
  }
  .energy-guage-wrap {
    position: relative;
    width: 960px;
    height: 64px;
    border-radius: 32px;
    border: 3px solid #202020;
    background: #eee;
  }
  .energy-guage {
    position: absolute;
    width: 50%;
    height: 100%;
    top: 0;
    bottom: 0;
  }
  .energy-guage.left {
    right: 0;
    transform: translateX(-100%);
  }
  .energy-guage.right {
    left: 0;
    transform: translateX(100%);
  }
  .energy-guage-bar {
    position: absolute;
    height: 100%;
    top: 0;
    bottom: 0;
    background: #aaaaaa;
  }
  .energy-guage.left .energy-guage-bar {
    right: 0;
    border-top-left-radius: 32px;
    border-bottom-left-radius: 32px;
  }
  .energy-guage.right .energy-guage-bar {
    left: 0;
    border-top-right-radius: 32px;
    border-bottom-right-radius: 32px;
  }
  .energy-guage-num-group {
    width: 100%;
    display: flex;
    justify-content: space-between;
    position: absolute;
    bottom: -32px;
    left: 0;
    right: 0;
  }
  .energy-guage-num {
    position: relative;
    font-size: 24px;
  }
  .energy-guage-num:not(:first-child, :last-child)::after {
    content: "";
    display: block;
    position: absolute;
    width: 0;
    height: 60px;
    border-left: 2px dashed #202020;
    top: -66px;
    left: 50%;
    transform: translateX(-50%);
  }

  .energy-result-wrap .step-item.border {
    border-color: #303030;
  }
  .energy-result-wrap .step-item-num {
    background-color: #aeaeae;
    color: #202020;
    text-transform: uppercase;
    font-size: 32px;
  }

  .energy-result-wrap.left .energy-result-top-title.left .num,
  .energy-result-wrap.right .energy-result-top-title.right .num {
    border-color: #00754a;
    background-color: #00754a;
    color: #fff;
  }
  .energy-result-wrap.left .energy-result-top-title.left .title,
  .energy-result-wrap.right .energy-result-top-title.right .title,
  .energy-result-wrap.left .step-item.left .default-title,
  .energy-result-wrap.right .step-item.right .default-title {
    color: #00754a;
  }
  .energy-result-wrap.left .energy-guage.left .energy-guage-bar,
  .energy-result-wrap.right .energy-guage.right .energy-guage-bar {
    background: #007a10;
  }
  .energy-result-wrap.left .step-item.left,
  .energy-result-wrap.right .step-item.right {
    border-color: #00754a;
  }
  .energy-result-wrap.left .step-item.left .step-item-num,
  .energy-result-wrap.right .step-item.right .step-item-num {
    background-color: #cbe2da;
    color: #00754a;
  }

  .default-txt.dot {
    position: relative;
    padding-left: 20px;
  }
  .default-txt.dot::after {
    content: "";
    display: block;
    position: absolute;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #333;
    left: 0;
    top: 19px;
  }
  .default-txt-s.dot::after {
    width: 4px;
    height: 4px;
    top: 15px;
  }
  .default-txt-l.dot::after {
    top: 21px;
  }
  .default-txt.green.dot::after {
    background: #007a10;
  }

  .energy-result-top-wrap {
    padding: 40px;
    padding-top: 56px;
    border: 3px solid #00754a;
    border-radius: 16px;
  }
  .energy-result-title-wrap {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding-bottom: 36px;
    border-bottom: 3px solid #00754a;
  }
  .energy-result-type {
    font-size: 56px;
    font-weight: bold;
    color: #00754a;
  }
  .energy-result-top-wrap .energy-result-wrap {
    padding: 24px 0 60px;
  }
  .energy-result-top-wrap .energy-result-wrap:not(:last-child) {
    border-bottom: 1px solid #e4e4e4;
  }
  .energy-result-top-wrap .energy-result-wrap:last-child {
    padding-bottom: 24px;
  }
  .energy-result-top-wrap .energy-guage-wrap {
    width: 800px;
  }
  .energy-result-top-wrap .energy-result-top-title {
    width: calc((100% - 800px) / 2);
  }
  .energy-result-top-wrap .energy-result-top-title.right {
    justify-content: flex-end;
  }

  /* 241127 추가 */
  .personality-type-wrap {
    position: relative;
    background: #d9eee6;
    border-radius: 16px;
    padding: 80px 0;
    z-index: 0;
  }
  .personality-type-wrap .line {
    position: absolute;
    width: 961px;
    height: 268px;
    bottom: 102px;
    left: 50%;
    transform: translateX(-50%);
    z-index: -1;
  }
  .personality-type-group {
    gap: 95px;
    justify-content: center;
    align-items: flex-end;
  }
  .personality-type-item {
    display: flex;
    flex-flow: column;
    gap: 16px;
    align-items: center;
  }
  .personality-type-item:nth-child(2n-1) {
    margin-bottom: 180px;
  }
  .personality-type-item .txt-group {
    gap: 32px;
    text-align: center;
  }
  .personality-type-item .txt {
    font-size: 30px;
    line-height: 1.2;
  }
  .personality-type-item .title {
    position: relative;
  }
  .personality-type-item .title::after {
    content: "";
    display: block;
    position: absolute;
    width: 30px;
    height: 5px;
    background: #00754a;
    bottom: -16px;
    left: 50%;
    transform: translate(-50%, 50%);
  }
  .personality-type-item .img {
    width: 240px;
  }

  /* 융합역량지수-04-04 에너지 역량 */
  .balance-chart-wrap {
    position: relative;
    width: 100%;
    height: 270px;
  }
  .balance-chart-bar {
    position: absolute;
    width: 520px;
    height: 84px;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center bottom;
  }
  .balance-chart-bar.left {
    background-image: url(${images.balance_left});
  }
  .balance-chart-bar.right {
    background-image: url(${images.balance_right});
  }
  .balance-chart-bar.center {
    background-image: url(${images.balance_center});
  }
  .balance-chart-core {
    position: absolute;
    width: 180px;
    height: 100%;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
  }
  .balance-chart-group {
    height: 100%;
    justify-content: space-between;
    align-items: flex-end;
    padding: 0 17px;
  }
  .balance-chart {
    width: 160px;
    height: 160px;
    position: relative;
    border-radius: 50%;
    background: #bbbbbb;
  }
  .balance-chart.active {
    background: #007a10;
  }
  .balance-chart-gauge {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }
  .balance-chart-circle {
    position: absolute;
    width: 80px;
    height: 80px;
    background: #fff;
    border-radius: 50%;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    line-height: 80px;
    font-size: 40px;
    font-weight: bold;
    color: #707070;
  }
  .balance-chart.active .balance-chart-circle {
    color: #007a10;
  }
  .balance-chart .title {
    font-size: 26px;
    font-weight: bold;
    color: #707070;
    text-align: center;
    position: absolute;
    bottom: -40px;
    width: 100%;
  }
  .balance-chart.active .title {
    color: #007a10;
  }
  .balance-chart-txt-wrap {
    padding-top: 32px;
    border-top: 3px solid #e4e4e4;
    margin-top: 90px;
  }

  .step-item.active {
    position: relative;
  }
  .step-item.active::after {
    content: "";
    display: block;
    position: absolute;
    width: calc(100% + 64px);
    height: 100%;
    background: #d5e8e1;
    top: 0;
    left: -32px;
    z-index: -1;
  }
  .step-item.active .step-item-num {
    background: #ffffff;
  }
  .step-item.active .default-txt {
    font-weight: bold;
  }

  /* 융합역량지수-05-03-힘의 균형 */
  .prescription {
    position: relative;
    padding-right: 480px;
  }
  .prescription::after {
    content: "";
    display: block;
    clear: both;
    position: absolute;
    bottom: 0px;
    right: 0px;
    width: 350px;
    height: 100%;
    background-image: url(${images.prescription});
    background-size: cover;
    background-repeat: no-repeat;
  }

  /* 융합역량지수-05-05-힘의 균형 */
  .wing-type-section {
    position: relative;
  }
  .page-common.page-25::after {
    content: "";
    display: block;
    clear: both;
    z-index: -1;
    position: absolute;
    bottom: 0px;
    left: 0px;
    width: 1920px;
    height: 62%;
    background: #d9eee6;
  }
  .wing-type-group {
    padding: 24px 0;
    gap: 120px;
    position: relative;
    z-index: 0;
  }
  .wing-type-group:not(:last-child) {
    border-bottom: 2px dashed #00754a;
  }
  .wing-type-group:not(:last-child)::after {
    content: "";
    display: block;
    position: absolute;
    width: 3px;
    height: 100%;
    background: #00754a;
    left: 50%;
    top: 50%;
    transform: translateX(-50%);
    z-index: -1;
  }
  .wing-type-group .num {
    position: absolute;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    border: 3px solid #00754a;
    text-align: center;
    line-height: 58px;
    background: #fff;
    font-size: 24px;
    font-weight: bold;
    color: #00754a;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
  .wing-type-item {
    width: calc(50% - 60px);
  }
  .wing-type-item:first-child {
    align-items: flex-end;
    text-align: right;
  }
  /* 241210 추가 */
  .balance-of-power-img-wrap {
    position: relative;
    .text-wrap {
      z-index: 9;
      position: absolute;
      width: 100%;
      height: 100%;
      .text-item {
        position: absolute;
        * {
          font-size: 25px;
        }
        .tit {
          font-weight: 700;
          margin-bottom: 12px;
        }
        .ul-list {
          position: relative;
          line-height: 1.2em;
          padding-left: 16px;
          margin-top: 3px;
          &::after {
            content: "";
            display: block;
            clear: both;
            position: absolute;
            top: 12px;
            left: 2px;
            width: 5px;
            height: 5px;
            border-radius: 50%;
            background-color: #000;
          }
        }
        &.active {
          * {
            color: #ff5b36;
          }
          .ul-list::after {
            background-color: #ff5b36;
          }
        }

        &.item1 {
          top: 20%;
          right: 0%;
        }
        &.item2 {
          top: 43%;
          right: 0%;
        }
        &.item3 {
          bottom: 22%;
          right: 0%;
        }
        &.item4 {
          bottom: 0%;
          right: 3%;
        }
        &.item5 {
          bottom: 0%;
          left: 13%;
        }
        &.item6 {
          bottom: 20%;
          left: 3%;
        }
        &.item7 {
          top: 43%;
          left: 0%;
        }
        &.item8 {
          top: 21%;
          left: 4%;
        }
        &.item9 {
          top: 0px;
          left: 40%;
        }
      }
    }
    .after-wrap {
      position: relative;
      &::after {
        content: "";
        display: block;
        clear: both;
        z-index: 9;
        position: absolute;
        width: 55px;
        height: 55px;
        border: 4px dashed #ff5b36;
        border-radius: 50%;
      }
      &.num_1::after {
        top: 31.5%;
        right: 35%;
      }
      &.num_2::after {
        top: 47.2%;
        right: 29.5%;
      }
      &.num_3::after {
        top: 64.8%;
        right: 31.8%;
      }
      &.num_4::after {
        top: 77.3%;
        right: 40.5%;
      }
      &.num_5::after {
        top: 77.3%;
        left: 42.4%;
      }
      &.num_6::after {
        top: 64.7%;
        left: 32.3%;
      }
      &.num_7::after {
        top: 47.2%;
        left: 29.6%;
      }
      &.num_8::after {
        top: 31.5%;
        left: 35.6%;
      }
      &.num_9::after {
        top: 23.8%;
        left: 48%;
      }
    }
  }

  /* 융합역량지수-06-01 관계와 소통 분석 */
  .ego-result-item {
    width: 100%;
    height: 135px;
    border-radius: 20px;
    border: 1px solid #e1e1e1;
    background: #fff;
    align-items: center;
    justify-content: center;
  }
  .ego-result-item .icon {
    width: 64px;
    height: 64px;
    object-fit: contain;
    object-position: center;
  }
  .ego-result-chart-wrap {
    align-items: center;
  }
  .ego-result-chart-group {
    width: calc(100% - 260px);
  }
  .ego-result-chart {
    height: 290px;
  }
  .ego-result-score-wrap {
    width: 220px;
    border-radius: 20px;
    border: 3px solid #e4e4e4;
    background: #fff;
  }
  .ego-result-score-item {
    padding: 24px 40px;
  }
  .ego-result-score-item .title {
    font-size: 24px;
    font-weight: 600;
  }
  .ego-result-score-item .txt {
    font-size: 24px;
    font-weight: normal;
  }
  .ego-result-title-group {
    justify-content: space-between;
    padding-left: 153px;
    padding-right: 122px;
  }
  .ego-result-title-group .default-txt {
    color: #707070;
  }
  .ego-result-txt-group {
    justify-content: space-between;
    padding-left: 116px;
    padding-right: 74px;
  }
  .ego-result-txt-group .default-txt-s {
    text-align: center;
    font-size: 24px;
    font-weight: 600;
  }

  /* 융합역량지수-07-01 인생점수 */
  .age-wrap {
    flex-flow: wrap;
  }
  .age-item {
    width: calc((100% - 16px * 3) / 4);
    padding: 20px 30px;
    border: 2px solid #dedede;
    background: #fff;
    border-radius: 20px;
  }
  .age-item .icon {
    width: 130px;
    height: 130px;
  }
  .default-txt-ss {
    font-size: 24px;
    font-weight: normal;
    line-height: 1.2;
  }
  .default-txt.right {
    text-align: right;
  }
  .age-score-wrap {
    gap: 2px;
  }
  .age-score {
    width: calc((100% - 2px * 9) / 10);
    height: 20px;
    border-radius: 10px;
    background: #eee;
  }
  .age-score.active {
    background: #00754a;
  }

  .life-score-section {
    position: relative;
    padding-top: 120px;
    padding-bottom: 710px;
  }
  .life-score-section::after {
    content: "";
    display: block;
    width: 1920px;
    height: calc(100% + 6.5625vw);
    background-color: #d9eee6;
    position: absolute;
    bottom: -6.5625vw;
    z-index: -1;
    left: -180px;
  }
  .life-score-section::before {
    content: "";
    display: block;
    width: calc(100% + 360px);
    height: 645px;
    position: absolute;
    bottom: 0px;
    left: -180px;
    z-index: 0;
    background-size: 1920px;
    background-repeat: no-repeat;
  }
  .life-score-title-wrap {
    margin-bottom: 40px;
    align-items: flex-end;
  }
  .life-score-section.age0::before {
    background-image: url(${images.life_score_section_0});
  }
  .life-score-section.age1::before {
    background-image: url(${images.life_score_section_1});
  }
  .life-score-section.age2::before {
    background-image: url(${images.life_score_section_2});
  }
  .life-score-section.age3::before {
    background-image: url(${images.life_score_section_3});
  }
  .life-score-section.age4::before {
    background-image: url(${images.life_score_section_4});
  }
  .life-score-section.age5::before {
    background-image: url(${images.life_score_section_5});
  }
  .life-score-section.age6::before {
    background-image: url(${images.life_score_section_6});
  }
  .life-score-section.age7::before {
    background-image: url(${images.life_score_section_7});
  }
  .life-score-title-wrap .img {
    width: 56px;
  }
  .life-score-title {
    font-size: 48px;
    font-weight: bold;
    color: #000;
  }
  .life-score-result-wrap {
    padding: 72px;
    border-radius: 20px;
    background: #ffffff;
  }
  .life-score-result-wrap > .row-group {
    width: calc(100% - 480px - 56px);
  }
  .life-score-result-wrap .title-wrap {
    padding-bottom: 56px;
    border-bottom: 2px solid #dedede;
  }
  .life-score-result-wrap .title-wrap .icon {
    width: 130px;
    height: 130px;
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
  }
  .life-score-section.age0 .life-score-result-wrap .title-wrap .icon {
    background-image: url(${images.age_icon_0_on});
  }
  .life-score-section.age1 .life-score-result-wrap .title-wrap .icon {
    background-image: url(${images.age_icon_1_on});
  }
  .life-score-section.age2 .life-score-result-wrap .title-wrap .icon {
    background-image: url(${images.age_icon_2_on});
  }
  .life-score-section.age3 .life-score-result-wrap .title-wrap .icon {
    background-image: url(${images.age_icon_3_on});
  }
  .life-score-section.age4 .life-score-result-wrap .title-wrap .icon {
    background-image: url(${images.age_icon_4_on});
  }
  .life-score-section.age5 .life-score-result-wrap .title-wrap .icon {
    background-image: url(${images.age_icon_5_on});
  }
  .life-score-section.age6 .life-score-result-wrap .title-wrap .icon {
    background-image: url(${images.age_icon_6_on});
  }
  .life-score-section.age7 .life-score-result-wrap .title-wrap .icon {
    background-image: url(${images.age_icon_7_on});
  }
  .life-score-result-wrap .title-wrap .score {
    font-size: 56px;
    font-weight: bold;
    color: #00754a;
  }
  .life-score-result-wrap .txt-group .default-txt {
    position: relative;
  }
  .life-score-result-wrap .txt-group .default-txt:not(:last-child)::after {
    content: "";
    display: block;
    position: absolute;
    width: 1px;
    height: 30px;
    background: #bfbfbf;
    right: -16px;
    top: 50%;
    transform: translate(-50%, -50%);
  }
  .life-score-result-wrap .chart {
    position: relative;
    width: 480px;
    height: 480px;
    border-radius: 50%;
    background: #d9eee6;
  }
  .life-score-result-wrap .chart-txt {
    display: block;
    position: absolute;
    width: 320px;
    height: 320px;
    background: #fff;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    text-align: center;
    line-height: 320px;
    font-size: 72px;
    font-weight: bold;
    color: #00754a;
  }

  /* 융합역량지수-07-02 인생점수 */
  .green-border-box.center {
    text-align: center;
    align-items: center;
    padding: 32px;
  }
  .icon-arrow-down {
    width: 40px;
  }
  .icon-arrow-right {
    transform: rotate(-90deg);
  }
  .icon-arrow-left {
    transform: rotate(90deg);
  }
  .green-tip-box {
    width: 50%;
    padding: 32px 24px;
    background: #d9eee6;
    border-radius: 16px;
    align-items: baseline;
  }
  .green-tip-box.center {
    text-align: center;
    align-items: center;
  }
  .green-tip-box .icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #155845;
    text-align: center;
    line-height: 64px;
    font-size: 26px;
    font-weight: bold;
    color: #fff;
  }
  .green-tip-box .default-txt {
    color: #155845;
  }
  .recons-img {
    width: 480px;
  }

  /* 융합역량지수-08-01 metaQ */
  .result-score.number {
    position: relative;
    margin-bottom: 88px;
  }
  .result-score-num-wrap {
    position: absolute;
    bottom: -40px;
    width: 100%;
    justify-content: space-between;
  }
  .result-score-num-wrap .num {
    font-size: 24px;
    transform: translateX(50%);
  }
  .result-score-num-wrap .num:last-child {
    transform: translateX(0);
  }
  .result-score-item .num {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 64px;
    height: 130px;
    line-height: 24px;
    border: 2px solid #aaa;
    background: #fff;
    text-align: center;
    font-size: 24px;
    font-weight: 600;
    color: #aaa;
    border-radius: 8px;
  }
  .result-score-wrap.type1 .result-score-item.current-score .num {
    border-color: #0077ff;
    color: #fff;
    background: #0077ff;
  }
  .result-score-wrap.type2 .result-score-item.current-score .num {
    border-color: #fcc200;
    color: #fff;
    background: #fcc200;
  }
  .result-score-wrap.type3 .result-score-item.current-score .num {
    border-color: #ff5b36;
    color: #fff;
    background: #ff5b36;
  }
  .result-score-wrap.type4 .result-score-item.current-score .num {
    border-color: #d7262e;
    color: #fff;
    background: #d7262e;
  }

  /* 융합역량지수-08-02-metaQ */
  /* .strategy-1 { background-image: url(${images.growth_strategy_01}); background-repeat: no-repeat; background-size: 408px; background-position: bottom right 20px; } */
  .strategy-1 {
    position: relative;
  }
  .strategy-1::after {
    background-image: url(${images.growth_strategy_01});
  }
  .strategy-2 {
    position: relative;
  }
  /* .strategy-2 { background-image: url(${images.growth_strategy_02}); background-repeat: no-repeat; background-size: 408px; background-position: bottom right 20px; } */
  .strategy-2::after {
    background-image: url(${images.growth_strategy_02});
  }
  .strategy-1::after,
  .strategy-2::after {
    content: "";
    display: block;
    clear: both;
    position: absolute;
    bottom: 0px;
    right: 20px;
    width: 400px;
    height: 315px;
    background-repeat: no-repeat;
    background-size: cover;
  }

  /* 융합역량지수-08-03 나의 강점 */
  .section-top-08 .img {
    width: 258px;
    top: unset;
    bottom: 0;
  }
  .letter-wrap {
    width: 1120px;
    margin: 0 auto;
    background: #fff;
    border-radius: 20px;
    border: solid 3px #eee;
    padding: 0 104px;
    padding-bottom: 560px;
    padding-top: 95px;
    position: relative;
  }
  .letter-wrap::after {
    content: "";
    display: block;
    width: 1280px;
    height: 685px;
    background-image: url(${images.letter_cover});
    background-repeat: no-repeat;
    background-size: 100% 100%;
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
  }
  .letter-wrap::before {
    content: "";
    display: block;
    width: 1885px;
    height: 905px;
    background-image: url(${images.letter_bg});
    background-repeat: no-repeat;
    background-size: 100% 100%;
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
  }
  .letter-title-wrap {
    margin-bottom: 56px;
    text-align: center;
  }
  .letter-sub-title {
    font-size: 30px;
    color: #9acc9a;
  }
  .letter-title {
    font-size: 56px;
    font-weight: bold;
  }
  .letter-item {
    justify-content: space-between;
    padding: 16px 0;
    border-bottom: 2px solid #00754a;
    min-height: 80px;
  }
  .letter-item.min {
    max-width: 1088px;
  }

  /* 융합역량지수-08-04 강점리스트 */
  .check-list {
    flex-flow: wrap;
    gap: 16px;
  }
  .check-item {
    width: calc(((100% - 16px * 4) / 5));
    padding: 22px 0;
    align-items: center;
  }
  .check-icon {
    width: 36px;
    height: 36px;
    border-radius: 4px;
    border: 1px solid #707070;
    background: #fff;
  }
  .check-txt {
    font-size: 30px;
  }

  .input-box {
    width: 100%;
    height: 88px;
    border: 2px solid #c9c9c9;
    background: #fff;
    border-radius: 4px;
    position: relative;
  }
  .input-box .icon {
    font-size: 40px;
    color: #00754a;
    position: absolute;
    left: 24px;
    top: 50%;
    transform: translateY(-50%);
  }
  .example-box {
    padding: 30px;
    border-radius: 8px;
    background: #f5f5f5;
  }
  .example-box .label {
    width: 64px;
    height: 36px;
    border-radius: 4px;
    background: #202020;
    color: #fff;
    text-align: center;
    line-height: 36px;
    font-size: 24px;
    margin-bottom: 8px;
  }

  /* 융합역량지수-08-05 핵심가치 리스트 */
  .check-list-wrap {
    gap: 40px;
    flex-flow: wrap;
  }
  .check-list-wrap .green-border-box {
    width: calc((100% - 80px) / 3);
    padding: 32px;
  }
  .check-list-title {
    height: 60px;
    line-height: 60px;
    border-radius: 30px;
    width: fit-content;
    padding: 0 32px;
    background: #cbe2da;
    font-size: 30px;
    font-weight: bold;
    margin: 0 auto;
  }
  .check-list-wrap .check-list {
    gap: 24px 0;
  }
  .check-list-wrap .check-item {
    width: 50%;
    padding: 0;
  }
  .check-list-wrap .check-item.wide {
    width: 100%;
  }
  .al-en {
    align-items: flex-end;
  }

  /* 융합역량지수-08-06-셀프코칭 */
  .empty-box-wrap {
    align-items: flex-start;
  }
  .empty-box {
    width: calc(100% - 40px);
    height: 188px;
  }
`;

function PageContainer({ resultPageData }) {
  const navigate = useNavigate();

  if (!resultPageData.name) {
    alert(
      "일시적인 오류가 발생했습니다. 문제가 지속될 경우 관리자에게 문의해 주세요."
    );
    navigate("/");
    window.location.reload();
  }

  // Recoil
  const [isPdfDownloadStart, setPdfDownloadStart] =
    useRecoilState(PdfDownloadStartAtom);
  const inspectId = isPdfDownloadStart.inspectId
    ? isPdfDownloadStart.inspectId
    : null;
  const name = isPdfDownloadStart.name ? isPdfDownloadStart.name : null;
  const phone = isPdfDownloadStart.phone ? isPdfDownloadStart.phone : null;

  const setPdfDownload = useSetRecoilState(PdfProgressAtom);

  // 페이지 리스트
  const printPageRefs = Array.from({ length: 41 }, () => React.createRef());

  // pdf 저장
  useEffect(() => {
    const handleDownloadPdf = async () => {
      const element = printPageRefs[0].current;
      if (!element) {
        return;
      }
      // ========== 기본 세팅 ==========
      const pdf = new jsPDF("p", "mm", "a4");

      // A4 사이즈 기준으로 캔버스를 PDF 크기에 맞추기
      const pdfWidth = 210;
      const pdfHeight = 297;
      // 캔버스 크기와 PDF 크기 비율 계산
      const ratio = Math.min(
        pdfWidth / element.offsetWidth,
        pdfHeight / element.offsetHeight
      );

      // 이미지의 너비와 높이를 비율에 맞게 조정
      const imgWidth = element.offsetWidth * ratio;
      const imgHeight = element.offsetHeight * ratio;

      //  ========== 페이지 추가 ==========
      for (let i = 0; i < printPageRefs.length; i++) {
        try {
          const element = printPageRefs[i].current;
          if (!element) {
            alert(
              "네트워크 속도나 기기 성능 문제로 PDF를 생성할 수 없습니다. 안정적인 환경에서 다시 시도해 주세요!"
            );
            navigate("/");
            window.location.reload();

            return;
          }
          const canvas = await html2canvas(element, {
            scale: 1.0,
            useCORS: true,
          });
          const imgData = canvas.toDataURL("image/png");
          if (i > 0) pdf.addPage(); // 첫 페이지는 addPage 필요 없음
          pdf.addImage(
            imgData,
            "JPEG",
            0,
            0,
            imgWidth,
            imgHeight,
            undefined,
            "FAST"
          );

          setPdfDownload(Math.ceil(((i + 1) / printPageRefs.length) * 100));
        } catch (error) {
          console.log(error);
        }
      }
      // const timestamp = Date.now()
      // pdf.save(`insight_pdf_file_${timestamp}.pdf`);

      // ========== 서버로 전송 ==========
      const pdfBlob = pdf.output("blob");

      // 서버로 보낼 FormData 생성
      const formData = new FormData();
      formData.append("id", inspectId);
      formData.append("name", name);
      formData.append("phone", phone);
      formData.append("pdf_file", pdfBlob, `insight_pdf_file_${inspectId}.pdf`); // 파일 이름 지정

      try {
        // 서버로 전송
        await axios.post(
          `${process.env.REACT_APP_API_URL}/inspect/insight/save/pdf`,
          formData
        );

        setPdfDownloadStart((prev) => ({
          ...prev,
          start: false,
        }));

        navigate("/mind-insight-test/done");
      } catch (error) {
        console.error("파일 전송 실패:", error);
        ShowErrorMsg(error);
        window.location.href = "/";
      }
    };

    handleDownloadPdf();
  }, []);

  // ref[3]
  const overviewBalanceOfPowerDataValue = [
    resultPageData.graph_2,
    resultPageData.graph_3,
    resultPageData.graph_4,
    resultPageData.graph_5,
    resultPageData.graph_6,
    resultPageData.graph_7,
    resultPageData.graph_8,
    resultPageData.graph_9,
    resultPageData.graph_1,
  ];
  const overviewBalanceOfPowerMaxValue = Math.max(
    ...overviewBalanceOfPowerDataValue
  );
  const overviewBalanceOfPowerMinValue = Math.min(
    ...overviewBalanceOfPowerDataValue
  );

  // 중앙값 구하기
  const sortedValues = [...overviewBalanceOfPowerDataValue].sort(
    (a, b) => a - b
  ); // 배열을 숫자 순으로 정렬

  // 가운데 값(중앙값) 구하기
  const middleIndex = Math.floor(sortedValues.length / 2);
  const median =
    sortedValues.length % 2 === 0
      ? (sortedValues[middleIndex - 1] + sortedValues[middleIndex]) / 2
      : sortedValues[middleIndex];

  const overviewBalanceOfPowerChartData = {
    labels: [2, 3, 4, 5, 6, 7, 8, 9, 1],
    datasets: [
      {
        label: "My Data",
        data: overviewBalanceOfPowerDataValue,
        fill: false,
        borderColor: "#007a10",
        borderWidth: 1,
        pointRadius: 10,
        pointBorderColor: "#007a10",
        pointBackgroundColor: "#007a10",
      },
    ],
  };

  const overviewBalanceOfPowerChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        offset: true,
        ticks: {
          stepSize: 10,
          font: {
            size: 18,
            weight: 600,
            family: "Pretendard",
          },
          display: false,
        },
        grid: {
          display: true,
          drawOnChartArea: true,
          color: "#ddd",
          drawTicks: true,
        },
      },
      y: {
        beginAtZero: false,
        min: overviewBalanceOfPowerMinValue,
        max: overviewBalanceOfPowerMaxValue,
        ticks: {
          count: 5,
          font: {
            size: 26,
            color: "#707070",
            family: "Pretendard",
          },
          callback: function (value) {
            return Math.round(value); // 소수점 없이 정수로 반올림
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: true,
        color: "#202020",
        font: {
          size: 26,
        },
        align: (context) => {
          const value = context.dataset.data[context.dataIndex];
          return value >= median ? "bottom" : "top";
        },
        clip: false,
      },
    },
    animation: {
      duration: 0,
    },
  };

  // 값 사이 세로선 추가
  const addMiddleGridLines = {
    id: "addMiddleGridLines",
    beforeDraw: (chart) => {
      const ctx = chart.ctx;
      const xAxis = chart.scales.x;
      const yAxis = chart.scales.y;
      const gridColor = chart.config.options.scales.x.grid.color;

      // 기존 tick 사이에 중간 그리드 선 추가
      for (let i = 0; i < xAxis.ticks.length - 1; i++) {
        const x1 = xAxis.getPixelForValue(xAxis.ticks[i].value);
        const x2 = xAxis.getPixelForValue(xAxis.ticks[i + 1].value);
        const middleX = (x1 + x2) / 2;

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(middleX, yAxis.top);
        ctx.lineTo(middleX, yAxis.bottom);
        ctx.lineWidth = 1;
        ctx.strokeStyle = gridColor;
        ctx.stroke();
        ctx.restore();
      }
    },
  };

  const overviewBalanceOfPowerBackgroundPlugin = {
    id: "overviewBalanceOfPower_CanvasBackgroundColor",
    beforeDraw: (chart) => {
      const {
        ctx,
        chartArea: { top, bottom, left, right },
      } = chart;
      const chartHeight = bottom - top;
      const rectHeight = 60; // 원하는 사각형 높이 (픽셀 단위)
      const rectY = top + (chartHeight - rectHeight) / 2; // 차트 중앙에 오도록 y좌표 계산

      ctx.save();
      ctx.fillStyle = "#eeeeee";
      // x: left, y: rectY, width: 전체 너비, height: rectHeight
      ctx.fillRect(left, rectY, right - left, rectHeight);
      ctx.restore();
    },
  };

  // ===== Chart =====

  // ref[12]
  const ref12DataList = [
    {
      name: "언어",
      value: resultPageData.language,
      dataName: "language",
      img: "icon_lang",
    },
    {
      name: "논리수학",
      value: resultPageData.math,
      dataName: "math",
      img: "icon_logic",
    },
    {
      name: "시간·공간",
      value: resultPageData.time,
      dataName: "time",
      img: "icon_space",
    },
    {
      name: "신체운동감각",
      value: resultPageData.body,
      dataName: "body",
      img: "icon_physical",
    },
    {
      name: "음악",
      value: resultPageData.music,
      dataName: "music",
      img: "icon_music",
    },
    {
      name: "대인관계",
      value: resultPageData.relationship,
      dataName: "relationship",
      img: "icon_friendly",
    },
    {
      name: "자기성찰",
      value: resultPageData.self,
      dataName: "self",
      img: "icon_personal",
    },
    {
      name: "자연",
      value: resultPageData.nature,
      dataName: "nature",
      img: "icon_nature",
    },
  ];
  const ref12SortedList = ref12DataList.sort((a, b) => b.value - a.value);
  const ref12TopThree = ref12SortedList.slice(0, 3); // 가장 높은 거 3개
  const ref12BottomTwo = ref12SortedList.slice(-2).reverse(); // 가장 낮은 거 2개

  // ====================

  const isLangTopThree = ref12TopThree.some((item) => item.name === "언어");
  const isMathTopThree = ref12TopThree.some((item) => item.name === "논리수학");
  const isTimeTopThree = ref12TopThree.some(
    (item) => item.name === "시간·공간"
  );
  const isBodyTopThree = ref12TopThree.some(
    (item) => item.name === "신체운동감각"
  );
  const isMusicTopThree = ref12TopThree.some((item) => item.name === "음악");
  const isRelationTopThree = ref12TopThree.some(
    (item) => item.name === "대인관계"
  );
  const isSelfTopThree = ref12TopThree.some((item) => item.name === "자기성찰");
  const isNatureTopThree = ref12TopThree.some((item) => item.name === "자연");

  const isLangBottomTwo = ref12BottomTwo.some((item) => item.name === "언어");
  const isMathBottomTwo = ref12BottomTwo.some(
    (item) => item.name === "논리수학"
  );
  const isTimeBottomTwo = ref12BottomTwo.some(
    (item) => item.name === "시간·공간"
  );
  const isBodyBottomTwo = ref12BottomTwo.some(
    (item) => item.name === "신체운동감각"
  );
  const isMusicBottomTwo = ref12BottomTwo.some((item) => item.name === "음악");
  const isRelationBottomTwo = ref12BottomTwo.some(
    (item) => item.name === "대인관계"
  );
  const isSelfBottomTwo = ref12BottomTwo.some(
    (item) => item.name === "자기성찰"
  );
  const isNatureBottomTwo = ref12BottomTwo.some((item) => item.name === "자연");

  // ref[18]
  const energyTypeInfo = {
    W: {
      name: "폭넓은(W)",
      desc: "에너지가 <br/>외부로 향함",
      img: "icon_w",
    },
    D: {
      name: "깊이 있는(D)",
      desc: "에너지가 <br/>내부로 향함",
      img: "icon_d",
    },
    P: {
      name: "실용적(P)",
      desc: "구체적이고 세부적으로 <br/>정보 인식",
      img: "icon_p",
    },
    I: {
      name: "통찰적(I)",
      desc: "예리하게 관찰하여 <br/>정보 인식",
      img: "icon_i",
    },
    L: {
      name: "논리 중심(L)",
      desc: "사실을 중심으로 <br/>객관적인 의사결정",
      img: "icon_l",
    },
    M: {
      name: "의미 중심(M)",
      desc: "관계와 가치를 <br/>중심으로 의사결정",
      img: "icon_m",
    },
    S: {
      name: "체계적(S)",
      desc: "계획적이고 규칙적인 <br/>생활양식 선호",
      img: "icon_s",
    },
    C: {
      name: "포용적(C)",
      desc: "유연하고 개방적인 <br/>생활양식 선호",
      img: "icon_c",
    },
  };
  const isWDSame = Math.abs(resultPageData.W - resultPageData.D) < 5;
  const isPISame = Math.abs(resultPageData.P - resultPageData.I) < 5;
  const isLMSame = Math.abs(resultPageData.L - resultPageData.M) < 5;
  const isSCSame = Math.abs(resultPageData.S - resultPageData.C) < 5;

  const selectWD = resultPageData.selectWD;
  const selectPI = resultPageData.selectPI;
  const selectLM = resultPageData.selectLM;
  const selectSC = resultPageData.selectSC;
  const selectedEnergyTypeArr = [selectWD, selectPI, selectLM, selectSC]; // 선택된 값을 배열로 만듦
  const energyTypeResult = selectedEnergyTypeArr.join("");

  // ref[21]
  const balanceOfPower = [
    {
      number: 1,
      title: "완벽을 추구하는 올곧은 사람",
      desc: [
        "1W2 사회변화를 주장하는 사람",
        "1W9 이상적인 사회를 실현하는 사람",
      ],
    },
    {
      number: 2,
      title: "친절한 조력가",
      desc: [
        "2W1 봉사할 줄 아는 사람",
        "2W3 주인 또는 안주인 역할을<br/>하는 사람",
      ],
    },
    {
      number: 3,
      title: "성공을 향해가는 열정적 야심가",
      desc: ["3W2 매력적인 사람", "3W4 전문성을 지닌 사람"],
    },
    {
      number: 4,
      title: "특별함을 지향하는 예술가",
      desc: ["4W3 기품있는 사람", "4W5 보헤미안 같은 자유를 꿈꾸는 사람"],
    },
    {
      number: 5,
      title: "관찰력이 뛰어난 연구가",
      desc: ["5W6 문제해결에 능한 사람", "5W4 관습에 저항하는 사람"],
    },
    {
      number: 6,
      title: "충직한 보안관",
      desc: ["6W7 친구가 되어 주는 사람", "6W5 분석 , 준비 , 방어하는 사람"],
    },
    {
      number: 7,
      title: "즐거운 모험가",
      desc: ["7W8 현실적인 감각이 있는 사람", "7W6 만능 재주꾼인 사람"],
    },
    {
      number: 8,
      title: "자신감 넘치는 도전가",
      desc: ["8W9 든든하고 편안한 사람", "8W7 독립적인 성향이 강한 사람"],
    },
    {
      number: 9,
      title: "조화를 이루는 평화주의자",
      desc: ["9W8 중재할 능력이 있는 사람", "9W1 원대한 이상을 가진 사람"],
    },
  ];

  // chart1
  const chartRef = useRef();

  useEffect(() => {
    const width = 674;
    const height = 674;

    const topThreeColor = { bgColor: "#E5F1EC", borderColor: "#0C7B53" };
    const bottomTwoColor = { bgColor: "#FCEDEE", borderColor: "#E0585D" };
    const commonColor = { bgColor: "#F2F2F2", borderColor: "#838383" };

    const data = {
      name: "root",
      children: [
        {
          name: "음악",
          value: resultPageData.music,
          color: isMusicTopThree
            ? topThreeColor.bgColor
            : isMusicBottomTwo
            ? bottomTwoColor.bgColor
            : commonColor.bgColor,
          borderColor: isMusicTopThree
            ? topThreeColor.borderColor
            : isMusicBottomTwo
            ? bottomTwoColor.borderColor
            : commonColor.borderColor,
        },
        {
          name: "신체운동",
          value: resultPageData.body,
          color: isBodyTopThree
            ? topThreeColor.bgColor
            : isBodyBottomTwo
            ? bottomTwoColor.bgColor
            : commonColor.bgColor,
          borderColor: isBodyTopThree
            ? topThreeColor.borderColor
            : isBodyBottomTwo
            ? bottomTwoColor.borderColor
            : commonColor.borderColor,
        },
        {
          name: "논리수학",
          value: resultPageData.math,
          color: isMathTopThree
            ? topThreeColor.bgColor
            : isMathBottomTwo
            ? bottomTwoColor.bgColor
            : commonColor.bgColor,
          borderColor: isMathTopThree
            ? topThreeColor.borderColor
            : isMathBottomTwo
            ? bottomTwoColor.borderColor
            : commonColor.borderColor,
        },
        {
          name: "시간·공간",
          value: resultPageData.time,
          color: isTimeTopThree
            ? topThreeColor.bgColor
            : isTimeBottomTwo
            ? bottomTwoColor.bgColor
            : commonColor.bgColor,
          borderColor: isTimeTopThree
            ? topThreeColor.borderColor
            : isTimeBottomTwo
            ? bottomTwoColor.borderColor
            : commonColor.borderColor,
        },
        {
          name: "언어",
          value: resultPageData.language,
          color: isLangTopThree
            ? topThreeColor.bgColor
            : isLangBottomTwo
            ? bottomTwoColor.bgColor
            : commonColor.bgColor,
          borderColor: isLangTopThree
            ? topThreeColor.borderColor
            : isLangBottomTwo
            ? bottomTwoColor.borderColor
            : commonColor.borderColor,
        },
        {
          name: "대인관계",
          value: resultPageData.relationship,
          color: isRelationTopThree
            ? topThreeColor.bgColor
            : isRelationBottomTwo
            ? bottomTwoColor.bgColor
            : commonColor.bgColor,
          borderColor: isRelationTopThree
            ? topThreeColor.borderColor
            : isRelationBottomTwo
            ? bottomTwoColor.borderColor
            : commonColor.borderColor,
        },
        {
          name: "자기성찰",
          value: resultPageData.self,
          color: isSelfTopThree
            ? topThreeColor.bgColor
            : isSelfBottomTwo
            ? bottomTwoColor.bgColor
            : commonColor.bgColor,
          borderColor: isSelfTopThree
            ? topThreeColor.borderColor
            : isSelfBottomTwo
            ? bottomTwoColor.borderColor
            : commonColor.borderColor,
        },
        {
          name: "자연",
          value: resultPageData.nature,
          color: isNatureTopThree
            ? topThreeColor.bgColor
            : isNatureBottomTwo
            ? bottomTwoColor.bgColor
            : commonColor.bgColor,
          borderColor: isNatureTopThree
            ? topThreeColor.borderColor
            : isNatureBottomTwo
            ? bottomTwoColor.borderColor
            : commonColor.borderColor,
        },
      ],
    };

    const pack = (data) =>
      d3
        .pack()
        .size([width - 2, height - 2])
        .padding(3)(d3.hierarchy(data).sum((d) => d.value));

    const root = pack(data);

    // Select the SVG container
    const svg = d3
      .select(chartRef.current)
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("style", "width: 100%; height: 100%; display: block;");

    const node = svg
      .selectAll("g")
      .data(root.descendants())
      .join("g")
      .attr("transform", (d) => `translate(${d.x},${d.y})`);

    node
      .append("circle")
      .attr("r", (d) => d.r)
      .attr("fill", (d) => d.data.color || "none")
      .attr("stroke", (d) => d.data.borderColor)
      .attr("stroke-width", 2);

    node
      .filter((d) => d.depth !== 0)
      .append("text")
      .attr("class", "label")
      .attr("dy", "0.3em")
      .selectAll("tspan")
      .data((d) => [d.data.name, `${d.data.value}%`] || "")
      .join("tspan")
      .attr("x", "0")
      .attr("y", "-0.2em")
      .attr("dy", (d, i) => (i === 0 ? "0em" : "1.2em")) // 첫 번째 줄은 0em, 두 번째 줄은 1.2em
      .text((d) => d);
  }, []);

  // chart 2
  const data = {
    labels: [
      "음악",
      "신체운동",
      "논리수학",
      "시간·공간",
      "언어",
      "대인관계",
      "자기성찰",
      "자연",
    ],
    datasets: [
      {
        label: "",
        data: [
          resultPageData.music,
          resultPageData.body,
          resultPageData.math,
          resultPageData.time,
          resultPageData.language,
          resultPageData.relationship,
          resultPageData.self,
          resultPageData.nature,
        ],
        fill: true,
        backgroundColor: "rgba(0, 117, 74, 0.2)",
        borderColor: "#00754a",
        borderWidth: 3,
      },
    ],
  };

  const options = {
    scales: {
      r: {
        min: 0, // 최소값 설정
        max: 100, // 최대값 설정
        angleLines: {
          display: true,
        },
        ticks: {
          stepSize: 20,
          font: {
            size: 18,
          },
        },
        pointLabels: {
          font: {
            size: 26,
            family: "Pretendard",
          },
        },
      },
    },
    elements: {
      line: {
        borderWidth: 3,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  // chart 3
  const dataValues = [
    resultPageData.JC,
    resultPageData.EC,
    resultPageData.MR,
    resultPageData.FE,
    resultPageData.AE,
  ];

  const colors = dataValues.map((value) => {
    if (value < 20) {
      return "#0077ff";
    } else if (value > 35) {
      return "#d7262e";
    } else {
      return "#007a10";
    }
  });

  const backgroundPlugin = {
    id: "customCanvasBackgroundColor",
    beforeDraw: (chart) => {
      const {
        ctx,
        chartArea: { top, bottom, left, right },
        scales: { y },
      } = chart;
      ctx.save();
      ctx.fillStyle = "#eeeeee";
      ctx.fillRect(
        left,
        y.getPixelForValue(35),
        right - left,
        y.getPixelForValue(20) - y.getPixelForValue(35)
      );
      ctx.restore();
    },
  };

  const chartData = {
    labels: [
      "JC\n(판단&통제)",
      "EC\n(허용&보호)",
      "MR\n(이성&합리)",
      "FE\n(자율&표현)",
      "AE\n(순응&의존)",
    ],
    datasets: [
      {
        label: "My Data",
        data: dataValues,
        fill: false,
        borderColor: "#007a10",
        borderWidth: 1,
        pointRadius: 10,
        pointBorderColor: colors,
        pointBackgroundColor: colors,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        offset: true,
        ticks: {
          stepSize: 20,
          font: {
            size: 18,
            weight: 600,
            family: "Pretendard",
          },
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        min: 0,
        max: 50,
        ticks: {
          stepSize: 10,
          font: {
            size: 26,
            color: "#707070",
            family: "Pretendard",
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: true,
        color: "#202020",
        font: {
          size: 26,
        },
        align: (context) => {
          const value = context.dataset.data[context.dataIndex];
          return value >= 35 ? "bottom" : "top"; // 35 이상이면 아래, 미만이면 위
        },
        // align: 'bottom',
        clip: false, // 레이블이 짤리지 않도록 설정
      },
    },
  };

  // chart 4
  const chartValue2 = resultPageData.life_average;
  const chartData2 = {
    datasets: [
      {
        data: [chartValue2, 100 - chartValue2],
        backgroundColor: ["#00754a", "#ffffff00"],
        borderWidth: 0,
        borderRadius: 40,
      },
    ],
  };

  const chartOptions2 = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "66.67%",
    hover: { mode: null },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
      datalabels: {
        display: false, // 데이터 레이블 플러그인 비활성화
      },
    },
    animation: {
      duration: 0, // 애니메이션 비활성화
    },
  };
  // ===== chart End =====

  // 데이터 계산
  const Ref3unrestScoreType =
    resultPageData.unrest.score <= 30
      ? "type1"
      : resultPageData.unrest.score <= 40
      ? "type2"
      : resultPageData.unrest.score <= 50
      ? "type3"
      : "type4";
  const Ref3unrestDetailText = [
    {
      title:
        '당신은 <span class="color">정상적인 수준의 불안</span>을 경험하고 있는 상태입니다.',
      desc: "위험이나 고통, 공포, 스트레스 상황 등에서는 누구나 일정한 정도의 불안을 경험합니다. 정상적인 불안 반응은 앞으로 일어날 일들을 대비하고, 위험 상황에 적절하게 대응하기 위해 필요한 감정입니다. <br/><br/>다만, 나는 주로 어떤 상황에서 불안을 느끼는지 의식적으로 점검해 볼 필요가 있습니다. 이번 기회에 불안과 공포, 공황의 차이를 이해하고 정상적인 불안과 병적인 불안에 대해 살펴보면서 나와 다른 주변 사람들을 조금이나마 더 잘 이해할 수 있는 계기가 되기를 바랍니다. ",
    },
    {
      title:
        '당신은 <span class="color">가벼운 정도의 불안</span>을 경험하고 있는 상태입니다.',
      desc: "위험이나 고통, 공포, 스트레스 상황 등에서는 누구나 일정한 정도의 불안을 경험합니다. 정상적인 불안 반응은 앞으로 일어날 일들을 대비하고, 위험 상황에 적절하게 대응하기 위해 필요한 감정입니다. 다만, 나는 주로 어떤 상황에서 불안을 느끼는지 의식적으로 점검해 볼 필요가 있습니다. 불안은, 불만과 함께 유발되는 정서적 반응입니다. 자신 안에 해소되지 않은 불만의 지점을 찾아보시길 권합니다.  <br/><br/>또한, 이번 기회에 불안과 공포, 공황의 차이를 이해하고 정상적인 불안과 병적인 불안에 대해 살펴보면서 나와 다른 주변 사람들을 조금이나마 더 잘 이해할 수 있는 계기가 되기를 바랍니다.",
    },
    {
      title:
        '당신은 <span class="color">상당한 정도의 불안</span>을 경험하고 있는 상태입니다.',
      desc: "위험이나 고통, 공포, 스트레스 상황 등에서는 누구나 일정한 정도의 불안을 경험합니다. 정상적인 불안 반응은 앞으로 일어날 일들을 대비하고, 위험 상황에 적절하게 대응하기 위해 필요한 감정입니다. 그러나 스트레스 상황에서 불안 공포에 압도되거나 과도한 반응으로 인해 일상생활이나 업무처리에 지장이 생긴다면 병적인 상태라고 볼 수 있습니다. 전문가와의 상담을 통해 자신의 불안 정서를 점검하고 대응 방안을 찾아보시길 권합니다.",
    },
    {
      title:
        '당신은 <span class="color">심한 불안</span>을 경험하고 있는 상태입니다. ',
      desc: "위험이나 고통, 공포, 스트레스 상황 등에서는 누구나 일정한 정도의 불안을 경험합니다. 정상적인 불안 반응은 앞으로 일어날 일들을 대비하고, 위험 상황에 적절하게 대응하기 위해 필요한 감정입니다. <br/><br/>그러나 스트레스 상황에서 불안 공포에 압도되거나 과도한 반응으로 인해 일상생활이나 업무처리에 지장이 생긴다면 병적인 상태라고 볼 수 있습니다. 빠른 시일 내에 전문가를 찾아 도움받으시길 권합니다.",
    },
  ];
  const Ref3selectDetailText =
    Ref3unrestScoreType === "type1"
      ? Ref3unrestDetailText[0]
      : Ref3unrestScoreType === "type2"
      ? Ref3unrestDetailText[1]
      : Ref3unrestScoreType === "type3"
      ? Ref3unrestDetailText[2]
      : Ref3unrestDetailText[3];
  const Ref3PagesubText =
    "당신은 공황발작을 경험했을 가능성이 높습니다. 갑작스럽게 호흡곤란이나 심장마비와 같은 응급상황으로 느껴지는 극심한 불안 증상이 발작(attack)처럼 경험되는 상태를 말합니다. 공황발작 증상은 보통 갑작스럽게 발생하여 10분 안에 최고조에 이릅니다. 이 증상이 한 달 이상 지속하는 경우에는 전문의의 도움을 받아야 합니다. ";

  // ref[4]
  const Ref4ScoreType =
    resultPageData.melancholy <= 30
      ? "type1"
      : resultPageData.melancholy <= 40
      ? "type2"
      : resultPageData.melancholy <= 50
      ? "type3"
      : "type4";
  const Ref4DetailText = [
    {
      title:
        '당신은 <span class="color">정상적인 수준의 우울</span>을 경험하고 있는 상태입니다.',
      desc: "살아가면서 누구나 위기, 실패, 상실 등을 경험하고 이때에는 좌절감을 느끼고 자신감이 떨어지는 등의 감정변화를 경험합니다. 이와 같은 우울감은 누구나 경험할 수 있는 정서적 반응 중의 하나이며 보통 수준의 우울감은 시간이 지나면 서서히 회복됩니다. <br/><br/>다만, 나는 주로 어떤 상황에서 우울을 느끼는지 의식적으로 점검해 볼 필요가 있습니다. 우울은, 신경전달물질과 호르몬, 생체 리듬 등의 변화와 관련 있는 생화학적 요인, 스트레스 상황 등과 같은 환경적 요인, 유전적 요인 등에 의해 발생할 수 있습니다. 이번 기회에 우울한 기분과 우울증의 차이에 대해 살펴보면서 나와 다른 주변 사람들을 조금이나마 더 잘 이해할 수 있는 계기가 되기를 바랍니다. ",
    },
    {
      title:
        '당신은 <span class="color">가벼운 정도의 우울</span>을 경험하고 있는 상태입니다. ',
      desc: "살아가면서 누구나 위기, 실패, 상실 등을 경험하고 이때에는 좌절감을 느끼고 자신감이 떨어지는 등의 감정변화를 경험합니다. 이와 같은 우울감은 누구나 경험할 수 있는 정서적 반응 중의 하나이며 보통 수준의 우울감은 시간이 지나면 서서히 회복됩니다. <br/><br/>다만, 나는 주로 어떤 상황에서 우울을 느끼는지 의식적으로 점검해 볼 필요가 있습니다. 우울은, 신경전달물질과 호르몬, 생체 리듬 등의 변화와 관련 있는 생화학적 요인, 스트레스 상황 등과 같은 환경적 요인, 유전적 요인 등에 의해 발생할 수 있습니다. ",
    },
    {
      title:
        '당신은 <span class="color">상당한 정도의 우울</span>을 경험하고 있는 상태입니다. ',
      desc: "살아가면서 누구나 위기, 실패, 상실 등을 경험하고 이때에는 좌절감을 느끼고 자신감이 떨어지는 등의 감정변화를 경험합니다. 이와 같은 우울감은 학력이나 지위, 경제적 수준 등과 관계없이 누구나 경험할 수 있는 정서적 반응 중의 하나이며 보통, 우울감은 시간이 지나면 서서히 회복됩니다. 그러나 일반적인 수준의 우울감을 넘어 우울증을 겪는 사람들은 대부분 의욕이 없고 무기력, 무감동하며 자신을 무능하고 무가치하게 느낍니다. 이런 상태가 지속 되면서 업무수행에 지장이 생기고 일상생활이 어려워집니다. 전문가와의 상담을 통해 자신의 우울 정서를 점검하고 대응 방안을 찾아보시길 권합니다. ",
    },
    {
      title:
        '당신은 <span class="color">심한 우울</span>을 경험하고 있는 상태입니다. ',
      desc: "살아가면서 누구나 위기, 실패, 상실 등을 경험하고 이때에는 좌절감을 느끼고 자신감이 떨어지는 등의 감정변화를 경험합니다. 이와 같은 우울감은 학력이나 지위, 경제적 수준 등과 관계없이 누구나 경험할 수 있는 정서적 반응 중의 하나이며 보통, 우울감은 시간이 지나면 서서히 회복됩니다. 그러나 일반적인 수준의 우울감을 넘어 우울증을 겪는 사람들은 대부분 의욕이 없고 무기력, 무감동하며 자신을 무능하고 무가치하게 느낍니다. 이런 상태가 지속 되면서 업무수행에 지장이 생기고 일상생활이 어려워집니다. 빠른 시일 내에 전문가를 찾아 도움 받으시길 권합니다. ",
    },
  ];
  const Ref4selectDetailText =
    Ref4ScoreType === "type1"
      ? Ref4DetailText[0]
      : Ref4ScoreType === "type2"
      ? Ref4DetailText[1]
      : Ref4ScoreType === "type3"
      ? Ref4DetailText[2]
      : Ref4DetailText[3];

  // ref[7]
  const Ref7ScoreType =
    resultPageData.anger.score <= 30
      ? "type1"
      : resultPageData.anger.score <= 40
      ? "type2"
      : resultPageData.anger.score <= 50
      ? "type3"
      : "type4";
  const Ref7DetailText = [
    {
      title:
        '당신은 <span class="color">정상적인 수준의 분노</span>를 경험하고 있는 상태입니다.',
      desc: "살아가면서 우리는 “누구 때문에 화가 났다”고 말하는 경우가 많습니다. 누군가의 행동이나 어떤 상황 때문에 화가 난 것처럼 보이지만, 실제로는 우리의 기대가 충족되지 않았을 때 분노하는 경우가 많습니다. 분노는 우리가 상황을 어떻게 해석하는지, 무엇을 기대하는지에 따라 달라질 수 있습니다. 분노 뒤에 숨겨진 진짜 이유를 이해하려고 노력한다면 보다 평화로운 마음을 가질 수 있고 안정적인 생활을 할 수 있습니다. 쉽게 분노하는 타인을 대할 때에도 이와 같은 방식으로 분노 뒤에 숨겨진 진짜 이유를 보려는 노력이 필요합니다. ",
    },
    {
      title:
        '당신은 <span class="color">가벼운 정도의 분노</span>를 경험하고 있는 상태입니다. ',
      desc: "살아가면서 우리는 “누구 때문에 화가 났다”고 말하는 경우가 많습니다. 누군가의 행동이나 어떤 상황 때문에 화가 난 것처럼 보이지만, 실제로는 우리의 기대가 충족되지 않았을 때 분노하는 경우가 많습니다. 분노는 우리가 상황을 어떻게 해석하는지, 무엇을 기대하는지에 따라 달라질 수 있습니다. 분노 뒤에 숨겨진 진짜 이유를 이해하려고 노력한다면 보다 평화로운 마음을 가질 수 있고 안정적인 생활을 할 수 있습니다. 쉽게 분노하는 타인을 대할 때에도 이와 같은 방식으로 분노 뒤에 숨겨진 진짜 이유를 보려는 노력이 필요합니다.",
    },
    {
      title:
        '당신은 <span class="color">상당한 정도의 분노</span>를 경험하고 있는 상태입니다. ',
      desc: "살아가면서 우리는 “누구 때문에 화가 났다”고 말하는 경우가 많습니다. 누군가의 행동이나 어떤 상황 때문에 화가 난 것처럼 보이지만, 실제로는 우리의 기대가 충족되지 않았을 때 분노하는 경우가 많습니다. 분노는 우리가 상황을 어떻게 해석하는지, 무엇을 기대하는지에 따라 달라질 수 있습니다. 분노 뒤에 숨겨진 진짜 이유를 이해할 수 있어야 합니다. 분노는 보편적인 인간의 감정이지만 치미는 분노를 참지 못하고 항상 어떤 방식으로든 표출하는 사람들은 대인관계의 어려움을 겪을 가능성이 있습니다. 이를 극복하기 위해 전문가의 도움을 받는 것이 좋겠습니다. ",
    },
    {
      title:
        '당신은 <span class="color">심한 분노</span>를 경험하고 있는 상태입니다. ',
      desc: "누군가의 행동이나 어떤 상황 때문에 화가 난 것처럼 보이지만, 실제로는 우리의 기대가 충족되지 않았을 때 분노하는 경우가 많습니다. 분노는 우리가 상황을 어떻게 해석하는지, 무엇을 기대하는지에 따라 달라질 수 있습니다. 분노 뒤에 숨겨진 진짜 이유를 이해할 수 있어야 합니다. 일반적인 수준의 분노를 넘어, 치미는 분노를 참지 못하고 항상 어떤 방식으로든 표출하는 사람들은 대인관계의 어려움을 겪을 가능성이 있습니다. 당신은 거의 항상 화가 나 있거나, 언제든 화를 낼 준비가 되어 있는 상태로 보일 수 있습니다. 이런 상태가 지속되면 업무수행에 지장이 생기고 원만한 대인관계를 통한 일상생활이 어려워집니다. 빠른 시일 내에 전문가를 찾아 도움받으시길 권합니다. ",
    },
  ];
  const Ref7selectDetailText =
    Ref7ScoreType === "type1"
      ? Ref7DetailText[0]
      : Ref7ScoreType === "type2"
      ? Ref7DetailText[1]
      : Ref7ScoreType === "type3"
      ? Ref7DetailText[2]
      : Ref7DetailText[3];
  const Ref7PagesubText =
    "폭력이 동반될 수도 있는 폭발적 분노 표현을 특징으로 하는 행동 장애로, 별로 중요하지 않은 사건에 대해서도 상황에 맞지 않게 분노를 폭발하는 증상을 보입니다. 분노를 표현하는 것을 문제해결의 가장 효과적인 방법으로 여겨 이와 같은 행동을 반복적으로 하는 특징이 있습니다. 호르몬 분비의 이상, 뇌 기능의 이상, 학대 경험 등과 같은 환경적 요인 등이 복합적으로 작용하여 발생할 수 있습니다. 정확히 진단하기 위해서는 정신건강의학 전문가의 도움이 필요합니다. ";

  // ref[9]
  const Ref9ScoreType =
    resultPageData.digital <= 25
      ? "type1"
      : resultPageData.digital <= 50
      ? "type2"
      : "type3";
  const Ref9DetailText = [
    {
      title:
        '당신은 <span class="color">일반적인 수준의 디지털 의존도</span> 상태입니다.',
      desc: "스마트 기기가 보편화된 사회에서 사람들이 온라인 상태에 있는 평균시간은 점차 증가하고 있습니다. 그러나 당신은 다소 장시간 온라인을 해도 마음만 먹으면 인터넷 사용을 스스로 통제할 수 있는 정도입니다. 학업이나 업무수행, 일상생활에서의 자기 행동 관리에 문제가 없습니다. 하지만 누구나 ‘실제의 나’와 ‘되고 싶은 나’의 모습을 혼동할 가능성이 있습니다. 의식적으로 나의 행동 패턴을 구체적이고 현실적인 방식으로 파악할 필요가 있습니다.",
    },
    {
      title:
        '당신은 <span class="color">잠재적 위험군 수준의 디지털 의존도</span> 상태입니다. ',
      desc: "스마트 기기가 보편화된 사회에서 사람들이 온라인 상태에 있는 평균시간은 점차 증가하고 있습니다. 당신은 인터넷 사용으로 크고 작은 문제들을 경험하고 있으며, 디지털 의존 현상이 실생활에도 부정적 영향을 미치는 상태입니다. 그러나 본인은 ‘이 정도는 문제 되지 않는다’고 생각할 수 있습니다. 지금과 같은 상황이 지속될 경우 정서적, 학습적, 업무적으로 문제가 발생할 수 있는 위험이 있으므로 새로운 생활습관 형성과 자기조절 능력 키우기에 노력해야 합니다. ",
    },
    {
      title:
        '당신은 <span class="color">고위험군 수준의 디지털 의존도</span> 상태입니다. ',
      desc: "인터넷 사용으로 인해 수면 시간도 매우 줄어들고 현실 생활에서도 인터넷에 접속하고 있는 듯한 착각을 하기도 하며 일상생활에서 장애 증상을 보일 수 있습니다. 대게 자신이 인터넷중독이라고 느끼며, 학업 곤란, 업무수행능력 저하, 심리적 불안정감, 우울한 기분, 대인관계 어려움 등을 겪습니다. 자기조절에 심각한 어려움을 보이며 충동성도 높은 편입니다. 인터넷중독 등 디지털 의존도 관련 상담 및 회복기관의 도움이 필요한 상황입니다. ",
    },
  ];
  const Ref9selectDetailText =
    Ref9ScoreType === "type1"
      ? Ref9DetailText[0]
      : Ref9ScoreType === "type2"
      ? Ref9DetailText[1]
      : Ref9DetailText[2];

  const Ref9StressScoreType =
    resultPageData.stress <= 35
      ? "type1"
      : resultPageData.stress <= 50
      ? "type2"
      : resultPageData.stress <= 70
      ? "type3"
      : "type4";
  const Ref9StressDetailText = [
    {
      title:
        '당신은 <span class="color">일반적인 수준의 스트레스</span> 상태입니다.',
      desc: "스트레스(Stress)의 어원은 '팽팽하게 조이다'라는 뜻의 라틴어 스트링게르(Stringer)로 알려져 있습니다. 물리학 분야에서 사용하기 시작했으나 20세기 들어 인체와의 상관관계가 연구되면서 그 개념이 확장되었습니다. 일반적으로 스트레스를 일으키는 요인은 업무와 가족, 대인관계 등에서 발생하는 ‘사회적 요인’, 질병이나 노화, 호르몬 변화 등에 의해 생기는 ‘생리적 요인’, 과로나 수면 부족, 한정된 공간 등과 같은 ‘환경적 요인’, 성격적 특징이나 자존감과 의존증 등에 의한 ‘심리적 요인’ 등이 있습니다. ‘스트레스’라고 하면 주로 그 요인이 무엇인지를 찾는 데에만 집중하지만, 스트레스 요인과 이에 대한 신체적 반응까지를 합한 의미입니다. 즉, 외부 자극이나 변화에 대한 개인의 신체적, 정신적, 행동적 반응 또는 적응을 의미하는 것으로 폭넓은 이해가 필요합니다. ",
    },
    {
      title:
        '당신은 <span class="color">초기단계 수준의 스트레스</span> 상태입니다. ',
      desc: "스트레스(Stress)의 어원은 '팽팽하게 조이다'라는 뜻의 라틴어 스트링게르(Stringer)로 알려져 있습니다. 물리학 분야에서 사용하기 시작했으나 20세기 들어 인체와의 상관관계가 연구되면서 그 개념이 확장되었습니다. 일반적으로 스트레스를 일으키는 요인은 업무와 가족, 대인관계 등에서 발생하는 ‘사회적 요인’, 질병이나 노화, 호르몬 변화 등에 의해 생기는 ‘생리적 요인’, 과로나 수면 부족, 한정된 공간 등과 같은 ‘환경적 요인’, 성격적 특징이나 자존감과 의존증 등에 의한 ‘심리적 요인’ 등이 있습니다. 초기단계 수준의 스트레스를 경험하고 있는 당신은 일상생활에서의 지장은 없겠으나 자신의 감정과 건강을 주시하고 의식적으로 관리하시길 권합니다. ",
    },
    {
      title:
        '당신은 <span class="color">중증 수준의 스트레스</span> 상태입니다.',
      desc: "스트레스(Stress)의 어원은 '팽팽하게 조이다'라는 뜻의 라틴어 스트링게르(Stringer)로 알려져 있습니다. 물리학 분야에서 사용하기 시작했으나 20세기 들어 인체와의 상관관계가 연구되면서 그 개념이 확장되었습니다. ‘스트레스’라고 하면 주로 그 요인이 무엇인지를 찾는 데에만 집중하지만, 스트레스 요인과 이에 대한 신체적 반응까지를 합한 의미입니다. 즉, 외부 자극이나 변화에 대한 개인의 신체적, 정신적, 행동적 반응 또는 적응을 의미하는 것으로 폭넓은 이해가 필요합니다. 중간 정도의 지수는 일상생활에서 어느 정도의 문제를 경험하고 있음을 시사합니다. 스트레스 관리의 목표는 모든 스트레스를 없애는 것이 아닙니다. 스트레스는 때로 유용한 자극이 되어 최고의 실적을 내는데 동기부여 기능을 하기도 합니다. 삶의 질과 활력을 유지하면서 스트레스의 악영향을 제한하는 것, 즉 스트레스가 너무 많지도 적지도 않은 최적의 상태를 유지하기 위해 꾸준히 노력하고 관리하는 자세가 중요합니다.",
    },
    {
      title:
        '당신은 <span class="color">매우 심각한 수준의 스트레스</span> 상태입니다. ',
      desc: "높은 지수는 현재 상당한 문제적 상황을 경험하고 있음을 나타내며, 이는 신체적, 정서적 건강에 부정적인 영향을 미칠 수 있습니다. 스트레스 관리의 목표는 모든 스트레스를 없애는 것이 아닙니다. 스트레스는 때로 유용한 자극이 되어 최고의 실적을 내는데 동기부여 기능을 하기도 합니다. 삶의 질과 활력을 유지하면서 스트레스의 악영향을 제한하는 것, 즉 스트레스가 너무 많지도 적지도 않은 최적의 상태를 유지하기 위해 꾸준히 노력하고 관리하는 자세가 중요합니다. 그러나 당신은 이미 높은 수준의 스트레스를 경험하고 있습니다. 이 경우 적극적으로 전문가의 도움을 구하고, 관리 전략을 개발하여 삶의 질을 개선하시길 권합니다. ",
    },
  ];
  const Ref9StressSelectDetailText =
    Ref9StressScoreType === "type1"
      ? Ref9StressDetailText[0]
      : Ref9StressScoreType === "type2"
      ? Ref9StressDetailText[1]
      : Ref9StressScoreType === "type3"
      ? Ref9StressDetailText[2]
      : Ref9StressDetailText[3];

  // ref[10]
  const Ref10ScoreType =
    resultPageData.burnout <= 15
      ? "type1"
      : resultPageData.burnout <= 35
      ? "type2"
      : resultPageData.burnout <= 60
      ? "type3"
      : "type4";
  const Ref10StressDetailText = [
    {
      title:
        '당신은 <span class="color">일반적인 수준의 업무집중도</span> 상태입니다.',
      desc: "일을 통해 삶의 활력을 얻고 자신의 존재 이유와 가치를 확인합니다. 새로 시작한 일 혹은 주어진 일에 최선을 다해 열정을 쏟는 정도로, 일과 삶의 균형을 적절하게 잘 유지하고 있습니다. 지금과 같은 상태가 잘 유지 관리 될 수 있도록 업무 외에 기분전환의 기회를 꾸준히 가지시길 권합니다. 다만, 이번 기회에 스트레스와 번아웃 증후군의 차이를 살펴보면서 주변에 이로 인해 어려움을 겪는 사람들을 조금이나마 가깝게 이해할 수 있게 되기를 바랍니다.",
    },
    {
      title:
        '당신은 <span class="color">초기단계 수준의 번아웃 증후군</span> 상태입니다. ',
      desc: "나의 일에 집중도를 높여 몰두하고 성과를 내기 위해 주변인들과의 시간을 줄입니다. 때에 따라서는 본인의 식사와 휴식 시간까지도 일하는 데 집중하며, 이것이 별문제가 아니라고 생각합니다. 그러나 이와 같은 일상이 지속되는 상황에서 업무가 가중된다면 중증 수준의 번아웃 단계로 진입할 위험성이 있습니다. 정기적인 휴식 시간과 업무 외에 기분전환의 기회를 꾸준히 만들어 일과 삶이 건강한 균형을 이룰 수 있도록 의식적으로 노력하시길 권합니다. 또한, 이번 기회에 일반적인 스트레스와 번아웃 증후군의 차이에 대해 이해할 수 있기를 바랍니다. ",
    },
    {
      title:
        '당신은 <span class="color">중증 수준의 번아웃 증후군</span> 상태입니다. ',
      desc: "일적인 면에서는 인정을 받을 수 있지만 충분한 휴식, 취미생활, 대인관계 등의 개념 없이 지속적으로 일만 생각하는 단계입니다. 주변인과의 갈등에 날카롭게 반응하며, 일에 방해가 되는 사회적인 어떤 관계도 맺지 않거나 불편함을 겪게 됩니다. 업무로 인한 스트레스를 술이나 약물, 쇼핑, 폭식 등에 의지해 해결하기도 합니다. 이상함을 감지한 주변에서 관심을 갖고 나의 상태에 대해 물어보기 시작합니다. 고민을 나눌 수 있는 가까운 친구나 동료, 또는 지인이 있다면 답답한 마음을 표출하는 것도 많은 도움이 됩니다. 다만, 혼자서 극복하기가 어렵다고 생각되면 전문가의 도움을 받아보시길 권합니다. ",
    },
    {
      title:
        '당신은 <span class="color">매우 심각한 수준의 번아웃 증후군</span> 상태입니다. ',
      desc: "내면의 공허함이 생기기 시작하며 이 공허함을 해결하기 위하여 술이나 약물, 쇼핑, 폭식 등에 의지했을 가능성이 높습니다. 당신을 이해하지 못하는 주변인에게는 더 방어적 태도를 취하게 되며 스스로의 건강도 방치합니다. 이러한 상황이 지속되는 동안, 많은 괴로움과 심적인 피로감에 지쳐있는 상태일 수 있습니다. 더 이상 자신을 방치하지 말고, 바로 전문가의 도움을 받아보실 것을 권해드립니다.",
    },
  ];
  const Ref10StressSelectDetailText =
    Ref10ScoreType === "type1"
      ? Ref10StressDetailText[0]
      : Ref10ScoreType === "type2"
      ? Ref10StressDetailText[1]
      : Ref10ScoreType === "type3"
      ? Ref10StressDetailText[2]
      : Ref10StressDetailText[3];

  // ref[22]
  const ref12Text = {
    DPLC: {
      name: "DPLC 체험형",
      desc: [
        "다양한 사람들과 교류하면서 당신을 드러내고 표현해야 합니다. ",
        "당신의 생각, 아이디어 등을 다른 사람들에게 말하는 연습을 해야 합니다.",
        "사실적인 정보 외에도 이론적이고 추상적인 개념에도 집중해야 합니다.",
        "다른 사람의 생각과 감정에 관심을 갖고, 공감을 먼저 하는 연습을 해야 합니다. ",
      ],
    },
    DPMC: {
      name: "DPMC 인내형",
      desc: [
        "상대에 대한 당신의 마음을 말과 행동으로 표현하는 연습을 해야 합니다.",
        "당신이 성취한 일을 다른 사람들에게 이야기하고 그들의 인정을 받는 경험을 해야 합니다.",
        "자신만의 시간과 공간을 마련하여 당신의 감정과 욕구에 귀 기울여야 합니다. ",
        "당신의 기준과 이상이 현실성 있고 실현 가능한 것인지 검토해보세요.",
        "계획을 세워서 목표를 달성하고 성취감을 느껴보세요.  ",
      ],
    },
    WPLC: {
      name: "WPLC 열정형",
      desc: [
        "구체적이고 사실적인 것뿐만 아니라, 추상적이고 이론적인 것에도 집중하는 것이 필요합니다. ",
        "일의 목표와 계획을 세울 때 다른 사람들의 의견을 반영하는 것이 좋습니다.",
        "다른 사람의 감정, 욕구, 가치관 등을 파악하고 배려해야 합니다. ",
        "빠르게 해결하기 보다는, 그 해결책이 당신과 집단에 미칠 영향을 고려해보세요.  ",
      ],
    },
    WPMC: {
      name: "WPMC 낙천형",
      desc: [
        "객관적이고 논리적으로 상황을 판단하고 결정을 하는 연습이 필요합니다. ",
        "이론이 뒷받침되면 당신의 지식과 경험은 더 풍부해질 수 있다는 사실을 기억하세요. ",
        "다양한 관심사와 더불어, 깊이 있게 공부하는 경험도 필요합니다. ",
        "계획을 세우고 그 계획대로 일을 진행하는 연습을 해야 합니다.",
      ],
    },
    DIMC: {
      name: "DIMC 개성형",
      desc: [
        "지금까지 자기성찰에 집중했다면, 이제 행동으로 옮겨야 할 때입니다.",
        "소진되지 않도록 당신만의 시간과 공간을 마련해서 휴식을 취하고 자신을 돌봐야 합니다. ",
        "당신의 계획이 현실적인지 검토해봐야 합니다.",
        "다른 사람들과 건강하게 비판적인 피드백을 주고 받는 연습을 해야 합니다. ",
        "당신의 아이디어, 가치관 등을 다른 사람들과 공유하고 도움을 받으세요. ",
      ],
    },
    DIMS: {
      name: "DIMS 고뇌형",
      desc: [
        "의견을 명확하고 자신있게 표현해야 합니다.",
        "당신의 계획을 이룰 수 없다고 판단되면, 적절한 때에 포기해야 합니다. ",
        "개방적인 태도로 정보를 받아들이고 다양한 관점으로 세상을 보도록 노력해야 합니다. ",
        "당신만의 시간과 공간에서 자신의 감정과 욕구는 어떤지 살펴보세요.",
        "비판적인 피드백을 당신에 대한 공격으로 받아들이지 않는 연습이 필요합니다. ",
      ],
    },
    WIMC: {
      name: "WIMC 재미형",
      desc: [
        "다른 사람의 생각, 가치관이나 삶에 과도하게 개입하지 않는 것이 좋습니다. ",
        "목표를 세우고 계획하여 당신이 계획한 활동을 신중하게 수행하고 완료하는 것이 필요합니다. ",
        "당신이 지루함을 느낄 수 있는 세부적이고 반복되는 작업을 해보는 연습이 필요합니다.",
        "비판적인 피드백을 공격적인 것이 아니라, 성장을 위한 밑거름이라고 받아들이세요. ",
        "주위 사람들과 충분히 논의하면서 심사숙고하여 결정을 하세요.",
      ],
    },
    WIMS: {
      name: "WIMS 격려형 ",
      desc: [
        "지나치게 충성하거나 헌신적으로 되지 않도록 신경 써야 합니다. ",
        "집단이나 대의명분에 맹목적으로 따르지 않도록 주의가 필요합니다. ",
        "사람들이 모두 나의 상식과 같지 않다는 사실을 기억하세요.",
        "사람들의 요구에 과하게 집중하는 것을 피해야 합니다. ",
        "조언이나 피드백을 할 때는 사적인 생각보다는 타인의 성장에 집중하여 객관적이고 유용한 내용을 전할 필요가 있습니다. ",
        "결정을 할때는 충분한 시간을 가져도 괜찮다는 사실을 기억하세요.",
        "발표를 할 때는 객관적인 정보를 제공하고 세부사항의 경우 초점을 유지해야 한다는 사실을 기억하세요.",
      ],
    },
    WPLS: {
      name: "WPLS 성과형",
      desc: [
        "다른 사람에게 높은 기준을 적용하지 않도록 주의해야 합니다.",
        "다른 사람의 감정, 욕구 등을 파악하고 세심하게 반응해야 합니다. ",
        "다른 사람의 생각, 강점, 능력을 인정하고 칭찬할 수 있도록 노력해야 합니다. ",
        "맥락을 잘 파악하고 주의를 기울여야 합니다. ",
      ],
    },
    WPMS: {
      name: "WPMS 사교형",
      desc: [
        "지나치게 많은 일을 하거나 소진되지 않도록, 분별력 있게 ‘아니오’라고 말하는 연습이 필요합니다. ",
        "공식적인 방식으로 피드백을 주고 받는 연습이 필요합니다. ",
        "시도해 보지 않은 방식을 제안받았거나 빠른 의사결정을 요구받을 때 조금 더 유연해질 필요가 있습니다. ",
        "새로운 방식으로 일을 하게 될 때 다른 사람들의 생각과 의견을 충분히 새겨들을 필요가 있습니다. ",
        "세부사항에 집착하기보다는 맥락을 파악하며 큰 그림을 살펴야 한다는 점을 기억해야 합니다. ",
      ],
    },
    DPLS: {
      name: "DPLS 안정형",
      desc: [
        "변화와 혁신에 더욱 개방적인 태도로 대처할 필요가 있습니다. ",
        "의사결정을 할 때 감정과 가치관의 영향을 고려할 필요가 있다는 사실을 기억해야 합니다. ",
        "눈앞의 성과나 세부사항에 너무 집중한 나머지 민감한 상호작용이나 큰 그림을 놓치지 않도록 조심해야 합니다. ",
        "조직에서 규칙을 적용할 때 융통성을 발휘할 필요가 있습니다. ",
        "할 수 있는 일이라고 할지라도, 추가적인 부담이 되는 일에는 ‘아니오’라고 말할 수 있는 선택과 집중력을 키워야 합니다. ",
      ],
    },
    DPMS: {
      name: "DPMS 양육형",
      desc: [
        "자신의 생각이나 의견을 적극적으로 표현하며 자신만의 리더십을 발휘할 필요가 있습니다.",
        "아이디어와 성과를 자주 공유하는 방법을 익히는 것이 좋습니다. ",
        "갈등이 발생할 수 있음을 기억하고, 불안한 마음을 좀 더 편안하게 받아들일 수 있도록 단련할 필요가 있습니다. ",
        "그동안 시도해보지 않았던 새로운 일들이나 방법을 실행해보고 다가오는 변화에 개방적인 태도를 취할 필요가 있습니다. ",
        "단기적이고 세부적인 일과 장기적이고 미래지향적인 일 사이에 균형을 잡을 수 있도록 노력해야 합니다. ",
      ],
    },
    WILC: {
      name: "WILC 표현형",
      desc: [
        "감정에 더 초점을 맞추고 세심하게 반응하는 것이 중요합니다. ",
        "다른 사람을 인정하고 그들에 대한 마음을 표현하는 것이 필요합니다. ",
        "현실과 이상 사이에서 적절한 균형점을 찾아보세요. ",
        "맥락 파악과 더불어 세부적이고 구체적인 사항에도 주의를 기울여야 합니다. ",
        "계획을 이루기 위해서 일상의 규범이나 규칙을 준수하도록 노력해야 합니다. ",
      ],
    },
    WILS: {
      name: "WILS 체계형",
      desc: [
        "당신과 관계 맺고 있는 사람들에게 관심을 갖고, 그들이 어떤 사람인지 알아가는 노력을 해야 합니다. ",
        "당신의 기준으로 다른 사람을 판단하지 말고, 그들이 가진 강점과 능력을 볼 수 있어야 합니다. ",
        "일을 빠르게 하기보다는 지금보다 여유로운 마음으로 진행해야 합니다. ",
        "동료들과 함께 논의하면서 결정을 내리는 것이 필요합니다. ",
      ],
    },
    DILC: {
      name: "DILC 독립형",
      desc: [
        "당신의 관심 분야 외에도 열린 태도로 다양한 사람, 분야에 관심을 갖도록 노력해야 합니다. ",
        "여러 사람들과 교류하며 상호작용 하는 노력을 해야 합니다. ",
        "당신의 일에만 몰두하기보다 주변에서 벌어지는 일들에 주의를 기울이도록 해야 합니다. ",
        "아이디어를 체계적으로 정리하는 연습이 필요합니다. ",
      ],
    },
    DILS: {
      name: "DILS 효율형",
      desc: [
        "세상과 사람에 대한 개방적이고 유연한 태도를 가져야 합니다. ",
        "다른 사람의 생각, 감정 등에 관심을 갖고 민감하게 파악하도록 노력해야 합니다. ",
        "다른 사람의 실수나 잘못을 질책하기보다 먼저 포용력 있는 태도로 받아들이는 연습을 해야 합니다. ",
        "다른 사람들과 일을 계획하고 협업하는 자세를 가져야 합니다.",
        "세부적이고 구체적인 사항에 집중하고 주의를 기울이는 연습이 필요합니다. ",
      ],
    },
  };

  // ref[33, 34]
  const Ref33DetailText = [
    {
      img: "metaq_1",
      score: "0<br>-<br>15",
      title: "심각한 위기상태",
      enTitle: "Critical State of Crisis",
      keywords: "죄의식, 처벌, 악한 잔혹함, 파괴, 폭력",
      desc: "모든 균형이 무너진 상태, 삶의 의미를 잃음, 즉각적 개입 필요",
      detail: `IQ와 EQ를 넘어서는 'μετά Q 융합역량지수'는, 인간이 ‘의미’와 ‘가치’의 문제를 깊이 있게 탐색하고
           통합적으로 해결해 나갈 때 발휘되는 고차원적 역량입니다. 이 지수는 우리의 행동과 삶을 보다 넓고
           깊은 의미의 맥락 속에 자리매김하도록 도우며, 자아나 의식적 사고를 초월한 내면의 깊은 지혜와
           연결된 능력을 반영합니다. 단지 기존의 가치를 인식하는 데 그치지 않고, 새로운 가치를 창조적으로
           발견하고 구성해내는 통합적 창의 역량이기도 합니다. 
           <br><br>
           이는 개인의 정서적 균형(EB), 성격균형(PB). 다중역량균형(MiB), 교류균형(CB), 삶의 만족도(LS) 등을
           종합적으로 분석하여 도출된 융합역량지수입니다. `,
      detailText_1: {
        title: "자기 존재를 파괴하고 싶은 충동에 휘말린 사람",
        content: `이 구간의 사람은 더 이상 자신을 '존재'로 여기지 않으며, 자기 인식은 <strong class="green">심각한 죄의식과 자기혐오</strong>로
               덮여 있습니다. 스스로를 끊임없이 비난하고, 자신의 존재 자체가 잘못되었으며 “나는 벌받아야
               마땅하다”는 <strong class="green">내면의 자기 처벌 목소리</strong>가 반복됩니다. 
               <br><br>
               이들은 자기 자신을 하나의 사람으로 느끼기보다는, 오히려 해를 입혀야 하는 존재, 제거되어야 할
               존재로 인식하며, <strong class="green">자해 충동, 자살 사고, 극단적 자기 통제 혹은 자기 학대</strong>의 형태로 파괴적 에너지를
               표출합니다. 
               <br><br>
               감정은 격렬하지만 제어되지 않으며, 그 감정의 뿌리는 대부분 <strong class="green">자기혐오, 억눌린 분노, 끝없는
               수치심</strong>으로 얽혀 있습니다. 삶은 견딜 수 없는 형벌처럼 느껴지고, 스스로를 지킬 이유도, 의미도, 가치도
               모두 사라져 있는 상태입니다. 
               <br><br>
               이 상태에 있는 사람은 자신에게 무슨 일이 일어나고 있는지를 언어화하기 어렵고, <strong class="green">말보다는 행동과
               침묵, 혹은 파괴를 통해 내면의 절규를 표현</strong>하게 됩니다. 이 시점에서의 ‘나’는 더 이상 생존을 향한
               의지를 갖지 못하고 있으며, 단지 <strong class="green">고통을 멈추는 방법을 찾기 위해 파괴를 선택할 위험성이 매우 높은
               상태</strong>입니다.`,
      },
      detailText_2: {
        title: "인간에 대한 신뢰가 완전히 붕괴된 사람",
        content: `이 단계의 사람은 타인을 향한 신뢰뿐 아니라, <strong class="green">타인과 자신 모두에 대한 인간적 존엄 자체를 상실</strong>한 상
                  태입니다. 과거의 심각한 상처나 폭력, 배신, 방임 등의 경험이 내면에서 왜곡되고 뒤엉켜, <strong class="green">타인을 폭력의
                  가해자, 혹은 나를 파괴하는 존재로 인식</strong>하게 되는 경우가 많습니다. 
                  <br><br>
                  이들은 타인의 도움이나 관심을 받는 것을 오히려 위협이나 감시로 받아들이며, <strong class="green">누군가가 가까이 오면
                  경계하거나 공격성으로 반응</strong>할 수 있습니다. 그 관계는 '함께 있음'이 아닌 '이겨야만 하는 싸움', '서로를
                  해치는 전쟁'처럼 느껴지고, 결국 사람과의 연결은 <strong class="green">위협, 감정 폭발, 냉소, 조종, 분노의 방식</strong>으로만 유지됩니다. 
                  <br><br>
                  또한, 과거 자신이 가했던 상처에 대한 죄책감이 왜곡되어, ‘나는 다른 사람을 망가뜨리는 존재’라는 자기
                  인식으로 이어지고, 그래서 더 이상 어떤 관계도 유지하거나 맺을 수 없다는 <strong class="green">관계 불능감</strong>에 휩싸입니다. 이러한 상태에서는 타인을 해하려는 의도 없이도 그 관계 자체가 상호 파괴적 방식으로만 재현되며, 
                  <strong class="green">결국 스스로를 더욱 고립된 절망의 감옥 속으로 밀어넣게 됩니다.</strong>`,
      },
      detailText_3: {
        title: "세상에 대한 분노와 절망이 뒤엉킨 사람",
        content: `이 구간에 있는 사람은 사회 전반에 대한 강한 불신과 증오, 냉소를 품고 있으며, 세상은 이미 자신을 거
                  부했고, 자신은 그 안에서 <strong class="green">완전히 배제된 존재</strong>라는 깊은 절망 속에 머뭅니다. 
                  <br><br>
                  사회 규범이나 제도는 더 이상 안전망이 아니라, <strong class="green">억압, 처벌, 위선, 배신의 구조</strong>로 느껴지며, 자신의 고통
                  을 무시하고 조롱해온 시스템 전체를 증오하는 상태에 이를 수 있습니다. 이러한 극단적인 분노는 자신
                  을 향하기도 하고, 때로는 세상을 향한 보복적 상상이나 파괴 충동으로 표출될 가능성도 존재합니다. (예: “이 세상은 잘못되었고, 누군가는 이 고통을 보여줘야 한다”는 식의 왜곡된 정의감)
                  <br><br>
                  가난, 낙인, 실패, 억압의 경험은 <strong class="green">자신을 비참하고 쓸모없는 존재</strong>로 만들며, 삶의 시스템 속에서의 이탈은
                  자발적 선택이 아닌, <strong class="green">무시당하고 쫓겨난 결과로 여겨집니다.</strong> 이러한 고립감은 종종 자기 존중감의 붕괴와
                  함께 세상과의 단절을 ‘운명처럼’ 받아들이는 무서운 체념으로 이어지기도 합니다. 
                  <br><br>
                  이 시점에서 중요한 것은, 이 고통이 당신 잘못이 아니며, <strong class="green">지금의 파괴적 충동 또한 '살고 싶다'는 내면의
                  왜곡된 외침일 수 있다는 사실을 누군가가 믿어주는 것</strong>입니다. 스스로를 지킬 수 없는 순간, 당신을 대신
                  지켜줄 <strong class="green">안전한 울타리, 조건 없는 보호, 판단 없는 존재의 수용이 절실히 필요합니다.</strong> 이 구간의 회복은
                  ‘혼자 싸워 이기는 것’이 아니라, <strong class="green">살아 있음을 함께 지켜주는 누군가와의 동행에서 시작됩니다.</strong>
                  <br><br>
                  ☆ 요약하자면, 이 구간에 해당하는 사람은<br>
                  - 자기 존재에 대한 극심한 혐오와 죄책감 속에서 스스로를 파괴하고 싶어 하며, <br>
                  - 타인과의 관계에서는 신뢰가 완전히 붕괴된 채 파괴적 방어만이 남아 있고,<br>
                  - 사회적으로는 철저히 배제되었다고 느끼며, 삶 전체를 부정하려는 충동에 사로잡힌 상태입니다.
                  <br><br>
                  이 시점은 생존 그 자체를 위협받는 마지막 위기 경고 지점입니다. 이들은 의지를 되찾는 것이 아니라, 생존 자체를 보호받아야 할 대상입니다. 그러므로 지금 필요한 것은 변화나 자각이 아닌, 절대적인 안전, 강력한 구조적 개입, 그리고 조용히 함께 있어줄 ‘타인의 온전한 존재’입니다.
                  `,
      },
    },
    {
      img: "metaq_2",
      score: "16<br>-<br>29",
      title: "완전한 균형붕괴",
      enTitle: "Complete Collapse of Balance",
      keywords: "비애, 무력함, 가난, 죄책감, 자기비하",
      desc: "심각한 심리적, 사회적 문제를 동반할 가능성, 집중적 치료와 변화가 필수적임",
      detail: `IQ와 EQ를 넘어서는 'μετά Q 융합역량지수'는, 인간이 ‘의미’와 ‘가치’의 문제를 깊이 있게 탐색하고
           통합적으로 해결해 나갈 때 발휘되는 고차원적 역량입니다. 이 지수는 우리의 행동과 삶을 보다 넓고
           깊은 의미의 맥락 속에 자리매김하도록 도우며, 자아나 의식적 사고를 초월한 내면의 깊은 지혜와
           연결된 능력을 반영합니다. 단지 기존의 가치를 인식하는 데 그치지 않고, 새로운 가치를 창조적으로
           발견하고 구성해내는 통합적 창의 역량이기도 합니다. 
           <br><br>
           이는 개인의 정서적 균형(EB), 성격균형(PB). 다중역량균형(MiB), 교류균형(CB), 삶의 만족도(LS) 등을
           종합적으로 분석하여 도출된 융합역량지수입니다.`,
      detailText_1: {
        title: "존재를 부정하며 자기 자신을 잃어버린 사람",
        content: `이 구간에 있는 사람은 더 이상 자기 자신을 하나의 존엄한 존재로 느끼지 못합니다. 삶 전체가 끝없는
                  비애와 무력감으로 가득 차 있으며, 자신에 대한 인식은 <strong class="green">죄책감, 자기비하, 무가치함의 언어</strong>로 채워져
                  있습니다. 
                  <br><br>
                  과거의 실수와 상처는 과장되거나 왜곡된 형태로 반복되며, ‘나는 잘못된 사람이다’, ‘태어나지 말았어야
                  했다’는 식의 <strong class="green">극단적인 자기부정</strong>이 뿌리 깊게 자리 잡고 있습니다. 
                  <br><br>
                  감정은 통제되지 않은 채 흘러내리거나 완전히 얼어붙고, 자신의 행동을 판단하고 조율할 힘이
                  사라지면서 <strong class="green">삶을 향한 의지 자체가 무너져내린 상태</strong>에 이르게 됩니다. 
                  <br><br>
                  이들은 자신을 돌보거나 회복할 가치가 없는 존재로 여겨 음식, 수면, 위생 등 <strong class="green">기초적인 생존 행위조차
                  방기</strong>하게 될 수 있으며, 삶을 지속할 이유를 찾지 못한 채 <strong class="green">존재의 바닥에 가까워진 느낌</strong>으로 하루하루를
                  버텨냅니다.`,
      },
      detailText_2: {
        title: "죄책감과 자기혐오로 타인조차 밀어내는 사람",
        content: `이 단계의 사람은 관계에 대한 기대도, 욕구도 거의 남아 있지 않습니다. 타인의 따뜻한 말이나 위로조차
                  도 “나는 그런 걸 받을 자격이 없다”는 생각으로 밀어내며, <strong class="green">다정한 관계는 오히려 더 큰 수치심과 죄책감
                  을 자극하는 요소</strong>로 작용합니다.
                  <br><br>
                  과거에 자신이 누군가에게 상처를 주었다는 기억, 혹은 타인에게 충분히 좋은 사람이 아니었다는 생각이
                  계속해서 되살아나며, <strong class="green">자기혐오의 형태로 내면을 갉아먹습니다.</strong> 결국 이들은 타인과 다시 관계 맺는 일이
                  자신은 물론 상대에게도 해가 된다고 느끼며, <strong class="green">사람들과의 단절을 스스로 선택하고 정당화</strong>하게 됩니다. 
                  <br><br>
                  고립과 단절은 더욱 깊어지고, 사람들의 말, 표정, 시선조차 두렵게 느껴지거나 무가치한 자신의 존재를
                  드러내는 거울처럼 여겨집니다. <strong class="green">관계는 회복의 자원이 아니라, 오히려 회피해야 할 위협이 되어버린 상태</strong>
                  입니다. `,
      },
      detailText_3: {
        title: "모든 시스템으로부터 이탈한 채 붕괴된 사람",
        content: `이 구간의 사람은 사회적 구조나 역할 속에서 더 이상 기능할 수 없는 상태에 도달해 있습니다. 직업, 학
                  업, 가족, 공동체의 일원으로서 자신의 위치를 유지할 수 없으며, <strong class="green">사회적 요구에 반응하는 능력 자체가 마
                  비된 상태</strong>에 이릅니다. 
                  <br><br>
                  삶을 유지하는 기본적 책임과 역할조차 감당하기 어려우며, 경제적 위기, 주거 불안, 고립, 건강 문제 등
                  이 복합적으로 얽히는 경우도 많습니다. 외부 세계는 자신에게 너무나 멀고 낯설게 느껴지고, <strong class="green">모든 것이
                  불공평하고 잔인한 시험처럼 보이게 되는 인지 왜곡</strong>이 깊어집니다. 
                  <br><br>
                  이들은 자신을 사회적으로 <strong class="green">실패한 존재</strong>, 혹은 <strong class="green">이미 탈락해버린 사람</strong>이라고 생각하며 ‘다시 일어설 수 없
                  다’는 절망적인 결론에 스스로를 가두게 됩니다. 도움의 손길조차 믿을 수 없으며, 사회적 연결과 복귀는
                  상상조차 할 수 없는 먼 세계의 이야기처럼 느껴집니다. 
                  <br><br>
                  때로는 이 사회 자체가 자신을 거부했고, 자신은 끝내 받아들여지지 않았다는 <strong class="green">근원적 거절감</strong> 속에서 깊
                  은 분노나 냉소가 극단적인 형태로 표출되기도 합니다. 
                  <br><br>
                  ☆ 요약하자면, 이 구간에 해당하는 사람은<br>
                  - 자기 자신에 대한 완전한 부정 속에서 자존감이 무너졌고, - 타인과의 관계에서도 죄책감과 수치심으로 단절을 자초하고 있으며, <br>
                  - 사회적으로는 모든 시스템과 의미로부터 이탈한 채 존재 자체가 붕괴되고 있는 상태입니다. 
                  <br><br>
                  이 시점은 생존과 연결을 위한 마지막 구조 요청의 단계입니다. 단순한 위로나 조언이 아니라, 삶을 다시
                  시작할 수 있는 조건을 함께 만들어줄 사람과 환경의 개입이 절박하게 요청됩니다. 회복은 이들에게 주
                  어지는 기회이자, 존재가 다시 회복될 수 있다는 근원적인 확신의 재건에서 시작되어야 합니다.`,
      },
    },
    {
      img: "metaq_3",
      score: "30<br>-<br>44",
      title: "심각한 정신적 고립",
      enTitle: "Severe Mental Isolation",
      keywords: "무기력, 황량함, 의미상실, 절망, 포기, 희망 없음, 고립, 의존",
      desc: "극단적인 우울감, 자기 상실, 관계 단절, 등의 위험. 즉각적인 개입 필요",
      detail: `IQ와 EQ를 넘어서는 'μετά Q 융합역량지수'는, 인간이 ‘의미’와 ‘가치’의 문제를 깊이 있게 탐색하고
           통합적으로 해결해 나갈 때 발휘되는 고차원적 역량입니다. 이 지수는 우리의 행동과 삶을 보다 넓고
           깊은 의미의 맥락 속에 자리매김하도록 도우며, 자아나 의식적 사고를 초월한 내면의 깊은 지혜와
           연결된 능력을 반영합니다. 단지 기존의 가치를 인식하는 데 그치지 않고, 새로운 가치를 창조적으로
           발견하고 구성해내는 통합적 창의 역량이기도 합니다. 
           <br><br>
           이는 개인의 정서적 균형(EB), 성격균형(PB). 다중역량균형(MiB), 교류균형(CB), 삶의 만족도(LS) 등을
           종합적으로 분석하여 도출된 융합역량지수입니다.`,
      detailText_1: {
        title: "삶 자체를 느낄 수 없는 황폐한 내면에 갇힌 사람",
        content: `이 구간의 사람은 감정, 사고, 감각이 거의 마비된 상태에 가깝습니다. 마치 삶이 멈춘 듯한 무감각
                  속에서, 스스로의 존재를 느끼지 못하고, 삶에 어떠한 의미나 기대도 품을 수 없습니다. 
                  <br><br>
                  내면은 황량한 벌판처럼 비어 있고, 슬픔이나 분노조차 희미해진 채, 그저 숨 쉬고 있는 육체만 남은
                  듯한 <strong class="green">깊은 무기력의 늪</strong>에 빠져 있습니다. 자신이 사라진 것 같은 감각, 세상과 단절된 듯한 이질감이
                  계속되며, “나는 존재해도, 존재하지 않는 것 같다”는 느낌이 일상을 지배합니다. 
                  <br><br>
                  기억은 과거의 후회로 고정되고, 미래는 공허하고 검은 안개 속처럼 막막하며, 지금 이 순간조차 <strong class="green">의미를
                  인식하지 못하는 무의미의 시간이 반복</strong>됩니다. 
                  <br><br>
                  삶을 새롭게 해석하거나 다시 시작해볼 힘은커녕, 살아가야 할 이유조차 떠올릴 수 없게 됩니다. <strong class="green">그저
                  살아 있다는 사실만이 남아 있을 뿐, 그것조차 나의 의지와는 무관한 일처럼 느껴집니다.</strong>`,
      },
      detailText_2: {
        title: "단절을 넘어, 관계 자체를 믿지 않는 사람",
        content: `이 단계의 사람은 더 이상 누군가와 관계를 맺는 것에 대해 기대하거나 바라는 마음이 없습니다. 과거의
                  관계에서 겪은 상처, 무시, 단절의 경험이 누적되면서 <strong class="green">타인은 나를 결코 이해하거나 지지하지 않을 것이
                  라는 절망감</strong>에 휩싸여 있습니다.
                  <br><br>
                  가끔 누군가 다가와도 그것이 진심인지, 혹은 오히려 더 아프게 할지 몰라 스스로 관계의 문을 걸어 잠
                  그고, 그 안에 갇힌 채 고립을 선택합니다. 이런 고립은 단순히 혼자 있는 것이 아니라, <strong class="green">관계를 불신하고, 스스로를 내어줄 수 없는 극단적 두려움에서 비롯된 것</strong>입니다. 
                  <br><br>
                  때로는 누군가에게 기대고 싶다는 마음이 들기도 하지만, 그 기대가 다시 좌절될까 봐 아예 의지하지 않
                  기로 스스로와 약속하고, 그 안에서 생존하려는 <strong class="green">정서적 철수와 위축이 반복</strong>됩니다. 관계란 위험한 것, 
                  <strong class="green">나는 결국 이해받지 못할 존재라는 근원적 확신이 이들을 깊은 외로움에 가두고</strong> 있습니다. 
                  <br><br>
                  누군가와 연결되는 일은 곧 상처받는 일이라는 인식이 굳어지며, 차라리 아무 관계도 맺지 않는 것이 안
                  전하다고 느끼게 됩니다. 그렇게 이들은 마음의 문을 닫는 것으로 자신을 보호하려 하지만, 결국 그 안에
                  서 더욱 깊이 고립되어 갑니다. `,
      },
      detailText_3: {
        title: "세상과 완전히 분리되어 존재하는 사람",
        content: `이 구간에 해당하는 사람은 사회적 삶의 구조와 흐름으로부터 완전히 분리되어 있습니다. 직업, 소속, 활
                  동, 책임과 같은 사회적 기능은 이미 멈추었거나, <strong class="green">그 어떤 것도 자기 존재와 연결되지 않는 외부의 소음
                  처럼 느껴지는 상태</strong>입니다. 
                  <br><br>
                  이들은 더 이상 사회 안에서 자신의 자리를 찾으려는 의지도 없고, 그럴 수 있다는 가능성조차 상상할
                  수 없는 지점에 와 있습니다. 자신은 낙오자가 아니라, 아예 <strong class="green">세상과 무관한 존재가 되어버렸다고 느끼며,</strong> 세상은 너무 빠르고 냉정하며 자신은 도저히 따라갈 수 없는 존재라고 단정짓습니다. 
                  <br><br>
                  그 결과, 삶은 ‘나의 삶’이 아니라 ‘견뎌야 하는 시간’으로 전락하고, 자기 의지를 발휘할 수 있는 능력은
                  무너져 스스로를 세상 한 귀퉁이에 조용히 내려놓는 방식으로 <strong class="green">삶을 포기해가기 시작</strong>합니다. 자기 파괴적
                  선택이나 극단적인 생각이 자연스럽게 스며드는 위험한 시기이기도 합니다. 
                  <br><br>
                  이 시점에서 중요한 것은, 이 고립이 당신 잘못이 아니라는 사실을 이해하고, <strong class="green">나조차 믿지 못하는 순간, 누군가의 ‘있는 그대로의 수용’이 당신에게 닿는 일이 반드시 필요</strong>하다는 것입니다. 회복은 혼자 시작할
                  수 있는 여정이 아닙니다. 이 단계에서는 <strong class="green">존재 자체를 붙들어줄 타인의 손길과, 삶의 의미를 함께 찾아갈
                  안전한 공간이 절대적으로 필요</strong>합니다. 당신이 사라지지 않도록, 누군가는 당신 곁에 있어야 합니다.
                  <br><br>
                  ☆ 요약하자면, 이 구간에 해당하는 사람은<br>
                  - 극심한 무기력과 공허함 속에서 자기 감각을 잃고 있으며, <br>
                  - 관계에 대한 신뢰를 잃은 채 스스로 단절을 선택하고 있고, <br>
                  - 사회와는 이미 마음의 선을 긋고 고립된 삶을 받아들이고 있는 상태입니다. 
                  <br><br>
                  이 상태는 삶의 의미, 관계의 희망, 존재의 가치를 모두 상실한 심리적 붕괴 직전의 마지막 지점입니다. 이제는 더 이상 내면의 힘만으로는 회복이 어려우며, 조건 없는 수용, 구조적인 개입, 그리고 함께 살아줄
                  사람의 존재가 절박하게 요청되는 시점입니다.
                  `,
      },
    },
    {
      img: "metaq_4",
      score: "45<br>-<br>59",
      title: "자아 해체 위험",
      enTitle: "Risk of Ego Disintegration",
      keywords: "한탄, 상실, 애도, 패배, 과거에 묶임, 낙오자 인정",
      desc: "자기 개념과 감정조절 능력이 심각하게 손상된 상태. 심리상담이나 치료가 필요함",
      detail: `IQ와 EQ를 넘어서는 'μετά Q 융합역량지수'는, 인간이 ‘의미’와 ‘가치’의 문제를 깊이 있게 탐색하고
               통합적으로 해결해 나갈 때 발휘되는 고차원적 역량입니다. 이 지수는 우리의 행동과 삶을 보다 넓고
               깊은 의미의 맥락 속에 자리매김하도록 도우며, 자아나 의식적 사고를 초월한 내면의 깊은 지혜와
               연결된 능력을 반영합니다. 단지 기존의 가치를 인식하는 데 그치지 않고, 새로운 가치를 창조적으로
               발견하고 구성해내는 통합적 창의 역량이기도 합니다. 
               <br><br>
               이는 개인의 정서적 균형(EB), 성격균형(PB). 다중역량균형(MiB), 교류균형(CB), 삶의 만족도(LS) 등을
               종합적으로 분석하여 도출된 융합역량지수입니다.`,
      detailText_1: {
        title: "나라는 존재 자체를 잃어버린 사람",
        content: `이 구간의 사람은 자기 자신에 대한 감각이 거의 무너진 상태입니다. '나는 누구인가', '왜 살아야
                  하는가'라는 질문에 대한 응답이 사라졌고, 과거의 상처, 상실, 실패, 한탄의 기억 속에서 <strong class="green">자신의
                  정체성이 완전히 갇혀 있는 상태</strong>입니다. 
                  <br><br>
                  감정은 더 이상 흐르지 않고, 응고된 채 가슴 속 깊은 곳에서 <strong class="green">애도와 절망</strong>으로 굳어져 있습니다. ‘내가
                  내 인생을 망쳤다’는 자기비난, ‘다 끝났다’는 포기감, ‘다시 시작할 수 없다’는 확신이 내면을 지배하며, 삶 전체가 과거에 묶인 채 정지된 듯한 느낌 속에 존재합니다. 
                  <br><br>
                  이 시점에서 ‘나’라는 개념은 <strong class="green">더 이상 살아 있는 정체성이 아니라, 부서지고 해체된 잔재</strong>처럼 느껴지며, 자신을 보는 눈은 극단적으로 차갑고, 냉소적이며, 가혹합니다. 스스로를 돌보려는 의지도 거의 남아
                  있지 않으며, 감정적 반응은 극도로 둔화되거나, 반대로 <strong class="green">감당하기 힘든 고통의 폭발</strong>로 나타날 수
                  있습니다. 
                  <br><br>
                  삶의 의미는 물론, 존재의 가치조차 느껴지지 않는 상태에서 자기 파괴적 사고와 행동이 반복될 수
                  있으며, <strong class="green">가장 낮은 수준의 자존감과 자기 인식</strong>이 이 시기를 지배합니다.`,
      },
      detailText_2: {
        title: "관계의 단절을 스스로 받아들인 사람",
        content: `이 단계의 사람은 이미 타인과의 연결 가능성을 포기하거나 단절한 상태에 가까워 있습니다. 더 이상 관
                  계가 회복될 수 있다고 믿지 않고, 사람들 속에 있을수록 자신이 더 외롭고 쓸모없는 존재처럼 느껴지기 때문에 <strong class="green">고립이 선택이 아니라 체념</strong>이 됩니다.
                  <br><br>
                  마음을 닫은 채 감정을 공유하지 않으려 하고, 도움을 받는다는 개념 자체를 무의미하게 여기며, 자신은
                  낙오자이며 이해받을 수 없다는 <strong class="green">깊은 외로움 속에 스스로를 가두는 경향</strong>이 강합니다. 
                  <br><br>
                  다른 사람과 연결되기보다, 과거의 상처받은 기억이나 관계 실패의 잔해 안에 갇혀 있고, 그 기억이 반복
                  적으로 현재를 파괴하는 방식으로 작용합니다. 
                  <br><br>
                  이들은 누군가가 다가올 때조차 방어적 태도를 보이거나, ‘이미 늦었다’며 마음을 열지 않고, <strong class="green">도움도 기대
                  하지 않는 무감각의 상태</strong>에 머물게 됩니다. 때로는 자기 존재 자체가 타인에게 부담이나 불편함일 것이
                  라는 <strong class="green">죄책감과 수치심</strong>을 품고 있으며, 관계는 삶의 자원이라기보다는 피하고 싶은 고통의 원천이 되어
                  있습니다. <strong class="green">사랑받고 싶은 마음이 있지만, 이미 그것을 받아들일 자격을 스스로 박탈해버린 상태</strong>입니다.`,
      },
      detailText_3: {
        title: "세상으로부터 낙오되었음을 받아들인 사람",
        content: `이 구간의 사람은 사회적 삶에서 완전히 이탈했거나, 마음속에서 이미 스스로를 <strong class="green">‘세상 밖 사람’으로 규정</strong>
                  해버린 상태입니다. 자신이 속할 수 있는 공동체나 역할은 없다고 믿으며, 과거에 소속되었던 자리에서도
                  상실, 거절, 실패의 기억만을 반복적으로 떠올립니다. 
                  <br><br>
                  삶의 구조와 연결되기보다는 <strong class="green">그 구조의 경계 밖으로 밀려나 있다는 감각,</strong> 다시 돌아갈 수 없다는 낙담, 어디에도 속하지 못한 존재라는 <strong class="green">절대적인 소외감</strong>이 이들을 지배합니다. 
                  <br><br>
                  세상의 속도는 너무 빠르고 냉정하게 느껴지며, 그 흐름에 따라가려는 시도조차 하지 않게 됩니다. 이들
                  은 종종 삶을 돌아보며 “나는 실패했다”, “이 사회에 더는 쓸모가 없다”고 말하고, 그 말에 진심을 담습니
                  다. 어떤 이들은 그 낙오감 속에서 완전히 침묵하거나, 또 어떤 이들은 세상을 향해 분노하거나, 자신을
                  향해 모든 책임을 돌리는 <strong class="green">무너진 자기 개념의 언어</strong>를 되뇌입니다. 
                  <br><br>
                  <strong class="green">삶 전체가 종결되어 가는 듯한 감각 속에서, 이들은 살아 있는 낙오자처럼 스스로를 받아들이고 있습니다.</strong>
                  <br><br>
                  ☆ 요약하자면, 이 구간에 해당하는 사람은<br>
                  - ‘나’라는 존재에 대한 개념이 해체되었고, <br>
                  - 관계 안에서도 고립과 단절을 자처하고 있으며, <br>
                  - 사회적으로는 자신을 이미 낙오자라고 규정한 채 존재감을 잃어버린 상태입니다. 
                  <br><br>
                  이 상태는 삶의 기본 에너지, 존재의 감각, 관계의 가능성이 모두 사라져가는 마지막 경고 신호입니다. 이
                  제는 노력보다 구조적 개입이, 위로보다 안전한 울타리가 필요한 시점이며, 치료적 보호, 지속적인 지지, 조건 없는 수용이 생존을 위한 최소한의 기반이 되어야 합니다.`,
      },
    },
    {
      img: "metaq_5",
      score: "60<br>-<br>74",
      title: "심리적 붕괴 직전",
      enTitle: "Near Psychological Breakdown",
      keywords: "슬픔, 상실의 두려움, 후회, 우울, 낙담",
      desc: "인생 전반에 대한 회의감과 무기력함이 강하며 즉각적인 지원과 변화가 필요",
      detail: `IQ와 EQ를 넘어서는 'μετά Q 융합역량지수'는, 인간이 ‘의미’와 ‘가치’의 문제를 깊이 있게 탐색하고
               통합적으로 해결해 나갈 때 발휘되는 고차원적 역량입니다. 이 지수는 우리의 행동과 삶을 보다 넓고
               깊은 의미의 맥락 속에 자리매김하도록 도우며, 자아나 의식적 사고를 초월한 내면의 깊은 지혜와
               연결된 능력을 반영합니다. 단지 기존의 가치를 인식하는 데 그치지 않고, 새로운 가치를 창조적으로
               발견하고 구성해내는 통합적 창의 역량이기도 합니다. 
               <br><br>
               이는 개인의 정서적 균형(EB), 성격균형(PB). 다중역량균형(MiB), 교류균형(CB), 삶의 만족도(LS) 등을
               종합적으로 분석하여 도출된 융합역량지수입니다.`,
      detailText_1: {
        title: "상실감과 후회 속에서 자기를 놓아버린 사람",
        content: `이 구간의 사람은 자신에 대해 더 이상 신뢰를 갖지 못하며, 삶의 어느 시점에서부터 무언가 돌이킬 수
                  없이 어긋났다는 <strong class="green">깊은 상실감</strong>과 자책에 사로잡혀 있습니다. 자신을 돌아볼 때 부끄러움, 후회, 무력감이
                  동시에 밀려오고, 그 감정의 무게에 짓눌려 자신을 다시 세우는 일이 거의 불가능하게 느껴집니다. 
                  <br><br>
                  삶의 방향을 잃은 채 표류하는 듯한 느낌이 강하고, 자신의 존재가 무의미하게 느껴지는 순간들이
                  하루에도 여러 번 찾아옵니다. 감정은 마치 깊은 늪처럼 가라앉아 있고, 마음은 아무것도 붙잡을 수 없을
                  정도로 허물어져 있는 상태입니다. 
                  <br><br>
                  특히 과거의 잘못이나 실패, 상처에 대한 <strong class="green">후회와 자기비난</strong>이 반복적으로 떠올라, 현재의 삶마저
                  마비시키는 힘으로 작용하게 됩니다. 스스로를 용서하는 능력은 거의 남아 있지 않으며, 자신이 가치
                  없는 존재라고 믿고 싶은 충동조차 일어납니다.
                  <br><br>
                  ‘이대로 사라지고 싶다’는 생각이 자주 떠오르며, 감정과 행동 사이에 연결고리가 느슨해져 일상의
                  기본적인 활동조차 어렵게 됩니다. 이들은 <strong class="green">스스로를 더 이상 붙잡지 못한 채 천천히 붕괴해가는 감각</strong>을
                  느끼며 살아가고 있습니다.`,
      },
      detailText_2: {
        title: "마음을 닫은 채 고립 속으로 숨어버린 사람",
        content: `이 단계의 사람은 타인과의 관계에서 깊은 상실과 단절을 경험하고 있습니다. 누군가와 가까워지고 싶은
                  마음이 아예 사라졌거나, 그 시도를 해봤자 또 실망하고 상처받을 것이라는 <strong class="green">확신에 가까운 불신</strong>이 자리 잡고 있습니다.

                  말을 건네고, 감정을 나누고, 함께 시간을 보내는 일이 도무지 감당되지 않는 피로와 부담으로 느껴지며,
                  ‘그냥 혼자가 낫다’는 선택 아닌 선택 속에서 <strong class="green">점점 더 고립의 방으로</strong> 숨어듭니다. 특히 중요한 사람과의
                  갈등, 상실, 관계 단절이 있었던 경우, 그 아픔은 아직 끝나지 않은 상처로 남아 있으며, 그 기억이 현재
                  의 관계에 대한 기대와 신뢰를 모두 가로막습니다. 심지어 타인의 호의조차도 부담스럽거나 가식처럼 느껴지며, 자신을 이해해줄 수 있는 사람은 없다고 느
                  끼게 됩니다. 감정적으로는 무감각하거나 지나치게 예민해지고, 사람을 만나고도 아무 감흥이 없거나, 반
                  대로 사소한 말에 깊이 상처받기도 합니다. <strong class="green">마음은 닫혀 있고, 관계는 끊어진 듯 보이며, 연결될 수 있다
                  는 희망은 거의 사라져 있는 상태</strong>입니다. `,
      },
      detailText_3: {
        title: "세상과 단절된 채 삶에서 이탈하고 있는 사람",
        content: `이 구간의 사람은 더 이상 세상과 건강한 방식으로 연결되어 있지 않으며, 자신이 사회 안에서 어떤 역
                  할을 하고 있는지조차 인식하기 어려운 상태에 있습니다. 과거에는 의미 있었던 일들도 지금은 무의미하
                  게 느껴지고, 어떤 일도 진심을 담아 하기 어렵거나, <strong class="green">그저 해야 하니까 하는 수준의 기능적 참여만 유지
                  하고 있을 가능성</strong>이 높습니다. 
                  <br><br>
                  사회적 의무를 감당하는 데 점점 더 어려움을 느끼며, 직장, 가족, 공동체 내에서 자신의 존재감은 흐려지
                  고, 말수가 줄어들고, 정서적 반응도 무뎌지며 <strong class="green">‘살아는 있지만 살아 있는 것 같지 않은’ 감각</strong>이 이어집니다. 
                  삶은 무겁고, 시간은 느리게 흐르며, 매일을 버티는 것이 하나의 고통이자 미션처럼 느껴지게 됩니다. 
                  <br><br>
                  아무도 자신을 필요로 하지 않는다는 느낌, 자신이 없어도 아무 일도 일어나지 않을 거라는 허탈감은 <strong class="green">존재의 
                  가치를 완전히 희미하게 만듭니다.</strong> 이 단계에서는 외부의 자극이 내면에 스며들지 않으며, 의미 없
                  는 일상이 반복되면서 삶 전체가 회색처럼 느껴지고, 심리적 탈진, 우울, 무기력, 극단적 선택에 대한 유
                  혹이 점점 더 가까이 다가오는 시점입니다. 
                  <br><br>
                  ☆ 요약하자면, 이 구간에 해당하는 사람은<br>
                  - 자기 자신에 대한 신뢰를 완전히 잃었고,<br>
                  - 타인과의 관계는 단절 또는 고립 상태에 있으며,<br>
                  - 사회적으로는 살아 있는 듯하지만 이미 이탈 중인 무의미한 삶을 이어가고 있습니다.
                  <br><br>
                  감정적으로는 한계점에 도달해 있으며, 자기 돌봄은 거의 불가능하고, 삶은 무거운 짐처럼 느껴집니다. 지금 이 시점은 단지 ‘버티기’가 아닌, 반드시 ‘멈추어야 할 순간’입니다. 외부로부터의 진심 어린 개입과 정서적 지지 없이는 회복의 실마리를 찾기 어렵기 때문에, 이 단계는 존재 자체를 다시 껴안고 일으켜줄 관계와 환경이 절대적으로 필요한 시간입니다.
                  `,
      },
    },
    {
      img: "metaq_6",
      score: "75<br>-<br>89",
      title: "극심한 불안정",
      enTitle: "Extreme Instability",
      keywords: "두려움, 불안, 근심, 회피, 공포스러움, 억압, 강박, 질투",
      desc: "자기조절 능력이 현저히 낮으며 감정적 어려움이 심화된 상태, 전문가 도움 필요",
      detail: `IQ와 EQ를 넘어서는 'μετά Q 융합역량지수'는, 인간이 ‘의미’와 ‘가치’의 문제를 깊이 있게 탐색하고 통합적으로 해결해 나갈 때 발휘되는 고차원적 역량입니다. 이 지수는 우리의 행동과 삶을 보다 넓고 깊은 의미의 맥락 속에 자리매김하도록 도우며, 자아나 의식적 사고를 초월한 내면의 깊은 지혜와 연결된 능력을 반영합니다. 단지 기존의 가치를 인식하는 데 그치지 않고, 새로운 가치를 창조적으로 발견하고 구성해내는 통합적 창의 역량이기도 합니다. 
               <br><br>
               이는 개인의 정서적 균형(EB), 성격균형(PB). 다중역량균형(MiB), 교류균형(CB), 삶의 만족도(LS) 등을 종합적으로 분석하여 도출된 융합역량지수입니다.
               `,
      detailText_1: {
        title: "감정의 혼란 속에서 자신을 잃어버린 사람",
        content: `이 구간에 해당하는 사람은 감정의 격랑 속에서 방향을 잃고 표류하는 상태입니다. 불안, 두려움, 근심이 일상 전반에 깔려 있으며, 그 감정이 언제 어떻게 터질지 모르는 <strong class="green">심리적 긴장과 공포감</strong> 속에서 살아갑니다.
                  <br><br>
                  자신의 내면을 돌아보는 것이 무서울 만큼, 감정은 억압되거나 뒤엉켜 있어 스스로도 무엇을 느끼는지, 왜 그런지 알 수 없는 경우가 많습니다. 이런 감정의 불분명함은 자기 인식과 행동의 단절로 이어지며, 자신을 바라보는 시선조차 왜곡되고, <strong class="green">자아감의 붕괴</strong>가 서서히 진행됩니다.
                  <br><br>
                  과거의 상처나 충격이 제대로 소화되지 않은 채 억눌려 있다가, 불쑥불쑥 현재의 감정에 덧입혀져 터지면서 <strong class="green">과잉 반응, 감정적 회피, 혹은 무기력</strong>으로 이어지기 쉽습니다. 이들은 자신이 통제할 수 없는 내면에 휘둘리며 점점 더 <strong class="green">자기 자신에게서 멀어지는 경험</strong>을 하게 됩니다.
                  <br><br>
                  때로는 자신의 감정을 완전히 무시하거나 거부하며, ‘그저 버텨야 한다’는 강박 속에서 감정과 몸의 신호를 차단하기도 하고, 때로는 극단적인 감정 폭발로 인해 스스로를 파괴하거나 소진시키는 방향으로 행동하게 되기도 합니다. <strong class="green">감정은 감지되지만 다룰 수 없는, 생생한 고통의 원천이 되어 있는 시점</strong>입니다.`,
      },
      detailText_2: {
        title: "신뢰는 없고, 두려움과 질투로 얽힌 사람",
        content: `이 단계의 사람은 타인과의 관계 안에서도 <strong class="green">안정감이나 소속감보다는 불신, 불안, 질투, 회피의 감정이 우세</strong>합니다. 관계 안에서 자신이 존중받거나 안전하다고 느끼기 어려우며, 누군가에게 가까이 가려는 마음과 동시에 ‘또 상처받을까 봐’라는 두려움이 겹쳐 끊임없이 마음의 문을 열었다 닫았다 하게 됩니다.
                  <br><br>
                  상대방의 말이나 행동에 과도하게 민감하게 반응하거나, 자신에 대한 무관심 혹은 평가에 대해 <strong class="green">과장된 해석과 감정적 상상을 덧붙이는 일</strong>이 반복되며, 결국 관계를 망가뜨리고 스스로 고립되는 방식이 습관화될 수 있습니다.
                  <br><br>
                  타인을 부러워하면서도 미워하고, 사랑하고 싶지만 믿지 못하는 <strong class="green">감정의 이중구조</strong>는 관계의 흐름을 왜곡시키고, 진정한 친밀감을 경험하지 못하게 만듭니다. 이런 과정에서 <strong class="green">질투, 비교, 애정결핍, 감정적 의존</strong> 등이 동시에 작동하며, 결국 타인과의 관계는 힘이 되어주기보다 자신을 더 취약하게 만드는 통로처럼 느껴지게 됩니다.
                  <br><br>
                  감정의 기복은 관계에서 반복적인 상처와 후회를 남기고, 신뢰는 쌓이기보다 사라져가며, <strong class="green">상대방의 존재는 점점 더 위협적인 요소로 인식</strong>되기도 합니다. 이들은 <strong class="green">진정으로 관계 맺고 싶은 갈망은 있으나, 그 갈망 자체가 두려움으로 변형되어 관계를 피하게 되는 역설을 반복</strong>합니다.
         `,
      },
      detailText_3: {
        title: "현실이 무겁고 위험하게 느껴지는 사람",
        content: `이 구간의 사람은 사회적 삶에서도 방향을 상실하고 있습니다. 자신이 사회에서 어떤 역할을 하고 있는지, 지금의 선택이 자신을 어디로 이끄는지에 대한 <strong class="green">기본적인 감각조차 희미해진 상태</strong>입니다. 일상적인 사회 활동조차 무겁고 부담스럽게 느껴지며, <strong class="green">과업이나 책임, 인간관계 등에서 오는 요구들이 위협처럼</strong> 받아들여질 수 있습니다.
                  <br><br>
                  이들은 일상의 작은 문제도 감정적으로 처리하기 어려워하고, 상황을 객관적으로 파악하거나 유연하게 대응하는 힘이 거의 남아 있지 않은 경우도 많습니다. 때로는 반복되는 실패와 부정적 평가를 통해 자기 효능감을 완전히 상실하거나, 사회적 현실 자체를 부정하며 <strong class="green">극단적인 회피, 격리, 고립, 또는 환상에 몰입하는 경향</strong>이 나타날 수 있습니다.
                  <br><br>
                  세상의 흐름은 자신과 무관한 듯 느껴지고, 삶 전체가 자신의 뜻과는 상관없이 흘러가고 있다는 ‘탈주체화된 느낌’을 자주 경험하게 됩니다. 이들은 겉으로는 살아가고 있지만, 내면 깊은 곳에서는 <strong class="green">자신이 사라지고 있다는 막연한 두려움과 무기력</strong> 속에서 허우적거리고 있습니다.
                  <br><br>
                  이런 상태가 지속되면 결국 <strong class="green">우울, 공황, 강박, 해리, 자해충동 등</strong>으로 이어질 가능성이 높아지며, 더 이상 스스로를 회복시킬 수 있는 내적 자원이 남아 있지 않은 상태에 이르게 됩니다.
                  <br><br>
                  ☆ 요약하자면, 이 구간에 해당하는 사람은<br>
                  - 자신의 감정을 알아차리거나 다룰 수 없는 상태에 이르렀으며,<br>
                  - 타인과의 관계는 불신과 질투, 회피로 얼룩져 있으며,<br>
                  - 사회적 삶에서도 현실에의 감각을 잃고 탈주체화된 상태에 머물러 있습니다.<br>
                  <br><br>
                  심리적 구조의 전반이 기능적 마비 상태에 가깝고, 자기 자신과의 연결, 타인과의 신뢰, 삶과의 접촉이 모두 약화된 위기 신호가 명확한 단계입니다. 이 시기는 반드시 전문가의 따뜻하고 지속적인 개입, 그리고 정서적 안전이 보장된 환경 속에서의 회복적 돌봄이 필요한 시점입니다. 무너진 삶을 다시 세우는 것은 혼자서 감당할 수 있는 과제가 아니며, 공감, 관계, 전문성의 손길이 절실한 구조 요청의 순간입니다.
                  `,
      },
    },
    {
      img: "metaq_7",
      score: "90<br>-<br>104",
      title: "전반적 불균형 상태",
      enTitle: "Overall Imbalance",
      keywords: "중독, 실망, 명예, 돈, 권력, 성도착, 외모 좌절",
      desc: "감정적, 신체적, 정신적으로 부정적인 영향이 미치고 있으며, 심리적 조정 필요함",
      detail: `IQ와 EQ를 넘어서는 'μετά Q 융합역량지수'는, 인간이 ‘의미’와 ‘가치’의 문제를 깊이 있게 탐색하고 통합적으로 해결해 나갈 때 발휘되는 고차원적 역량입니다. 이 지수는 우리의 행동과 삶을 보다 넓고 깊은 의미의 맥락 속에 자리매김하도록 도우며, 자아나 의식적 사고를 초월한 내면의 깊은 지혜와 연결된 능력을 반영합니다. 단지 기존의 가치를 인식하는 데 그치지 않고, 새로운 가치를 창조적으로 발견하고 구성해내는 통합적 창의 역량이기도 합니다. 
               <br><br>
               이는 개인의 정서적 균형(EB), 성격균형(PB). 다중역량균형(MiB), 교류균형(CB), 삶의 만족도(LS) 등을 종합적으로 분석하여 도출된 융합역량지수입니다.
               `,
      detailText_1: {
        title: "자아와의 단절 속에 외부 자극에 종속된 사람",
        content: `이 구간의 사람은 더 이상 자신의 내면을 자율적으로 조율하거나 이해하는 능력이 거의 남아 있지 않으며, 감정은 통제되지 않은 채 불쑥불쑥 튀어나오고, 생각은 왜곡되고 단절되어 자신에게조차 낯선 상태가 됩니다.
                  <br><br>
                  자신의 감정과 신체 감각, 사고 흐름이 하나의 통합된 흐름으로 작동하지 않기 때문에, <strong class="green">삶이 마치 남의 인생을 구경하듯 멀게 느껴지거나,</strong> 아예 ‘나’라는 존재가 무감각하게 사라져버린 듯한 공허함 속에 빠질 수 있습니다. 자신을 제대로 돌보는 능력도 극단적으로 약화되어 있으며, 감정을 조절하거나 판단을 내릴 수 있는 기준이 흐릿해진 채로 <strong class="green">즉각적인 쾌락, 중독적 만족, 강박적 행동</strong>에 점점 더 휘둘리게 됩니다.
                  <br><br>
                  이들은 외부 자극을 통해 삶의 감각을 되찾으려 애쓰지만, 그 자극이 끝나면 더 깊은 공허와 자기 혐오가 남게 되고, 그 감정을 잊기 위해 또다시 자극을 찾는 <strong class="green">파괴적인 순환 고리</strong>에 갇히게 됩니다. 이런 반복 속에서 자신이 누구인지, 어떤 삶을 살아가고 있는지를 감각할 수 없게 되며, 자신을 가치 있는 존재로 느끼는 능력 자체가 극도로 위축됩니다. <strong class="green">삶의 의미를 찾기보다는 어떻게든 견디는 데 집중하며, 점점 더 자기 존재와의 연결이 끊어지고 맙니다.</strong>
                  `,
      },
      detailText_2: {
        title: "소유와 통제로 왜곡된 관계를 반복하는 사람",
        content: `이 단계의 사람은 더 이상 타인을 동등하고 존엄한 존재로 대하기보다, 자신의 결핍을 메우기 위한 수단, 또는 통제 가능한 대상처럼 느끼게 됩니다. 타인과의 관계는 <strong class="green">신뢰, 진정성, 교감이 아닌, 소유, 지배, 이상화와 실망의 반복이라는 불안정한 흐름 속에서 유지</strong>되며, 누군가에게 기대고 싶으면서도 그 기대가 충족되지 않을 때 폭력적 실망이나 감정적 파괴로 이어질 수 있습니다.
                  <br><br>
                  특히 감정적·성적 관계에서는 상대를 통해 자신의 존재 가치를 확인받고자 하며, 그 <strong class="green">욕구가 좌절될 경우 상대를 미워하거나 조종하려는 태도로 전환되기도</strong> 합니다. 관계는 삶의 중심이 되지만, 그 중심은 ‘사랑’이 아닌 ‘소유’와 ‘반응’에 의해 정의되며, 그로 인해 건강한 관계는 유지되기 어렵고, 깊은 외로움과 불신만 쌓이게 됩니다.
                  <br><br>
                  타인에 대한 기대는 과도하게 높거나, 아예 단절적으로 낮아지며, 그로 인해 <strong class="green">극단적인 이중 태도 “너 없인 안 돼” 혹은 “다 꺼져버려”가 반복되곤</strong> 합니다. 결국, 이들은 관계를 통해 연결되기를 바라면서도, 자신이 품고 있는 상처와 왜곡된 욕망에 의해 관계를 망가뜨리는 방식을 반복합니다. 누군가와 연결될수록 상처가 더 깊어진다는 공포가 내면 깊이 자리 잡고 있습니다. 그래서 결국 이들은 <strong class="green">친밀함을 원하면서도, 스스로 친밀함을 피하고 차단하는 역설적인 고립을 반복</strong>하게 됩니다.`,
      },
      detailText_3: {
        title: "명예, 돈, 권력에 집착하지만 더는 채워지지 않는 사람",
        content: `이 구간의 사람은 사회적 기준과 성취 욕구에 과도하게 집착하면서도, 그 어떤 성취도 만족으로 이어지지 않는 <strong class="green">심리적 허기 상태</strong>를 반복합니다. 성공과 인정, 권력, 외모, 돈, 명예 등을 통해 자존감을 회복하려 하지만, 그 노력은 늘 더 큰 공허로 되돌아오며, ‘나’는 점점 더 외적 조건에 종속된 존재로 축소됩니다.
                  <br><br>
                  자신이 살아가는 이유, 일의 의미, 사회적 관계의 본질에 대해 스스로 자문하지 못하고, 그저 세상이 정해놓은 성공의 공식을 쫓다가 자신을 잃게 됩니다. 결과적으로 그들은 성공했어도 불행하고, 많이 가졌어도 항상 부족하며, 좋은 평판을 얻었어도 마음속에 ‘나는 아무것도 아니다’라는 공허한 울림이 사라지지 않습니다.
                  <br><br>
                  어떤 때에는 사회 구조나 타인을 향한 <strong class="green">극단적인 불만과 냉소</strong>로 돌아서기도 하고, 때로는 자신에 대한 분노를 자기 파괴적 행동으로 돌리는 위험한 경향도 나타날 수 있습니다. 중독, 강박, 성도착, 외모 집착, 극단적 자기비하, 무력감과 무기력은 이 구간에서 <strong class="green">삶이 부패되어 가는 외적 신호들</strong>일 수 있습니다.
                  <br><br>
                  이들은 사회 속에서 ‘살고 있다’기보다, <strong class="green">끌려다니며 소진되고 있는 느낌</strong>으로 버티고 있으며, 삶은 점점 더 무의미하고 위험한 방향으로 흘러가게 됩니다. 이 시점에서 <strong class="green">중요한 것은 삶을 다시 성취의 대상이 아닌, 존재의 본질로 바라보며 스스로를 다시 껴안는 용기</strong>입니다.
                  <br><br>
                  ☆ 요약하자면, 이 구간에 해당하는 사람은<br>
                  - 내면과 단절된 채 외부 자극과 욕망에 종속되고,<br>
                  - 관계 안에서는 소유와 조종의 방식으로 반복적인 파괴를 경험하며,<br>
                  - 사회적 성공조차도 공허하게 느껴지는 깊은 내적 붕괴 상태에 있습니다.
                  <br><br>
                  자아의 통합은 사실상 해체 직전에 있으며, 삶 전체가 반복적인 실망, 중독, 왜곡된 기대 안에서 무너지려는 순간입니다. 이 단계에서 필요한 것은 단지 ‘조언’이나 ‘노력’이 아니라, 치료적 개입과 절대적인 수용, 그리고 인간 존재로서의 존엄성을 다시 회복하는 깊은 회복의 길입니다.
                  `,
      },
    },
    {
      img: "metaq_8",
      score: "105<br>-<br>119",
      title: "심각한 불균형 경고",
      enTitle: "Severe Imbalance Warning",
      keywords: "욕망, 불만과 부정, 갈망, 집착, 노예화",
      desc: "내적 불안과 스트레스, 낮은 삶의 만족도로 삶의 질 개선이 시급히 요구됨",
      detail: `IQ와 EQ를 넘어서는 'μετά Q 융합역량지수'는, 인간이 ‘의미’와 ‘가치’의 문제를 깊이 있게 탐색하고 통합적으로 해결해 나갈 때 발휘되는 고차원적 역량입니다. 이 지수는 우리의 행동과 삶을 보다 넓고 깊은 의미의 맥락 속에 자리매김하도록 도우며, 자아나 의식적 사고를 초월한 내면의 깊은 지혜와 연결된 능력을 반영합니다. 단지 기존의 가치를 인식하는 데 그치지 않고, 새로운 가치를 창조적으로 발견하고 구성해내는 통합적 창의 역량이기도 합니다. 
               <br><br>
               이는 개인의 정서적 균형(EB), 성격균형(PB). 다중역량균형(MiB), 교류균형(CB), 삶의 만족도(LS) 등을 종합적으로 분석하여 도출된 융합역량지수입니다.`,
      detailText_1: {
        title: "욕망과 공허 사이에서 방황하는 사람",
        content: `이 구간의 사람은 내면의 불안정함이 극에 달해 있으며, 감정·사고·욕구·정체성이 서로 충돌하며 <strong class="green">자기 통합의 기반이 거의 무너진 상태</strong>에 놓여 있습니다. 무엇을 느끼는지, 왜 그런 반응을 하는지조차 스스로 설명하기 어려운 지경에 이르며, 감정과 생각이 끊임없이 엇갈리고, 삶 전체가 피로하고 무의미하게 느껴질 수 있습니다.
                  <br><br>
                  이들은 삶에서 무언가를 절실히 갈망하지만, 그 갈망의 대상이 뚜렷하지 않거나, 끊임없이 바뀌고 허무함으로 되돌아옵니다. 자신이 진짜 원하는 것이 무엇인지 알 수 없으면서도, 끊임없이 더 많이 갖고, 더 많이 느끼고, 더 많이 인정받으려는 욕망에 사로잡혀 있습니다. 그 욕망은 순간적으로 활력을 주지만, 이내 공허로 전환되며 더 큰 집착과 자기부정을 낳습니다. 
                  <br><br>
                  삶에 대한 통제감은 거의 사라졌고, 자신의 감정이나 행동을 주체적으로 조절하는 능력 또한 약화되어 있습니다. 자신을 돌보거나 회복하려는 의지는 있으나, 그 방법을 찾지 못한 채 무력함에 사로잡히는 반복 속에 머물게 됩니다.
                  <br><br>
                  이 시기의 내면은 한편으로는 무기력하고, 다른 한편으로는 극단적인 갈망과 불만으로 가득 차 있으며,
                  그로 인해 <strong class="green">자기 자신을 삶의 주인공으로 여기는 감각 자체가 희미해진 상태</strong>입니다. 내면의 질서를 회복하기 위한 첫걸음은, <strong class="green">욕망과 감정의 소용돌이 속에서 ‘나는 누구인가’라는 질문을 다시 꺼내는 데서 시작</strong>됩니다.
                  `,
      },
      detailText_2: {
        title: "인정에 대한 집착과 외면 사이에서 오락가락하는 사람",
        content: `이 단계의 사람은 타인과의 관계에서 진정한 연결을 이루기가 매우 어렵습니다. 인정받고 싶은 마음이 지나치게 강해져, 타인의 반응에 과도하게 집착하게 되며, 조금만 무시당했다고 느껴도 깊은 상처와 분노를 느끼는 <strong class="green">감정 기복이 큰 대인 구조를 형성</strong>하게 됩니다.
                  <br><br>
                  관계 안에서는 끊임없이 누군가로부터 감정적 보상을 받고자 하며, 그 보상이 지속되지 않으면 곧 실망과 원망으로 태세가 전환됩니다. 때로는 상대에게 집착하고, 때로는 그 집착에서 벗어나기 위해 일부러 차갑게 끊어내려 하지만, 그 어느 쪽에서도 <strong class="green">지속적인 안정감을 경험하지 못합니다.</strong>
                  <br><br>
                  ‘나를 왜 몰라주지?’라는 감정이 기본 정서처럼 깔려 있고, 이로 인해 <strong class="green">끊임없는 감정적 논쟁과 갈등의 가능성</strong>이 내포되어 있습니다. 자신의 내면에 집중하거나 스스로를 돌아보기보다, 타인의 반응에 반사적으로 움직이게 되며, 관계는 소모적이고 불안정한 양상을 띕니다.
                  <br><br>
                  이 시기의 사람은 누구보다 타인과 연결되고 싶어 하지만, 그 갈망이 지나쳐 오히려 <strong class="green">타인을 삶의 중심에 두는 의존적인 방식으로 연결되기 쉽습니다.</strong> 결국 관계는 자신을 확인하고 정당화하기 위한 수단으로 변질되고, 진심 어린 만남과 깊은 신뢰 형성은 매우 어려워집니다. 관계의 회복은 타인을 바꾸려는 시도보다, 스스로를 정직하게 들여다보는 내면의 여정에서 출발해야 합니다.`,
      },
      detailText_3: {
        title: "삶 전체가 억눌리고 끌려가는 듯 느껴지는 사람",
        content: `이 구간에 있는 사람은 사회적 삶에서 자신의 자리를 확신하지 못하고, 맡은 일이나 역할에 대해 지속적으로 무력감과 회의감을 느낍니다. 스스로 선택한 삶이라기보다, 타인이나 상황에 떠밀려 어쩔 수 없이 살아가는 느낌이 강하며, 일상은 점점 피로하고 무감각해집니다.
                  <br><br>
                  삶의 의미나 목적은 흐려져 있으며, 무엇을 위해 이 일을 하고 있는지, 왜 이 관계에 속해 있는지에 대한 <strong class="green">근본적인 회의</strong>가 뒤따릅니다. 그렇다고 지금의 상황을 바꾸려는 구체적인 실천이 일어나지도 않고, 현실을 버텨내는 데에만 에너지를 소진하며 살아가는 경향이 강합니다.
                  <br><br>
                  사회에서의 부정적 경험(예를 들어, 반복되는 실패, 무시당함, 소외감 등)은 자기 존재에 대한 부정감으로 이어지며, 자신은 무가치한 사람이라는 낙인(self-stigma)을 내면화하게 되기도 합니다. 이들은 직면하고 있는 문제의 실마리를 외부에서 찾으려 하지만, 지속적인 변화나 치유를 위해서는 <strong class="green">내면의 틀 자체를 전환해야 할 필요성</strong>이 절실한 시점입니다. 그렇지 않으면, 삶은 점점 더 감정적 노예 상태로 떨어지고, 자기 존재는 <strong class="green">욕망과 결핍의 회로에 사로잡힌 채 방향을 잃어버리게 됩니다.</strong>
                  <br><br>
                  <strong class="green">이 시점에서 중요한 것은 ‘나에게 진짜 중요한 것은 무엇인가’라는 질문을 삶의 중심에 다시 세우는 용기입니다.</strong>
                  <br><br>
                  ☆ 요약하자면, 이 구간에 해당하는 사람은<br>
                  - 내면의 중심이 붕괴되어 있으며,<br>
                  - 욕망과 불만 사이에서 삶의 통제력을 잃어가고,<br>
                  - 관계와 사회 속에서 자기 존재를 점점 더 소외시키는 상태에 있습니다.
                  <br><br>
                  삶의 만족도는 극히 낮고, 정서적·행동적 회복 능력도 약해져 있는 이 시기는 삶의 재구성과 근본적인 자기 성찰이 시급하게 요청되는 경고 단계입니다. 그러나 이 깊은 어둠 안에서도, 단 하나의 정직한 성찰, 단 한 번의 진심 어린 멈춤은 회복의 전환점이 될 수 있습니다.
                  `,
      },
    },
    {
      img: "metaq_9",
      score: "120<br>-<br>134",
      title: "조화부족",
      enTitle: "Lack of Harmony",
      keywords: "논쟁, 호전적, 자유를 바라는, 복수",
      desc: "주요 균형 요소가 어긋나 있으며 자기조절과 대인관계에서 어려움이 발생할 수 있음",
      detail: `IQ와 EQ를 넘어서는 'μετά Q 융합역량지수'는, 인간이 ‘의미’와 ‘가치’의 문제를 깊이 있게 탐색하고 통합적으로 해결해 나갈 때 발휘되는 고차원적 역량입니다. 이 지수는 우리의 행동과 삶을 보다 넓고 깊은 의미의 맥락 속에 자리매김하도록 도우며, 자아나 의식적 사고를 초월한 내면의 깊은 지혜와 연결된 능력을 반영합니다. 단지 기존의 가치를 인식하는 데 그치지 않고, 새로운 가치를 창조적으로 발견하고 구성해내는 통합적 창의 역량이기도 합니다. 
               <br><br>
               이는 개인의 정서적 균형(EB), 성격균형(PB). 다중역량균형(MiB), 교류균형(CB), 삶의 만족도(LS) 등을 종합적으로 분석하여 도출된 융합역량지수입니다.`,
      detailText_1: {
        title: "억압된 감정이 충돌로 드러나는 사람",
        content: `이 구간의 사람은 내면의 여러 감정과 욕구가 뒤엉켜 있으며, 그것들을 스스로 인식하거나 조율하는 능력이 부족한 상태입니다. 억눌린 분노, 좌절감, 상실감이 마음속 깊이 자리하고 있지만, 그 감정들을 다루는 방법을 배우지 못했기 때문에 <strong class="green">종종 충동적이거나 극단적인 방식으로 감정이 분출</strong>되곤 합니다.
                  <br><br>
                  자신을 돌아보는 데 익숙하지 않으며, 내면의 혼란이 정리되지 않은 채 계속 쌓이면서 자기 자신에 대한 혼동과 불신이 커지고, <strong class="green">감정 조절이 어려워질수록 ‘진짜 나’와 연결된 감각은 더욱 약해</strong>집니다.
                  <br><br>
                  내면의 불편함을 인식할 때마다 그것을 마주하기보다는, 그 원인을 바깥에서 찾으려는 경향이 강합니다.
                  이로 인해 자기 내면을 회복하거나 통합하려는 시도보다는, <strong class="green">외부와의 대립을 통해 문제를 해결하려는 방어기제</strong>가 더욱 강화되기도 합니다.
                  <br><br>
                  삶이 불공평하다는 인식이 강하고, 자신이 당했던 고통이나 억압에 대한 반작용으로서의 자유에 대한 갈망이 극단적인 형태로 나타나기도 합니다. 때때로 과거의 상처에 집착하며, 복수심이나 반항심을 원동력 삼아 삶을 살아가려는 경향도 드러납니다.
                  <br><br>
                  이러한 감정 에너지는 삶에 활력을 주는 듯 보이지만, 결국 자신을 더욱 고립시키고 내면의 진짜 회복을 방해하는 힘으로 작용합니다. 감정의 근원을 인식하지 못한 채 반복되는 투쟁은, 삶 전체를 전쟁터처럼 만들 수 있습니다.`,
      },
      detailText_2: {
        title: "논쟁과 충돌을 반복하며 거리감을 유지하는 사람",
        content: `이 단계의 사람은 타인과의 관계에서 안정감을 느끼기 어렵습니다. 조금만 위협을 느껴도 강하게 반응하거나, 사소한 의견 차이도 감정적으로 확대하여 <strong class="green">논쟁이나 갈등 상황으로 몰고 가는 경향</strong>이 나타납니다. 이들은 자신을 방어하려는 과정에서 <strong class="green">종종 호전적이거나 공격적인 태도</strong>를 취하며, 상대방과 대화를 나누기보다는 이기고자 하는 욕구가 앞서게 됩니다.
                  <br><br>
                  이러한 태도는 상대와의 진정한 연결을 방해하고, 결국 더 깊은 고립과 불신을 만들게 됩니다. 특히 가까운 관계에서는 더욱 격해지는 감정 반응을 보이며, 상대의 말이나 태도에 숨은 의도까지 의심하고 해석하려는 경향이 있습니다. 이로 인해 상대방은 자칫 잘못된 말이나 사소한 표현 하나로도 큰 갈등에 휘말릴 수 있어, 주변 사람들과의 관계가 불안정하고, 지속 가능성이 떨어질 수 있습니다.
                  <br><br>
                  이들은 자유롭고 싶다는 욕구가 강하지만, 정작 관계 안에서는 자신이 끊임없이 상대를 통제하려 하거나,
                  상대를 통해 자신의 상처를 반복 확인하는 방식으로 관계를 맺습니다. <strong class="green">자유를 갈망하면서도 분노의 굴레에 갇혀 관계를 파괴적으로 사용하는 역설적 상황</strong>이 반복됩니다. 
                  <br><br>
                  결국 이들은 관계를 통해 치유받기보다는, 상처를 되풀이하고 고립을 심화시키는 악순환에 갇히게 될 위험이 있음을 기억해야 합니다. 
                  `,
      },
      detailText_3: {
        title: "억울함과 투쟁심으로 움직이는 사람",
        content: `이 구간의 사람은 사회 속에서 자신을 설명할 수 있는 언어가 부족하거나, 자신이 받아야 할 정당한 대우를 받지 못하고 있다는 인식에 깊이 사로잡혀 있습니다. 이로 인해 종종 <strong class="green">내면의 억울함과 분노를 사회적 갈등과 대립 구조로 투사</strong>하며, 자신을 방어하고자 하는 강한 투쟁심을 드러냅니다.
                  <br><br>
                  사회적 역할이나 공동체 안에서 책임을 수행하기보다, <strong class="green">제도나 권위에 대한 불신, 강자에 대한 저항, 부당함에 대한 분노</strong>를 중심에 두고 행동할 수 있으며, 그 과정에서 협력보다는 대립, 참여보다는 철회의 방식이 반복됩니다.
                  <br><br>
                  특정한 조직이나 사람에게 배신당한 경험이 있을 경우, 그 상처를 소화하지 못한 채 <strong class="green">지속적인 복수심이나 단절 욕구</strong>로 이어질 수 있고, 이는 장기적으로 자신이 사회 안에서 설 자리를 스스로 좁히는 결과로 이어질 수 있습니다.
                  <br><br>
                  이 단계에서는 사회적 활동 자체가 스트레스로 느껴질 수 있으며, 타인과 협력하거나 소속되는 상황에서 감정적 방어가 먼저 작동하기 때문에 <strong class="green">함께 살아가는 경험 자체를 회피하거나 왜곡하는 경향</strong>이 생깁니다. 하지만 이런 <strong class="green">갈등의 뿌리를 정직하게 들여다볼 수 있다면, 이들 안에 있는 정의감, 자유의지, 고통을 딛고자 하는 힘은 충분히 회복적 방향으로 전환될 수 있습니다.</strong>
                  <br><br>
                  ☆ 요약하자면, 이 구간에 해당하는 사람은<br>
                  - 자기 내면의 억눌린 감정을 외부로 투사하며,<br>
                  - 대인관계에서 논쟁과 통제를 반복하고,<br>
                  - 사회 속에서도 불신과 갈등을 중심으로 관계를 맺으려는 경향이 강한 상태입니다.
                  <br><br>
                  이 상태는 삶 전체에 지속적인 긴장감과 방어적 에너지를 생성하며, 스스로를 더욱 피로하게 만들 수 있습니다. 그러나 이 갈등의 뿌리를 자각하고 다룰 수 있다면, 자유를 향한 갈망은 치유와 재창조의 방향으로 전환될 수 있습니다.`,
      },
    },
    {
      img: "metaq_10",
      score: "135<br>-<br>149",
      title: "불안정한 성향",
      enTitle: "Unstable Tendency",
      keywords: "분노, 가짜 나, 미움, 증오, 공격성, 적대적인 상황",
      desc: "감정, 성격, 교류, 인생 만족도 중 2개 이상의 영역에서 심각한 불균형 감지",
      detail: `IQ와 EQ를 넘어서는 'μετά Q 융합역량지수'는, 인간이 ‘의미’와 ‘가치’의 문제를 깊이 있게 탐색하고 통합적으로 해결해 나갈 때 발휘되는 고차원적 역량입니다. 이 지수는 우리의 행동과 삶을 보다 넓고 깊은 의미의 맥락 속에 자리매김하도록 도우며, 자아나 의식적 사고를 초월한 내면의 깊은 지혜와 연결된 능력을 반영합니다. 단지 기존의 가치를 인식하는 데 그치지 않고, 새로운 가치를 창조적으로 발견하고 구성해내는 통합적 창의 역량이기도 합니다. 
               <br><br>
               이는 개인의 정서적 균형(EB), 성격균형(PB). 다중역량균형(MiB), 교류균형(CB), 삶의 만족도(LS) 등을 종합적으로 분석하여 도출된 융합역량지수입니다.`,
      detailText_1: {
        title: "가짜 자아에 갇혀 진짜 자신을 외면하는 사람",
        content: `이 구간의 사람은 <strong class="green">겉으로 보이는 ‘나’와 내면의 진짜 ‘나’ 사이에 큰 간극</strong>을 경험합니다. 내면에서는 불안, 분노, 공허감이 얽혀 있으며, 그로 인해 자신의 감정을 왜곡하거나 외면하는 경향이 강하게 나타납니다. 삶의 만족감은 낮고, 스스로에 대한 신뢰가 부족하지만, 이 사실을 인정하는 것 자체가 두렵거나 받아들이기 어려워 보일 수 있습니다.
                  <br><br>
                  <strong class="green">자신의 감정을 섬세하게 인식하거나 다룰 수 있는 힘이 부족</strong>하며, 때로는 감정이 갑작스럽게 폭발하거나, 차가운 냉소로 변형되어 드러나기도 합니다. 이들은 자신이 느끼는 분노, 억울함, 미움을 정당화하거나 외부로 투사하면서, 자신의 고통을 타인의 탓으로 돌리는 심리적 방어에 익숙해져 있습니다. 
                  <br><br>
                  이런 감정은 순간적으로 자신을 보호하는 것처럼 느껴지지만, 결국은 자기 자신에 대한 오해를 깊게 만들고, 내면의 통합을 방해하게 됩니다. 감정과 생각, 행동이 일관되지 않기 때문에, 자신의 삶이 어디로 향하고 있는지 혼란스럽게 느껴질 수 있으며, 삶의 주요한 선택에서조차 ‘내가 진심으로 원해서 한 결정이었는가’라는 물음이 남게 됩니다.
                  <br><br>
                  무엇보다도, 스스로가 어떤 사람인지, 무엇을 원하는지에 대한 감각이 무뎌져 있고, 삶을 주체적으로 살아간다기보다는 <strong class="green">타인의 시선이나 외부 자극에 따라 반응적으로 움직이는 삶</strong>을 살기 쉽습니다. 그 결과, 진짜 나로서의 삶은 점점 더 흐릿해지고, 스스로를 조종할 수 없는 대상처럼 느끼는 무력감과 좌절이 깊어질 수 있습니다.`,
      },
      detailText_2: {
        title: "적대감과 방어로 연결이 차단된 사람",
        content: `이 구간의 사람은 대인관계에서 지속적인 분노, 불신, 적대감을 느끼는 경향이 강합니다. 타인의 행동을 곧잘 위협으로 해석하고, 작은 자극에도 예민하게 반응하며, 감정적으로 격한 반응을 보이거나 관계를 단절하려는 행동을 반복할 수 있습니다.
                  <br><br>
                  진짜 자신의 감정을 드러내기보다는 <strong class="green">‘가짜 나’—강해 보이는 자아, 무관심한 척하는 자아—를 앞세우며
                  타인에게 상처받지 않으려는 심리적 방어선</strong>을 강하게 유지합니다. 하지만 이러한 태도는 실제로는 관계의 피로와 고립을 더욱 심화시키며, 결국 외로움과 소외감이 누적되어 다시 분노로 되돌아오는 악순환의 고리를 형성합니다.
                  <br><br>
                  이들은 타인에게 인정받고 싶은 욕구와 함께, 타인을 미워하고 증오하려는 감정도 공존하고 있어 관계 안에서 극단적인 감정의 요동을 경험할 수 있습니다. 상대가 가까워질수록 불안해지고, 멀어지면 버려졌다는 느낌에 사로잡히는 등 <strong class="green">감정적 경계와 친밀감의 기준이 왜곡</strong>되어 있는 경우가 많습니다.
                  <br><br>
                  방어적 반응이 반복되다 보면, 결국 타인에 대한 신뢰를 거의 잃게 되고, 자신조차 ‘왜 나는 이렇게 반응하는가’를 인식하지 못한 채 관계를 계속해서 망가뜨리는 방식으로 살아가게 됩니다.
                  `,
      },
      detailText_3: {
        title: "삶의 맥락에서 자신을 소외시키는 사람",
        content: `이 구간의 사람은 사회 속에서 자신의 역할이나 위치에 안정감을 느끼기 어렵습니다. 직장, 가족, 공동체 등 어떤 소속에서도 ‘나는 여기 있어도 괜찮은 사람인가’라는 질문이 반복되며, 무기력감과 소외감을 자주 경험합니다. 그로 인해 사회적 관계 안에서도 자기 보호를 위해 <strong class="green">공격적이거나 냉소적인 태도</strong>를 보이기 쉽고, 비판을 들었을 때는 그것을 성장의 기회로 받아들이기보다 <strong class="green">정체성 자체에 대한 위협</strong>으로 받아들이는 경향이 강합니다.
                  <br><br>
                  타인이나 제도, 사회 구조에 대한 강한 불신을 갖고 있으며, 때때로 자신의 실패를 외부로 정당화하거나 <strong class="green">체념과 냉소 속에 자기 책임을 회피</strong>하려는 태도가 나타날 수 있습니다. 이는 사회 속에서 반복되는 실패 경험과 좌절을 내면화하지 못한 결과일 수 있습니다.
                  <br><br>
                  자기 통합이 이루어지지 않은 상태에서는 무엇이 나의 선택이고, 무엇이 타인의 강요였는지를 구분하기 어렵고, 그로 인해 삶의 방향감각을 잃은 채 방황하거나 <strong class="green">기능적으로는 참여하고 있으나 내면은 철저히 분리된 상태로 존재</strong>하게 됩니다. 이러한 분열 상태는 <strong class="green">가짜로 살아가는 느낌, 존재의 공허함</strong>을 심화시키고,
                  장기적으로는 심리적 탈진과 자아 붕괴의 위험으로 이어질 수 있기 때문에 <strong class="green">내면을 회복하는 치유적 개입과 정서적 지지가 꼭 필요</strong>한 시점입니다.
                  <br><br>
                  ☆ 요약하자면, 이 구간에 해당하는 사람은<br>
                  - 내면의 자기감각이 흐릿하고 감정적 폭발과 억압이 교차하며,<br>
                  - 대인관계에서 분노와 적대감이 반복되고,<br>
                  - 사회 속에서는 역할 수행과 자기 존재 사이의 괴리를 경험하는 상태입니다.
                  <br><br>
                  불안정한 감정의 물결 속에서 ‘진짜 나’를 상실하고 있지만, 이 상태를 자각하고 받아들이는 것만으로도 회복의 문은 열릴 수 있습니다. 이 시기는 가장 혼란스러우면서도, 동시에 가장 깊은 회복이 가능한 출발점입니다.`,
      },
    },
    {
      img: "metaq_11",
      score: "150<br>-<br>164",
      title: "중간 수준의 불균형",
      enTitle: "Mid-level Imbalance",
      keywords: "교만과 허세, 방어적, 파벌, 오만함, 부정부패",
      desc: "자기통찰 능력 부족, 스트레스 요인으로 균형이 흔들림",
      detail: `IQ와 EQ를 넘어서는 'μετά Q 융합역량지수'는, 인간이 ‘의미’와 ‘가치’의 문제를 깊이 있게 탐색하고 통합적으로 해결해 나갈 때 발휘되는 고차원적 역량입니다. 이 지수는 우리의 행동과 삶을 보다 넓고 깊은 의미의 맥락 속에 자리매김하도록 도우며, 자아나 의식적 사고를 초월한 내면의 깊은 지혜와 연결된 능력을 반영합니다. 단지 기존의 가치를 인식하는 데 그치지 않고, 새로운 가치를 창조적으로 발견하고 구성해내는 통합적 창의 역량이기도 합니다. 
               <br><br>
               이는 개인의 정서적 균형(EB), 성격균형(PB). 다중역량균형(MiB), 교류균형(CB), 삶의 만족도(LS) 등을 종합적으로 분석하여 도출된 융합역량지수입니다.`,
      detailText_1: {
        title: "자기 이미지에 갇혀 진짜 나를 보지 못하는 사람",
        content: `이 구간에 해당하는 사람은 자기 자신을 깊이 바라보는 능력이 부족하며, 스스로를 특정한 이미지로 과장하거나 포장하려는 경향이 강하게 나타납니다. 자신의 불완전함이나 연약한 부분을 인정하기 어려워하고, 외부로부터의 인정과 통제 가능한 이미지를 통해 자존감을 유지하려 합니다.
                  <br><br>
                  내면의 불안과 스트레스를 직접 다루기보다는, 그 감정을 억압하거나 외부 환경, 타인, 시스템에 전가하는 <strong class="green">회피적 방어기제</strong>가 강하게 작동합니다. 스스로를 되돌아보는 성찰의 힘이 약하기 때문에, 감정이 격해질 경우 갑작스러운 폭발, 냉소, 냉정한 태도로 감정을 무장한 채 대응하게 됩니다.
                  <br><br>
                  이들은 ‘나는 잘하고 있다’, ‘문제는 나에게 있지 않다’는 확신 속에 머무르며, <strong class="green">자기 확장보다 자아 고착이 강화되는 방향</strong>으로 반응할 수 있습니다. 자기 서사 안에만 갇혀 실제 삶과의 불일치가 커지고 있지만, 그 간극을 인식하거나 다룰 여유가 부족한 상태입니다.
                  <br><br>
                  하지만 이 시기야말로 <strong class="green">진짜 내면을 정직하게 마주해야 할 필요가 가장 큰 시기</strong>이기도 합니다. 진짜 나를 방어 없이 바라보는 것이 어렵고 불편하겠지만, 그 불편함을 직면하는 순간부터 회복과 변화의 가능성이 열리기 시작합니다.`,
      },
      detailText_2: {
        title: "인정투쟁과 소속불안을 반복하는 사람",
        content: `이 단계의 사람은 타인과의 관계에서 <strong class="green">인정받고 우위를 점하고자 하는 욕구</strong>가 강하게 작동합니다. 자신의 위치나 역할을 지키기 위해 타인과 비교하고 경쟁하는 경향이 있으며, 관계 안에서의 대등함보다는 ‘누가 더 강한가’라는 역학 속에서 행동합니다.
                  <br><br>
                  가까운 관계일수록 진짜 감정을 드러내기보다는 방어적 태도를 취하게 되며, 누군가에게 지적받거나 조언을 들을 때 쉽게 불쾌해지거나 부정적으로 반응할 수 있습니다. 감정을 표현하더라도 감정의 본질보다는, 그 감정을 통해 ‘상대보다 우위에 있거나 우월해 보이려는 표현 방식’이 두드러지게 나타납니다.
                  <br><br>
                  이들은 종종 파벌을 만들거나 특정 집단 안에서 <strong class="green">자신의 입지를 굳히는 방식으로 안전을 확보</strong>하려 합니다. 하지만 이 방식은 일시적인 안정을 줄 수는 있어도, 진짜 친밀감이나 신뢰는 쌓기 어렵고, 오히려 감정의 고립과 왜곡을 심화시킬 수 있습니다.
                  <br><br>
                  또한, 자신에게 불리하다고 판단되는 관계에서는 감정과 사실을 분리하기보다, <strong class="green">감정적으로 과장하거나 회피하는 방식으로 관계를 통제하려는 경향</strong>이 나타납니다. 이러한 패턴은 자기 방어의 결과일 수 있지만, 그로 인해 더 깊은 상호작용이 막히고 진정한 연결은 어려워지는 상황을 반복하게 됩니다. 
                  <br><br>
                  이러한 패턴은 자기 방어의 결과일 수 있지만, 그로 인해 더 깊은 상호작용이 막히고 진정한 연결은 어려워지는 상황을 반복하게 됩니다. <strong class="green">이 반복 속에서 관계는 점차 소진되고, 자신 또한 점점 더 고립감과 피로</strong>를 느끼게 됩니다.`,
      },
      detailText_3: {
        title: "외적 위치를 통해 내면의 공허를 채우려는 사람",
        content: `이 구간의 사람은 사회 속에서 <strong class="green">역할, 지위, 성과, 소속</strong>을 매우 중요하게 여기며, 그것을 통해 자신의 가치를 증명하려는 욕구가 강합니다. 사회적 책임이나 역할 수행 자체보다, 그 역할을 통해 얼마나 영향력 있고 강한 사람으로 보일 수 있는지가 더 중요한 요소가 됩니다.
                  <br><br>
                  그러나 이러한 외적 집착은 내면의 불안과 무력감을 감추기 위한 보상심리일 수 있으며, 지나친 통제욕, 과시, 경쟁심으로 이어질 경우 주변과의 갈등을 유발하거나 불필요한 부정적 이미지로 연결되기 쉽습니다. 비판이나 피드백을 위협으로 받아들이고, 실수를 인정하지 않으려는 태도는 자신뿐 아니라 공동체 전체에도 영향을 미칠 수 있습니다. 때로는 편법이나 형식주의, 책임 회피 등의 방식으로 <strong class="green">‘겉은 그럴듯하지만 속은 공허한’ 방식의 참여</strong>가 나타날 수도 있습니다.
                  <br><br>
                  이들은 사회 속에서 소속되기를 원하면서도, 동시에 누구보다 독립적이고 뛰어나야 한다는 모순된 기대 속에 놓여 있습니다. 진짜 나를 중심에 두지 않으면, 사회적 역할은 공허한 껍데기로 남기 쉽습니다. 이 시기에는 <strong class="green">사회적 이미지 뒤에 숨은 나의 감정, 가치, 동기를 정직하게 성찰해보는 것이 필요</strong>하며,
                  ‘무엇을 위해 이 길을 가고 있는가?’에 대한 질문이 내면에 깊이 울려야 합니다.
                  <br><br>
                  ☆ 요약하자면, 이 구간에 해당하는 사람은<br>
                  - 자기 감정과 생각을 방어적 태도로 덮고 있으며,<br>
                  - 관계 속에서도 진짜 연결보다 우위와 통제를 추구하고,<br>
                  - 사회적으로는 지위와 역할에 집착하지만 내면의 방향성은 흐릿한 상태입니다.
                  <br><br>
                  자기 성찰과 감정 조절의 힘이 약한 상태로, 겉은 강하지만 속은 무력한 구조가 반복되는 경향이 있습니다. 이 시기는 내면의 불편한 진실을 인정하고, 진짜 나를 회복하기 위한 용기 있는 첫 성찰이 필요한 시점입니다.`,
      },
    },
    {
      img: "metaq_12",
      score: "165<br>-<br>179",
      title: "내적조화 부족",
      enTitle: "Lack of Inner Harmony",
      keywords: "자존심, 자아팽창, 요구가 많음, 과장, 방어적, 분열, 부정",
      desc: "자아 성장과 감정적 불균형, 자기성찰 능력 부족",
      detail: `IQ와 EQ를 넘어서는 'μετά Q 융합역량지수'는, 인간이 ‘의미’와 ‘가치’의 문제를 깊이 있게 탐색하고 통합적으로 해결해 나갈 때 발휘되는 고차원적 역량입니다. 이 지수는 우리의 행동과 삶을 보다 넓고 깊은 의미의 맥락 속에 자리매김하도록 도우며, 자아나 의식적 사고를 초월한 내면의 깊은 지혜와 연결된 능력을 반영합니다. 단지 기존의 가치를 인식하는 데 그치지 않고, 새로운 가치를 창조적으로 발견하고 구성해내는 통합적 창의 역량이기도 합니다. 
               <br><br>
               이는 개인의 정서적 균형(EB), 성격균형(PB). 다중역량균형(MiB), 교류균형(CB), 삶의 만족도(LS) 등을 종합적으로 분석하여 도출된 융합역량지수입니다.`,
      detailText_1: {
        title: "자기 안에서 갈등하고 방어하는 사람",
        content: `이 구간에 해당하는 사람은 겉으로는 문제가 없어 보이지만, 내면에서는 불안정한 감정과 생각이 자주 충돌하고 있습니다. 자신의 감정을 직면하기 어려워하고, 불편한 감정을 회피하거나 외부 탓으로 돌리는 경향이 강합니다. 감정과 사고, 행동 사이의 연결이 분리되어 있어 <strong class="green">자기 안의 일관성과 통합감이 부족한 상태</strong>입니다.
                  <br><br>
                  이들은 자신의 약점을 그대로 바라보기 어려워 방어적 태도로 대응하며, 실수나 실패를 인정하기보다 <strong class="green">자존심을 지키려는 욕구</strong>가 우선되곤 합니다. 때로는 자신의 모습을 과장하거나, 자신이 느끼는 불안을 감추기 위해 지나치게 강한 척, 바쁜 척, 괜찮은 척을 하며 자신을 보호하려 합니다.
                  <br><br>
                  감정이 올라올 때 이를 섬세하게 식별하지 못하고, 상황에 따라 감정을 억누르거나 갑작스럽게 폭발시키는 등 <strong class="green">극단적인 반응</strong>을 보일 수 있습니다. 자신을 돌아보는 성찰력이 아직 충분히 형성되지 않았기 때문에, 내면의 혼란을 스스로 해결하기 어려운 상태입니다.
                  <br><br>
                  하지만 이러한 불균형 속에서도 자기 안의 불편함을 느끼고 있다는 것 자체가 <strong class="green">내적 변화의 가능성을 암시하는 신호</strong>일 수 있습니다. <strong class="green">자신을 바라보는 방식을 조금씩 조정해가는 과정이 필요한 시점</strong>입니다. 지금 느끼는 내면의 불편함은, 당신이 스스로와 진짜로 연결되기 위한 첫 번째 신호일 수 있습니다.`,
      },
      detailText_2: {
        title: "인정 욕구와 방어적 태도가 공존하는 사람",
        content: `이 단계의 사람은 타인과의 관계에서 인정받고자 하는 욕구가 강하게 나타납니다. 관계 안에서 자신의 존재가 충분히 존중받지 못한다고 느낄 때, 과도한 기대, 불만, 혹은 실망이 빠르게 고조될 수 있습니다. 타인에게 자신을 강하게 어필하거나, 과장된 방식으로 표현하려는 경향이 있으며, 동시에 거절이나 비판에는 민감하게 반응합니다.
                  <br><br>
                  <strong class="green">작은 지적에도 쉽게 방어적이 되거나, 상대의 의도를 오해하는 경우</strong>도 많습니다. 관계를 유지하고 싶지만, 깊은 친밀감이 생기려 할 때 오히려 스스로 거리를 두거나, 감정의 벽을 세우는 모순적인 행동이 반복될 수 있습니다. 이는 마음속 깊은 곳에 자리 잡은 <strong class="green">불신과 분열된 자아 이미지</strong>에서 비롯된 반응입니다.
                  <br><br>
                  다른 사람의 감정을 공감하거나 이해하기보다는, 자신이 중심이 되어야 한다는 무의식적인 태도에 사로잡히기도 하며, 자신의 요구가 충분히 충족되지 않을 때 쉽게 분노나 실망에 빠지게 됩니다.
                  <br><br>
                  그러나 이런 <strong class="green">반복되는 갈등의 패턴을 자각</strong>하고 받아들일 수 있다면, 그 자체가 <strong class="green">관계 회복의 출발점</strong>이 될 수 있습니다. 이 시기는 관계 안에서 자신의 반응을 관찰하고 감정을 다루는 연습을 통해 조금씩 전환할 수 있는 중요한 전환기입니다.`,
      },
      detailText_3: {
        title: "외형에 집중하지만 내면의 방향성이 불분명한 사람",
        content: `이 구간에 있는 사람은 사회적 역할이나 외적 성과에 집중하는 경향이 강합니다. 겉으로 보기에는 성실하고 열정적으로 보일 수 있지만, 실제 내면에서는 <strong class="green">혼란, 피로, 공허감</strong>이 자리하고 있습니다. 삶의 방향이나 일의 의미에 대해 깊이 생각하기보다는 그저 바쁘게 움직이며 정체성을 채우려는 방식을 선택할 가능성이 높습니다.
                  <br><br>
                  스스로에 대한 기대는 크지만, 현실적인 한계나 피로에 부딪히면 좌절하거나 타인 탓을 하며 방어적으로 반응하게 됩니다. 자신의 책임을 받아들이기보다 <strong class="green">비난이나 정당화를 통해 자존감을 보호</strong>하려는 태도가 앞서게 됩니다.
                  <br><br>
                  사회 속에서의 평가나 인정이 중요하게 여겨지며, 실제로 많은 에너지를 외부 성과와 비교에 소비하게 됩니다. 그 과정에서 내면의 욕구와 진짜 자아는 점점 더 가려지게 되고, <strong class="green">'내가 왜 이 일을 하는가',</strong> '지금 이 삶이 내게 어떤 의미인가'에 대한 질문이 흐릿해집니다.
                  <br><br>
                  이 시기에는 멈춰 서서 <strong class="green">자신이 추구하는 가치를 다시 묻고 정리하는 과정</strong>이 필요합니다. 삶의 방향성이 정립되지 않으면, 자존심이나 성취감으로 채워진 겉모습에 비해 내면은 공허하고 분열된 상태에 머무를 수 있습니다.
                  <br><br>
                  ☆ 요약하자면, 이 구간에 해당하는 사람은<br>
                  - 자기 안의 감정을 방어적으로 처리하며 통합하지 못하고,<br>
                  - 관계 속에서 인정욕구와 방어반응이 얽혀 있는 상태이며,<br>
                  - 사회적 역할 수행에는 집중하지만 내면의 방향은 흐릿한 상황에 놓여 있습니다.
                  <br><br>
                  이 시기는 불편한 진실과 마주해야 하는 용기, 그리고 내면의 질서를 회복하려는 첫 시도가 요구되는 시기입니다. 겉으로 보이는 균형이 아닌, 진짜 자기 자신과 연결되기 위한 자기성찰의 문턱에 서 있는 단계입니다.`,
      },
    },
    {
      img: "metaq_13",
      score: "180<br>-<br>194",
      title: "부분적 불균형",
      enTitle: "Partial Imbalance",
      keywords: "용기, 긍정, 힘을 얻음, 동기부여, 성취, 결단, 인내, 도전",
      desc: "일부 영역에서 균형이 부족하지만 자기조절을 통해 개선이 가능한 상황",
      detail: `IQ와 EQ를 넘어서는 'μετά Q 융합역량지수'는, 인간이 ‘의미’와 ‘가치’의 문제를 깊이 있게 탐색하고 통합적으로 해결해 나갈 때 발휘되는 고차원적 역량입니다. 이 지수는 우리의 행동과 삶을 보다 넓고 깊은 의미의 맥락 속에 자리매김하도록 도우며, 자아나 의식적 사고를 초월한 내면의 깊은 지혜와 연결된 능력을 반영합니다. 단지 기존의 가치를 인식하는 데 그치지 않고, 새로운 가치를 창조적으로 발견하고 구성해내는 통합적 창의 역량이기도 합니다. 
               <br><br>
               이는 개인의 정서적 균형(EB), 성격균형(PB). 다중역량균형(MiB), 교류균형(CB), 삶의 만족도(LS) 등을 종합적으로 분석하여 도출된 융합역량지수입니다.`,
      detailText_1: {
        title: "흔들림 속에서도 방향을 세우려는 사람",
        content: `이 구간에 해당하는 사람은 삶의 여러 영역에서 균형을 유지하려고 애쓰고 있지만, 일부 감정이나 행동 패턴에서 아직 안정되지 않은 부분이 존재합니다. 자기 자신에 대한 인식은 부분적으로 깨어 있으나, 감정이 요동칠 때 중심을 잡기 어려운 순간이 반복될 수 있습니다.
                  <br><br>
                  그럼에도 불구하고, 이들은 <strong class="green">자기 삶을 더 나은 방향으로 바꾸고자 하는 의지와 결단력</strong>을 지니고 있습니다. 불편한 감정이나 무력감이 들 때에도, 그것을 외면하거나 억압하기보다는 ‘어떻게 해야 이 상황을 넘어설 수 있을까’를 고민하며 해답을 찾으려 합니다.
                  <br><br>
                  이 단계에서는 자신을 되돌아보는 시간이 많아지고, 이전에 피하거나 무시했던 감정과 마주할 용기가 조금씩 생겨납니다. 상처받은 경험, 억눌렸던 욕구, 혼란스러웠던 선택의 순간들이 다시 떠오르며, 그 모든 것들을 정리하고 통합하려는 <strong class="green">내면의 도전과 배움의 흐름</strong>이 시작됩니다.
                  <br><br>
                  아직 안정적이지는 않지만, <strong class="green">성장하고자 하는 내면의 긍정 에너지</strong>가 점차 강해지고 있으며, 삶을 진지하게 개선해나가고 싶은 동기부여의 불씨가 살아나고 있는 시점입니다. 이 불균형은 위기가 아니라 <strong class="green">변화와 전환의 전조</strong>일 수 있습니다.
                  `,
      },
      detailText_2: {
        title: "불안과 기대 사이에서 연결을 시도하는 사람",
        content: `이 단계의 사람은 타인과의 관계에서 안정감과 불안감이 공존하는 상태에 있습니다. 가까워질수록 상처받을까 두렵고, 멀어질수록 외로움이 느껴지는 이중적인 감정 안에서 <strong class="green">진짜 관계를 맺고 싶은 마음과 조심스러움이 동시에 존재</strong>합니다.
                  <br><br>
                  때때로 타인의 시선이나 평가에 과민하게 반응하기도 하며, 관계를 유지하는 과정에서 감정의 소모를 크게 경험할 수 있습니다. 하지만 이러한 어려움 속에서도, <strong class="green">스스로를 이해시키고 타인을 이해하고자 하는 감정적 노력과 인내가 점차 강화</strong>되고 있습니다.
                  <br><br>
                  관계 속에서 반복되는 패턴이나 갈등을 인식하기 시작하며, 내가 어떤 방식으로 타인과 연결되고 있는지를 돌아보려는 시도가 생깁니다. 때로는 서툴게 감정을 표현하거나 충동적으로 반응할 수 있지만, 그 뒤에 ‘다시 해보고 싶다’, ‘이해받고 싶다’는 <strong class="green">긍정적 회복 욕구</strong>가 숨어 있습니다.
                  <br><br>
                  이들은 단절을 선택하기보다 <strong class="green">관계 안에서 성장하려는 도전 의식</strong>을 품고 있으며, 자신의 마음을 정직하게 표현하는 연습을 통해, 점점 더 진정성 있는 소통의 기반을 다져갑니다. <strong class="green">이 과정은 느리고 불완전하지만, 분명히 앞으로 나아가고 있습니다.</strong> 진짜 나와 타인을 연결하는 길은 서툴고 멀게 느껴질지라도, 그 한 걸음 한 걸음이 관계의 뿌리를 조금씩 단단히 내리고 있습니다.`,
      },
      detailText_3: {
        title: "나만의 방식으로 다시 힘을 얻으려는 사람",
        content: `이 구간의 사람은 사회 속에서의 역할 수행에 있어서 의욕과 피로, 자신감과 회의감이 엇갈리는 복합적인 상태에 놓여 있습니다. 업무나 공동체 활동에서 성실히 참여하려 하지만, 과도한 기대나 반복되는 실패 경험이 쌓이면 쉽게 지치고 흔들릴 수 있습니다.
                  <br><br>
                  하지만 이들은 <strong class="green">자신만의 방식으로 회복하고 일어설 줄 아는 힘</strong>을 키워가고 있습니다. 어느 순간에는 과감히 정리하거나 멈추는 결단력을 발휘하기도 하며, 필요할 때는 새로운 도전을 위해 스스로를 다시 세우는 강한 추진력이 나타나기도 합니다.
                  <br><br>
                  자신이 속한 환경에서의 부조화나 부당함에 예민하게 반응하면서도, 그것을 극복하려는 구체적인 행동으로 나아가려는 경향이 있습니다. 감정에만 머무르지 않고, <strong class="green">‘내가 할 수 있는 최선이 무엇인지’를 찾아내려는 의식적 실천의 기운</strong>이 자라고 있습니다.
                  <br><br>
                  이들은 아직 삶의 모든 부분에서 안정감을 갖춘 것은 아니지만, 지금의 불균형을 통해 삶의 본질적인 의미를 찾아가는 중이며, <strong class="green">성취에 대한 열망과 실제 행동력이 함께 움직일 수 있는 전환점</strong>에 서 있습니다.
                  <br><br>
                  ☆ 요약하자면, 이 구간에 해당하는 사람은<br>
                  - 자신의 불안정함을 인식하고 개선하려는 용기를 내고 있으며,<br>
                  - 관계 속에서도 진심으로 연결되려는 노력을 지속하고,<br>
                  - 사회 속에서 자신의 방식으로 힘을 회복하고 다시 참여하려는 태도를 지닌 사람입니다.
                  <br><br>
                  아직 흔들림이 남아있지만, 그 안에서 변화의 의지와 실천의 씨앗이 분명히 움트고 있습니다. 이 단계는 불균형을 넘어 진짜 성장을 위한 에너지가 깨어나는 시기입니다.`,
      },
    },
    {
      img: "metaq_14",
      score: "195<br>-<br>209",
      title: "일반적인 균형상태",
      enTitle: "Ordinary Balanced State",
      keywords: "중용, 신뢰, 해방, 만족, 융통성, 유연함, 자신감, 여유",
      desc: "삶의 여러 측면에서 무난한 균형을 유지, 특정 영역에서의 조정이 필요한 상황",
      detail: `IQ와 EQ를 넘어서는 'μετά Q 융합역량지수'는, 인간이 ‘의미’와 ‘가치’의 문제를 깊이 있게 탐색하고 통합적으로 해결해 나갈 때 발휘되는 고차원적 역량입니다. 이 지수는 우리의 행동과 삶을 보다 넓고 깊은 의미의 맥락 속에 자리매김하도록 도우며, 자아나 의식적 사고를 초월한 내면의 깊은 지혜와 연결된 능력을 반영합니다. 단지 기존의 가치를 인식하는 데 그치지 않고, 새로운 가치를 창조적으로 발견하고 구성해내는 통합적 창의 역량이기도 합니다. 
               <br><br>
               이는 개인의 정서적 균형(EB), 성격균형(PB). 다중역량균형(MiB), 교류균형(CB), 삶의 만족도(LS) 등을 종합적으로 분석하여 도출된 융합역량지수입니다.`,
      detailText_1: {
        title: "일상을 조화롭게 살아가려는 사람",
        content: `이 구간에 해당하는 사람은 자기 삶을 전체적으로 보면 무난하게 잘 살아내고 있으며, 크게 흔들리는 위기 없이 비교적 안정적인 상태를 유지하고 있습니다. 스스로의 감정과 행동에 대해 기본적인 자각이 있으며, 극단적인 감정 반응 없이 일상의 흐름에 적응하고 살아가는 <strong class="green">균형 있는 자아감</strong>을 지니고 있습니다.
                  <br><br>
                  이들은 자신에 대해 큰 불만 없이 받아들이며, 자기 삶에 일정 부분 만족을 느끼고 있습니다. 특정 영역에서는 더 나아가고 싶은 욕구나 조정의 필요를 느끼기도 하지만, 그것이 삶 전체를 흔들 만큼 큰 갈등으로 이어지지는 않습니다. <strong class="green">기본적인 자신감과 자기 효능감을 바탕으로, 삶을 큰 무리 없이 꾸려나갈 수 있는 내적 여유</strong>를 갖추고 있습니다.
                  <br><br>
                  다만 이 여유가 때로는 정체감으로 이어질 가능성도 있으며, <strong class="green">보다 깊은 자기 이해나 내면의 해방감을 얻기 위해서는 의식적인 탐색과 실천이 필요한 시점</strong>이기도 합니다.
                  <br><br>
                  이 구간의 사람은 자신의 한계를 인정하면서도 무력감에 빠지지 않고, 성장할 수 있는 여지를 긍정적으로 바라보는 유연한 태도를 유지합니다. 삶을 억지로 끌고 가기보다, 흐름에 맞춰 조율하며 살아가려는 중용의 미덕이 자연스럽게 드러납니다.`,
      },
      detailText_2: {
        title: "기본적인 신뢰를 바탕으로 관계를 유지하는 사람",
        content: `이 단계의 사람은 타인과의 관계에서 무리 없이 잘 어울리며, <strong class="green">심한 갈등 없이 적당한 거리감과 친밀감을 조율</strong>할 수 있습니다. 기본적인 신뢰를 바탕으로 사람들과 관계를 맺으며, 대부분의 상황에서 <strong class="green">사회적 역할을 충실히 수행할 수 있는 관계의 유연성</strong>을 갖추고 있습니다.
                  <br><br>
                  관계 속에서 특별히 깊은 고통을 경험하지는 않았지만, 가끔은 자신이 진짜 원하는 관계의 모습이 무엇인지 돌아보게 됩니다. 자신이 주체적으로 관계를 만들어가는지, 아니면 습관처럼 무난함에 머물고 있는지에 대해 질문이 떠오르기도 합니다.
                  <br><br>
                  이들은 관계의 유지 자체에 큰 어려움을 느끼지 않지만, 가끔씩 <strong class="green">더 깊이 연결되고 싶은 욕구나, 반대로 혼자 있고 싶은 충동 사이에서 자기 안의 균형점을 조정해야 할 필요성</strong>을 느끼기도 합니다.
                  <br><br>
                  친절하고 배려 깊은 사람이지만, 때로는 피곤함을 감추고 무던하게 넘기는 경향도 있으며, 그런 태도 속에 자기 감정이 억눌려 있는 경우도 있을 수 있습니다. 이런 사람일수록, <strong class="green">관계 속에서 자신의 욕구를 정직하게 드러내는 연습이 성장의 열쇠</strong>가 될 수 있습니다. 지금의 안정감이 때론 익숙함에 머물게 할 수 있지만, 그 안에 잠든 자기 감정을 깨우는 용기가 진짜 친밀함의 문을 엽니다.`,
      },
      detailText_3: {
        title: "무난하게 참여하며 자기 위치를 지키는 사람",
        content: `이 단계에 이른 사람은 사회적 삶에서도 큰 문제 없이 잘 기능하고 있습니다. 직장, 가족, 공동체에서 주어진 역할을 무리 없이 수행하며, <strong class="green">과도한 스트레스나 좌절 없이 비교적 원활한 사회적 관계망을 유지</strong>합니다. 자신이 맡은 역할에 책임감을 갖고 있으며, 지속적으로 수행할 수 있는 <strong class="green">기본적인 안정감과 성실성</strong>이 돋보입니다.
                  <br><br>
                  그럼에도 불구하고, 일부 영역에서는 내면의 충족감이 약하거나, ‘이게 정말 내가 원하는 길일까?’ 하는 질문이 속으로 떠오르기도 합니다. 사회 속에서 어느 정도 자리를 잡았지만, 그 자리가 <strong class="green">진짜 자기 자신과 얼마나 연결되어 있는가에 대한 고민이 시작</strong>될 수 있습니다.
                  <br><br>
                  이들은 변화에 대한 두려움보다는 새로운 가능성에 대한 여유 있는 호기심을 지니고 있으며, 보다 의미 있는 사회적 참여를 위한 준비 단계에 서 있습니다. 완전한 전환보다는, 작은 조정이나 방향의 전환을 통해 자기 삶을 더 온전히 만들 수 있다는 자신감과 융통성을 갖고 있습니다.
                  <br><br>
                  이런 사람은 <strong class="green">더 높은 조화와 진정성을 향해 나아갈 수 있는 기반이 이미 마련되어 있으며, 지금의 안정에 머무르지 않고, 그 안에서 더 깊은 만족과 방향성을 찾아가는 성숙의 여정 위에 있습니다.</strong> 참여의 과정에 가치를 둡니다. 작지만 의미 있는 변화의 씨앗이 이 구간에서 자라고 있습니다.
                  <br><br>
                  ☆ 요약하자면, 이 구간에 해당하는 사람은<br>
                  - 무난하고 조화롭게 일상을 살아가면서,<br>
                  - 관계와 사회 속에서도 신뢰와 유연함을 유지하며,<br>
                  - 자신의 삶을 더 의미 있게 조정해가려는 여유와 가능성을 지닌 사람입니다.
                  <br><br>
                  삶이 안정된 만큼, 이제는 ‘조금 더 진짜 나답게’ 살아가기 위한 내면의 정돈과 탐색이 필요한 시점이며,
                  지금의 평온함을 더 깊은 통합으로 확장할 수 있는 준비가 되어 있는 단계입니다.`,
      },
    },
    {
      img: "metaq_15",
      score: "210<br>-<br>224",
      title: "균형유지단계",
      enTitle: "Balance-Maintenance Stage",
      keywords: "자주성, 낙관, 의도, 친절, 최선, 배움, 참여, 저항극복",
      desc: "전반적인 균형을 유지, 스트레스나 환경의 변화에 영향을 받아 불균형 발생",
      detail: `IQ와 EQ를 넘어서는 'μετά Q 융합역량지수'는, 인간이 ‘의미’와 ‘가치’의 문제를 깊이 있게 탐색하고 통합적으로 해결해 나갈 때 발휘되는 고차원적 역량입니다. 이 지수는 우리의 행동과 삶을 보다 넓고 깊은 의미의 맥락 속에 자리매김하도록 도우며, 자아나 의식적 사고를 초월한 내면의 깊은 지혜와 연결된 능력을 반영합니다. 단지 기존의 가치를 인식하는 데 그치지 않고, 새로운 가치를 창조적으로 발견하고 구성해내는 통합적 창의 역량이기도 합니다. 
               <br><br>
               이는 개인의 정서적 균형(EB), 성격균형(PB). 다중역량균형(MiB), 교류균형(CB), 삶의 만족도(LS) 등을 종합적으로 분석하여 도출된 융합역량지수입니다.`,
      detailText_1: {
        title: "중심을 지키려 애쓰는 사람",
        content: `이 구간에 해당하는 사람은 자신을 지탱하고 있는 <strong class="green">내면의 균형을 점차 인식</strong>하고 있으며, 스스로의 <strong class="green">삶을 주도적으로 살아가려는 의지</strong>가 비교적 뚜렷하게 드러납니다. 다만 그 중심이 아직 단단히 자리 잡은 것은 아니기에, <strong class="green">스트레스나 낯선 환경의 자극에 의해 균형이 무너지거나 흔들릴 가능성</strong>이 있습니다.
                  <br><br>
                  이들은 자신의 감정과 반응을 어느 정도 인식하고 있으며, 무엇이 자신에게 도움이 되고 해가 되는지를 구분하려는 노력도 꾸준히 이어가고 있습니다. 감정의 기복이 있을 수 있지만, 그 속에서도 <strong class="green">자신을 다독이며 회복하려는 낙관적 태도</strong>가 돋보입니다. 자기 자신에게 최선을 다하고자 하는 진심이 있으며, 실패하거나 실수했을 때에도 자책보다는 다음을 위한 의미를 찾으려 합니다.
                  <br><br>
                  삶의 흐름을 있는 그대로 받아들이는 연습이 진행 중이며, 내가 통제할 수 있는 것과 그렇지 않은 것을 구분하려는 내면의 훈련이 시작된 단계입니다. <strong class="green">스스로를 더 깊이 이해하고 싶다는 의지가 내면에 자라나고 있으며, 자기 삶에 대한 책임을 타인에게 넘기지 않으려는 자주적인 태도가 형성되어가고 있습니다.</strong>
                  <br><br>
                  이러한 노력은 아직 완성되지 않았지만, 성장에 대한 참여의식과 의도적 선택이 반복될수록 더욱 선명해집니다. 그 반복 속에서 자신을 지탱하는 내면의 중심축은 점차 단단해지고, 삶을 주도하는 힘으로 자리 잡기 시작합니다.`,
      },
      detailText_2: {
        title: "친절과 배움 속에서 관계를 이어가려는 사람",
        content: `이 단계의 사람은 관계에서 자신의 감정을 억누르지 않으면서도, 상대방을 무조건적으로 배려하거나 희생하지 않으려는 <strong class="green">균형 잡힌 접근을 시도</strong>합니다. 다만, 가까운 관계일수록 경계가 흐릿해지고 감정적으로 휘둘리는 일이 반복될 수 있습니다.
                  <br><br>
                  그럼에도 불구하고, 이들은 <strong class="green">관계 속에서 꾸준히 ‘더 나은 방식’을 찾고자 노력하는 사람들</strong>입니다. 상대방의 감정이나 말에 예민하게 반응하면서도, 그 안에서 자신이 무엇을 배울 수 있을지를 고민하고자 하는 배움 중심의 자세가 나타납니다.
                  <br><br>
                  누군가에게 실망하거나 상처받았을 때에도 완전히 단절하기보다는, 그 <strong class="green">경험을 통해 자신의 기대와 경계를 조율하려는 내면의 움직임</strong>이 감지됩니다. 이들은 관계의 어려움을 피하지 않으려 하며, 때로는 내면의 저항을 인식하고도 스스로를 밀어붙이는 것이 아니라, 천천히, 그러나 <strong class="green">분명하게 자신의 마음을 드러내려는 용기</strong>를 키워가고 있습니다.
                  <br><br>
                  친절함은 이들에게 있어 습관이 아니라 선택입니다. 억지로 좋은 사람으로 보이려 하기보다, 진심에서 우러나는 작은 배려를 실천하려고 애쓰며, 그런 관계들이 삶을 더 따뜻하게 만든다는 사실을 조금씩 체감하고 있습니다.`,
      },
      detailText_3: {
        title: "의도적으로 참여하며 의미를 찾으려는 사람",
        content: `이 단계에 이른 사람은 사회 속에서 자신이 맡은 역할에 대해 수동적인 책임보다 의미 있는 참여자로서의 자리를 모색하려는 노력이 보입니다. 단지 해야 할 일에 그치는 것이 아니라, <strong class="green">‘왜 이 일을 하고 있는가’, ‘어떤 방향으로 나아가고 싶은가’를 자문하며 자신의 삶을 구성하려는 의도</strong>가 분명해지고 있습니다.
                  <br><br>
                  하지만 외부의 변화나 예기치 못한 상황 앞에서는 내면의 균형이 흔들리기도 하고, 때로는 자신감을 잃기도 합니다. 그럼에도 불구하고, 이들은 포기하지 않고 <strong class="green">저항을 극복하면서 의미를 찾아가려는 실천의 자세</strong>를 잃지 않습니다.
                  <br><br>
                  일이나 관계, 공동체 활동 속에서 점점 더 자신을 드러내는 경험을 시도하며, <strong class="green">소극적인 방관자에서 한 걸음씩 벗어나 ‘참여자’로 서보려는 노력</strong>이 느껴집니다. 완전한 주도성은 아직 자리잡지 않았더라도, 그 가능성과 방향성이 이미 분명히 드러난 상태입니다.
                  <br><br>
                  이들은 자신의 영향력이 작더라도, 좋은 방향으로 흘러가고자 하는 의지를 실천에 담으려 하며, 성공의 결과보다 진심 어린 태도와 꾸준한 참여의 과정에 가치를 둡니다. <strong class="green">작지만 의미 있는 변화의 씨앗이 이 구간에서 자라고 있습니다.</strong>
                  <br><br>
                  ☆ 요약하자면, 이 구간에 해당하는 사람은<br>
                  - 자신의 중심을 세우기 위해 스스로에게 정직하게 다가가며,<br>
                  - 관계 안에서도 친절과 배움을 선택하려 하고,<br>
                  - 사회 속에서도 의미 있는 참여자로 살아가려는 의지를 가진 사람입니다.
                  <br><br>
                  아직 흔들림이 남아있지만, 그 안에서 최선을 다하는 태도와 성장의 방향은 분명히 자리잡고 있습니다. 이 단계는 의도적 실천을 통해 불균형을 조정하며 성장으로 나아가는 중요한 전환점입니다.`,
      },
    },
    {
      img: "metaq_16",
      score: "225<br>-<br>239",
      title: "안정적인 성장단계",
      enTitle: "Stable Growth Phase",
      keywords: "포용, 용서, 초월, 조화, 책임, 다양성",
      desc: "자기개발과 삶의 방향성 명확. 성장의 여지 발견",
      detail: `IQ와 EQ를 넘어서는 'μετά Q 융합역량지수'는, 인간이 ‘의미’와 ‘가치’의 문제를 깊이 있게 탐색하고 통합적으로 해결해 나갈 때 발휘되는 고차원적 역량입니다. 이 지수는 우리의 행동과 삶을 보다 넓고 깊은 의미의 맥락 속에 자리매김하도록 도우며, 자아나 의식적 사고를 초월한 내면의 깊은 지혜와 연결된 능력을 반영합니다. 단지 기존의 가치를 인식하는 데 그치지 않고, 새로운 가치를 창조적으로 발견하고 구성해내는 통합적 창의 역량이기도 합니다. 
               <br><br>
               이는 개인의 정서적 균형(EB), 성격균형(PB). 다중역량균형(MiB), 교류균형(CB), 삶의 만족도(LS) 등을 종합적으로 분석하여 도출된 융합역량지수입니다.`,
      detailText_1: {
        title: "나의 다양한 모습과 함께 살아가는 사람",
        content: `이 구간에 해당하는 사람은 자신의 과거와 현재, 장점과 약점, 성공과 실패를 있는 그대로 받아들이며 살아갈 수 있는 내적 토대를 점차 갖추어 나가고 있습니다. 자기 안의 복잡한 감정과 생각들을 억누르거나 미화하지 않고, <strong class="green">서로 다른 자아의 면면을 조율하며 삶의 중심으로 통합하려는 노력</strong>이 드러납니다.
                  <br><br>
                  삶에서 겪은 상처나 후회되는 경험에 대해서도 스스로를 꾸짖거나 회피하기보다는 용서하고 포용하려는 태도를 지니고 있으며, <strong class="green">완벽하지 않은 자기 자신을 받아들이는 힘이 자라고 있는 단계</strong>입니다. 그 과정에서 자신에게 더욱 솔직해지고, 실수조차 자신의 일부분으로 통합하는 자세가 자연스럽게 자리잡습니다.
                  <br><br>
                  이들은 <strong class="green">자신의 한계를 극복하려는 의지와 함께, 그 한계를 있는 그대로 껴안고 살아갈 수 있는 초월적 성숙의 가능성</strong>을 보여줍니다. 감정이 요동칠 때도 그것에 압도되기보다, 중심을 회복하려는 자각이 있으며, <strong class="green">자신에게 필요한 회복의 시간과 공간을 조율할 줄 압니다.</strong>
                  <br><br>
                  또한, 삶의 방향에 대해 비교적 뚜렷한 감각을 가지고 있고, 자기개발을 위한 노력을 일상적으로 지속하며, 성장의 여지를 부정하지 않고 기꺼이 받아들이는 내면의 유연함을 갖고 있습니다. 이러한 <strong class="green">자기 수용의 힘은 점차 자기 확장에 대한 긍정적인 기대로 연결되어, 삶을 더 넓은 차원에서 조망할 수 있는 능력으로 발전해갑니다.</strong>`,
      },
      detailText_2: {
        title: "다름을 인정하고 조화롭게 연결되는 사람",
        content: `이 단계의 사람은 <strong class="green">타인과의 관계에서 다름을 받아들이는 힘이 성숙해지고 있습니다.</strong> 사람마다 가치관, 반응 방식, 표현 방식이 다르다는 사실을 이해하며, 그 차이를 두려워하기보다 다양성의 일부로 받아들이려는 노력이 보입니다. 이는 단순한 이해에 그치지 않고, 실제 관계 속에서 감정적으로 유연한 대응으로 나타납니다.
                  과거에는 상처가 되었을 만한 말이나 행동도, 이제는 좀 더 너른 시선에서 해석하며 감정을 조절할 수 있게 되고, <strong class="green">상대방의 배경과 맥락을 고려하면서 관계를 바라보는 포용력</strong>이 생겨납니다.
                  <br><br>
                  이들은 <strong class="green">‘누가 옳고 그른가’의 논리보다, ‘서로 어떻게 이해하고 다시 만날 수 있을까’를 중심</strong>에 둡니다. 자신과 타인의 실수에 대해 용서할 줄 알며, <strong class="green">상처 위에 새로운 관계의 기반을 쌓아가는 회복적 태도</strong>가 나타납니다. 관계의 어려움도 성장의 일부로 받아들이며, 갈등을 피하기보다 그 안에서 더 깊은 이해로 나아가고자 합니다. 이는 감정적 거리감이 아니라, 관계를 성숙하게 책임지는 자세로 연결됩니다.
                  <br><br>
                  이들은 “누가 옳은가”보다 “어떻게 함께 살아갈 수 있는가”를 중심에 두고 관계를 맺습니다. 타인과의 연결 속에서 <strong class="green">단순한 친밀감보다 책임 있는 공존을 실현하려고 노력</strong>하는 사람이기도 합니다. 이러한 자세는 주변 사람들에게 안정감을 주며, <strong class="green">긴 호흡으로 관계를 지속할 수 있는 기초</strong>가 됩니다.`,
      },
      detailText_3: {
        title: "책임 있게 기여하며 삶의 조화를 추구하는 사람",
        content: `이 단계에 있는 사람은 사회적 역할과 책임을 피하거나 무겁게만 여기지 않습니다. 자신이 속한 조직, 가족, 공동체 안에서 어떤 방식으로 기여할 수 있을지를 고민하며, 자신의 자리를 성실히 지키는 태도를 보여줍니다. 이는 단지 맡은 역할을 잘 수행한다는 차원을 넘어, 그 안에서 <strong class="green">더 나은 방향을 제안하고 조율할 수 있는 책임 있는 주체</strong>로서의 모습까지 포함합니다.
                  <br><br>
                  이들은 단지 개인의 성공만을 추구하기보다는, 함께 잘 사는 것, 다른 이와 함께 성장하는 것에 점차 가치를 둡니다. 이를 위해 자신의 시간과 자원을 조율하고, 필요할 때는 자발적으로 헌신하며 <strong class="green">공동의 조화를 도모하려는 책임 있는 자세</strong>가 돋보입니다.
                  <br><br>
                  이런 실천은 작지만 깊은 신뢰를 만들어내며, 이들이 속한 집단에 긍정적인 영향력을 미치는 토대가 됩니다. 사회가 요구하는 방식과 나만의 가치가 충돌할 때도 그 둘 사이에서 조화를 이루기 위한 내적 탐색을 게을리하지 않으며, 의견이 다른 사람들과도 대화를 통해 연결점을 찾고자 합니다.
                  <br><br>
                  그 과정에서 이들은 ‘다수의 선택’이 아니라 ‘내가 믿는 옳음’을 기준 삼되, 그 옳음을 강요하지 않고 부드럽게 나누는 방식으로 실천하려 합니다. 이들은 <strong class="green">무리 속에서 스스로를 잃지 않고, 조화로운 삶을 위해 균형 있게 기여하는 존재로 성장 중입니다.</strong>
                  사회적 참여, 일의 의미, 사람들과의 연결 속에서 자신의 방향성과 존재 이유를 조금씩 더 분명히 해나가고 있습니다. <strong class="green">자신의 삶이 사회 전체 안에서 어떤 방식으로 쓰일 수 있는지를 의식하며 살아가는, 책임 있는 성숙의 단계</strong>라 할 수 있습니다.
                  <br><br>
                  ☆ 요약하자면, 이 구간에 해당하는 사람은<br>
                  - 자신을 포용하고 성장의 여지를 열린 마음으로 바라보며,<br>
                  - 타인의 다름을 수용하고 관계를 회복적으로 유지하려 하고,<br>
                  - 사회 속에서 책임 있게 조화를 실현하려는 태도를 지닌 사람입니다.
                  <br><br>
                  성숙의 방향을 향해 나아가는 이들은 삶의 다양성을 기꺼이 껴안으며, 초월과 실천 사이의 조화로운 균형을 모색하고 있습니다.`,
      },
    },
    {
      img: "metaq_17",
      score: "240<br>-<br>254",
      title: "균형잡힌 성장형",
      enTitle: "Balanced Growth",
      keywords: "이성, 이해, 합리 의미, 가치, 논리",
      desc: "자기 성찰, 조절 능력, 다양한 요소의 균형, 지속적인 성장",
      detail: `IQ와 EQ를 넘어서는 'μετά Q 융합역량지수'는, 인간이 ‘의미’와 ‘가치’의 문제를 깊이 있게 탐색하고 통합적으로 해결해 나갈 때 발휘되는 고차원적 역량입니다. 이 지수는 우리의 행동과 삶을 보다 넓고 깊은 의미의 맥락 속에 자리매김하도록 도우며, 자아나 의식적 사고를 초월한 내면의 깊은 지혜와 연결된 능력을 반영합니다. 단지 기존의 가치를 인식하는 데 그치지 않고, 새로운 가치를 창조적으로 발견하고 구성해내는 통합적 창의 역량이기도 합니다. 
               <br><br>
               이는 개인의 정서적 균형(EB), 성격균형(PB). 다중역량균형(MiB), 교류균형(CB), 삶의 만족도(LS) 등을 종합적으로 분석하여 도출된 융합역량지수입니다.`,
      detailText_1: {
        title: "생각과 감정을 균형 있게 통합해 나가는 사람",
        content: `이 구간에 해당하는 사람은 자신의 내면을 감정 중심으로만 바라보지 않고, 감정과 생각, 신념과 행동 간의 관계를 이성적으로 성찰할 수 있는 능력을 갖추고 있습니다. <strong class="green">삶에서 일어나는 경험을 감정에만 의존해 해석하기보다, 그 안의 의미와 맥락을 차분히 분석하고 이해하려는 이성적 사고의 기반</strong>이 마련되어 있습니다.
                  <br><br>
                  스스로를 돌아볼 때 감정에 휘둘리기보다는 “왜 이런 감정이 일어났을까?”, “이 상황에서 내가 배울 수 있는 것은 무엇일까?” 와 같은 질문을 통해 자신을 다층적으로 이해해 나갑니다. 문제 상황이나 스트레스가 생겼을 때 감정을 억누르지 않으면서도, 논리적으로 상황을 분석하고, 자신의 행동 방식을 조정할 수 있는 <strong class="green">자기조절력</strong>이 두드러지게 나타납니다.
                  <br><br>
                  이들은 자신에 대해 명확한 기준을 가지고 있으며, <strong class="green">삶의 방향이나 목적에 대해서도 단편적인 감정이 아닌 깊이 있는 사고와 가치 기반의 질문을 통해 스스로의 길을 설계</strong>해 나갑니다. 자기 인생의 방향키를 다른 사람에게 맡기지 않고, 스스로의 이성과 신념에 따라 조용히 항해해 나가는 존재입니다.`,
      },
      detailText_2: {
        title: "타인의 입장을 이해하고 존중하는 균형 있는 사람",
        content: `이 구간의 사람은 관계에서 감정적인 반응을 즉각적으로 쏟아내기보다, 한 걸음 물러서서 상대방의 입장과 감정을 이해하려는 태도를 가집니다. <strong class="green">상대의 말과 행동에 감정적으로 반응하기보다는, “저 사람은 왜 저런 선택을 했을까?”, “그 말 뒤에 어떤 감정이나 신념이 있을까?” 와 같은 이해 기반의 사고</strong>가 관계에 반영됩니다.
                  <br><br>
                  갈등이 발생했을 때도 감정의 폭발이나 침묵의 회피보다는 대화를 통해 문제를 풀어나가고자 하며, 상대와 나의 차이를 분명히 인식하면서도 그것이 단절로 이어지지 않도록 <strong class="green">합리적인 거리와 건강한 경계를 유지하려고 노력</strong>합니다.
                  <br><br>
                  관계의 진정성을 중요하게 여기며, 기분에 좌우되지 않고 일관성 있는 태도로 사람들과 관계를 맺습니다.
                  이들은 <strong class="green">사람과 사람 사이의 갈등을 개선의 기회로 보고, 그 안에서 서로에 대한 이해를 확장시켜 나가려는 태도</strong>를 지니고 있습니다.
                  <br><br>
                  또한, 타인의 감정에만 휩쓸리지 않으며, 관계를 더 오래 지속할 수 있는 방식이 무엇인지, <strong class="green">서로에게 도움이 되는 소통이 무엇인지에 대해 생각하며 관계를 조율</strong>합니다.`,
      },
      detailText_3: {
        title: "가치와 의미를 기반으로 삶의 방향을 세우는 사람",
        content: `이 단계에 이른 사람은 자신이 사회 속에서 맡고 있는 역할을 단순히 ‘해야 할 일’로 보지 않습니다. 그 일이 나의 삶에 어떤 의미를 지니는지, 나의 가치관과 얼마나 맞닿아 있는지를 고민하며, <strong class="green">목적 있는 삶과 실천</strong>을 향해 나아갑니다.
                  <br><br>
                  이들은 외적인 성취나 평가에만 의존하지 않고, <strong class="green">스스로에게 “이 일이 나와 타인, 사회에 어떤 가치를 더하는가?”라는 질문을 던지며 자신의 활동을 점검</strong>합니다. 이러한 사고는 그들에게 일의 방향성과 지속성을 부여하며, 내면의 기준에 따른 선택을 가능하게 합니다.
                  <br><br>
                  조직이나 공동체 속에서도 감정적으로 휘둘리기보다는, <strong class="green">논리적이고 명확한 사고를 바탕으로 협업의 방향과 관계의 흐름을 조율할 수 있는 역량</strong>을 갖추고 있습니다. 
                  <br><br>
                  이들은 말과 행동의 일치를 중요하게 여기며, <strong class="green">설득보다는 이해와 의미의 공유를 통해 영향력을 발휘</strong>하려는 사람들입니다. 무리 속에 있더라도 자신만의 중심을 유지하며, 상황에 휩쓸리기보다 이성적으로 해석하고 판단할 줄 아는 <strong class="green">분별력 있는 시민의식</strong>이 돋보입니다.
                  <br><br>
                  ☆ 요약하자면, 이 구간에 해당하는 사람은<br>
                  - 자신의 내면을 이성과 감정의 균형 속에서 바라보고,<br>
                  - 관계 속에서 이해와 존중을 바탕으로 소통하며,<br>
                  - 사회적 역할을 가치와 의미를 중심으로 설계해 나가는 사람입니다.
                  <br><br>
                  삶의 흐름을 차분하게 통찰하며, 자신만의 기준으로 방향을 잡고 성장의 궤도를 안정감 있게 이어가는 합리적이고 조화로운 존재입니다.`,
      },
    },
    {
      img: "metaq_18",
      score: "255<br>-<br>269",
      title: "높은 조화상태",
      enTitle: "Highly Harmonized State",
      keywords: "사랑, 선한 영향력, 온화, 공존, 직관",
      desc: "모든 영역에서 안정적 균형, 주도적인 나의 삶을 살아감",
      detail: `IQ와 EQ를 넘어서는 'μετά Q 융합역량지수'는, 인간이 ‘의미’와 ‘가치’의 문제를 깊이 있게 탐색하고 통합적으로 해결해 나갈 때 발휘되는 고차원적 역량입니다. 이 지수는 우리의 행동과 삶을 보다 넓고 깊은 의미의 맥락 속에 자리매김하도록 도우며, 자아나 의식적 사고를 초월한 내면의 깊은 지혜와 연결된 능력을 반영합니다. 단지 기존의 가치를 인식하는 데 그치지 않고, 새로운 가치를 창조적으로 발견하고 구성해내는 통합적 창의 역량이기도 합니다. 
               <br><br>
               이는 개인의 정서적 균형(EB), 성격균형(PB). 다중역량균형(MiB), 교류균형(CB), 삶의 만족도(LS) 등을 종합적으로 분석하여 도출된 융합역량지수입니다.`,
      detailText_1: {
        title: "자신을 온전히 이해하고 따뜻하게 대하는 사람",
        content: `이 구간에 이른 사람은 자신의 내면을 섬세하게 인식하고, 그것을 따뜻하게 받아들이는 능력을 지니고 있습니다. 감정이 일어날 때 그것을 억누르거나 무시하지 않고, 조용히 들여다보며 스스로의 마음에 귀를 기울입니다. 자신을 판단하기보다 이해하려 하고, 부족함이나 상처까지도 품을 수 있는 내면의 온기가 깃들어 있습니다.
                  <br><br>
                  <strong class="green">삶에서 겪는 여러 상황을 통합적으로 바라볼 수 있으며, 과거의 실수나 고통도 성장의 일부로 받아들이는 여유</strong>가 생깁니다. 완벽함보다 진정성을 중요하게 여기며, 자신을 조급하게 몰아세우기보다는
                  <strong class="green">‘지금 여기의 나’를 충분히 인정하고 지지</strong>해줍니다.
                  <br><br>
                  이들은 자기 돌봄을 일상의 일부로 받아들이고, 스스로에게 필요한 것과 해로운 것을 구분할 줄 아는 내면의 감각이 깨어 있습니다. 감정의 균형이 무너질 때 이를 회복하는 자기만의 방식이 있으며, 마음이 보내는 미세한 신호에 예민하게 반응할 줄 아는 <strong class="green">직관적인 자기 이해력</strong>을 지니고 있습니다.
                  <br><br>
                  삶을 통제의 대상이 아니라, 함께 살아내야 할 흐름으로 받아들이며, 그 흐름 안에서 스스로를 부드럽게 이끌어가는 <strong class="green">조화로운 주체성</strong>을 갖추고 있습니다. ‘나’란 더 이상 극복해야 할 대상이 아니라, 함께 살아가며 깊이 이해해야 할 가장 소중한 동반자입니다.`,
      },
      detailText_2: {
        title: "함께 살아가는 삶을 실천하는 사람",
        content: `이 단계의 사람은 타인을 삶의 경쟁자가 아닌 함께 살아가는 존재로 인식합니다. <strong class="green">상대방을 도구화하거나 판단의 대상으로 삼지 않고, 있는 그대로의 모습을 인정하며 관계를 맺으려는 태도</strong>를 갖추고 있습니다.
                  <br><br>
                  이들은 상대방의 감정을 헤아리는 능력이 뛰어나며, 말보다는 분위기나 표정, 기류를 통해 상대가 느끼는 정서를 섬세하게 감지하고 응답할 수 있는 직관적인 공감력을 지닙니다. 때로는 말 한마디보다, 조용한 배려와 시선 하나로 깊은 위로를 전달할 수 있습니다.
                  <br><br>
                  관계 안에서 조화를 중요하게 여기며, 억지로 맞추거나 자신을 희생하기보다 서로가 편안하게 공존할 수 있는 방식이 무엇인지 고민하고 실천합니다. 자신의 감정을 솔직하게 표현하되, 그것이 타인을 위협하거나 상처 주지 않도록 <strong class="green">신중하고 부드러운 소통을 선택</strong>할 줄 압니다.
                  <br><br>
                  이들은 갈등을 피하거나 억누르지 않고, 그 안에서 서로의 입장을 존중하며 해결점을 찾아가는 힘을 갖추고 있습니다. <strong class="green">누군가를 변화시키려 하기보다는, 함께 있을 때 자연스럽게 변화를 느낄 수 있는 공간을 만들어주는 사람</strong>입니다. 관계 속에서 진심과 신뢰를 쌓아가며, 사랑과 온기, 존중이 흐르는 연결을 실천합니다. 이들이 머무는 자리에는 사람 사이에 다시 믿음이 싹트고, 함께 살아가는 삶의 가능성이 조용히 자라납니다.`,
      },
      detailText_3: {
        title: "부드럽지만 강한 영향력을 지닌 사람",
        content: `이 단계에 있는 사람은 외적 성공이나 명예를 쫓기보다, <strong class="green">자신이 지닌 가치와 철학을 실천하며 살아가는 것을 더 중요하게 여깁니다.</strong> 직업적 성취나 사회적 지위에 집착하지 않고, 자신이 서 있는 자리에서 자연스럽게 긍정적인 영향을 퍼뜨리는 존재입니다.
                  <br><br>
                  이들은 리더십을 권위로 행사하기보다, 자신의 태도와 방식으로 말 없이 방향을 제시합니다. 사람들을 설득하려 하지 않고, 스스로 그러한 삶을 살아감으로써 <strong class="green">자연스럽게 타인의 마음에 울림을 주는 존재의 힘</strong>을 지니고 있습니다.
                  <br><br>
                  사회적 기준이나 트렌드에 휘둘리지 않으며, 필요한 일에는 적극적으로 참여하되, 자신의 중심을 잃지 않고 상황을 조율할 수 있는 균형 감각이 뛰어납니다. 모임이나 조직 안에서도 조화를 추구하고, <strong class="green">서로의 역할을 존중하면서 공동의 목표를 향해 나아갈 수 있는 분위기를 만듭니다.</strong>
                  <br><br>
                  이들은 변화와 고정 사이의 균형을 이해하고 있으며, 무언가를 바꾸려 할 때에도 무리하거나 강요하지 않습니다. 자연스럽게, 부드럽게, 그러나 분명한 의도를 가지고 세상과 연결되어 있습니다. 그 결과, 이 구간의 사람은 <strong class="green">작지만 깊은 선한 영향력을 발휘하며, 공동체와 사회 안에서 따뜻하고 신뢰할 수 있는 사람으로 기억</strong>됩니다.
                  <br><br>
                  ☆ 요약하자면, 이 구간에 해당하는 사람은<br>
                  - 자기 자신을 따뜻하게 이해하고,<br>
                  - 타인과 함께 살아가는 방식을 실천하며,<br>
                  - 사회 속에서도 부드럽고 조화로운 영향력을 발휘하는 존재입니다.
                  <br><br>
                  사랑, 직관, 공존이라는 키워드가 삶의 전반에 조용히 깃들어 있으며, 삶 전체에 조화를 이뤄내고자 하는 온화하고도 단단한 내면의 상태를 보여줍니다.`,
      },
    },
    {
      img: "metaq_19",
      score: "270<br>-<br>284",
      title: "심화된 통합",
      enTitle: "Deep Integrated wisdom",
      keywords: "기쁨, 평온, 변형, 변화, 감사",
      desc: "높은 수준의 통합, 지혜롭고 균형 잡힌 삶을 영위",
      detail: `IQ와 EQ를 넘어서는 'μετά Q 융합역량지수'는, 인간이 ‘의미’와 ‘가치’의 문제를 깊이 있게 탐색하고 통합적으로 해결해 나갈 때 발휘되는 고차원적 역량입니다. 이 지수는 우리의 행동과 삶을 보다 넓고 깊은 의미의 맥락 속에 자리매김하도록 도우며, 자아나 의식적 사고를 초월한 내면의 깊은 지혜와 연결된 능력을 반영합니다. 단지 기존의 가치를 인식하는 데 그치지 않고, 새로운 가치를 창조적으로 발견하고 구성해내는 통합적 창의 역량이기도 합니다. 
               <br><br>
               이는 개인의 정서적 균형(EB), 성격균형(PB). 다중역량균형(MiB), 교류균형(CB), 삶의 만족도(LS) 등을 종합적으로 분석하여 도출된 융합역량지수입니다.`,
      detailText_1: {
        title: "나를 깊이 이해하고 돌볼 수 있는 사람",
        content: `이 단계에 이른 사람은 자기 자신을 ‘문제 삼기’보다, 있는 그대로 바라볼 수 있는 성숙한 시선을 가지고 있습니다. <strong class="green">자기 내부에서 일어나는 감정과 생각, 반응을 인식하고, 그것들이 어디서 비롯되었는지를 통찰할 수 있는 힘</strong>이 있습니다. 자기 자신을 변화시켜야 할 대상으로만 보지 않고, 그 자체로 소중한 존재로 여기는 존중의 태도가 자리잡고 있습니다.
                  <br><br>
                  이들은 실수하거나 흔들리는 자신을 비난하기보다, 다정한 시선으로 바라보며 회복을 위한 자리를 마련합니다. 불안이나 두려움이 올라올 때 그것을 억누르기보다, 호기심을 가지고 들여다볼 수 있으며, 그 <strong class="green">감정 속에 담긴 삶의 메시지를 읽어내려는 태도</strong>를 지닙니다.
                  <br><br>
                  자기 감정의 원인을 외부 탓으로 돌리기보다, 자신 안의 역사를 통해 이해하려는 노력이 자연스럽습니다.
                  삶을 ‘이겨내는 것’보다 ‘함께 살아가는 것’으로 받아들이며, 그 속에서 더욱 강인하고 부드러운 내면을 길러냅니다. 스스로를 돌보는 일은 더 이상 특별한 일이 아니라, 매일의 루틴 안에 자연스럽게 녹아 있습니다.
                  <br><br>
                  <strong class="green">마음의 균형이 무너지려 할 때, 자신만의 회복 루틴(산책, 글쓰기, 음악, 대화 등)을 알고 실천할 수 있는 내적 자원이 마련되어 있습니다.</strong>`,
      },
      detailText_2: {
        title: "깊은 공감 속에서 진짜 만남을 추구하는 사람",
        content: `이 단계의 사람은 타인의 말 너머에 있는 감정과 욕구를 민감하게 감지하고, <strong class="green">단지 말로만이 아닌 ‘존재 전체’로 타인과 만나는 태도</strong>를 지니고 있습니다. 표현되지 않은 감정을 헤아리고, 때로는 말보다 침묵과 시선으로 상대를 위로할 줄 압니다.
                  <br><br>
                  타인의 고통을 자신의 것으로 끌어안지 않고, 그 고통이 그 사람의 삶 안에서 어떤 의미를 가지는지를 함께 바라보려 합니다. 도움이 필요한 사람과 거리를 적절히 유지하면서도, 그 사람이 홀로 무너지지 않도록 <strong class="green">조용히 곁을 지켜주는 지혜로운 따뜻함</strong>이 있습니다.
                  <br><br>
                  이들은 관계에서 갈등이 생겼을 때 도망치거나 맞서기보다, 상대와의 차이를 탐색하고, 감정의 언저리를 함께 건드릴 수 있는 대화를 시도합니다. 때로는 불편한 감정을 감수하면서도 진심을 전달하고, 상대의 마음을 이해하려는 과정 자체를 관계의 일부로 받아들입니다.
                  <br><br>
                  이들에게 친밀함이란 ‘같은 생각을 하는 것’이 아니라, ‘다른 생각을 하더라도 관계를 포기하지 않는 것’입니다. 그래서 이들은 <strong class="green">주변 사람들에게 정서적 안전감을 제공</strong>하며, <strong class="green">자신의 존재만으로도 관계 안에 신뢰와 회복의 분위기를 만들어냅니다.</strong>`,
      },
      detailText_3: {
        title: "균형 잡힌 시선으로 조화를 실천하는 사람",
        content: `이 단계에 이른 사람은 사회적 역할을 수행하면서도 자신을 잃지 않는 건강한 자기감(selfhood)을 유지할 수 있습니다. 외부의 평가에 지나치게 의존하지 않고, <strong class="green">자신이 가진 가치와 신념을 기준 삼아 주체적으로 일하고 관계 맺으며, 상황을 해석</strong>합니다.
                  <br><br>
                  자신이 속한 조직이나 공동체에서 조화를 중요하게 여기며, 조화를 위해 자신을 희생하기보다 <strong class="green">자기-타자-전체의 균형 속에서 최적의 길을 선택하려는 노력</strong>이 드러납니다.
                  <br><br>
                  이들은 리더가 아니더라도 자기 위치에서 진정성 있는 영향력을 발휘합니다. 맡은 일을 책임감 있게 해내는 것을 넘어, 조직과 사회가 어떤 방향으로 나아가야 하는지에 대한 <strong class="green">통찰을 가지고 있으며, 그 통찰을 겸손하고 일관된 방식으로 실천</strong>합니다.
                  <br><br>
                  사회적 기대에 완전히 부합하지 않더라도 스스로를 부끄러워하지 않으며, 필요한 비판과 제안을 할 수 있는 용기도 지니고 있습니다. 세상의 변화 속도에 휘둘리기보다, 그 변화의 흐름을 자신의 가치와 연결하여 해석할 수 있는 <strong class="green">‘의식 있는 시민’으로서 살아가려는 태도가 기본</strong>입니다.
                  <br><br>
                  이러한 사람은 공동체 안에서 안정감을 주는 존재로 인식되며, <strong class="green">타인의 의견을 경청하고, 자신의 목소리도 분명히 내며, 무리 속에서 자신을 잃지 않고, 관계 속에서 조화를 실현하려는 성숙한 힘을 발휘</strong>합니다.
                  <br><br>
                  ☆ 요약하자면, 이 구간에 해당하는 사람은<br>
                  - 스스로의 감정과 삶을 깊이 성찰하고,<br>
                  - 관계 속에서 진심 어린 연결을 지향하며,<br>
                  - 사회 안에서도 자기다움을 지키며 조화롭게 살아가는 존재입니다.
                  <br><br>
                  진정한 통합을 향한 깊고 단단한 내적 준비가 되어 있으며, 삶을 통해 자신과 타인을 조용히 성장시키는 사람이라 할 수 있습니다.`,
      },
    },
    {
      img: "metaq_20",
      score: "285<br>-<br>300",
      title: "완전한 통합",
      enTitle: "Complete Integration",
      keywords: "깨달음, 평화, 진짜 나, Self",
      desc: "최상의 균형, 자기 이해, 성장 극대화, 균형과 조화",
      detail: `IQ와 EQ를 넘어서는 'μετά Q 융합역량지수'는, 인간이 ‘의미’와 ‘가치’의 문제를 깊이 있게 탐색하고 통합적으로 해결해 나갈 때 발휘되는 고차원적 역량입니다. 이 지수는 우리의 행동과 삶을 보다 넓고 깊은 의미의 맥락 속에 자리매김하도록 도우며, 자아나 의식적 사고를 초월한 내면의 깊은 지혜와 연결된 능력을 반영합니다. 단지 기존의 가치를 인식하는 데 그치지 않고, 새로운 가치를 창조적으로 발견하고 구성해내는 통합적 창의 역량이기도 합니다. 
               <br><br>
               이는 개인의 정서적 균형(EB), 성격균형(PB). 다중역량균형(MiB), 교류균형(CB), 삶의 만족도(LS) 등을 종합적으로 분석하여 도출된 융합역량지수입니다.`,
      detailText_1: {
        title: "내면 전체가 하나로 연결된 사람",
        content: `이 단계에 있는 사람은 <strong class="green">자기 내면의 다양한 목소리들과 조화</strong>를 이루며 살아갑니다. 감정, 생각, 신념, 상처, 그림자와 같은 내면의 요소들이 서로 분리되지 않고, 하나의 흐름 안에서 자연스럽게 연결되어 있습니다. 
                  <br><br>
                  자기 자신을 판단하거나 억압하지 않고, <strong class="green">있는 그대로의 존재로 받아들이는 수용의 태도</strong>를 지니고 있습니다. 이러한 자기 수용은 단순히 자신을 이해하고 받아들이는 차원을 넘어, <strong class="green">삶 전체를 포괄하는 통합된 시각</strong>으로 이어집니다. 
                  <br><br>
                  이들은 자신의 과거와 현재, 가능성과 한계를 함께 껴안으며 살아가며, 삶의 모든 경험을 의미 있는 배움으로 전환해낼 수 있습니다. 고통스러운 기억이나 실패의 순간조차도 자기 연민을 통해 소화하며, 그 안에서 교훈을 발견하고 스스로를 더 깊이 성장시키는 자원으로 삼습니다. 자기비판이나 불안에 휘둘리지 않고, <strong class="green">흔들리는 감정 속에서도 중심을 잃지 않으며, 어떤 상황에서도 평정심을 유지할 수 있는 내적 안정감</strong>을 갖추고 있습니다.
                  <br><br>
                  이들은 스스로를 돌보는 일에 익숙하며, 명상이나 일기, 사색과 같은 방법으로 자기와 깊이 있는 대화를 지속하는 습관이 있습니다. 삶의 방향을 외부의 기준이 아닌, 자신의 내면에서 찾으며, <strong class="green">‘왜 사는가’라는 근본적인 질문에 대해 나름의 확고한 응답을 가지고 있는 존재</strong>입니다.`,
      },
      detailText_2: {
        title: "경계를 허물고 신뢰를 여는 깊은 연결",
        content: `이 단계의 사람은 타인과의 관계 속에서도 높은 수준의 신뢰와 공감을 실천할 수 있습니다. 표면적인 친절이나 역할 수행이 아닌, <strong class="green">진심에서 우러난 연결을 추구</strong>합니다. 말보다 마음이 먼저 전해지고, 상대방의 감정이나 욕구를 민감하게 감지할 수 있는 섬세한 공감력을 지녔습니다.
                  <br><br>
                  관계에서 힘의 균형을 무너뜨리는 통제나 의존의 함정에 빠지지 않으며, 자신과 타인의 경계를 존중하고, 그 안에서 자유롭고 건강한 연결을 지향합니다. 필요한 경우에는 <strong class="green">‘거절’과 ‘거리두기’를 분명하게 할 수 있지만, 그 과정에서 상처를 남기지 않는 섬세한 소통 능력</strong>도 함께 갖추고 있습니다.
                  <br><br>
                  이 단계의 사람은 자신이 사랑받을 가치가 있다는 것을 내면 깊이 믿고 있으며, 타인에게 인정받기 위해 자신을 꾸미거나 과장하지 않아도 된다는 안정감을 가지고 있습니다. 그래서 상대방에게도 <strong class="green">‘있는 그대로의 너’가 괜찮다는 메시지를 전달해줄 수 있고, 그것은 관계 속에서 깊은 치유와 회복의 공간을 만들어냅니다.</strong>
                  <br><br>
                  타인의 변화에 집착하지 않고, 함께 성장하는 관계를 만들기 위해 필요한 것들을 조용히 실천합니다. 때로는 함께 침묵할 줄 알고, 때로는 위로의 말보다 따뜻한 시선 하나로 상대의 마음에 귀 기울일 줄 아는 사람입니다. 관계는 이들에게 있어 경쟁의 공간이 아니라, <strong class="green">서로의 가능성을 발견하는 공동의 여정</strong>입니다.`,
      },
      detailText_3: {
        title: "진정성과 영향력을 겸비한 성숙한 리더",
        content: `완전한 통합의 단계에 이른 사람은 <strong class="green">사회 속에서 자신이 누구인지 명확히 알고, 그 정체성에 기반하여 자신만의 방식으로 세상과 연결</strong>됩니다. 직업이나 지위, 소속과 같은 외적 역할에 자신을 고정시키지 않으며, 그 어떤 위치에서도 자신다움을 잃지 않고 살아가는 힘을 갖고 있습니다.
                  <br><br>
                  이들은 주어진 틀 안에 갇히기보다, 그 틀을 새롭게 재구성하며 <strong class="green">새로운 가능성을 여는 사람</strong>입니다. 사회적 역할을 수행함에 있어서도 자기만의 철학과 가치관이 뚜렷하며, 타인의 기대에 부응하기보다 <strong class="green">더 나은 방향으로 초대하는 영향력</strong>을 행사합니다. 조직이나 공동체 안에서도 신뢰와 존중을 받으며, 직접적인 권위를 행사하지 않아도 자연스럽게 리더십이 형성됩니다.
                  <br><br>
                  이 단계에 있는 사람은 성공이나 성과에 집착하지 않습니다. 그보다는 자신이 하는 일이 누군가에게, 사회 전체에 어떤 긍정적인 파장을 일으키는지에 주목합니다. 자신의 에너지를 공공의 선을 위해 쓰는 것에 기쁨을 느끼며, 더 나아가 환경, 인권, 교육, 치유 등 <strong class="green">공동체적 가치를 실현하는 삶을 지향</strong>합니다.
                  <br><br>
                  사회 속에서 ‘보여지는 나’와 ‘진짜 나’ 사이의 간극이 거의 없기에, 어떤 자리에 있어도 일관된 태도와 진정성을 유지할 수 있습니다. 이로 인해 이들은 갈등을 중재하고, 사람들을 하나로 모으는 <strong class="green">내면의 중심축 역할</strong>을 할 수 있습니다. 이러한 삶의 방식은 단지 자기만족에 머물지 않고, <strong class="green">타인의 성장과 사회적 변화에까지 이르는 넓은 파장을 만들어냅니다.</strong> 말보다 존재 자체로 설득력 있는 삶을 살아가는, 진정한 성숙의 모델이라 할 수 있습니다.`,
      },
    },
  ];

  const getSelectedIndex = (metaQScore) => {
    if (metaQScore > 300) {
      return Ref33DetailText.length - 1;
    }
    if (metaQScore < 0) {
      return 0;
    }

    return Ref33DetailText.findIndex((item) => {
      const [min, max] = item.score.split("<br>-<br>").map(Number);
      return metaQScore >= min && metaQScore <= max;
    });
  };

  const selectedIndex = getSelectedIndex(resultPageData.metaQ);

  const selectedItem = Ref33DetailText[selectedIndex];
  const previousItem =
    selectedIndex > 0 ? Ref33DetailText[selectedIndex - 1] : null;
  const nextItem =
    selectedIndex < Ref33DetailText.length - 1
      ? Ref33DetailText[selectedIndex + 1]
      : null;

  // ref[29]
  const ref29ScoreType =
    resultPageData.life_average < 50
      ? "50"
      : resultPageData.life_average < 61
      ? "60"
      : resultPageData.life_average < 71
      ? "70"
      : resultPageData.life_average < 81
      ? "80"
      : resultPageData.life_average < 91
      ? "90"
      : "91";

  const jcDetail = {
    낮음: {
      name: "낮은 JC 유형",
      desc: `
                  부모와 같은 양육자들의 생각이나 행동, 또는 느낌을 내면화한 것으로 성인이 된 현재의 나에게 계속해서 영향을 주고 있는 말이나 동작이 포함되어 있습니다. <strong class="green">이 영역은 주로 비판, 비난, 처벌, 통제와 관련이 있으며 규칙과 규율을 가르치고 양심을 심어주는 역할</strong>도 합니다.
                  따라서, JC 유형 점수가 낮은 <strong class="blue">당신은 어지간해서는 남들을 비판하지 않으며 융통성이 있습니다. 타인에게 관대한 성격이라 붙임성 있고 느긋하다는 평가를 받습니다. 하지만 다소 우유부단하고 자기 주관이 부족하여 주변에서 답답함을 느끼기도 합니다. 타인에게 관대하듯이 자신에게도 관대하여 게으르고 못 미덥다는 평가를 받을 수도 있습니다.</strong> 
                  <strong class="green">이 영역의 점수가 평균 이상으로 높아질수록 권위적인 태도가 나올 수 있고, 낮아질수록 소신이 없거나 무절제한 사람이 될 수 있다는 사실을 기억하고 책임감을 가지고 규칙을 지키려고 노력해야 합니다.</strong> 자신의 양심에 맞게 올바른 판단을 내리고 이를 행동에 옮기는 훈련이 필요합니다. 
                  <strong>낮은 JC 유형은, 높은 MR 유형과의 소통에서 불편함을 느낄 수 있습니다. JC 영역의 점수를 높이면 이 부분도 자연스럽게 해소될 수 있습니다.</strong> 
               `,
    },
    적당함: {
      name: "적당한 JC 유형",
      desc: `
                  부모와 같은 양육자들의 생각이나 행동, 또는 느낌을 내면화한 것으로 성인이 된 현재의 나에게 계속해서 영향을 주고 있는 말이나 동작이 포함되어 있습니다. <strong class="green">이 영역은 주로 비판, 비난, 처벌, 통제와 관련이 있으며 규칙과 규율을 가르치고 양심을 심어주는 역할</strong>도 합니다.
                  따라서, JC 유형 점수가 평균적인 <strong class="blue">당신은 책임감, 정의감, 인간의 도리 등 일반적인 가치들을 중시하는 사람이며 이것을 지키는 것이 상식이라고 생각하는 건강한 사람입니다. 그러나 내가 생각하는 이상이 구현되지 않거나, 상식이 지켜지지 않는 상황에서는 타인과 조직을 부정적으로 판단하기 때문에 섣부른 단정적 판단을 경계해야 합니다. </strong>
                  이 영역의 점수가 평균 이상으로 높아질수록 권위적인 태도가 나올 수 있고, 낮아질수록 소신이 없거나 무절제한 사람이 될 수 있다는 사실을 기억하고 대인관계 안에서 자신의 말이나 행동을 의식적으로 돌아보는 것이 좋습니다. 
               `,
    },
    높음: {
      name: "높은 JC 유형",
      desc: `
                  부모와 같은 양육자들의 생각이나 행동, 또는 느낌을 내면화한 것으로 성인이 된 현재의 나에게 계속해서 영향을 주고 있는 말이나 동작이 포함되어 있습니다. <strong class="green">이 영역은 주로 비판, 비난, 처벌, 통제와 관련이 있으며 규칙과 규율을 가르치고 양심을 심어주는 역할</strong>도 합니다.
                  따라서, JC 유형 점수가 높은 <strong class="blue">당신은 성실하고 책임감 있으며 자신의 이상을 주도적으로 이끌 수 있어 조직에서는 쉽게 신뢰받고 성공할 수 있습니다. 그러나 자신이나 타인에게 엄격함을 강요하거나 비판적인 경향이 있습니다. 자기주장이 강하여 타인의 의견을 잘 듣지 않거나 책임감이 강하여 결정된 일은 책임지고 끝까지 완수하려 합니다. 충고나 조언을 자주하며 타인에 대한 평가나 사물의 옳고 그름이 명확합니다. </strong>
                  이 영역의 점수가 평균 이상으로 높아질수록 권위적인 태도가 나올 수 있고, 낮아질수록 소신이 없거나 무절제한 사람이 될 수 있다는 사실을 기억하고 대인관계 안에서 자신의 말이나 행동을 의식적으로 돌아보는 것이 좋습니다. 
                  <strong>높은 JC 유형은 낮은 MR 유형 또는 높은 FE 유형과의 소통에서 불편함을 느낄 수 있습니다. JC 영역의 점수를 낮추어 평균을 유지하면 이 부분도 자연스럽게 해소될 수 있습니다. </strong>
               `,
    },
  };
  const ecDetail = {
    낮음: {
      name: "낮은 EC 유형",
      desc: `
                  부모와 같은 양육자들의 생각이나 행동, 또는 느낌을 내면화한 것으로 성인이 된 현재의 나에게 계속해서 영향을 주고 있는 말이나 동작이 포함되어 있습니다. <strong class="green">이 영역은 주로 허용, 보호, 동정, 공감 등과 관련이 있으며 어린아이의 성장을 돕는 자상한 부모와 같은 역할을 하기도</strong> 합니다.
                  따라서, EC 유형 점수가 낮은 당신은 <strong class="blue">상대에게 방임적이고 무정한 태도를 보여 사람들에게 무관심하다는 소리를 들을 수 있습니다. 반면, 정에 휩쓸리지 않고 냉정함을 유지하며, 객관적으로 일을 처리하기 때문에 똑부러진다는 평가를 받기도 합니다. 또, 자신의 처세에 따라 주변 사람을 간섭하거나 괴롭히지 않고 스스로 문제해결을 하는 자기주도적인 사람으로 보일 수도 있습니다. 하지만, 따뜻하지 않은 성격 때문에 남들과 가까워지기 어렵고, 공감하는 능력이 많이 떨어질 경우, 타인에게 불안감을 줄 수도 있습니다.</strong>
                  이 영역의 점수가 평균 이상으로 높아질수록 상대방의 독립심이나 자신감을 빼앗을 수 있고, 낮아질수록 무정하거나 공감력이 떨어지는 사람이 될 수 있다는 사실을 기억하고 이 영역의 균형을 위해 노력해야 합니다. 
                  <strong>낮은 EC 유형은, 높은 FE 유형 또는 높은 AE 유형과의 소통에서 불편함을 느낄 수 있습니다. EC 영역의 점수를 높이면 이 부분도 자연스럽게 해소될 수 있습니다. </strong>
                `,
    },
    적당함: {
      name: "적당한 EC 유형",
      desc: `
                  부모와 같은 양육자들의 생각이나 행동, 또는 느낌을 내면화한 것으로 성인이 된 현재의 나에게 계속해서 영향을 주고 있는 말이나 동작이 포함되어 있습니다. <strong class="green">이 영역은 주로 허용, 보호, 동정, 공감 등과 관련이 있으며 어린아이의 성장을 돕는 자상한 부모와 같은 역할을 하기도 합니다.</strong>
                  따라서, EC 유형 점수가 평균적인 <strong class="blue">당신은 전형적인 모성으로 표현되는 온화하고 헌신적인 면이 있습니다. 사람들에게 상냥하게 말을 건네며 ‘잘했구나’, ‘훌륭해’라고 격려합니다. 상대방의 입장에서 생각하고 상대의 장점을 발견하려 노력하며 용기를 북돋아 줍니다. 규칙에 너무 얽매이지 않고 그렇다고 느슨하지도 않으며 자신의 가치 기준에서 냉정하게 사물을 판단해, 임기응변으로 행동하는 것이 가능합니다. 사람에 대한 편견을 갖지 않으며 친절한 마음가짐으로 행동합니다. 그러나 타인이 도움을 요청하면 거절하는게 쉽지 않습니다. </strong>
                  이 영역의 점수가 평균 이상으로 높아질수록 상대방의 독립심이나 자신감을 빼앗을 수 있고, 낮아질수록 무정하거나 공감력이 떨어지는 사람이 될 수 있다는 사실을 기억하고 이 영역의 균형을 위해 노력해야 합니다.
               `,
    },
    높음: {
      name: "높은 EC 유형",
      desc: `
                  부모와 같은 양육자들의 생각이나 행동, 또는 느낌을 내면화한 것으로 성인이 된 현재의 나에게 계속해서 영향을 주고 있는 말이나 동작이 포함되어 있습니다. <strong class="green">이 영역은 주로 허용, 보호, 동정, 공감 등과 관련이 있으며 어린아이의 성장을 돕는 자상한 부모와 같은 역할을 하기도 합니다.</strong>
                  따라서, EC 유형 점수가 높은 당신은 <strong class="blue">다정다감하게 행동하는 것이 최대의 장점입니다. 나이나 지위, 성별에 관계없이 많은 사람들을 공정하게 대하는 인기인 스타일로 다양한 유형의 사람들과 좋은 관계를 구축하는 것이 특기이며 모두의 중심에 있는 경우가 많습니다. 그러나 때로는 타인을 위해서 무리하는 경우가 있고, 이로 인해 관계에서 손해를 보는 경우가 생깁니다. 이 영역의 점수가 높으면 다른 사람을 과보호하거나 과하게 개입하여 상대방의 자주성과 자립성을 약하게 하며 의존적으로 만들 수 있습니다.</strong> 또, 이 영역의 점수가 극단적으로 높으면, 버릇이 없거나 무절제한 상황을 용인하고 타인의 부탁을 거절하지 못하여 스트레스를 받으면서도 무리한 생황을 지속할 수 있으니 이 점을 경계해야 합니다. 
               `,
    },
  };
  const mrDetail = {
    낮음: {
      name: "낮은 MR 유형",
      desc: `
                  성인으로서 지금 여기에 대한 직접적 반응으로서 사실을 중심으로 관찰한 행동이나 판단, 감정을 주관합니다. <strong class="green">이 영역은 주로 ‘이성’과 관련이 있으며 객관적 사고, 분석적 사고, 냉철한 판단, 정확한 계산 등과 관련된 행동적 단서들로 측정</strong>됩니다.  
                  따라서, MR 유형 점수가 낮은 <strong class="blue">당신은 일관성이 없고 정확한 판단이나 이해력이 부족하며 무계획적일 때가 많습니다. 심할 경우, 감정의 기복에 따라 냉정함을 잃거나 주위를 잘 살피지 못하고 사태를 명확히 판단하지 못할 수 있습니다. 사실에 따라 객관적으로 생각하는 연습이 잘 되어 있지 않아 매우 주관적이거나 즉흥적이라는 평가를 받을 수 있습니다. 갑작스러운 감정의 변화나 감당하기 어려운 스트레스가 어디로부터 시작되었는지 깊이 있게 들여다보려는 노력이 필요합니다. 문제상황 발생시, 그 근본 원인을 찾으려는 철학적 사고가 필요함을 인식하고 이를 위한 연습이 필요</strong>합니다. 
                  <strong>낮은 MR 유형은, 높은 JC 유형 또는 높은 MR 유형과의 소통에서 불편함을 느낄 수 있습니다. MR 영역의 점수를 높이면 이 부분도 자연스럽게 해소될 수 있습니다. </strong>
               `,
    },
    적당함: {
      name: "적당한 MR 유형",
      desc: `
                  성인으로서 지금 여기에 대한 직접적 반응으로서 사실을 중심으로 관찰한 행동이나 판단, 감정을 주관합니다. <strong class="green">이 영역은 주로 ‘이성’과 관련이 있으며 객관적 사고, 분석적 사고, 냉철한 판단, 정확한 계산 등과 관련된 행동적 단서들로 측정</strong>됩니다. 
                  따라서, MR 유형 점수가 평균적인 <strong class="blue">당신은 이성적이고 합리적으로 판단하며 자신과 타인을 적절히 통제하는 기능을 갖추고 있습니다. 관계가 원만하며, 상대의 상황을 잘 파악하고 적당한 소통과 배려, 공감을 할 수 있습니다. </strong>
                  이 영역의 점수가 평균 이상으로 높아질수록 냉정하거나 사무적이고 자기중심적인 태도가 나올 수 있고, 낮아질수록 불안정하고 무계획적이며 판단력이 떨어지는 사람이 될 수 있다는 사실을 기억하고 대인관계 안에서 자신의 말이나 행동을 의식적으로 돌아보는 것이 좋습니다. 
               `,
    },
    높음: {
      name: "높은 MR 유형",
      desc: `
                  성인으로서 지금 여기에 대한 직접적 반응으로서 사실을 중심으로 관찰한 행동이나 판단, 감정을 주관합니다. <strong class="green">이 영역은 주로 ‘이성’과 관련이 있으며 객관적 사고, 분석적 사고, 냉철한 판단, 정확한 계산 등과 관련된 행동적 단서들로 측정</strong>됩니다. 
                  따라서, MR 유형 점수가 높은 <strong class="blue">당신은 자신의 생각이 분명하고 스스로에게 어울리는 행동을 하는 것이 장점입니다. 계획성이 있어서 쓸데없다고 판단하는 것은 하지 않으려는 보수적인 면도 있습니다. 무관심한 사람이나 계획성이 없는 사람과 어울리기를 꺼려하고, 판단이 빠르고 냉철하여 ‘어차피 이 일은 안될 일’ ‘어차피 이 사람은 안된다’라고 생각하면 단번에 잘라버리는 경향이 있습니다. ‘언제, 어디서, 누가, 무엇을, 어떻게, 왜?’를 너무 따져 묻는 경우가 있어서 완고하고 깐깐한 사람이라는 이미지를 풍길 수 있습니다. 만사를 공평하게 대해야 한다는 생각 때문에 그 어느쪽에서도 치우치지 않아 고립을 자처하거나 양쪽 모두에게 서운함을 살 수 있습니다. </strong>
                  이 영역의 점수가 평균 이상으로 높아질수록 냉정하거나 사무적이고 자기중심적인 태도가 나올 수 있고, 낮아질수록 불안정하고 무계획적이며 판단력이 떨어지는 사람이 될 수 있다는 사실을 기억하고 대인관계 안에서 자신의 말이나 행동을 의식적으로 돌아보는 것이 좋습니다. 
                  <strong>높은 MR 유형은, 낮은 JC 유형 또는 높은 FE 유형과의 소통에서 불편함을 느낄 수 있습니다. MR 영역의 점수를 낮추어 평균을 유지하면 이 부분도 자연스럽게 해소될 수 있습니다. </strong>
               `,
    },
  };
  const feDetail = {
    낮음: {
      name: "낮은 FE 유형",
      desc: `
                  아동기 시절부터 재연되는 행동, 사고, 감정으로 어린 시절 실제로 경험하고, 느끼거나 행동했던 것과 같은 감정이나 행동을 성인이 된 지금에도 나타내는 상태입니다. 어린시절 부모와 같은 양육자에 대응하기 위해 습관화된 반응양식을 포함합니다. <strong class="green">이 영역은 주로 감정적, 본능적, 자기중심적, 호기심, 창의성 등과 관련이 있으며 이 영역이 평균적일수록 건강한 상태라고 여깁니다. </strong>
                  따라서, FE 유형 점수가 낮은 <strong class="blue">당신은 타인의 시선을 너무 의식해서 자신의 기분에 따라 솔직하게 행동하는 것이 어렵습니다. 자신의 감정을 거의 개방하지 않고 자신의 생활을 감정적으로 즐기려하지 않습니다. 표현력이 부족하며 남에게 자신의 생각을 전달하는 것이 어렵고 움츠러들어 주위를 곤란하게 할 수 있습니다. 하기 싫거나 힘든 부탁을 받아도 거절하지 못하고 자신의 기분을 억누르거나 타협해 버리는 경향이 있습니다. 또, 겉으로만 타인과 어울리는 경우가 많아 상대방이 어떻게 생각하고 있을까를 신경 쓰느라 본심을 드러내지 못하고 스스로 지치는 경우가 많습니다. 새로운 일에 대한 도전도 가능하면 피하기 때문에 인간관계에서 큰 갈등으로 연결되는 일은 거의 없습니다. 성실하고 상냥하고 곤란한 일이 있어도 참기 때문에 주위에 자신의 생각을 전하지 못하고, 소극적인 인상을 풍깁니다. 자신의 억압된 감정이 무엇인지 살펴보고, 의식적으로 표현하고 드러내는 연습이 필요</strong>합니다. 
                  낮은 FE 유형은, 높은 MR 유형과의 소통에서 불편함을 느낄 수 있습니다. FE 영역의 점수를 높이면 이 부분도 자연스럽게 해소될 수 있습니다. 
               `,
    },
    적당함: {
      name: "적당한 FE 유형",
      desc: `
                  아동기 시절부터 재연되는 행동, 사고, 감정으로 어린 시절 실제로 경험하고, 느끼거나 행동했던 것과 같은 감정이나 행동을 성인이 된 지금에도 나타내는 상태입니다. 어린 시절 부모와 같은 양육자에 대응하기 위해 습관화된 반응양식을 포함합니다. <strong class="green">이 영역은 주로 감정적, 본능적, 자기중심적, 호기심, 창의성 등과 관련이 있으며 이 영역이 평균적일수록 건강한 상태라고 여깁니다. </strong>
                  따라서, FE 유형 점수가 평균적인 <strong class="blue">당신은 타인과의 관계에 어려움이 없고 솔직한 사람입니다. 적당한 호기심과 열정을 갖고 있으면서도 남을 배려할 줄 알고 자신의 일에 책임감을 갖고 있는 건전한 상태입니다. 자신의 의견을 잘 발표하며 행동이 자연스럽고 밝은 편입니다. 
                  개방적이고 솔직하며 감정표현을 잘하는 유형이지만, 자아가 억압받을 경우 스트레스를 받으면서 자기 비하를 할 수도 있습니다. 자신의 열정과 일, 공부의 즐거움을 꾸준히 유지하면서 이 영역의 균형을 위해 노력해야</strong> 합니다.
               `,
    },
    높음: {
      name: "높은 FE 유형",
      desc: `
                  아동기 시절부터 재연되는 행동, 사고, 감정으로 어린 시절 실제로 경험하고, 느끼거나 행동했던 것과 같은 감정이나 행동을 성인이 된 지금에도 나타내는 상태입니다. 어린 시절 부모와 같은 양육자에 대응하기 위해 습관화된 반응양식을 포함합니다. <strong class="green">이 영역은 주로 감정적, 본능적, 자기중심적, 호기심, 창의성 등과 관련이 있으며 이 영역이 평균적일수록 건강한 상태라고 여깁니다. </strong>
                  따라서, FE 유형 점수가 높은 <strong class="blue">당신은 발랄하고 기운이 넘치기 때문에 특별한 행사에 함께 하고 싶은 존재입니다. 그러나 자신이 싫은 것이나 흥미가 없는 것은 타인에게 미뤄버리고 흥미가 있는 것에만 집중하려 합니다. 그러다가 직장에서 생각지도 못한 성과를 얻는 경우도 있지만 이로 인해 주변에 불편한 감정이 생기는 사람도 있습니다. 감정을 우선으로 내세워 돌진하지만 미워할 수 없는 캐릭터로 주위 사람들도 언제나 용서해주곤 합니다. 물어보거나 망설이기 보다는 직접 도전하는 편이며 스스럼없이 어리광을 부립니다.</strong> 이 영역이 너무 강해지면, 충동적이고 자기중심적이며 무책임한 모습을 보일 수 있다는 사실을 기억하고 대인관계 안에서 자신의 말이나 행동을 의식적으로 돌아보는 것이 좋습니다. 
               `,
    },
  };
  const aeDetail = {
    낮음: {
      name: "낮은 AE 유형",
      desc: `
                  아동기 시절부터 재연되는 행동, 사고, 감정으로 어린 시절 실제로 경험하고, 느끼거나 행동했던 것과 같은 감정이나 행동을 성인이 된 지금에도 나타내는 상태입니다. 어린 시절 부모와 같은 양육자에 대응하기 위해 습관화된 반응양식을 포함합니다. <strong class="green">이 영역은 주로 순응적, 타협적, 협조적, 억압, 자기희생 등과 관련이 있으며 부모와 같은 양육자에게 순종하려고 노력하던 어린 시절의 모습을 반영</strong>합니다. 
                  따라서, AE 유형 점수가 낮은 <strong class="blue">당신은 자신감 있고 적극적인 성격이라, 자기가 하려는 일은 주도적으로 진행하는 전형적인 리더십 있는 성격입니다. 하지만 자기 주관이 뚜렷하고, 어지간해서는 굽히지 않는 성격이라서 남들과 부딪히기도 쉽고, 독불장군이라며 사람들이 멀리할 때도 있습니다. 망설이지 않고 적극적으로 일을 추진하는 과정에서 타인의 감정과 성향을 파악하지 못하여 갈등이 발생할 수 있으며 이때에도 자신만의 기준이 명확하여 관계를 신경쓰지 않고 밀어붙일 수 있습니다. 모두가 나와 같이 자신감이나 추진력이 있는 것은 아니라는 점을 기억하고 다른 사람의 감정과 성향을 존중하려는 의식적인 노력이 필요</strong>합니다.
               `,
    },
    적당함: {
      name: "적당한 AE 유형",
      desc: `
                  아동기 시절부터 재연되는 행동, 사고, 감정으로 어린 시절 실제로 경험하고, 느끼거나 행동했던 것과 같은 감정이나 행동을 성인이 된 지금에도 나타내는 상태입니다. 어린 시절 부모와 같은 양육자에 대응하기 위해 습관화된 반응양식을 포함합니다. <strong class="green">이 영역은 주로 순응적, 타협적, 협조적, 억압, 자기희생 등과 관련이 있으며 부모와 같은 양육자에게 순종하려고 노력하던 어린 시절의 모습을 반영</strong>합니다. 
                  따라서, AE 유형 점수가 평균적인 <strong class="blue">당신은 자기절제와 협동심이 균형을 이룬 상태입니다. 겸손함, 협동심, 인내심을 갖추고 있기 때문에 주변으로부터 착한 사람, 좋은 사람, 무난한 사람으로 인정받을 수 있습니다. 즉, 주변을 편안하게 하거나 분위기를 쇄신하는 노력을 통해 안정감과 적당한 활력을 줄 수 있는 사람</strong>입니다.
                  이 영역의 점수가 평균 이상으로 높아질수록 자기비하, 열등감 등으로 자기 억압적인 태도가 나올 수 있고, 낮아질수록 소통이 어려운 자기중심적인 사람이 될 수 있다는 사실을 기억하고 대인관계 안에서 자신의 말이나 행동을 의식적으로 돌아보는 것이 좋습니다.
               `,
    },
    높음: {
      name: "높은 AE 유형",
      desc: `
                  아동기 시절부터 재연되는 행동, 사고, 감정으로 어린 시절 실제로 경험하고, 느끼거나 행동했던 것과 같은 감정이나 행동을 성인이 된 지금에도 나타내는 상태입니다. 어린 시절 부모와 같은 양육자에 대응하기 위해 습관화된 반응양식을 포함합니다. <strong class="green">이 영역은 주로 순응적, 타협적, 협조적, 억압, 자기희생 등과 관련이 있으며 부모와 같은 양육자에게 순종하려고 노력하던 어린 시절의 모습을 반영</strong>합니다. 
                  따라서, AE 유형 점수가 높은 <strong class="blue">당신은 온화하고 겸손한 성격이라서 갈등이 거의 발생하지 않고, 남들이 시킨 일을 꼼꼼히 해내는 '모범생'의 이미지가 있습니다. 그러나 자신감이 없어서 남들에게 의존하기 쉽고, 자기감정을 억압하느라 열등감을 품거나 스트레스가 쌓이는 일도 잦습니다. 
                  항상 배려하고 타인을 의식하며 상대방의 마음에 들도록 노력합니다. 세부적인 일까지 심하게 신경을 쓰며 속으로는 불만이 있더라도 표면에 나타내지 않습니다. 생각이나 행동에 자신이 없으며 자신의 생각을 말하지 못해서 후회하는 경우가 많고, 불쾌한 감정을 웃는 얼굴로 표현할 때가 있습니다. </strong>
                  이 영역의 점수가 평균 이상으로 높아질수록 자기비하, 열등감 등으로 자기 억압적인 태도가 나올 수 있고, 낮아질수록 소통이 어려운 자기중심적인 사람이 될 수 있다는 사실을 기억하고 자신의 감정과 생각을 자유롭게 표현하는 연습을 의식적으로 해야 합니다.
               `,
    },
  };

  // ref[13]
  const ref13Data = {
    language: {
      desc: `단어를 효과적으로 사용하는 능력과 언어를 이해하고 실용적 영역을 조작하는 능력을 말합니다. ${
        isLangTopThree ? '<span class="green">' : '<span class="red">'
      }단어의 소리, 리듬, 의미에 대한 감수성이나 언어의 다른 기능에 대한 민감성 등과 관련된 능력입니다.</span> 토론 학습 시간에 두각을 나타내며, 유머나 말 잇기 게임, 낱말 맞추기 등을 잘합니다. ${
        isLangTopThree ? '<span class="green">' : '<span class="red">'
      }다양한 단어를 잘 활용하여 말을 잘하는 달변가가 많으며, 똑같은 글을 써도 심금을 울리기도 하고, 웃음을 자아내기도 합니다.</span> 강의력, 설득력, 이야기 창조능력 등이 뛰어납니다.`,
    },
    math: {
      desc: `숫자를 효과적으로 사용하는 능력, 사물 사이의 논리적 계열성을 이해하고 유사성과 차이점을 측정하는 능력을 말합니다. ${
        isMathTopThree ? '<span class="green">' : '<span class="red">'
      }논리적 과정에 대한 문제들을 보통 사람들보다 빠른 속도로 해결할 수 있습니다. 추론을 잘 이끌어내며, 체계적이고 과학적인 방법을 동원해서 문제를 파악합니다.</span> 숫자에 강하고, 차량번호나 전화번호, 기념일 등도 남들에 비해 잘 기억하는 경우가 많습니다.`,
    },
    time: {
      desc: `${
        isTimeTopThree ? '<span class="green">' : '<span class="red">'
      }대상을 시각화하는 능력, 내적인 이미지나 사진과 영상을 창출하는 능력, 시공간적 세계를 정확하게 인지하는 능력과 3차원의 세계를 잘 변형시키는 능력을 말합니다.</span> 공간적 지능은 색깔, 선, 모양, 형태, 공간 등과 그 관계에 대한 민감성과 관련 있습니다. 밤하늘의 별을 보고 방향을 잘 찾아내며, 처음 방문한 곳도 다시 찾아가는 데 어려움을 느끼지 않습니다. 또, 시공간적 아이디어들을 도표, 지도, 그림 등으로 잘 나타내고, 시각적으로 표현하는 디자인, 그림 그리기, 만들기 등을 좋아합니다.`,
    },
    body: {
      desc: `신체의 운동을 손쉽게 조절하는 능력, 손을 사용하여 사물을 만들어내고 변형시키는 능력을 말합니다. ${
        isBodyTopThree ? '<span class="green">' : '<span class="red">'
      }생각이나 느낌을 글이나 그림보다는 몸동작으로 표현하는 능력이 뛰어납니다.</span> 율동이나 레크리에이션, 무용, 연극 등을 잘하며 손재주가 좋다는 말을 많이 듣습니다. 자동차 운전, 스케이트, 스키 등을 다른 사람보다 쉽게 배운다든지 나무를 잘 타고 오르는 능력이 있습니다. ${
        isBodyTopThree ? '<span class="green">' : '<span class="red">'
      }즉 몸의 균형 감각과 촉각이 다른 사람들에 비해 발달 되어 있습니다.</span>`,
    },
    music: {
      desc: `음악에 대한 직관적 이해와 분석적이고 기능적인 능력을 말합니다. ${
        isMusicTopThree ? '<span class="green">' : '<span class="red">'
      }소리, 리듬, 진동과 같은 음의 세계에 민감하고, 사람의 목소리와 같은 언어적 형태의 소리뿐만 아니라 비언어적 소리에도 예민합니다.</span> 음악의 형태를 잘 감지하고, 음악적 유형을 잘 구별할 뿐만 아니라 다른 음악 형태로 변형시키기도 잘할 수 있습니다. ${
        isMusicTopThree ? '<span class="green">' : '<span class="red">'
      }소리 전체를 다루는 능력이기 때문에 청각-진동적인 지능이라고 부르기도 합니다.</span>`,
    },
    relationship: {
      desc: `다른 사람들과 교류하고, 이해하며, 그들의 행동을 해석하는 능력을 말합니다. ${
        isRelationTopThree ? '<span class="green">' : '<span class="red">'
      }다른 사람들의 기분, 감정, 의향, 동기 등을 인식하고 구분할 수 있는 능력과 표정, 음성, 몸짓 등에 대한 감수성, 대인관계에서 나타나는 여러 가지 다양한 힌트, 신호, 단서, 암시 등을 변별하는 역량, 또 이들에 효율적으로 대처하는 능력이 높습니다.</span> 친구가 많고 집단에서 중앙에 위치하는 경우가 많습니다. 유능한 정치인, 지도자, 또는 성직자들은 대인관계 지능이 우수한 사람들이 많습니다.`,
    },
    self: {
      desc: `대인관계 지능과 유사한 특성이며, 자신을 이해하고, 느낄 수 있는 인지적 능력을 말합니다. ${
        isSelfTopThree ? '<span class="green">' : '<span class="red">'
      }자신을 잘 아는 능력, 자신의 장단점을 인식하고 스스로 의사결정을 내리는 능력, 자신의 감정, 기분을 잘 조절하는 능력, 자신을 잘 표현하고 행동을 규제하는 능력이 발달되어 있습니다.</span> ‘나는 누구인가?, 나는 어떤 감정을 가졌는가?, 왜 이렇게 행동하는가?’ 등과 같은 자기 존재에 대해 이해하는 것으로 자기 존중감이 높고, 자신의 문제를 스스로 해결하기 위해 노력합니다. 이 지능이 낮은 사람들은 자신을 주변환경으로부터 독립된 존재로서 인식하는데 어려움을 겪습니다.`,
    },
    nature: {
      desc: `${
        isNatureTopThree ? '<span class="green">' : '<span class="red">'
      }사물을 구별하고 분류하는 능력과 환경의 특징을 사용하는 능력, 사물과 인간의 관계를 설정하는 능력 등 자연 현상에 대한 유형을 규정하고 분류하는 능력을 말합니다.</span> 원시 사회에서는 먹을 수 있는 것과 없는 것을 그들의 자연탐구 지능에 의존하여 알아냈습니다. 현대 사회에서는 기후 형태의 변화에 대한 감수성과 같은 능력으로 나타납니다. 이 역량이 높은 사람들은 자연 친화적이고, 꽃이나 풀, 식물, 광물, 동물을 잘 분류하고 인식합니다. 동식물의 모양, 크기, 지형 등에 관심이 많고 자연 현상에 민감하며 정확하게 탐구합니다.`,
    },
  };

  // ref[29]
  const ref29ScoreResult = {
    50: {
      review:
        "당신은 지금 인생에 대한 총체적인 점검과 성찰을 해야 하는 시간입니다. 당신 내면의 ‘불만’은 ‘불안’을 만들고, 불안은 ‘분노’ 또는 ‘우울’로 표현됩니다.",
      remember:
        '<span class="green">변화는 반응이 아니라 이제 선택해야 하는 현실이 되었습니다.</span> 우리는 삶의 위기, 트라우마, 상실, 비극, 질병 등을 경험하고 나서야 변화의 길을 모색합니다. 사람은 잘 변하지 않습니다. 그래서 ‘사람이 변하면 죽을 때가 되었나’ 라고들 말하기도 합니다. 사람은 인생의 커다란 위기의 순간, 내가 누구인지, 내가 무엇을 하고 있는지, 어떻게 살아가는지, 어떤 것을 의지하고, 믿고 따르고 있는지, 어떤 생각과 감정을 느끼고 있는지 성찰(Reflection)하게 됩니다. <br/><br/>마음을 바꾸고자 하지만 그것이 쉽지는 않습니다. 내가 가지고 있는 믿음의 뿌리는 나를 둘러싸고 있던 종교, 문화, 사회, 교육, 가족, 매스미디어 등 과거에 주입되거나 학습된 수많은 패러다임들입니다. 시대가 변하고 있습니다. 우리가 알고 있는 것보다 현실은 더 넓고 깊고 빠르게 변화하고 있습니다. 그럼에도 ‘자신만이 옳다’라고 생각하면 당신은 변화할 수 없습니다. 내가 가지고 있는 생각이 편협할 수도 있고, 나의 판단이 잘못될 수도 있습니다. <span class="green">‘내가 틀릴 수도 있다’라는 생각이 당신 변화의 시작이어야 합니다.</span>',
      reinterpretation:
        '마음의 5%만이 의식적이며 나머지 95%는 무의식적인 프로그램, 기억된 행동, 습관적 감정 반응에서 나온다고 합니다. <span class="green">우리는 하루에 95%가량을 무의식적인 상태로 보내는 것입니다. 겉으로는 깨어있는 것 같지만, 무의식이 나를 움직이고 있는 것입니다.</span> 내가 무슨 생각을 하고, 무슨 행동을 하며, 어떤 감정을 느끼는지 더이상 알아차리지 못한다면 잘못된 삶은 습관이 되어버립니다. <span class="green">우리가 깨야 하는 가장 큰 것은 우리 자신이 되어버린 습관이라는 무서운 ‘틀’, 자동적 사고입니다.</span> 진짜 주인인 의식은 잠들어 버리고 무의식이 몸을 지배합니다. <br/><br/>무의식은 기억된 감정들과 일치하는 결정을 내리도록 명령하고 있는 몸입니다. 몸이 마음을 지배하고 있는 것입니다. 중독이 되어버린 몸이 마음을 지배해버립니다. 이제 우리는 이 과정을 통해 잠재의식(무의식) 안으로 들어가 그것을 새롭게 하는 프로그래밍 작업을 해야합니다. <span class="green">낡은 생각과 느낌의 패턴을 찾아 끊어 버리고, 우리가 되고자 하는 모습에 맞추어 새로운 생각과 감정의 패턴을 재학습 혹은 재연결해야 합니다.</span> 몸은 새로운 마음에 길들이면 더 이상 반대로 작용하지 않고 조화를 이루게 됩니다. 이것이 변화의 핵심이자 자기 창조(Recreation)의 핵심이며 재해석의 포인트입니다. ',
      refresh:
        '그동안 내가 불안하고, 우울하고, 분노했던 기억들을 톺아보면 대부분 지나간 시간에 대한 후회와 원망, 일어나지도 않은 미래의 일에 대한 걱정과 불안이 지배적이었다는 것을 깨닫게 됩니다. 존재하지 않는 과거와 다가오지 않은 미래가 소중한 현재, 지금 여기를 병들에 하고, 멈추게 하고, 좌절하게 만들거나 포기하게 만들었던 것입니다. 그래서 중요한 방법이 <span class="green">‘지금여기’에 집중하는 것, 몰입하는 것입니다.</span><br/><br/> 내가 행복해 하는 시간은 부쩍 빨리 지나가 버립니다. 운전을 하거나, 영화를 보거나, 좋은 사람과 좋은 시간을 보내는 것에 몰입하면 시간을 잊어버립니다. 자연 속에 고요히 앉아 있거나, 조용하고 쾌적한 공간에서 명상하는 시간 등의 몰입은 지금 여기의 소중한 가치를 일깨워줍니다. <span class="green">현재에 몰입하는 체험은 우리의 과거를 내려놓고, 미래의 새로운 마음을 창조할 수 있는 좋은 기회가 될 수 있습니다.</span>',
      reformation: [
        {
          name: "마음 읽기(독심술)",
          content:
            "다른 사람에 대한 충분한 근거 없이, ‘저 사람은 이러저러한 생각을 할 것이다’라고 가정한다. ",
          result: "“그는 내가 실패자라고 생각할 것이다.”",
          tip: "인지 왜곡이 일어날 수 있는, 당신 안에 부정적인 신념을 구분하고 분류해 보는 것이 좋습니다.",
        },
        {
          name: "점치기(예언)",
          content:
            "미래를 예측한다. ‘미래에 어떤 위험이나 재앙이 있을 것이다’라고 생각한다.",
          result: "“나는 분명 이번 시험에 떨어질 것이다.”",
          tip: "혹시 당신은 다른 사람들의 마음을 먼저 이렇게 생각하지는 않는가요?",
        },
      ],
      reformation_desc: [
        "우리는 때때로 다른 사람의 생각이나 미래의 결과를 정확히 알지 못하면서도 추측하고 단정 짓는 습관을 갖고 있습니다. 이는 마음 읽기나 점치기라는 인지 왜곡으로, 자신을 부정적인 감정이나 스트레스에 빠뜨릴 수 있습니다. 이러한 습관을 바로잡기 위해 자신의 생각을 인식하고, 더 합리적이고 긍정적인 사고로 전환하는 연습이 필요합니다.",
        '이러한 생각의 고리들이 당신을 자꾸 수렁으로 밀어 넣고 있는 것입니다. <span class="green">내 안에서 만들어 놓은 타인과 자신에 대한 부정적인 생각의 고리들을 끊어내야 합니다. 그래야 다시 시작할 수 있습니다.</span> 용기를 내어 인생재건축을 시작해봅니다. 정확한 자기분석은 인생을 변화하는 첫걸음이 될 것입니다.',
      ],
      reconstruction:
        '나도 모르게 ‘나는 부적합한 사람이며 타인에게 거절당할 지도 모른다’는 두려움을 갖게 됩니다. 이와 동시에 ‘실패’에 대한 두려움에 내 능력을 제대로 발휘하지도 못하고, 창피당할 것이 두려워 감정을 숨기는 일이 많았습니다. 이 점은 스스로에 대한 과도한 기대 때문에 시작된 것일 수 있습니다. <span class="green">더 잘해야 하고, 더 유능해 보여야 하고, 더 완벽해야 한다는 기대가 타인과 나를 비교하고 자꾸만 외부 시선을 의식하게 합니다.</span> <br/><br/>다른 사람의 칭찬을 얻기 위해 나 자신의 모습을 저버리는 일을 그만두어야 합니다. 그리고 용기를 내어 <span class="green">‘내 모든 감정은 나의 것이며 나의 생각과 말과 행동은 타인의 인정과 감사를 받기 위해서가 아니라 내가 진정 원하기 때문’임을 선언해야 합니다.</span> <br/><br/>"나는 과거의 상처를 통해 배우고, 그것을 딛고 나 자신만의 길을 걸어갈 용기를 선택할 것입니다."',
    },
    60: {
      review:
        "당신은 일부러라도 시간을 내어 인생에 대한 점검을 해야하는 시기입니다. 당신 내면의 ‘불만’은 ‘불안’을 만들고, 불안은 ‘분노’ 또는 ‘우울’로 표현됩니다.",
      remember:
        '<span class="green">당신의 기억 속에 있는 부정적인 감정들을 이해하는 작업을 해야 합니다.</span> 누구에게나 삶의 기회는 단 한 번 주어지지만, 그 삶을 바라보는 각자의 방식에 따라 서로 다른 길을 가게 됩니다. 삶에 대한 긍정과 낙관은 다가오는 일들에 대한 희망과 신뢰를 가지고 나의 에너지를 결집하여 효과적인 일처리와 성공적인 결과를 만들어 낼 수 있습니다. 그러나 부정적인 인식과 그릇된 믿음은 당신을 더욱 더 난처한 상황으로 이끌어 갈 것입니다. <span class="green">변화는 당신에게 선택의 문제가 아니라 ‘용기’의 문제입니다.</span> <br/><br/>과거 불편한 일들이나 불편했던 사람들에 대한 기억을 해결하지 않은 채 그대로 지나고서는 현재의 ‘나’를 충만하게 살아갈 수 없습니다. 현재의 ‘나’를 변화시키지 못한다면 미래의 ‘나’ 역시 다르지 않은 그대로의 모습일 것입니다. <span class="green">먼저 당신이 해야 할 일은 지난 기억 속에 있는 부정적인 ‘나’의 모습을 발견하고 생각을 변화시킬 수 있는 프로그램에 참여하거나, 스스로 자신을 분석하고 치유하는 마음돌봄 작업을 시작하는 것입니다.</span>',
      reinterpretation:
        '당신에게 있는 수치심, 죄의식, 무기력과 슬픔, 두려움에 대해 점검해 보아야 합니다. 나에게 일어났던 비참했던 기억들, 억울했던 사건이나, 나에게 해를 입힌 가해자에 대한 원망들을 돌아보세요. <br/><br/>당신이 이루지 못했던 것들로 인해 가지게 된 무기력과 슬픔을 기억하세요. 절망하고, 포기하고, 희망이 없었던 시간을 생각해 보세요. 심한 무기력과 우울감에 무력해지고, 움직이기조차 어려웠던 시간이 있었나요? 아무도 나를 이해 해주지 않는 것 같아서 답답하고 괴로웠던 시간이 있었나요? <br/><br/>그것은 당신 탓이 아닙니다. 당신만의 문제는 아닙니다. <span class="green">당신 인생의 ‘불만’은 ‘불안’을 야기합니다. 그 불안이 밖으로 터져 오르면 ‘분노’가 되고, 안으로 묶어두면 ‘우울’이 됩니다. 이제 지난 시간의 부정적인 기억을 정돈하는 작업이 필요합니다.</span> 당신 감정의 균형을 되찾기 위해 노력해야 하는데 그 시작은 부정적인 과거의 사슬에서 벗어나려는 노력일 것입니다. 그래서 인생을 재해석(Reinterpretation)하는 작업이 필요합니다.',
      refresh:
        '그동안 내가 불안하고, 우울하고, 분노했던 기억들을 톺아보면 대부분 지나간 시간에 대한 후회와 원망, 일어나지도 않은 미래의 일에 대한 걱정과 불안이 지배적이었다는 것을 깨닫게 됩니다. <span class="green">존재하지 않는 과거와 다가오지 않은 미래가 소중한 현재, 지금 여기를 병들게 하고, 멈추게 하고, 좌절하게 만들거나 포기하게 만들었던 것입니다. 그래서 중요한 방법이 ‘지금여기’에 집중하는 것, 몰입하는 것입니다.</span> <br/><br/>내가 행복해 하는 시간은 부쩍 빨리 지나가 버립니다. 운전을 하거나, 영화를 보거나, 좋은 사람과 좋은 시간을 보내는 것에 몰입하면 시간을 잊어버립니다. 자연 속에 고요히 앉아 있거나, 조용하고 쾌적한 공간에서 명상하는 시간 등의 몰입은 지금 여기의 소중한 가치를 일깨워줍니다. 현재에 몰입하는 체험은 우리의 과거를 내려놓고, 미래의 새로운 마음을 창조할 수 있는 좋은 기회가 될 수 있습니다. ',
      reformation: [
        {
          name: "마음 읽기(독심술)",
          content:
            "다른 사람에 대한 충분한 근거 없이, ‘저 사람은 이러저러한 생각을 할 것이다’라고 가정한다. ",
          result: "“그는 내가 실패자라고 생각할 것이다.”",
          tip: "인지 왜곡이 일어날 수 있는, 당신 안에 부정적인 신념을 구분하고 분류해 보는 것이 좋습니다.",
        },
        {
          name: "점치기(예언)",
          content:
            "미래를 예측한다. ‘미래에 어떤 위험이나 재앙이 있을 것이다’라고 생각한다.",
          result: "“나는 분명 이번 시험에 떨어질 것이다.”",
          tip: "혹시 당신은 다른 사람들의 마음을 먼저 이렇게 생각하지는 않는가요?",
        },
      ],
      reformation_desc: [
        "우리는 때때로 다른 사람의 생각이나 미래의 결과를 정확히 알지 못하면서도 추측하고 단정 짓는 습관을 갖고 있습니다. 이는 마음 읽기나 점치기라는 인지 왜곡으로, 자신을 부정적인 감정이나 스트레스에 빠뜨릴 수 있습니다. 이러한 습관을 바로잡기 위해 자신의 생각을 인식하고, 더 합리적이고 긍정적인 사고로 전환하는 연습이 필요합니다.",
        '이러한 생각의 고리들이 당신을 자꾸 수렁으로 밀어 넣고 있는 것입니다. <span class="green">내 안에서 만들어 놓은 타인과 자신에 대한 부정적인 생각의 고리들을 끊어내야 합니다. 그래야 다시 시작할 수 있습니다. </span>용기를 내어 인생재건축을 시작해봅니다. 정확한 자기분석은 인생을 변화하는 첫걸음이 될 것입니다.',
      ],
      reconstruction:
        '나도 모르게 ‘나는 부적합한 사람이며 타인에게 거절당할 지도 모른다’는 두려움을 갖게 됩니다. 이와 동시에 ‘실패’에 대한 두려움에 내 능력을 제대로 발휘하지도 못하고, 창피당할 것이 두려워 감정을 숨기는 일이 많았습니다. 이 점은 스스로에 대한 과도한 기대 때문에 시작된 것일 수 있습니다. <span class="green">더 잘해야 하고, 더 유능해 보여야 하고, 더 완벽해야 한다는 기대가 타인과 나를 비교하고 자꾸만 외부 시선을 의식하게 합니다.</span> <br/><br/>다른 사람의 칭찬을 얻기 위해 나 자신의 모습을 저버리는 일을 그만두어야 합니다. 그리고 용기를 내어 <span class="green">‘내 모든 감정은 나의 것이며 나의 생각과 말과 행동은 타인의 인정과 감사를 받기 위해서가 아니라 내가 진정 원하기 때문’임을 선언해야 합니다.</span> <br/><br/>"나는 과거의 상처를 통해 배우고, 그것을 딛고 나 자신만의 길을 걸어갈 용기를 선택할 것입니다."',
    },
    70: {
      review:
        "당신은 지금 ‘변화’가 필요한, 인생의 중요한 변곡점에 서 있습니다. 변화는 당신에게 선택의 문제가 아니라 ‘용기’의 문제입니다. ",
      remember:
        '당신은 삶에 대한 만족도가 그리 높지 않습니다. 돈과 명예, 권력, 외모 등 밖으로 보여지는 ‘나’를 위해 많은 힘을 쓰고 살아왔습니다. 남들은 눈치채지 못했을지라도, 자신은 스스로에 대해 만족하지 못하고 실망도 많았을 것입니다. 자칫 존재에 대한 공허함으로 무언가에 중독될 수 있는 가능성이 있습니다. <br/><br/><span class="green">내면에 채워지지 않은 나의 갈망을 ‘재소환’해서 기억해야 합니다. 내가 원했던 것, 바라고 지향하며 살았던 것, 나의 인생의 목표와 방향에 대한 총체적인 점검을 해야 할 시간입니다.</span> 내가 할 수 있는 것과 할 수 없는 것을 잘 구별하여, 할 수 없는 것들은 ‘나를 위해’ 내려놓고, 할 수 있는 것 가운데에서 무엇에 집중할지를 선택해야 합니다. <br/><br/>자신에 대한 불안이 타인에 대한 증오나 공격성을 표출하는 방향으로 나아가서는 안 됩니다. 상대와의 논쟁을 멈추고, 진짜 ‘나’를 찾아 나가는 작업을 시작해야 합니다. 다시 숨을 고르고 인생의 지향과 목표를 점검하는 시간이 필요합니다.',
      reinterpretation:
        '심리학자 융(C.G.Jung)이 집단 심리의 한 측면으로 설명하는 개념이 ‘페르소나(가면)’입니다. 페르소나는 사회가 그 사람에게 기대하는 역할로, 우리는 그 가면을 쓰고 배우처럼 연기합니다. 그것은 부정적인 것만이 아닌 게, 사회생활을 하는데 어쩔 수 없는 요소이기 때문입니다. 바깥세상은 멋진 페르소나, 가령 ‘친절한’, ‘유능한’, ‘명랑한’, ‘가정적인’ 가면을 가진 사람들을 특별히 우대하기 때문에 우리는 페르소나와 자신을 동일시하고 싶은 유혹에 빠지게 됩니다. 하지만 인생의 위기상황에 이르면 우리는 그 ‘가짜’에 한계를 느끼고 부담스럽게 생각합니다. 참된 자신의 모습, 진정한 ‘자기’를 만나고자 합니다. 인생의 어려운 순간 “나는 누구인가?”라는 핵심 질문을 비로소 던지게 됩니다. <br/><br/><span class="green">당신을 해석하는 중요한 지점은 ‘내가 다른 사람들에게 보이고자 했던 모습’을 성찰하는 데서부터 시작합니다.</span> 타인의 좋은 평판을 얻기 위해 나는 어떤 노력을 했었는지 기억해 내는 것이 출발점이 되면 좋겠습니다. 진짜 ‘나’에서 벗어나 있는 가짜 ‘나’를 찾아내는 작업이 잘 된다면 당신은 성공적인 자기 변화를 이룰 수 있게 될 것입니다. ',
      refresh:
        '<strong>Self Imagination Training 자기 이미지 훈련이 필요합니다.<br/><br/></strong> 새로운 무엇인가를 창조하기 위해서는 <span class="green">일상적으로 생각하고 느끼는 방식을 변화시켜야 합니다.</span> 매일 동일한 방식으로 생각하고 느낀다면 계속해서 동일한 상황을 만들어 낼 수밖에 없을 것입니다. 이미 ‘자신’이 되어버린 삶에서 벗어나기 위해서는 삶의 환경보다 더 크게 생각하고, 몸에 기억된 느낌보다 더 커져야 하며 새로운 시간선(영원)을 만들어야 합니다. <span class="green">변화하고자 하는 이상적인 ‘나’를 이미지화해야 합니다.</span> <br/><br/>뇌가 과거의 완전한 기록이고 마음이 의식의 산물이라고 할 때, 어떤 의미에서 우리는 항상 과거 속에서 생각하고 있다고 할 수 있습니다. 기억에 상응하는 뇌의 어떤 부분이 반응함으로써 우리는 과거와 동일한 수준의 마음을 만들어 내고 있는 것입니다. 무의식적인 행동을 하면서 우리는 과거의 자아와 연결되어 있습니다. <span class="green">만족스럽지 못한 나의 모습을 변화시키고자 하지만 나는 오늘도 이전과 다르지 않은 똑같은 생각, 똑같은 행동, 똑같은 감정을 매일 경험하고 있습니다.</span> 과연 이렇게 과거의 에너지에 묶여 우리의 미래 인생을 변화시킬 수 있을까요? 변화된 나의 이상적인 모습을 그려내야 합니다. 그것이 시작입니다.',
      reformation: [
        {
          name: "낙인찍기",
          content:
            "당신이나 타인에 대해 전반적으로 부정적으로 평가하거나 단정 짓는다.",
          result: "“나는 어차피 안 될 놈이다.”<br/>“그는 무책임한 사람이다.”",
          tip: "자신의 판단이 감정적 반응이나 고정관념에서 비롯된 것은 아닌지 성찰하고 열린 마음으로 받아들이는 태도를 유지해야 합니다.",
        },
        {
          name: "부정적 필터링",
          content: "부정적인 것에만 초점을 두어 바라본다.",
          result:
            "“어차피 그 사람은 나를 떠날거예요.”<br/>“그 일을 하지 말았어야 했어요.”",
          tip: "당신이 바라보는 것이 현실이 됩니다. 누구에게나 동일하게 펼쳐지는 세상임을 인식하며 당신의 초점이 어디, 어느 지점에 가 있는지 성찰해 보아야 합니다.",
        },
      ],
      reformation_desc: [
        '<span class="green">인지 왜곡 점검을 통해 당신 안에 있는 부정적인 신념을 구분하고 분류해야 합니다.</span> ‘낙인찍기’의 부정적 패턴은 사실 본인 스스로에 대한 인식의 투사일 가능성이 높습니다. 부정적인 사고를 깨지 않고서는 새로운 것이 들어갈 자리를 마련할 수 없습니다.',
        '<span class="green">‘낙인찍기’는 직관을 가장한 선입견일 수 있습니다. 세상에 대한 무분별한 부정적인 렌즈를 내려놓아야 합니다. </span>내가 가진, 부정적 인식의 회로를 인식하는 것에서부터 새로운 세상이 시작될 것입니다. 내가 할 수 있는 일과 없는 일을 잘 구별해야 합니다. 나의 부정적 인식에 실체가 있는지, 무엇이 현실이고 무엇이 추측이고 가정인지 분별할 수 있어야 합니다. 가치 있고, 의미 있는 일에 힘을 싣고 살아야 나의 남은 인생이 더욱 빛날 것입니다. 용기를 내십시오, 이제 진짜 내 삶을 시작해야 하는 시간이 왔습니다.',
      ],
      reconstruction:
        '우리는 스스로 포기하지 못하는 것들에 묶여 더 중요한 것을 놓치는 경우들이 많습니다. 우선, 나의 능력을 과장하려는 욕구를 포기해야 합니다. 나에 대한 과도한 기대를 내려놓는 것부터 시작해야 할 것이고, 이것은 나를 다른 사람과 비교하려고 하는 마음을 멈출 수 있도록 도와줄 것입니다. <span class="green">나 자신이 아닌, 다른 사람의 칭찬을 얻기 위해 자신의 모습을 저버리는 일을 이제는 그만두어야 합니다.</span> <br/><br/>나의 내면에는 내가 인정하지 않아 발현되지 않은 강력한 힘이 있습니다. 두려움 없이 내 모습 그대로를 누구에게나 보여 줄 수 있는 힘이 있고, 누구보다 강하고 독립적인 존재임을 믿어야 합니다. 뿐만 아니라, 나는 타인을 잘 돌보고, 배려하며 선한 마음을 가지고 있음을 믿어야 합니다. 다른 사람의 성취와 성공을 내 일처럼 기뻐할 줄 알며, 타인과의 비교나 경쟁이 의미 없음을 알고 있습니다. <span class="green">누구도 대신 채워줄 수 없는 ‘나에 대한 확신’을 나 스스로 바로 세워야 합니다.</span> <br/><br/>"나는 내 삶의 주인으로서, 나의 가치를 스스로 정의하고 타인의 시선이나 평가에 흔들리지 않을 힘을 가질 것입니다."',
    },
    80: {
      review:
        "당신의 인생은 지금 용기를 내야 하는 시점에 이르렀습니다. 변화는 당신에게 선택의 문제가 아니라 ‘용기’의 문제입니다. ",
      remember:
        '<span class="green">당신은 지금 변화해야 하는 시간에 이르렀습니다. 당신은 어떤 것이든 실행할 수 있는 힘을 가지고 있고, 동기부여도 충만합니다.</span> 모험과 성취, 결단과 인내, 변화와 도전을 받아들일 힘이 있습니다. 그러나 아직 많이 배워야 하는 시간이고 경험해야 하는 시간입니다. <br/><br/><span class="green">당신이 지금 생의 순간에 기억해야 할 것은 ‘중용’의 가치입니다. 주어진 삶에 만족하면서도 유연함과 융통성을 가져야 성공하고 성취하고 앞으로 나아갈 수 있습니다.</span> 주어진 것들에서 해방되어 자유를 체험하지만 스스로에게 온전히 만족하지 못하고 있습니다. 내 삶과 인생에 대해 내면으로부터 온전히 만족하지 못하면 삶에는 늘 ‘불안’이라는 구름이 끼어 듭니다. 그 불안은 때로는 나를 ‘우울’하게 하기도 하고 나를 ‘분노’하게 만들기도 합니다. 그래서 정확한 자기분석(Self Analysis)이 필요한 것입니다.',
      reinterpretation:
        '심리학자 융(C.G.Jung)이 집단 심리의 한 측면으로 설명하는 개념이 ‘페르소나(가면)’입니다. 페르소나는 사회가 그 사람에게 기대하는 역할로, 우리는 그 가면을 쓰고 배우처럼 연기합니다. 그것은 부정적인 것만이 아닌 게, 사회생활을 하는데 어쩔 수 없는 요소이기 때문입니다. 바깥세상은 멋진 페르소나, 가령 ‘친절한’, ‘유능한’, ‘명랑한’, ‘가정적인’ 가면을 가진 사람들을 특별히 우대하기 때문에 우리는 페르소나와 자신을 동일시하고 싶은 유혹에 빠지게 됩니다. 하지만 인생의 위기상황에 이르면 우리는 그 ‘가짜’에 한계를 느끼고 부담스럽게 생각합니다. 참된 자신의 모습, 진정한 ‘자기’를 만나고자 합니다. 인생의 어려운 순간 “나는 누구인가?”라는 핵심 질문을 비로소 던지게 됩니다. <br/><br/><span class="green">당신을 해석하는 중요한 지점은 ‘내가 다른 사람들에게 보이고자 했던 모습’을 성찰하는 데서부터 시작합니다.</span> 타인의 좋은 평판을 얻기 위해 나는 어떤 노력을 했었는지 기억해 내는 것이 출발점이 되면 좋겠습니다. 진짜 ‘나’에서 벗어나 있는 가짜 ‘나’를 찾아내는 작업이 잘 된다면 당신은 성공적인 자기 변화를 이룰 수 있게 될 것입니다.',
      refresh:
        '<strong>Self Imagination Training 자기 이미지 훈련이 필요합니다.<br/><br/></strong> 새로운 무엇인가를 창조하기 위해서는 <span class="green">일상적으로 생각하고 느끼는 방식을 변화시켜야 합니다.</span> 매일 동일한 방식으로 생각하고 느낀다면 계속해서 동일한 상황을 만들어 낼 수밖에 없을 것입니다. 이미 ‘자신’이 되어버린 삶에서 벗어나기 위해서는 삶의 환경보다 더 크게 생각하고, 몸에 기억된 느낌보다 더 커져야 하며 새로운 시간선(영원)을 만들어야 합니다. <span class="green">변화하고자 하는 이상적인 ‘나’를 이미지화해야 합니다.</span> <br/><br/>뇌가 과거의 완전한 기록이고 마음이 의식의 산물이라고 할 때, 어떤 의미에서 우리는 항상 과거 속에서 생각하고 있다고 할 수 있습니다. 기억에 상응하는 뇌의 어떤 부분이 반응함으로써 우리는 과거와 동일한 수준의 마음을 만들어 내고 있는 것입니다. 무의식적인 행동을 하면서 우리는 과거의 자아와 연결되어 있습니다. <span class="green">만족스럽지 못한 나의 모습을 변화시키고자 하지만 나는 오늘도 이전과 다르지 않은 똑같은 생각, 똑같은 행동, 똑같은 감정을 매일 경험하고 있습니다.</span> 과연 이렇게 과거의 에너지에 묶여 우리의 미래 인생을 변화시킬 수 있을까요? 변화된 나의 이상적인 모습을 그려내야 합니다. 그것이 시작입니다.',
      reformation: [
        {
          name: "이분법적 사고",
          content: "전부 아니면 전무, 빛과 어둠, 정의-불의로 나누어 생각한다. ",
          result:
            "“나는 모두에게 거부당한다.”<br/>“공무원들은 부정, 부패하다.”",
          tip: '세상에는 빛과 어둠만이 존재하지 않습니다. 선과 악, 천사와 악마, 시시비비만으로 세상을 이해할 수 없습니다. <span class="green">이분법적인 사고는 흑백논리를 만들고 나의 선택과 판단의 영역을 좁게 만들어 버립니다. </span>세상은 그렇게 두 개로 쪼개져 존재하지 않습니다.',
        },
        {
          name: "당위적 사고",
          content: "모든 것들을 ‘~해야만 한다’라는 관점에서 해석한다.",
          result:
            "“이겨야만 한다.”<br/>“정직해야 한다.”<br/>“한 우물을 파야 한다.”",
          tip: '반드시 해야만 하는 것은 없습니다. 그저 하는 것입니다. <span class="green">때로 우리는 이러한 당위에 묶여 스스로를 세상의 가장 좁은 감옥에 집어넣습니다. 그것은 바로 ‘나’라고 하는 감옥입니다.</span>',
        },
      ],
      reformation_desc: [
        '우리는 종종 세상을 흑백으로 나누거나, "반드시 그래야만 한다"는 생각에 갇히는 경우가 있습니다. 이분법적 사고와 당위적 사고는 상황을 단순화하거나 자기 자신과 타인에게 불필요한 부담을 줄 수 있습니다. 이를 극복하기 위해 다양한 관점을 받아들이고, 유연한 사고방식을 연습하는 것이 중요합니다.',
        "세상은 하나의 정답만 있는 단순한 곳이 아니라, 다양한 가능성과 선택지가 공존하는 풍부한 공간임을 기억해야 합니다. 자신과 타인에게 더 너그러운 시각을 가지며, 완벽함보다 성장과 균형을 추구하는 태도가 우리를 더 자유롭게 할 것입니다.",
      ],
      reconstruction:
        '우리는 스스로 포기하지 못하는 것들에 묶여 더 중요한 것을 놓치는 경우들이 많습니다. 우선, 나의 능력을 과장하려는 욕구를 포기해야 합니다. 나에 대한 과도한 기대를 내려놓는 것부터 시작해야 할 것이고, 이것은 나를 다른 사람과 비교하려고 하는 마음을 멈출 수 있도록 도와줄 것입니다. <span class="green">나 자신이 아닌, 다른 사람의 칭찬을 얻기 위해 자신의 모습을 저버리는 일을 이제는 그만두어야 합니다. 내 안에는 나약하고 부족한 모습이 있지만, 스스로 전체성과 완전성을 실현할 수 있는 ‘성장하는 인간’, 특별한 사람입니다.</span> <br/><br/>나의 내면에는 내가 인정하지 않아 발현되지 않은 강력한 힘이 있습니다. 두려움 없이 내 모습 그대로를 누구에게나 보여 줄 수 있는 힘이 있고, 누구보다 강하고 독립적인 존재임을 믿어야 합니다. 뿐만 아니라, 나는 타인을 잘 돌보고, 배려하며 선한 마음을 가지고 있음을 믿어야 합니다. 다른 사람의 성취와 성공을 내 일처럼 기뻐할 줄 알며, 타인과의 비교나 경쟁이 의미 없음을 알고 있습니다. <span class="green">나와 타인의 성장을 위해 나 자신을 확장시킬 의지가 내 안에 있습니다. 누구도 대신 채워줄 수 없는 ‘나에 대한 확신’을 나 스스로 바로 세워야 합니다.',
    },
    90: {
      review:
        "당신은 바른 견해를 가지고 있는 사람입니다. 열린 마음을 가지고 있습니다.당신 안에는 ‘나와 타인의 성장을 위해 나를 확장시키려는 의지’가 있습니다. 이제 이 의지를 구체적으로 실현해야 할 때입니다. ",
      remember:
        '<span class="green">당신은 지금 통합해야 하는 시간에 이르렀습니다. 당신은 어떤 것이든 실행할 수 있는 힘을 가지고 있고, 동기부여도 충만합니다.</span> 모험과 성취, 결단과 인내, 변화와 도전을 받아들일 힘이 있습니다. 아직 많이 배워야 하는 시간이고 경험해야 하는 시간입니다. <br/><br/><span class="green">당신이 지금 생의 순간에 기억해야 할 것은 ‘중용’의 가치입니다. 주어진 삶에 만족하면서도 유연함과 융통성을 가져야 성공하고 성취하고 앞으로 나아갈 수 있습니다.</span> 주어진 것들에서 해방되어 자유를 체험하지만 스스로에 대한 확신이 흔들릴 때가 있습니다. 내 삶과 인생에 대해 내면으로부터 온전히 만족하지 못하면 삶에는 늘 ‘불안’이라는 구름이 끼어 듭니다. 그 불안은 때로는 나를 ‘우울’하게 하기도 하고 나를 ‘분노’하게 만들기도 합니다. 그래서 정확한 자기분석(Self Analysis)이 필요한 것입니다.',
      reinterpretation:
        '<span class="green">당신은 신뢰할만한 사람이며, 자유로운 영혼입니다. 항상 삶에 만족하고, 융통성이 있으며 유연함을 가지고 살아갑니다. 특히, 결과보다는 과정을 소중하게 생각하고, 타인을 긍정하고 자신을 인정합니다.</span> 그러한 자신감과 여유가 당신을 더 안전한 자리에 머물게 하고, 시시비비와 판단을 넘어선 자리에서 당신은 공동체에 선한 영향력을 줄 수 있는 사람입니다. <br/><br/>당신은 Meta-Q점수로 ‘중도’ 역량의 수준에 있을 가능성이 높습니다. 이 단계에서는 일을 적절히 하는데 그치지만, 그 다음 단계 자발적이고 창의적인 수준에서는 다가오는 일들을 보다 다른 차원으로 확장시키고 변화시킬 능력을 가집니다. 이 단계에서는 변화가 빠르게 일어나며 주제들에 대한 이해의 수준이 깊어집니다. 이들은 <span class="green">자연스럽게 다른 이들에게 도움이 되고, ‘공동선’에 공헌합니다.</span> 또한, <span class="green">내적 문제를 직면하며 배우는데 큰 장애물이 없습니다.</span> 당신에게는 이 단계로 나아갈 내면의 힘이 있음을 믿고 실현해야 합니다. ',
      refresh:
        '<strong>Retreat_피정 </strong><br/><br/>피정(避靜)은 피세정념(避世靜念)의 줄임말입니다. 세상을 피해 마음을 모아들인다는 말입니다. ‘Retreat’는 전쟁 중에 후퇴한다는 의미도 있습니다. 세상은 온통 전쟁터 같습니다. 날마다 벌어지는 많은 일들이 우리 마음을 혼란하게 합니다. <span class="green">집을 떠나 작고 고요한 자리에 머물러 그동안 살아온 인생을 돌아보고 성찰하며 새로운 삶을 다짐하고 자신을 변화시킬 수 있는 시간이 필요합니다.</span> <br/><br/><strong>Walking Meditation_걷기명상</strong><br/><br/> 인생은 피정의 연속입니다. 걷기 명상은 걷기의 신체적 효과에, 정신적 건강을 위해 ‘명상의 마음챙김’을 합친 개념입니다. 걸으면서 자신을 돌아봅니다. 몸을 관찰하면서 주변 세상과 ‘혼연일체’가 되어야 합니다. 소리를 듣고, 냄새를 맡고, 사물을 바로 보아야 합니다. 발이 땅에 어떻게 닿는지도 유심히 관찰하고 생각해 봅니다. 이런 감각에 완전히 몰입하려고 애써야 합니다. 다른 생각을 과감히 떨쳐내야 합니다. <span class="green">서서히 삶을 새롭게 자각할 수 있는 단순한 행동을 취하며 복잡한 마음을 정돈합니다.</span> 걷기 명상을 통해 우리는 자기 자신에게 더 친절해지고, 자신의 정체성과 행위에 대한 섣부른 판단을 줄이고 집중력을 높일 수 있었다는 고백을 듣게 됩니다.',
      reformation: [
        {
          name: "개인화",
          content: "부정적인 사건에 대해 자신을 탓하거나 비난한다. ",
          result: "“나 때문에 죽었다.”<br/>“나 때문에 일이 잘못됐다.” ",
          tip: '<span class="green">이미 지나간 과거의 사건 가운데 아직도 내 마음을 묶어두어 자유를 방해하는 일들이 있습니다. </span>그 일은 나 때문에 일어난 것이 아니며, 내가 책임질 수도 없음을 알지만 마음으로부터 놓아주기가 힘듭니다.',
        },
        {
          name: "후회하는 태도",
          content: "과거에 ‘더 잘했어야 했는데’라고 생각한다.",
          result:
            "“그런 말을 해서는 안 되는 것이었는데.”<br/>“공부를 좀 더 열심히 했어야 했는데.”",
          tip: '지나간 일에 대한 후회나 원망도 이제는 모두 내려놓아야 합니다. 당신은 그때 나름의 최선을 다했던 것입니다. 당신이 다른 어떤 선택을 했었더라도 지금과 크게 달라지진 않았을 것입니다. <span class="green">그저 지나간 일은 우리에게 늘 교훈이었음을 명심해야 합니다.</span>',
        },
      ],
      reformation_desc: [
        '부정적인 사건이 일어날 때, 우리는 종종 모든 책임을 자신에게 돌리거나 "그때 그렇게 했더라면" 하며 후회에 사로잡히곤 합니다. 이러한 개인화와 후회는 문제 해결보다는 스스로를 더욱 위축시키고 감정적으로 소진되게 만듭니다. 이를 멈추기 위해, 사건의 원인을 객관적으로 분석하고, 과거를 바라보는 대신 현재와 미래를 위한 행동에 집중하는 태도가 필요합니다.',
        "피정이나, 걷기명상 등의 방법으로 내 안에 묶여있는 해결되지 않는 감정을 분명히 하고 떠나보내는 연습을 해야 합니다. 일상 중에 연습할 수도 있고, 특별히 시간을 내어 차분히 정돈하는 시간을 갖는 것도 중요합니다.",
      ],
      reconstruction:
        '<span class="green">‘창조적인 삶’을 산다는 것은 ‘아무도 아닌(nobody)’ 존재로 산다는 것입니다.</span> 우리는 자꾸 무엇인가, 누군가가 되려고 합니다. 어디인가 소속되려고 하고, 무언가에 기대어 자신의 존재를 증명하려고 합니다. 무엇인가 되려는 것이, 누구인가로 존재하려는 것이 나의 스트레스의 핵심입니다. 자신을 잊어버릴 정도로 어딘가에 몰입해 있었던 경우가 있습니까? 몰입의 순간에는 당신이 이미 알고 있는 세계에서 분리됩니다. 더이상 내가 소유한 물건들, 내가 아는 사람들, 해야 하는 일들, 특정한 장소에 살았던 이런저런 장소들과 관련된 누군가가 아닙니다. 무아(無我)의 상태가 되는 것입니다. <span class="green">우리가 더 이상 외부환경 속에 사람과 장소, 사물에 초점을 맞추지 않는다면 우리는 인생을 새롭게 재건축할 수 있을 것입니다. 당신의 변화를 응원하고 지지합니다.</span> <br/><br/>"과거는 바꿀 수 없지만, 현재의 선택은 나의 미래를 바꿀 수 있음을 기억해야 합니다. 스스로를 탓하기보다 배운 것을 바탕으로 더 나은 길을 찾아가는 것이 진정한 성장이자 회복의 시작입니다."',
    },
    91: {
      review:
        "감성에서 이성으로, 이성에서 영성으로. 당신 안에는 ‘나와 타인의 성장을 위해 나를 확장시키려는 의지’가 가득하고 바위도 움직일만큼의 힘이 있습니다. ",
      remember:
        '이성 그 자체로는 진실에 대한 지침을 제공하지 못합니다. 이성은 논리의 방법론이 두드러지는 기술 세계에서 대단히 효과적이지만 역설적으로 더 높은 의식 수준에 도달하는 데에는 중대한 장애물입니다. 이성은 특정한 것을 다루지만 사랑은 전체를 다룹니다. 사랑은 위치를 취하지 않고, 따라서 전체적이며 위치성의 분리를 넘어섭니다. 그러면 ‘서로 함께하는 것’이 가능한데 어떤 장벽도 없기 때문입니다. 사랑은 포괄적이고 계속해서 자아 감각을 확장합니다. <br/><br/>사랑이 점점 더 무조건적이게 되면서, 내면의 기쁨으로 경험되기 시작합니다. 기쁨이 모든 활동에 끊임없이 동반됩니다. 기쁨은 어떤 외부적 근원에서가 아니라 존재의 매 순간 내면에서 발생합니다. 91점 이상은 <span class="green">치유의 수준이며. 영적인 것에 기반하는 자조 그룹의 수준입니다.</span>',
      reinterpretation:
        '<span class="green">주관과 객관 사이의 구별이 사라지며 지각의 특정한 중심점이 없습니다.</span> 이 수준에 있는 개인들은 흔히 스스로 세상에서 물러납니다. 어떤 이들은 영적 스승이 되고 어떤 이들은 인류의 향상을 위해 익명으로 일합니다. 몇몇은 각자의 분야에서 위대한 천재가 되어 사회에 크게 이바지합니다. 보통 공식 종교는 초월 되고 <span class="green">모든 종교의 기원이 되는 순수 영성</span>으로 대체됩니다. <br/><br/>이 수준에서 보는 세상은 의의와 근원이 압도적인, 절묘하게 조율된 진화적 춤 속에서 계속해서 흐르고 진화하게 되었습니다. 이 놀라운 드러남은 <span class="green">마음에 무한한 침묵이 자리하며 개념화를 중단</span>시켰습니다. <span class="green">바위처럼 굳건한 힘의 현존 때문에 모든 것이 다른 모든 것과 연결됩니다.</span> 위대한 미술 작품과 음악, 건축물은 우리를 일시적으로 더 높은 의식 수준들로 데려갈 수 있으며 또한 영감을 주고 시대를 초월하는 것으로 널리 인정받습니다. ',
      refresh:
        '<strong>마인드 리허설(Mind Rehearsal) : 생각이 경험이 된다.<br/><br/></strong> 마음을 신경과학에서는 ‘활동 중인 뇌’라고 말합니다. 생각을 달리하는 것만으로도 뇌를 변화시킬 수 있습니다. 생각만으로 나의 행동과 태도, 믿음을 바꿀 수 있습니다. 머릿속에서 시연함으로써(Mind Rehearsal) 뇌의 회로를 변경하는 것입니다. 이제 생각을 바꾸면 뇌가 바뀝니다. 뇌를 변화시키기 위해서는 몇 가지 전제가 필요합니다. 첫째, <span class="green">새로운 지식을 학습해야</span> 합니다. 둘째, <span class="green">직접 코칭을 받는 것</span>이 중요합니다. 셋째, <span class="green">주의를 기울이며 의식해야</span> 합니다. 마지막으로, <span class="green">반복하면서 뇌의 시냅스를 활성화시켜야</span> 합니다. 계속되는 학습은 시냅스의 연결을 만들어 냅니다. <br/><br/>오랫동안 홀로 변하고자 했는데 그러지 못한 사람에게 도움을 주고자 한다면 이와 같은 과정을 안내할 수 있습니다. 익숙한 수련자의 지도에 따라 마음을 훈련시켜 새로운 경험이 뇌의 시냅스 연결을 더욱 강화시킬 수 있다면 뇌의 연결 회로는 변하고, 나는 변화할 수 있습니다. 결국 ‘뇌’를 바꾸는 노력을 해야 ‘나’를 변화시킬 수 있는 것입니다. <span class="green">‘뇌’가 변해야 ‘내’가 변합니다.</span>',
      reformation: [
        {
          name: "후회하는 태도",
          content: "과거에 ‘더 잘했어야 했는데’라고 생각한다.",
          result:
            "“그런 말을 해서는 안 되는 것이었는데.”<br/>“공부를 좀 더 열심히 했어야 했는데.”",
          tip: "이제 지나간 일들에 대해 후회나 원망을 할 필요가 없습니다. 당신은 충분히 지금 여기 잘살고 있기 때문입니다.",
        },
        {
          name: "스스로 비난하는 (자기비판 영역)",
          content:
            "‘바라는 나’, ‘이상적인 내 모습’을 그려놓고 이에 부합하지 않는 자신을 비난한다.",
          result: "“모두에게 존경받아야 하는데 사람들은 모두 나를 싫어한다.”",
          tip: "스스로 자신을 비난하며 왜곡된 인지를 하는 부분은 없는지 확인하고 구체적인 행동의 변화를 계획해 봅니다.",
        },
      ],
      reformation_desc: [
        "당신 안에 있는 강력한 의지와 잠재력을 더 키우기 위해, 스스로에게 끊임없이 질문하고 내면의 목소리를 경청해 보세요. 작은 일이라도 실천하며 자신의 성장 과정을 스스로 축하하고, 타인의 성장을 진심으로 응원하는 태도를 유지하세요. 그리고 어려움이 닥칠 때마다 이를 배움의 기회로 삼아, 당신만의 고유한 길을 계속 걸어가세요",
        "당신은 이미 성장과 변화를 위한 모든 가능성을 품고 있습니다. 아직 남아 있는 부적절한 행동 패턴이나 인지습관은 스스로 구체적인 계획을 세워 바로잡을 수 있습니다. 이제 그 가능성을 믿고, 한 걸음씩 나아가며 당신만의 빛을 세상에 드러내세요.",
      ],
      reconstruction:
        '우리는 ‘누군가’로 살아가려고 합니다. 사회적인 역할, 책임, 위치, 관계의 양상, 교류, 직업 등을 통해 누군가가 되어 존재합니다. 그런데 그런 모습은 진짜 내가 아닙니다. 그것은 생존을 위하여 선택한 나의 전략이고 모습입니다. 어떤 직업으로서 일하는 내가 진정한 ‘나’는 아닙니다. <br/><br/>누군가와의 관계로 만들어진, 어떤 조직과의 관계로 규정된 내가 ‘나’는 아닙니다. 우리는 무엇인가를 내어 맡긴다는 것을 어려워합니다. 다른 누구에게 나에 대한 통제권을 준다는 것인데 이러한 것을 허용하는 관념을 받아들이기 어려워하는 것입니다. 그러나 무한한 힘, 신성한 능력을 가진 무한한 지혜에 자신을 내맡긴다는 것은 조금 더 수월할 것입니다. <br/><br/>에고의 통제를 이제 내려놓아야 한다는 것입니다. <span class="green">눈에 보이지 않는 힘은 실재하며, 나의 에고가 잠잠해질 때 최고의 해결책이 흘러나올 수 있습니다. 내려놓으면서 가져야 할 감정은 진실, 성실, 겸손, 정직, 확신, 명료함, 열정, 신뢰입니다.</span>',
    },
  };

  // ref[34]
  const ref34ToGrowth = {
    1: [
      {
        name: "“나는 내려놓습니다.”",
        items: [
          "너무 엄격한 기준으로 나와 타인을 구속하는 것을",
          "나의 노력과 행동에 대해 꼭 공정하게 보상받아야 한다는 생각을",
          "자제심을 잃고 비이성적으로 행동할지도 모른다는 두려움을",
          "내 잘못에 대해 비난을 받을지 모른다는 두려움을",
          "나 자신의 행동을 합리화하려는 성격을",
          "내가 변화시킬 수 없는 것들에 대한 집착을",
          "내가 다른 사람을 판단할 수 있는 위치에 있다고 믿는 것을",
          "나 자신과 다른 사람을 완벽으로 몰고 가려는 행동을",
          "나 자신의 정신적, 육체적 고통을 외면하는 것을",
          "내 삶을 지나치게 질서 있고, 효율적으로 통제하려는 나의 욕구를",
          "잘못된 부분에 습관적으로 초점을 맞추는 태도를",
          "모든 것을 바로 잡는 일이 나에게 달렸다는 생각을",
        ],
      },
      {
        name: "“나는 받아들입니다.”",
        items: [
          "나의 감정에 더 귀기울일 것을",
          "긴장을 풀고 주어진 삶을 즐길 것을",
          "매사에 최선을 다한 것으로 충분하다는 것을",
          "타인에게 배울 점이 많다는 것에 감사함을",
          "타인에게 부드러움과 존경심으로 너그럽게 대할 것을",
          "나의 감정은 정당한 것이며, 나도 감정을 가질 권리가 있음을",
          "내 실수에 대해 나 자신을 비난하지 않을 것을",
          "나 자신과 타인에 대해 부드럽고 관대해질 것을",
          "평온한 마음으로 관용적이고 인내하는 모습을",
          "흑백논리에서 벗어나 진행형, 과정으로 생각해보는 연습을",
          "나의 한계와 약점 또한 나의 일부이며, 이를 통해 성장할 수 있음을",
          "지금의 나 자신도 충분히 가치 있고 소중한 존재임을",
        ],
      },
    ],
    2: [
      {
        name: "“나는 내려놓습니다.”",
        items: [
          "다른 사람을 향한 나의 분노와 원망을",
          "사랑하는 사람들을 독점하려는 마음을",
          "나를 사랑하게 만들려고 아첨하는 것을",
          "내가 타인을 위해 한 일로 그들의 관심을 불러일으키려는 생각을",
          "내 요구에 충분히 응해주지 못한 것에 대해 상대방이 죄책감을 느끼게 만들려는 마음을",
          "아무도 나를 원하지 않고 사랑하지 않을 것이라는 두려움을",
          "다른 사람도 내가 원하는 방식으로 내 도움에 보답해야 한다는 기대감을",
          "나의 모든 신체적인 병, 아픔 그리고 불평을",
          "내 자신이 희생되었다는 느낌, 혹사당했다는 느낌에 사로잡히는 것을",
          "다른 사람이 나를 필요로 하게 만들려는 욕심을",
          "나의 부정적인 감정을 인정하지 않으려는 습성을",
          "내가 다른 사람에게 끊임없이 필요하다고 느껴야만 내 존재가치가 증명된다는 생각을",
        ],
      },
      {
        name: "“나는 받아들입니다.”",
        items: [
          "나 자신을 사랑할 것을",
          "나 자신의 성장과 발전에 힘쓸 것을",
          "내 모든 감정은 나의 것임을 두려워하지 않고 받아들일 것을",
          "나는 내 행동 동기에 대해 분명한 의식을 갖고 있음을",
          "나의 행복이 타인을 기쁘게 하는데 있는 것이 아님을",
          "사랑하는 사람을 소유하려 하지 않을 것을",
          "아무 조건 없이 타인을 사랑할 것을",
          "다른 사람이 내게 베풀어 준 모든 것에 대해 감사함을",
          "내 행복과 가치는 내 내면에서 비롯된다는 것을",
          "다른 사람의 선택과 삶을 존중하고, 그들의 여정을 있는 그대로 지켜볼 것을",
          "도움을 줄 때 나 자신의 한계를 인정하고, 스스로를 돌보는 것도 중요하다는 것을",
        ],
      },
    ],
    3: [
      {
        name: "“나는 내려놓습니다.”",
        items: [
          "나는 부적합한 사람이며 타인에게 거절당할지도 모른다는 두려움을",
          "실패와 창피를 당하는 것에 대한 두려움을",
          "내 능력을 발휘하기 위해 자신의 감정을 숨기는 것을",
          "최고가 되기 위해 가혹하게 자신을 몰고 가는 성격을",
          "나 자신과 나의 능력을 과장하려는 욕구를",
          "나를 다른 사람과 비교하려고 하는 성격을",
          "지속적인 타인의 주목과 칭찬에 대한 갈망을",
          "불안정한 나 자신을 보상하기 위해 교만해지는 것을",
          "나의 실수와 부족함을 감춰야 한다는 생각을",
          "다른 사람을 훼방하는 것이 자신을 더 낫게 만들 수 있다는 믿음을",
          "다른 사람들과 그들의 행운을 시기하는 감정을",
          "내가 이루어낸 결과만이 나의 가치를 증명한다고 믿는 것을",
        ],
      },
      {
        name: "“나는 받아들입니다.”",
        items: [
          "성공과 관계없이 내 자신은 가치 있는 존재임을",
          "내 중심은 나이며 건강한 정서를 가졌음을",
          "두려움 없이 나의 참모습을 보여 줄 수 있음을",
          "나는 타인을 잘 돌보고, 선한 마음을 가지고 있음을",
          "다른 사람들이 내게 주는 사랑을 있는 그대로 받아들일 것임을",
          "다른 사람의 행복과 복지를 위해 기꺼이 일할 것을",
          "나를 존중해주는 사람들에 대해 책임이 있음을",
          "다른 사람의 성취와 성공을 내 일처럼 기뻐할 것을",
          "과정과 노력이 결과만큼이나 중요하다는 것을",
          "실패는 성장의 일부이며, 나를 더 단단하게 만들어준다는 것을",
          "나 자신을 있는 그대로 사랑하고 존중하는 것이 진정한 성공임을",
          "다른 사람의 시선보다 나의 내면의 평화와 만족이 더 중요하다는 것을",
        ],
      },
    ],
    4: [
      {
        name: "“나는 내려놓습니다.”",
        items: [
          "자기 파괴적인 모든 생각과 행동을",
          "내 멋대로인 나의 정서와 행동을",
          "내가 하찮은 존재이며 바람직하지 못한 존재라는 생각을",
          "자신에 대해 절망하고 실망하는 모든 감정을",
          "자신에 대한 모든 의심과 정서적 취약성을",
          "우울함, 피로감 그리고 내성적인 성격을",
          "나 자신과 타인에 대한 모든 비현실적인 기대를",
          "나는 타인과 다른 대우를 받아야 한다는 생각을",
          "다른 사람들로부터 도망쳐 나 자신을 보호하려는 생각을",
          "내 감정에 연연해하며 과거에 사로잡히는 일을",
          "나의 고통이 나를 특별하게 만든다는 생각을",
          "나의 부족함을 채우기 위해 끊임없이 외부에서 무언가를 찾아야 한다는 집착을",
        ],
      },
      {
        name: "“나는 받아들입니다.”",
        items: [
          "내 감정에 따라 내가 누구임이 결정되는 것이 아님을",
          "내가 표현하는 감정만이 내가 누구인지 보여준다는 사실을",
          "내 자신을 있는 그대로 열어 보일 것을",
          "내가 경험한 모든 것을 내 성장에 사용할 것을",
          "나의 삶, 나의 친구들 그리고 나 자신은 선하다는 것을",
          "나 자신을 사랑하고 부드럽게 대할 것을",
          "나의 삶이 더 고상한 삶으로 변하고 있음을",
          "내가 이 세상에 선함과 아름다움을 가져오고 있음을",
          "과거의 상처로부터 자유로울 수 있다는 것을",
          "나의 가치는 나의 독특함이 아니라 내면의 따뜻함과 진정성에서 나온다는 것을",
          "내 주변에 있는 평범한 일상 속에서도 기쁨과 만족을 발견할 수 있음을",
          "지금 이 순간, 나는 이미 충분히 아름답고 소중한 존재임을",
        ],
      },
    ],
    5: [
      {
        name: "“나는 내려놓습니다.”",
        items: [
          "나를 둘러싼 세상에 대한 모든 두려움을",
          "내 마음의 무력감과 절망감 그리고 불안과 동요를",
          "나의 신체적 건강과 외모를 무시하는 일을",
          "냉소적이며 다른 사람의 평범함을 경멸하는 것을",
          "세상에는 의지할 사람이 아무도 없다는 생각을",
          "사람들에게서 숨어버리고 싶고, 숨기고 싶은 마음을",
          "정신세계로 도피함으로써 현실을 회피하려는 마음을",
          "타인을 파괴하고 그들의 평화가 깨지기를 바라는 생각을",
          "타인이 나를 부당하게 이용할지도 모른다는 두려움을",
          "무엇을 하기 전에 항상 좀 더 알아야 한다는 생각을",
          "내가 모든 것을 혼자 해결해야 한다는 고립된 책임감을",
          "세상은 항상 나를 위협한다고 여기는 방어적인 사고를",
        ],
      },
      {
        name: "“나는 받아들입니다.”",
        items: [
          "불확실하고 모호한 것들도 수용할 것을",
          "나는 안전하며, 나의 삶은 현실에 기초하고 있음을",
          "나의 삶과 나의 노력은 의미 있고 가치 있는 일임을",
          "나의 창조성의 가치를 알며 유머감각을 가지고 있음을",
          "내 몸의 강인함과 신비함을",
          "나의 미래와 인류에 대한 믿음을",
          "대등한 입장에서 자신감을 가지고 다른 사람에게 다가갈 것을",
          "타인에게 자비로운 마음을 가질 때 마음의 평정을 찾을 수 있음을",
          "진심을 다해 타인을 지지하고 후원할 것을",
          "다른 사람의 도움과 연결이 내 삶을 풍요롭게 한다는 것을",
          "지식을 쌓는 것만큼 실제로 경험하며 행동하는 것이 중요하다는 것을",
          "내가 가진 재능과 통찰을 세상과 나누는 것이 큰 기쁨을 준다는 것을",
        ],
      },
    ],
    6: [
      {
        name: "“나는 내려놓습니다.”",
        items: [
          "타인에게 버림 받게 될지도 모른다는 두려움을",
          "실패를 자초하며 자신을 벌하는 나의 성격을",
          "나 자신에 대한 열등감과 미래에 대한 심한 공포를",
          "나의 문제에 과잉반응하고 지나치게 과장하는 행동을",
          "나 자신의 불안정을 감추기 위해 '거친 행동'을 하는 것을",
          "나의 두려움과 불안을 다른 사람에게 투사하는 습관을",
          "처벌 당하고 함정에 빠질 것 같은 느낌에 사로잡혀 자포자기하는 심정을",
          "나와는 다른 성격의 사람을 두려워하고 싫어하는 것을",
          "내 자신의 문제와 실수에 대해 타인을 비난하는 성격을",
          "나 자신의 안전을 위해 다른 사람을 필요로 하는 성격을",
          "내가 믿는 체계를 무비판적으로 고수하려는 고집을",
          "모든 상황에서 통제와 확실성을 추구하려는 집착을",
        ],
      },
      {
        name: "“나는 받아들입니다.”",
        items: [
          "나는 독립적이며 능력 있는 존재임을",
          "그룹이나 관계 속에서 나의 정체성을 가질 수 있음을",
          "나 자신과 재능, 나의 미래에 대한 믿음이 있음을",
          "확신과 마음의 평정을 갖고 고난에 대응할 수 있음을",
          "안심하며 무슨 일이든 최선을 다할 수 있음을",
          "어떤 상황에서도 용기 있게 행동할 수 있음을",
          "내 안에 진정한 권위가 있다는 것을 알게 되었음을",
          "모든 사람들과 친밀한 관계를 가질 수 있음을",
          "나를 필요로 하는 모든 사람에게 넉넉하며 관대하게 대할 수 있음을",
          "불확실한 상황에서도 신뢰를 바탕으로 나아갈 수 있음을",
          "타인의 지지와 협력으로 내가 더욱 강해질 수 있음을",
          "두려움을 인정하면서도 용기 있게 행동할 수 있는 내 잠재력을",
        ],
      },
    ],
    7: [
      {
        name: "“나는 내려놓습니다.”",
        items: [
          "내가 충분하지 않을 것이라는 두려움을",
          "무모하고 파괴적인 충동들, 그리고 모든 강박관념과 탐닉들을",
          "일시적인 만족을 위해 나의 건강과 행복을 희생하는 일을",
          "자기수양 부족으로 내게 온 기회를 망쳐버리는 일을",
          "내 행동의 결과로부터 도망치려는 것을",
          "끊임없이 주의 산만한 행동으로 자기 자신을 회피하려는 것을",
          "나 자신이 할 수 있는 것 이상으로 자신을 부풀리는 성격을",
          "외적인 것들이 나를 행복하게 해줄 수 있다는 믿음을",
          "나의 불안함 때문에 자신을 위험한 상황이나 행동으로 몰아가는 것을",
          "나의 좌절감을 토로하기 위해 타인을 창피주거나 악용하는 일을",
          "항상 더 많은 선택과 경험을 추구하려는 끝없는 욕심을",
          "고통과 어려움을 회피하려는 충동을",
        ],
      },
      {
        name: "“나는 받아들입니다.”",
        items: [
          "조용히 내면에 집중할 때 가장 행복하다는 것을",
          "박탈감을 느끼지 않고 자신에게 '아니오'라고 말할 수 있음을",
          "내가 필요한 것을 충분히 가지고 있다는 사실을",
          "나는 어려움 속에서 더 강하다는 사실을",
          "내 일상적인 삶에 만족할 수 있음을",
          "내가 맡은 기획들을 끝까지 완성시킬 수 있음을",
          "나의 삶에 영적 차원이 있음을",
          "살아 있다는 것에 진심으로 감사함을",
          "진심으로 사람들을 돌보며 그들의 안녕과 행복을 위해 일할 것을",
          "지금 이 순간에도 충분한 기쁨과 행복이 존재한다는 것을",
          "어려움과 고통 속에서도 배울 수 있는 가치가 있음을",
          "진정한 자유는 외적인 자극이 아니라 내면의 평화에서 온다는 것을",
        ],
      },
    ],
    8: [
      {
        name: "“나는 내려놓습니다.”",
        items: [
          "패배하는 것에 대한 두려움을",
          "결코 모든 일에 두려워하지 말아야 한다는 생각을",
          "내 자신이 연약해지거나 나약해지는 것에 대한 두려움을",
          "타인이 나를 지배하게 될 것이라는 두려움을",
          "내가 내 삶의 모든 일들을 통제해야만 한다는 생각을",
          "타인을 위협해서라도 그들이 나의 길을 따라와야 한다는 믿음을",
          "내 삶에서 다른 사람은 필요 없다는 믿음을",
          "건강과 대인관계를 파괴시키더라도 나의 자존심을 지키려는 것을",
          "나에게 동의하지 않은 사람은 나를 적대하는 사람이라고 생각하는 것을",
          "나의 강인함을 증명하기 위해 필요 이상으로 자신을 몰아붙이는 것을",
          "취약한 모습을 보이는 것이 곧 약점이라는 믿음을",
          "모든 것을 내 방식대로 이루어야 한다는 고집을",
        ],
      },
      {
        name: "“나는 받아들입니다.”",
        items: [
          "나는 훌륭하며, 존중받을 자격이 있는 존재임을",
          "내 안에 부드러운 감정과 선한 충동이 존재함을",
          "나는 타인에게 두려운 사람이 아닌 온화한 사람이 될 수 있음을",
          "나는 내 자신을 잘 다스리고 내 욕망을 길들일 수 있음을",
          "타인을 믿으며 그들의 행복과 평안을 진심으로 바랄 것을",
          "넓은 마음으로 내가 얻은 영광을 다른 이들과 나눌 것을",
          "나보다 더 훌륭한 권위자가 존재함을",
          "타인을 옹호함으로써 내게 만족함을",
          "나는 타인을 사랑하며 그 사랑을 돌려달라고 말할 수 있음을",
          "진정한 강인함은 타인을 배려하고 사랑할 때 더욱 빛난다는 것을",
          "도움을 요청하는 것이 약함이 아니라 인간적인 용기임을",
          "내가 속한 공동체와의 협력과 신뢰가 나를 더 풍요롭게 한다는 것을",
        ],
      },
    ],
    9: [
      {
        name: "“나는 내려놓습니다.”",
        items: [
          "나의 모든 태만함과 건망증 그리고 일을 쉽게 포기하는 성격을",
          "나 자신의 삶에 대해 적극적으로 관심 갖지 않으려는 생각을",
          "유쾌하지 않거나 어려운 일들은 무엇이든 피하려는 생각을",
          "문제가 압도적으로 커질 때까지 그것을 무시하려는 습관을",
          "나 자신을 바라보는 것에 대한 두려움을",
          "감각을 잃고 정서적으로 무감각한 상태에 빠지려는 성격을",
          "몸에 밴 습관이나 정해진 일상 속에서 자신을 잃어버리는 것을",
          "나의 공격성을 직시하지 않으려는 것을",
          "평화를 유지하기 위해 어떤 사람과도 잘 지내려는 습관을",
          "자신의 발전을 위해 노력하기보다 타인의 도움으로 살려는 생각을",
          "갈등을 두려워하며 내 의견을 숨기려는 태도를",
          "나의 삶에서 중요한 결정을 미루거나 회피하려는 습관을",
        ],
      },
      {
        name: "“나는 받아들입니다.”",
        items: [
          "나는 이제 확신이 있으며 강하고 독립적인 존재임을",
          "나의 생각을 발전시키고, 이를 통해 사고할 수 있음을",
          "내 자신과 나의 능력에 대해 자랑스럽게 여길 것을",
          "나는 어려울 때도 충실하며 믿음직한 사람임을",
          "깨어나 기민한 눈으로 주변 세계를 볼 것을",
          "두려움 없이 나 자신을 깊이 성찰할 수 있음을",
          "나의 미래에 대해 흥미와 열정을 가지고 있음을",
          "삶이 내게 주는 모든 것을 적극적으로 수용할 것을",
          "내 안에 강력한 치유의 힘이 있음을",
          "나의 목소리와 의견은 가치 있으며 이를 표현할 권리가 있음을",
          "진정한 평화는 나의 욕구와 목표를 존중하면서 이룰 수 있음을",
          "나는 나의 삶을 주체적으로 선택하고 변화시킬 수 있음을",
        ],
      },
    ],
  };

  const PersonalityType = () => {
    const combine = (a, b, leftValue, rightValue) =>
      leftValue === rightValue ? [a, b] : [leftValue > rightValue ? a : b];

    const WD = combine("W", "D", resultPageData.W, resultPageData.D);
    const PI = combine("P", "I", resultPageData.P, resultPageData.I);
    const LM = combine("L", "M", resultPageData.L, resultPageData.M);
    const SC = combine("S", "C", resultPageData.S, resultPageData.C);

    const result = [];
    WD.forEach((wd) =>
      PI.forEach((pi) =>
        LM.forEach((lm) => SC.forEach((sc) => result.push(wd + pi + lm + sc)))
      )
    );

    return result;
  };

  const PersonalityTypeValue = PersonalityType();

  const PersonalityTypeText = {
    DPLC: "조용하고 말이 없으며, 논리적·분석적·객관적으로 인생을 관찰하는 유형입니다. 사실적인 정보를 조직하기 좋아합니다. 일과 관계 되지 않는 이상 자신을 개방하지 않으며, 가까운 친구들 외에는 대체로 사람들을 사귀지 않습니다. 일상생활에 있어 매우 적응력이 강하며, 과학분야·기계계통·엔지니어링 분야에 관심이 많습니다. 만약 이 유형이 기계나 기술 분야에 흥미가 없다면, 그 대신 비조직화된 사실들을 조직화하는 재능이 많으므로 법률·경제·마케팅·판매·통계 분야에 관심이 많을 수 있습니다.",
    DPMC: "말보다는 행동으로 따뜻함을 나타내며, 동정적입니다. 그러나 상대방을 잘 알게 될 때까지 이 따뜻함을 잘 드러내지 않습니다. 내적인 이상향과 개인적인 가치에 준하여 사람이나 일을 대하며, 말로써 잘 표현하지 않습니다. 자신의 주관이나 가치를 타인에게 요구하지 않으며 매우 겸손합니다. 적응력이 높고 관용적이며, 지금 여기를 즐깁니다. 자연에 대한 사랑과 미적 감각, 균형(비례) 감각이 뛰어납니다. 일의 목표 달성에 연연하지 않으며 여유로운 태도를 보입니다.",
    WPLC: "관대하고 느긋합니다. 사람이나 사건에 대해서 선입견을 갖지 않으며 개방적입니다. 자신과 타인에게 관용적이며, 일을 있는 그대로 보고 받아들이므로 갈등이나 긴장이 일어나는 상황을 잘 무마하는 능력이 있습니다. ‘꼭 이렇게 되어야 한다’는 규범을 적용하기 보다 누구나 만족할 수 있는 해결책을 모색하고 타협하며 적응하는 힘이 있습니다. 현재 상황에 주의집중을 하고, 현실을 있는 그대로 보는 자연스러운 경향은 현실적 문제 해결에 뛰어난 능력으로 발휘되기도 합니다.",
    WPMC: "친절하고 수용적이며 어떤 상황에도 잘 적응하고 타협적입니다. 선입견이 별로 없으며 개방적이고 관용적입니다. 다른 사람의 일이나 활동에 관심이 많으며, 새로운 사건 혹은 물건에도 관심과 호기심이 많습니다. 이론이나 책을 통해 배우기보다 실생활을 통해 배우는 것을 선호합니다. 추상적 관념이나 이론보다는 구체적 사실들을 잘 기억하는 편입니다. 논리적 분석보다는 인간중심의 가치에 따라 결정을 내립니다. 그러므로 동정적이고 사람들에게 관심이 많으며, 때로 재치가 있고 꾀와 끼가 많습니다. 특히 사람들을 접하는 일에 능숙합니다. 사람과 사물을 다루는 상식이 풍부합니다. ",
    DIMC: "조화로운 관계를 중요시해서 분쟁을 회피하고 화를 잘 참는 편입니다. 칭찬과 인정에 약하며, 정에 얽매여서 거절을 잘 하지 못합니다. 내면세계에 관심이 많고, 말하기 보다는 명상을 하며 자신의 세계에 머무릅니다. 내면에 갈등이 많고 복잡하기도 합니다. 평소 계획을 잘 세우지 않는 편이며, 세우더라도 계획대로 잘 진행하지 못합니다. 창의적이고 개성이 있으며 예술 방면에 관심이 많은 편입니다. 하고 싶은 일에는 열정적이며 반복되는 일에 쉽게 지루함을 느낍니다. 현실감각이 없는 편이며, 공상가나 이상주의자라는 말을 듣습니다. 낭만적인 사람으로서 자기만의 이상향이 있지만 모든 사람들에게 이상향을 적용하기는 어렵습니다.",
    DIMS: "창의력과 통찰력이 뛰어납니다. 강한 직관력으로 의미와 진실된 관계를 추구합니다. 뛰어난 영감으로 말없이 타인에게 영향력을 행사합니다. 독창성과 독립심이 강하며, 확고한 신념과 뚜렷한 원리원칙을 가지고 있습니다. 공동의 이익을 가져오는 일에 심혈을 기울이고 동료애를 중시하는 경향이 있으며 사람들로부터 존경을 받습니다. 열정으로 자신들의 신념을 구현시켜 나가는 정신적 지도자들이 이 유형에 속합니다. 남에게 강요하기보다 행동과 권유로 사람들의 마음을 움직여 따르게 만드는 지도력이 있습니다.",
    WIMC: "열성적이고 창의적입니다. 풍부한 상상력과 영감을 가지고 새로운 프로젝트를 잘 시작합니다. 충동적 에너지가 넘쳐 즉흥적으로 일을 빠르게 해결하는 능력이 있습니다. 관심이 있는 일이면 무엇이든 척척 해내는 열성파입니다. 뛰어난 통찰력으로 그 사람 안에 있는 성장 가능성을 봅니다. 자신이 가진 열성으로 다른 사람들도 프로젝트에 흥미를 가지고 참여하게 하며, 다른 사람을 잘 도와줍니다.",
    WIMS: "동정심과 동료애가 많습니다. 친절하고 재치 있으며 화합을 아주 중요하게 여깁니다. 민첩하고 참을성이 많고 성실합니다. 다른 사람들의 의견을 존중하고 그 가치를 봅니다. 공동선을 위해 대체로 상대방 의견에 동의하고, 새로운 아이디어에 대한 호기심이 많습니다. 글로 쓰기보다는 말로써 생각을 잘 표현합니다. 편안하고 능수능란하게 계획을 제시하거나 조직을 이끌어 가는 능력이 있습니다.",
    WPLS: "일을 조직하고 프로젝트를 계획하고 실행시키는 능력이 있습니다. 현실적이고 사실적이며 체계적·논리적으로 사업이나 조직체를 이끌어가는 타고난 재능을 가졌습니다. 혼란스러운 상태나 불분명한 상태 또는 실용성이 없는 분야에는 크게 흥미를 느끼지 못하지만, 필요에 따라 응용하는 힘이 있습니다. 분명한 규칙을 중요시하며, 그에 따라 행동하면서 일을 추진하고 완성합니다. 어떤 계획이나 결정을 내릴 때 확고한 사실에 바탕을 두고 이행합니다. 대체로 결과를 바로 볼 수 있는 일을 즐기는 편입니다. 기업체·조직체를 이끌며, 행정관리 및 생산건축 분야의 일을 잘 합니다.",
    WPMS: "친절하고 재치가 있습니다. 동정심과 동료애가 많습니다. 다른 사람들에게 관심을 쏟고 화합을 도모하는 일을 중요하게 여깁니다. 양심적이고 정리정돈을 잘하며 참을성이 많습니다. 다른 사람을 잘 돕고, 다른 사람들의 지지를 받으면 일에 더욱 열중하는 편입니다. 그러나 다른 사람들의 무관심한 태도에 민감합니다.",
    DPLS: "실제 사실에 대하여 정확하고 체계적으로 기억하며, 일 처리에 있어서도 신중하며 책임감이 강합니다. 집중력이 높으며 강한 현실감각으로 일을 조직적으로 처리한다. 위기 상황에서도 쉽게 흥분하지 않으며 일관성 있고 보수적인 입장을 취합니다. 개인의 감정을 잘 드러내지 않으나 심적으로는 개인적 취향이 확고합니다. 현재의 문제를 해결할 때 과거의 경험을 잘 적용하며, 반복되는 일상적인 일에 대한 인내력이 강합니다. 때로 일의 세부사항에 집착하는 경향이 있으나 업무수행이나 문제해결에 매우 확고하고 분별력 있는 태도를 보입니다.",
    DPMS: "책임감이 강하고 온정적이며 헌신적입니다. 치밀성과 반복을 요하는 일을 끝까지 해나가는 인내력이 있습니다. 이들이 가진 침착성과 인내력은 가정이나 집단에 안정성을 가져다 줍니다. 다른 사람의 사정을 고려하며, 자신과 타인의 감정 흐름에 민감한 편입니다. 일 처리에 있어 현실감각을 가지고 실제적이고 조직적으로 이행합니다. 경험을 통해서 자신이 틀렸다고 인정하기까지 어떠한 난관이 있어도 꾸준히 밀고 나갑니다.",
    WILC: "재빠르고 영리하며, 활기차고 기민합니다. 새롭고 도전적인 문제를 해결하는데 있어 수완이 좋고, 거리낌이 없습니다. 개념적 가능성을 창출하고 그 후에 전략적으로 이를 분석합니다. 타인을 파악하는데 능숙합니다. 일상적인 일을 지루해하며, 똑같은 일을 동일한 방식으로 처리하는 경우가 드물고 흥미가 계속해서 바뀌는 경향이 있습니다. 어떤 의견이나 사람에 반대하는 일을 두려워하지 않으며, 논란이 될 만한 주제에 대해 격렬하게 논쟁하는 일을 즐깁니다. 그렇다고 반론을 제기하는 데만 관심이 있거나 악의를 지닌 것은 아닙니다. 지식이 풍부하고 호기심이 넘치며 활기찬 유머 감각으로 다른 사람을 즐겁게 할 수 있는 성격이지만, 논쟁에서 즐거움을 찾는 성향이 있을 뿐입니다.",
    WILS: "활동적입니다. 행정적인 일과 장기계획을 선호하며 논리적이고 분석적입니다. 사전 준비를 철저히 하며, 계획하고 조직하고 체계적으로 목표달성을 추진하는 지도자들이 많습니다. 비능률적이거나 불확실한 상황에서는 인내심이 없습니다. 솔직하고 결정력과 통솔력이 있으며 거시적 안목으로 일을 밀고 나갑니다. 관념 자체에 집중하는 경향이 있으며 관념 이면의 사람에는 관심이 별로 없습니다.",
    DILC: "조용하고 과묵하나 관심이 있는 분야에 대해서는 말을 잘합니다. 매우 분석적·논리적·객관적이며 비평을 잘합니다. 일의 원리와 아이디어에 관심이 많으며, 실체보다는 실체가 안고 있는 가능성에 관심이 많습니다. 이해가 빠르고 높은 직관력으로 상황을 깊이 있게 보는 능력이 있습니다. 개인적인 인간관계나 파티 혹은 잡담에는 흥미가 별로 없습니다. 아이디어를 깊이 있게 나눌 수 있는 사람들과의 관계를 좋아하며, 때로 어떤 아이디어에 몰입하여 주위에서 돌아가고 있는 일에 둔할 때가 있습니다.",
    DILS: "행동과 사고가 독창적입니다. 내적인 신념과 비전은 산이라도 움직일 만큼 강합니다. 16가지 유형 중에서 가장 독립적이고 단호하며, 때로는 고집이 강합니다. 자신이 가진 영감과 목적을 실현시키려는 의지와 결단력과 인내심을 가지고 있습니다. 자신과 타인의 능력을 중요시하며, 목적달성을 위하여 온 시간과 노력을 바쳐 일합니다. 직관력과 통찰력이 활용되는 분야에서 능력을 발휘합니다. 과학, 엔지니어링, 발명, 정치, 철학 분야 등에 강합니다. ",
  };

  // 힘의 균형과 날개
  const BalanceOfPowerWingsData = {
    1: {
      title: "완벽을 추구하는 올곧은 사람",
      contents: [
        {
          desc: "자신만의 분명한 기준이 있어서 타인에게 비판을 받거나 상대방의 일 처리가 엉성할 때 갈등이 생기기 쉽습니다. 침묵을 지키거나 방어적인 태도를 취하고 경직된 자세나 강한 시선을 보내는 방식 등으로 분노를 드러냅니다. 스스로 책임감이 강하여 누구든 자기가 맡은 일은 책임지고 완수할 것을 기대합니다. 이 과정에서 감시하거나 통제하는 태도를 보이게 되면서 갈등이 발생합니다. 실수나 잘못에 대한 책임관계 기준이 명확하여 자신의 책임이 아닐 때는 선을 긋습니다. 때로는 자신의 일에만 몰두하여 다른 사람의 일에는 신경을 쓰지 않으며 도와주는 것 자체가 바람직하지 않다고 생각하는 경향이 있습니다. 이로 인해 이기주의, 냉정한 사람이라는 평가를 받고 대인관계 안에서 갈등이 발생할 수 있습니다.",
          descType: "p",
        },
        {
          desc: [
            "누군가의 이름이나 기념일, 작은 변화 등 사소한 일을 중요하게 여깁니다. ",
            "점잖은 농담이나 유머를 사용합니다.",
            "반대보다는 ‘~하면 어떨까?’라는 완곡한 표현을 사용합니다. ",
            "내가 틀렸을 수도 있다는 생각으로 타인의 이야기를 듣고 입장을 헤아려 봅니다. ",
            "상대방이 불평할 때 부드럽게 대하고 잘 들어줍니다.",
          ],
          descType: "list",
        },
        {
          desc: [
            "비판하거나 못마땅해하지 말고, 모범을 보임으로써 본받게 합니다.",
            "감시나 통제가 아니라, 상황을 체크하며 안내자로서 역할합니다.",
            "스스로 약속한 것을 지키고, 자신의 기준을 낮추어 봅니다. ",
            "실수한 사람을 관대하게 대하고 용기를 줍니다. ",
            "단순하게 생각하고 느긋하게 현재를 즐겨봅니다. ",
          ],
          descType: "list",
        },
      ],
    },
    2: {
      title: "친절한 조력가",
      contents: [
        {
          desc: "사랑하고 사랑받는 일에서 원동력을 얻는 이 유형의 사람들은 호의를 무시당했다고 느껴질 때 갈등이 생깁니다. 감정을 오랫동안 억누르다가 한순간 폭발적으로 표현합니다. 내가 베푼 친절과 배려에 상응하는 반응을 기대하기 때문에 상대방이 자신을 존중하지 않거나 무관심할 때 자존심이 상하게 됩니다. 그러나 상대방이 자신의 잘못을 인정하고 사과하거나 다시 특별한 관심을 보이면 쉽게 잊고 용서합니다. 개인적인 일로 인해 생긴 감정을 조직이나 공적인 일에까지 영향을 주는 경향이 있습니다. ",
          descType: "p",
        },
        {
          desc: [
            "충고나 평가를 할 때는 긍정적인 면을 먼저 말한 후에 합니다. ",
            "투덜대는 투나, 간접적으로 불만을 표시하지 말고 직접적으로 원하는 바를 말합니다. ",
            "나서서 도움을 주기보다는 스스로 창조적인 활동을 할 수 있도록 협조합니다. ",
            "물질적인 것이든 정서적인 것이든 균형감 있게 주고 받도록 합니다. ",
            "타인을 위한 행동에 나서기 전에 상대가 정확히 원하는 바가 무엇인지 확인합니다. ",
          ],
          descType: "list",
        },
        {
          desc: [
            "자신의 감정과 숨겨진 욕망 등을 의식적으로 인식해봅니다.",
            "관계와 일을 구분하여, 관계 때문에 업무에 객관성이 떨어지지 않도록 합니다. ",
            "다른 사람들의 칭찬을 있는 그대로 받아들입니다. ",
            "나의 욕구를 충족하기 위해 은근히 다른 사람을 조종하지 말고 내 욕구를 정직하게 다룹니다. ",
            "부담스러운 부탁이나, 하고싶지 않은 일에는 명확하게 ‘아니오’라고 말합니다.",
          ],
          descType: "list",
        },
      ],
    },
    3: {
      title: "성공을 향해가는 열정적 야심가",
      contents: [
        {
          desc: "목표를 달성하는 것이 매우 중요한 가치이기 때문에 이를 방해하거나 자신이 유능한 사람으로 보이지 않을 때 갈등이 발생합니다. 목표달성을 위해 다른 사람보다 더 빠르게, 더 많이 일하기 때문에 이 과정에서 다른 사람에게 부담을 주거나 시기심을 유발할 수 있습니다. 불편한 감정을 숨기려고 하지만 목소리나 말투, 어조에서 분명히 느껴집니다. 논리적인 방식으로 문제를 해결하고 명확한 말투를 씁니다. 자신이 잘 모르는 주제나 관심이 없는 주제 또는 자신에게 도움이 되지 않는 주제는 피하려는 경향이 있습니다. ",
          descType: "p",
        },
        {
          desc: [
            "바쁜 하루에 잠시 시간을 내어 소중한 사람과 진정으로 소통해봅니다. ",
            "일상의 중간중간 의식적으로 휴식을 취합니다. ",
            "세상이 원하는 사람이 되기 보다, 자신이 원하는 것이 무엇인지 생각해봅니다.",
            "다른 사람들의 관심사, 잘하는 것, 취미 등에 관심을 가져봅니다. ",
            "불쑥 찾아오는 사람과 대화하고, 불필요해 보이는 대화에도 함께 해 귀 기울여 봅니다.",
          ],
          descType: "list",
        },
        {
          desc: [
            "나와 내가 하는 일의 성과는 동일하지 않으며 구분되는 것임을 인식합니다. ",
            "‘일’과 ‘관계’의 균형을 잡기 위해 시간적 여유를 허락합니다.",
            "바로 행동에 돌입하려는 마음을 다스리면서 자신의 감정에 귀 기울입니다. ",
            "요가나 명상, 일기쓰기나 산책 등의 시간을 가지며 자신을 돌아봅니다. ",
            "어떤 일의 성공을 혼자 이루었다고 생각하거나 혼자 이루어야 한다고 생각하는 마음을 경계합니다.",
          ],
          descType: "list",
        },
      ],
    },
    4: {
      title: "특별함을 지향하는 예술가",
      contents: [
        {
          desc: "자신의 가치관에 어긋나는 일을 할 때 갈등이 발생합니다. 새롭고 자극적인 것을 추구하면서 자신이 특별한 존재로 인정받는 것에 만족감을 느끼기 때문에 이를 이해하지 못하는 사람들과 갈등이 생깁니다. 나에게 관심을 두지 않는 사람에게 관심받기 위해 노력하고 정작 관심을 얻어 낸 후 관계가 가까워지면 스스로 멀어지며 거리를 두기 때문에 이 과정에서 갈등이 발생하기도 합니다. 예술적이며 감정적으로 예민하기 때문에 무던한 사람들은 이를 이해하기 어렵습니다. ",
          descType: "p",
        },
        {
          desc: [
            "삶의 긍정적인 측면에 대해 생각해보는 시간을 갖습니다.",
            "감정이 행동을 결정하지 않도록 노력합니다. ",
            "압도감을 느끼는 상황에선 복식호흡 같은 평온을 찾는 호흡법을 익힙니다. ",
            "긍정적이든 부정적이든 타인의 피드백에 관대한 태도로 받아들이는 연습을 합니다. ",
            "함께 일하는 부분에서 자신의 책임을 분명히 인식하고 노력합니다. ",
          ],
          descType: "list",
        },
        {
          desc: [
            "감정적으로 흔들릴 때는 왜 그런지 스스로에게 묻고 생각해봅니다. ",
            "평범하고 사소한 것에도 위대한 힘이 있음을 인정합니다. ",
            "일을 시작한 후 중간에 포기하고 싶은 마음이 들 때 시간을 두고 다시 시작할 힘을 키웁니다. ",
            "협력하고 협조하는 일은 더 강한 결과를 낼 수 있음을 기억합니다. ",
            "내가 있는 지금 이 순간을 즐기고 그 순간에 최선을 다합니다. ",
            "조바심을 잠재우고 몸을 움직여 운동하거나 에너지를 발산합니다. ",
          ],
          descType: "list",
        },
      ],
    },
    5: {
      title: "관찰력이 뛰어난 연구가",
      contents: [
        {
          desc: "다른 사람과 함께 협력하며 일하는 것에 서툴기 때문에 이 과정에서 갈등이 발생합니다. 혼자 생각하고 자신만의 방식으로 분석하며 결정하기 때문에 함께 일하는 사람들의 의견을 종합하는 데 미숙할 수 있습니다. 조직 안에서 관찰자의 모습으로 보일 때가 많아서 소극적인 인상을 주기 쉽습니다. ",
          descType: "p",
        },
        {
          desc: [
            "타인의 이야기를 경청하고 공감하는 연습을 합니다. ",
            "주변 사람들의 도움이 나의 성장에 큰 영향을 줄 수 있다는 사실을 믿고 노력합니다. ",
            "함께 즐길 수 있는 책이나 영화, 전시장, 토론 등의 화제를 찾아서 함께 해 봅니다. ",
            "혼자만의 시간과 공간이 필요할 때는 양해를 구하고 충분한 시간을 갖되 이 시간이 자신에게 주는 의미에 대해 사람들과 함께 나눕니다. ",
            "집중하고, 빠져들어 주변 상황을 인지하지 못할 때가 있음을 기억하고 불필요한 오해가 생길 때는 부드러운 말로 설명합니다. ",
          ],
          descType: "list",
        },
        {
          desc: [
            "조직에서 일을 할 때는 협력이 필수적임을 기억합니다. ",
            "함께 일할 때나 대화할 때는 항상 상대방을 존중하는 태도를 취합니다. ",
            "팀의 목표달성을 위해 동참하고 있음을 적극적으로 표현합니다. ",
            "나의 감정을 존중하며 몸과 마음이 연결되어 있음을 기억합니다. ",
            "안전만을 도모하지 말고 과감하게 뛰어들 때는 용기를 내어 실행합니다. ",
            "언어적 의사소통에서 어감과 어투, 태도 등이 중요하다는 사실을 인정합니다. ",
          ],
          descType: "list",
        },
      ],
    },
    6: {
      title: "충직한 보안관",
      contents: [
        {
          desc: "신뢰할 수 있는 관계에서 충실하게 다른 사람을 돕는 일에 가치를 두는 이 유형의 사람들은 상대방이 권위를 남용할 때 갈등이 생깁니다. 한 발짝 뒤로 물러서는 태도를 취하면서도 집요하게 분석하고 민감하게 반응합니다. 안전과 안정감을 추구하는 이들에게는 조심성과 의심이 많다보니 다른 사람들에게 부정적 태도로 보이기 쉽습니다. 함께 진취적으로 나아가고자 할 때 문제를 제기하거나 부정적 감정으로 전체적인 분위기를 침체시킴으로써 갈등이 발생할 수 있습니다. ",
          descType: "p",
        },
        {
          desc: [
            "사람들이 지레짐작하게 내버려 두지 말고, 진짜 의도를 솔직하고 분명하게 말합니다. ",
            "누군가의 의견이 궁금할 때는 집요하게 파고들지 말고, 친근하게 궁금증을 표현합니다. ",
            "걱정과 불안으로 불편한 마음이 올라올 때 그것을 반드시 겉으로 표현해야 하는 것은 아님을 깨닫고, 불편함이 다른 사람에게 전염되지 않도록 경계합니다. ",
            "약속을 지키지 않는 사람을 단번에 제외시키지 말고, 관계의 가능성을 열어둡니다. ",
          ],
          descType: "list",
        },
        {
          desc: [
            "실패를 예상하기 보다는 잘한 것에 더 집중하려 노력합니다. ",
            "만반의 준비를 해도 통제 불가능한 영역이 있다는 것을 이해합니다. ",
            "스스로의 감정을 인정해주고, 실제 일어난 일과 상상으로 만들어낸 불안을 구분합니다. ",
            "스스로 결정할 수 있는 능력과 권한이 있음을 믿고 용기를 냅니다. ",
            "성공이 보장되지 않은 일에 도전하고 결과에 연연하지 않는 연습을 합니다. ",
          ],
          descType: "list",
        },
      ],
    },
    7: {
      title: "즐거운 모험가",
      contents: [
        {
          desc: "항상 즐거움을 추구하는 이 유형의 사람들은 지루하거나 평범한 일을 해야 할 때 갈등이 생깁니다. 일상에서 발생하는 작은 갈등상황을 회피하고 자신의 행동을 정당화하기 위해 타인을 비난할 수 있습니다. 유연하고 즉흥적인 사고방식으로 인해 안전과 안정을 추구하는 사람들에게는 부담스러운 존재로 인식될 수 있습니다. 큰 그림을 보려 하기 때문에 작고 세세한 면을 놓치기 쉽고, 지나친 낙관으로 다른 사람들을 불안하게 할 수 있습니다. ",
          descType: "p",
        },
        {
          desc: [
            "나와 함께 있는 것이 모두에게 즐거움일 수 없다는 사실을 기억합니다. ",
            "나에게 자유가 중요한 만큼 상대의 자유도 존중해 주어야 합니다. ",
            "직설적이고 솔직한 표현이 항상 좋지만은 않다는 사실을 기억합니다.",
            "하기 싫은 일을 해야할 때도 있고 조직 안에서 감수해야 하는 부분이 있을 수 있음을 인식합니다. ",
            "듣기 싫은 말을 하는 상대의 말을 차단하지 않고 끝까지 들어본 후 상대의 입장에서 생각하기 위해 노력합니다. ",
          ],
          descType: "list",
        },
        {
          desc: [
            "명상이나 산책, 일기쓰기 등 자신과 보내는 시간을 만들어 자신에 대해 깊이 있게 알아가는 시간을 갖습니다. ",
            "다른 사람과 의견을 나눌 때는 끝까지 경청하고, 상대를 존중하며 격려해줍니다. ",
            "충동성이 나를 지배하지 않도록 조절하기 위해 노력합니다. ",
            "계획을 구체적으로 세우고 일을 끝까지 해냅니다. ",
            "모든 아이디어를 표현하거나 실현해야 하는 것은 아님을 기억합니다. ",
          ],
          descType: "list",
        },
      ],
    },
    8: {
      title: "자신감 넘치는 도전가",
      contents: [
        {
          desc: "자기주장이 강한 이 유형의 사람들은 누군가 자신의 행동에 책임지지 않을 때나 상대방이 진실하지 않을 때 갈등이 생깁니다. 단호한 태도를 취하고 감정의 변화를 공개적으로 표출하므로 다른 사람들과 충돌하기 쉽습니다. 그러나 이들은 나름의 방식으로 감정을 통제하고 있기 때문에 공격적이라는 말을 들을 때 당황하거나 더 거친 표현이 나올 수 있습니다. 옳다 그르다는 기준이 명확하고 이분법적 사고방식에 익숙하기 때문에 상황에 유연하게 대응하는 사람들에게는 부담스럽게 느껴지기도 합니다. 모호함과 부정확함을 견디기 힘들어 하기 때문에 이를 짚고 넘어가는 과정에서 갈등이 생기는 경우가 많습니다. ",
          descType: "p",
        },
        {
          desc: [
            "내가 권한을 인정받고 싶듯이 타인도 그러함을 알고 인정해줍니다. ",
            "감정이 격하게 일어날 때 한 발 물러서 생각하고 잠시라도 혼자만의 시간을 갖습니다. ",
            "세상의 이치를 이분법으로 모두 설명할 수 없음을 인정합니다. ",
            "의견을 내세우거나 주장할 때 상대가 압박감을 느낄 수 있음을 기억하고 이를 경계합니다. ",
            "타인에게 책임과 통제를 온전히 맡겨봅니다. ",
          ],
          descType: "list",
        },
        {
          desc: [
            "어느 부분에 취약한 것이 약점이 아님을 이해합니다. ",
            "체력적으로 한계가 있음을 깨닫고 자신을 돌보는 자세가 필요합니다. ",
            "타인과 소통하는 방법은 매우 다양하다는 것을 인식합니다. ",
            "타인을 비난하거나 지배하려는 마음은 불안감에서 시작된다는 것을 기억합니다. ",
            "다른 사람들에게도 나와 같은 격한 감정이 있고 불안감이 있다는 사실을 기억합니다.",
          ],
          descType: "list",
        },
      ],
    },
    9: {
      title: "조화를 이루는 평화주의자",
      contents: [
        {
          desc: "조화와 평화를 최우선 가치로 두는 이 유형의 사람들은 불가피하게 다른 사람과 맞서야 할 때 갈등이 생깁니다. 맞서지 않기 위해 갈등을 피하다보니 진정한 속마음을 드러내지 않으려 하고 이 과정에서 속을 알 수 없는 사람이라는 평가를 받기도 합니다. 결정적인 상황에서 어느 편도 들지 않으려다 보니 양쪽 모두에게 소외감을 느끼게 하고, 불필요한 오해를 만들기도 합니다. 타인을 수용하는 폭이 넓은 반면, 한계에 이르면 감정이 폭발하거나 완전히 돌아서게 됩니다. 이 경우, 아주 사무적인 태도를 보이거나 계산적인 태도로 수동적 공격을 하기도 합니다. ",
          descType: "p",
        },
        {
          desc: [
            "나의 침묵이 때로는 동의로 인식될 수 있음을 기억합니다. ",
            "결정하기 어려울 때는 시간적 여유를 달라고 양해를 구한 후 어떤 방식으로든 최종 결정하여 매듭을 짓습니다. ",
            "화합과 일치도 중요하지만 명확한 평가 기준이 이를 더 효과적으로 만들고 불필요한 감정 소모를 줄여줄 수 있음을 기억합니다. ",
            "먼저 할 일과 다음에 할 일을 구분하고 정확한 목표를 세웁니다. ",
          ],
          descType: "list",
        },
        {
          desc: [
            "자신의 욕구와 필요를 의식적으로 인식하는 시간이 필요합니다. ",
            "갈등이 좋은 것은 아니지만, 나쁜것만도 아님을 이해합니다. ",
            "직접적으로 단호하게 표현하는 방법을 연습합니다. ",
            "그동안 시도하지 않았던 색다른 경험이나 도전이 삶에 활력을 준다는 사실을 인지합니다. ",
            "평화나 안정감이 깨진다고 느껴질때면, 그 문제를 회피하려 한다는 사실을 기억하고 경계합니다. ",
            "어떤 문제든 그냥 사라지지 않음을 기억하고 문제에 직면합니다.",
          ],
          descType: "list",
        },
      ],
    },
  };

  const wingsTypeData = {
    1: [
      {
        name: "1w9",
        desc: "평화롭고 이상적인 사회를 추구하며 실현하고자 합니다. 침착하고 차분하며 다른 사람들과 조화를 이루며 살기 위해 노력합니다. 다른 사람을 신뢰하고 수용하며, 함께 협력하여 행동합니다. 또한 이성적이며 상황에 유연하게 대처합니다. ",
        advantage: [
          "물질적인 욕심보다는 더 나은 세상을 만들려고 합니다. ",
          "완벽주의 성향으로 근면, 성실합니다.",
          "개인적인 소신과 가치가 강합니다. ",
          "논리적이면서 감성적인 면도 있습니다. ",
          "문제를 세세하게 처리하고 해결합니다. ",
        ],
        reflection: [
          "이상이 높아서 자신의 기준대로 세상이 변해야 한다고 생각합니다. ",
          "자신에 대한 높은 기준으로 자기비판적입니다. ",
          "화를 직접 표출하지는 않지만 수동공격적입니다.",
          "관계를 중시하느라 판단력이 약해지기도 합니다. ",
          "다른 사람의 부탁을 쉽게 거절하지 못합니다. ",
        ],
      },
      {
        name: "1w2",
        desc: "사회 변화를 추구하며, 사회 변화를 위해 힘을 보탭니다. 큰 가치와 원칙을 가지고 있습니다. 자신의 삶에 대한 원대한 꿈을 안고 공동체에서 적극적으로 역할을 합니다. 자신보다는 다른 사람들의 요구를 우선하는 경향이 있습니다. ",
        advantage: [
          "정의감이 높습니다. ",
          "지역 사회에 봉사하고 문제를 개선합니다. ",
          "문제에 대한 창의적인 아이디어와 해결책을 제시합니다. ",
          "다른 사람을 위해 헌신합니다. ",
          "다른 사람에게 필요한 것을 잘 파악합니다. ",
        ],
        reflection: [
          "의도하지 않았지만 다른 사람을 통제할 수도 있습니다. ",
          "편향된 신념과 가치를 가질 수 있습니다. ",
          "대인관계에서 쉽게 좌절할 수 있습니다.  ",
          "자신과 타인을 비판적으로 바라봅니다.  ",
          "자기중심적이고 경직되어 보일 수 있습니다. ",
        ],
      },
    ],
    2: [
      {
        name: "2w1",
        desc: "다른 사람을 위해 봉사할 줄 아는 사람입니다. 문제를 해결하고 목적을 달성하는 것이 원동력이 됩니다. 또한 다른 사람을 도우며, 상대가 자신을 필요로 하는 것을 보면서 원동력을 얻습니다. ",
        advantage: [
          "다른 사람을 아낌없이 지원하고 격려합니다. ",
          "다른 사람에게 필요한게 무엇인지 파악합니다. ",
          "선입견 없이 다른 사람을 대합니다. ",
          "잘하고 싶어서 열심히 일합니다. ",
        ],
        reflection: [
          "스트레스 받는 상황에선, 자기비판적이고 불안정합니다. ",
          "다른 사람의 칭찬과 인정을 갈망합니다. ",
          "자신의 욕구는 무시하고 다른 사람을 위해 헌신합니다. ",
          "나를 위한 비판을 받아들이기 어려워합니다. ",
        ],
      },
      {
        name: "2w3",
        desc: "다른 사람들을 잘 도우며 목표 달성을 위해 노력합니다. 이들에게는 자신을 돌보는 시간, 새로운 사람을 만나고 관계 맺는 것, 팀을 조직하고 이끄는 일이 원동력이 될 수 있습니다. 이들에게는 다른 사람보다 자신에게 필요한 것이 무엇인지를 먼저 찾고, 자신에게 충분한 휴식을 주는 일이 필요합니다. ",
        advantage: [
          "상황에 잘 적응하고 유연하게 대처합니다.",
          "낙관적이고 긍정적인 면에 집중합니다.",
          "사람들이 신뢰하고 인기가 많습니다.",
          "효과적인 의사소통 기술을 지니고 있습니다.",
        ],
        reflection: [
          "스트레스 받는 상황에선 일에 몰두하거나 자신을 잘 돌보지 않을 수 있습니다.",
          "타인에게 비춰지는 자신의 모습에 신경을 많이 씁니다.",
          "선의의 거짓말을 지나치게 하는 경우가 있습니다.",
          "잘못된 부분에 대한 지적을 받아들이기 어려워합니다.",
        ],
      },
    ],
    3: [
      {
        name: "3w2",
        desc: "외향적이고 친절하며, 매력적인 사람입니다. 사람들과 잘 어우러지고 사회적인 기술이 좋습니다. 다른 사람들이 성장할 수 있도록 지원하고 영감을 불어넣습니다. 자신과 타인의 감정을 잘 알아차립니다. ",
        advantage: [
          "일과 관계의 균형을 잘 맞추고 있습니다.",
          "사교적이며 다른 사람들과 잘 어울립니다. ",
          "다른 사람들의 감정, 욕구를 잘 알아차립니다.",
        ],
        reflection: [
          "다른 사람의 인정을 강하게 바랄 수 있습니다.",
          "인정을 받지 못하면 공격적인 모습을 보일 수 있습니다.",
          "다른 사람의 기대에 부응하기 위해 무리합니다. ",
          "다른 사람에게 맞추느라 자신의 욕구를 잘 알아차리지 못합니다.",
        ],
      },
      {
        name: "3w4",
        desc: "체계적이고 조직적인 전문가입니다. 업무를 잘 완수하기 위해 끊임없이 노력하며 헌신합니다. 성공을 위해 타인과는 다른 특별함을 추구합니다. 자신의 감정과 욕구를 알아차리고, 외적인 성공보다는 자신이 진정으로 원하는 것은 무엇인지를 고민해보는 시간이 필요합니다. ",
        advantage: [
          "개인과 일의 균형을 잘 맞추고 있습니다.",
          "자신의 내면에 집중합니다. ",
          "관계를 소중히 여기고 사람들과 상호작용합니다.",
        ],
        reflection: [
          "변덕스럽고 자신에게만 몰두할 수 있습니다. ",
          "사회적인 활동을 피할 수 있습니다.",
          "스트레스 받는 상황에선 피상적인 대인관계를 맺을 수 있습니다.",
          "지나친 우월감을 가질 수 있습니다. ",
        ],
      },
    ],
    4: [
      {
        name: "4w3",
        desc: "창의적이고 활력이 넘치며, 추진력이 있습니다. 체계적이고 효율적으로 일을 처리합니다. 자신의 매력과 스타일을 찾기 위해 노력하며, 자신의 스타일에 자부심이 있습니다. ",
        advantage: [
          "다른 사람의 감정을 잘 이해합니다.",
          "자신에 대해 잘 알고 있습니다. ",
          "창의적이며 효율적인 아이디어를 제시합니다.",
          "일관되며 진정성이 있습니다.",
          "열심히 일하며 근면합니다.",
        ],
        reflection: [
          "스트레스 받는 상황에서 감정적이며, 자기비판적입니다.",
          "과도하게 자신에게 몰두합니다. ",
          "타인이 보는 자신의 모습에 신경을 많이 씁니다.",
          "타인의 삶을 부러워 합니다. ",
        ],
      },
      {
        name: "4w5",
        desc: "높은 호기심과 통찰력을 갖고 있습니다. 창의적인 것에 관심이 많으며 독서를 좋아합니다. 자유로운 영혼이며, 사람들과 어울리기보다 자신의 삶에 집중합니다. ",
        advantage: [
          "자신이 누구인지 알기 위해 성찰합니다.",
          "호기심과 탐구심, 창의성이 있습니다.",
          "열정적이며 높은 집중력을 보입니다.",
          "진정성 있는 대화를 할 수 있습니다.",
          "자신만의 멋을 내는 감각이 있습니다.",
        ],
        reflection: [
          "자신에게 너무 몰두하여 스스로 고립될 수 있습니다.",
          "규칙, 명령 등을 지키기 위해 지나치게 노력합니다.",
          "생각은 많지만 실천력이 약합니다.",
          "스트레스를 받는 상황에선 쉽게 물러서는 모습을 보일 수 있습니다. ",
        ],
      },
    ],
    5: [
      {
        name: "5w4",
        desc: "호기심이 강하고 창의성이 있으며, 철학적인 사고를 하는 사람입니다. 가치 있는 새로운 기술을 습득하는 일에 흥미를 보입니다. 자기를 성찰하며, 다른 사람들의 인정과 존경을 받는 것이 이들에게 원동력이 됩니다. ",
        advantage: [
          "예술감각과 지식에 대한 열정으로 독창적인 작품을 만듭니다.   ",
          "세심하게 일을 처리합니다.  ",
          "집중력이 좋고 일을 끝까지 완수합니다.  ",
          "독립적으로 일을 합니다. ",
        ],
        reflection: [
          "자신의 지식과 성취감에 지나친 자부심을 가집니다. ",
          "다른 사람과 거리를 두며 고립될 수 있습니다. ",
          "현실성이 없어보이기도 합니다. ",
          "예민하며 사회적인 상황에 약합니다.",
        ],
      },
      {
        name: "5w6",
        desc: "사람들과 잘 협력하고, 자신이 가진 지식을 적극적으로 활용해 문제를 해결합니다. 상황을 잘 분석하고 논리적입니다. 문제를 해결할 때, 사회에 기여할 때, 흥미로운 지식을 습득하고 활용하는 것, 자신만의 시간을 가지며 재충전하는 것이 이들의 원동력이 됩니다.  ",
        advantage: [
          "문제를 분석하고 해결하는 능력이 뛰어납니다. ",
          "어려운 문제도 침착하게 해결합니다.",
          "집중력이 높습니다. ",
          "배움에 대한 열정이 강합니다.",
        ],
        reflection: [
          "자신만의 세계에 빠져있을 수 있습니다. ",
          "다른 사람에게 차가운 인상을 줄 수 있습니다. ",
          "동기부여가 되지 않으면 행동하기 어려워합니다. ",
          "스트레스 받는 상황에선 방어적인 태도를 보일 수 있습니다.  ",
        ],
      },
    ],
    6: [
      {
        name: "6w5",
        desc: "논리적이고 분석적인 유형입니다. 안전감을 중요시하고, 친밀하고 안정적인 관계를 추구합니다. 신뢰할만한 직업이나 대인관계를 맺는 것, 다른 사람의 인정, 복잡한 문제 해결이 원동력이 될 수 있습니다. 또한 정서적으로 불안정할 때 스스로를 고립시키지 않는 것, 문제가 발생하면 신뢰할 수 있는 사람과 상의하는 연습이 필요합니다. 혼자만의 시간을 가지며 재충전할 수 있는 시간과 공간을 마련해야 합니다. ",
        advantage: [
          "지적 호기심과 열정이 강합니다.",
          "실용적이며 효율적으로 문제를 해결합니다. ",
          "섬세하고 세부사항을 잘 파악합니다. ",
          "독립적으로 일을 잘 처리합니다. ",
          "갈등이 생겼을 때 논리적으로 잘 표현합니다. ",
        ],
        reflection: [
          "감정 표현을 잘 하지 못합니다. ",
          "냉소적이고 차가운 사람으로 보일 수 있습니다.",
          "사담을 좋아하지 않고, 명확하고 간결한 대화를 선호합니다.",
          "부정적인 생각을 하지 않기 위해 많은 노력을 기울입니다. ",
          "다른 사람과 거리를 두고 멀어지는 경향이 있습니다. ",
        ],
      },
      {
        name: "6w7",
        desc: "안전을 추구하는 한편, 재미와 즐거움을 선호합니다. 매력적인 사람이며, 사람 사이의 신뢰를 중요시합니다. 상대를 통제하려고 하지 않습니다. 또한 새롭고 재밌는 경험, 새로운 사람과의 관계, 가족·친구와 친밀한 관계 유지, 어려운 문제를 해결하는 일이 이들의 원동력이 됩니다. ",
        advantage: [
          "약속을 잘 지키고 신뢰할 수 있습니다.  ",
          "문제가 발생해도 침착하게 대응합니다. ",
          "실용적이며 효율적으로 문제를 해결합니다.",
          "다른 사람들을 잘 살피며 일을 처리합니다.  ",
        ],
        reflection: [
          "스트레스 받는 상황에서 감정 조절이 어렵습니다. ",
          "결정을 해야 할 때 우유부단해질 수 있습니다. ",
          "신뢰를 쌓아가는데 시간이 걸립니다.  ",
          "물질적인 소유욕이 강합니다.",
          "특정 문제에 지나치게 몰두합니다.  ",
        ],
      },
    ],
    7: [
      {
        name: "7w6",
        desc: "낙천적이며 작은 것에서도 기쁨을 찾습니다. 행복을 중요하게 생각하고 성취감을 얻기를 원합니다. 기회를 놓치는 것을 두려워합니다. 새로운 아이디어 제시와 경험, 창의성, 다양한 선택, 새로운 사람과의 관계가 이들에게 원동력이 될 수 있습니다. 그리고 부정적인 생각과 마주하는 것, 자신에 대한 비판을 통해 성장하는 것, 모든 순간이 흥미롭지 않다는 것을 받아들여야 합니다. ",
        advantage: [
          "상대를 기꺼이 도와주며 협조적입니다.  ",
          "다른 사람에 대한 공감과 이해를 잘합니다.",
          "스트레스 받는 상황에서도 침착함을 유지합니다. ",
          "상황 파악을 빠르게 하고 해결합니다.",
        ],
        reflection: [
          "일과 관계에서 쉽게 지루함을 느낍니다. ",
          "자신을 의심하며 결정을 못 미더워합니다.",
          "다른 사람들의 의견에 쉽게 영향을 받습니다.",
          "불안해지면 생각이 잘 정리되지 않습니다. ",
        ],
      },
      {
        name: "7w8",
        desc: "다른 사람들과의 상호작용을 선호하며 현실주의자입니다. 인상적이고 흥미로운 경험을 하기를 원합니다. 관심의 중심이 되는 것, 새로운 목표를 달성하는 것, 다른 사람들과 친밀한 관계를 맺는 것, 재미있는 경험이 이들에게 원동력이 됩니다. ",
        advantage: [
          "자신감과 매력을 갖고 있습니다.",
          "긍정적이고 낙천적입니다.",
          "에너지가 많고 열정적입니다.",
          "논리적이며 자기 의견을 잘 표현합니다. ",
          "위기가 발생해도 침착하게 행동합니다.",
        ],
        reflection: [
          "자기중심적입니다.",
          "물질적인 소유욕이 있습니다. ",
          "충동적이며 무뚝뚝한 인상을 줄 수 있습니다. ",
          "다른 사람에게 거만하게 행동합니다.",
          "실패에 대한 불안을 느낍니다. ",
        ],
      },
    ],
    8: [
      {
        name: "8w7",
        desc: "독립적인 성향이 강하며 통제 받는 것을 싫어합니다. 인간중심적이며 낙관적으로 삶을 살아갑니다. 실용적이고 창의적인 생각을 합니다. 사람들과 함께 시간을 보내고, 억눌린 에너지를 발휘하는 것이 이들에게 원동력이 됩니다. ",
        advantage: [
          "다른 사람들에게 영감을 주고 효과적으로 이끕니다.",
          "논리적이고 공정함을 바탕으로 결정합니다.",
          "자신의 생각과 의견을 명확하게 전달합니다. ",
          "낙관적이며 혁신적으로 일합니다. ",
          "가족, 친구들과 친밀한 관계를 잘 유지합니다.",
        ],
        reflection: [
          "모든 일에 지나치게 열정적입니다.  ",
          "단조로운 일상을 지루하게 여깁니다. ",
          "종종 다른 사람을 자신보다 아래로 보기도 합니다. ",
          "권위적인 말을 잘 받아들이지 못합니다.",
          "감정적인 부분에서 인내심이 낮습니다.  ",
        ],
      },
      {
        name: "8w9",
        desc: "든든하고 편안한 사람입니다. 차분하며 다른 사람을 잘 수용합니다. 자율적이고 독립적인 모습을 추구합니다. 다른 사람을 잘 이끄는 리더의 자질을 가지고 있습니다. 자율적이고 독립적인 환경에서 리더로서의 면모가 빛을 발합니다. ",
        advantage: [
          "대담하고 자신감 있습니다. ",
          "의지력이 강합니다.",
          "다양한 사람들과 잘 어울립니다.  ",
          "근면 성실하고 신뢰할 수 있습니다. ",
        ],
        reflection: [
          "도움을 거부하고 스스로 해결하려고 합니다. ",
          "통제 당하는 것을 꺼려해서 자신이 규칙을 만듭니다",
          "감정 표현을 어려워합니다.  ",
          "관계에서 욕구가 채워지지 않으면 감정적인 모습을 보입니다. ",
        ],
      },
    ],
    9: [
      {
        name: "9w8",
        desc: "삶의 균형과 평화를 추구하며 중재할 힘이 있는 사람입니다. 독립적이고 모험가 같은 면모가 있습니다. 침착함을 유지하면서 자신의 감정을 잘 표현합니다. 그리고 가까운 사람과의 갈등을 피하지 않고, 대인관계는 개인의 노력만으로 되지 않음을 받아들이는 것이 필요합니다.",
        advantage: [
          "여러 집단을 잘 연결하고 이끕니다. ",
          "다양한 관점으로 상황을 바라봅니다. ",
          "협상을 잘하고 자신의 의견을 잘 표현합니다. ",
          "사람들을 잘 격려하고 지원합니다. ",
        ],
        reflection: [
          "평화를 위해 자신보단 상대를 위한 결정을 합니다. ",
          "어려운 상황을 회피하려고 합니다. ",
          "통제 받는 느낌이 들면 고집스러운 모습을 보입니다. ",
        ],
      },
      {
        name: "9w1",
        desc: "진지하며 원대한 이상을 갖고 있습니다. 낙관적이며 내면의 평화를 추구합니다. 신중하고 재치가 있으며, 질서정연한 것을 좋아합니다. 사람들에게 친절하고 이해심이 많습니다. 평화로운 일상과 여러 조직의 화합, 격려와 지지를 받는 것이 원동력이 될 수 있습니다. ",
        advantage: [
          "다른 사람을 돕고 지원합니다. ",
          "다양한 관점으로 상황을 바라봅니다. ",
          "의지력이 강하고 자신의 신념과 원칙이 있습니다. ",
          "자신의 감정을 잘 표현합니다.  ",
        ],
        reflection: [
          "갈등을 피하려고 합니다.  ",
          "자신에게 비판적인 경우가 있습니다.  ",
          "몽상가적인 모습을 보일 때가 있습니다.",
          "자신에게 필요한 것들을 놓칠 수 있습니다.",
        ],
      },
    ],
  };

  // 관계와 소통 분석
  const aboutJC =
    resultPageData.JC >= 0 && resultPageData.JC <= 19
      ? "낮음"
      : resultPageData.JC >= 20 && resultPageData.JC <= 35
      ? "적당함"
      : resultPageData.JC >= 36 && resultPageData.JC <= 50
      ? "높음"
      : "범위를 벗어남";
  const aboutEC =
    resultPageData.EC >= 0 && resultPageData.EC <= 19
      ? "낮음"
      : resultPageData.EC >= 20 && resultPageData.EC <= 35
      ? "적당함"
      : resultPageData.EC >= 36 && resultPageData.EC <= 50
      ? "높음"
      : "범위를 벗어남";

  const aboutMR =
    resultPageData.MR >= 0 && resultPageData.MR <= 19
      ? "낮음"
      : resultPageData.MR >= 20 && resultPageData.MR <= 35
      ? "적당함"
      : resultPageData.MR >= 36 && resultPageData.MR <= 50
      ? "높음"
      : "범위를 벗어남";
  const aboutFE =
    resultPageData.FE >= 0 && resultPageData.FE <= 19
      ? "낮음"
      : resultPageData.FE >= 20 && resultPageData.FE <= 35
      ? "적당함"
      : resultPageData.FE >= 36 && resultPageData.FE <= 50
      ? "높음"
      : "범위를 벗어남";
  const aboutAE =
    resultPageData.AE >= 0 && resultPageData.AE <= 19
      ? "낮음"
      : resultPageData.AE >= 20 && resultPageData.AE <= 35
      ? "적당함"
      : resultPageData.AE >= 36 && resultPageData.AE <= 50
      ? "높음"
      : "범위를 벗어남";

  const RelationCommuniValues = [
    { name: "JC", result: aboutJC },
    { name: "EC", result: aboutEC },
    { name: "MR", result: aboutMR },
    { name: "FE", result: aboutFE },
    { name: "AE", result: aboutAE },
  ];
  const RelationCommuniHighItems = RelationCommuniValues.filter(
    (item) => item.result === "높음"
  );
  const RelationCommuniRowItems = RelationCommuniValues.filter(
    (item) => item.result === "낮음"
  );

  // 요약본
  const powerBalanceDesc = {
    1: "완벽을 추구하는 올곧은 사람입니다.",
    2: "친절한 조력가입니다.",
    3: "성공을 향해가는 열정적 야심가입니다.",
    4: "특별함을 지향하는 예술가입니다.",
    5: "관찰력이 뛰어난 연구가입니다.",
    6: "충직한 보안관입니다.",
    7: "즐거운 모험가입니다.",
    8: "자신감 넘치는 도전가입니다.",
    9: "조화를 이루는 평화주의자입니다.",
  };

  // return
  if (
    !resultPageData ||
    !ref29ScoreType ||
    !images ||
    !ref29ScoreResult ||
    !ref12BottomTwo[0].img ||
    !PersonalityTypeValue ||
    ref12TopThree.length !== 3 ||
    ref12BottomTwo.length !== 2
  ) {
    return;
  }

  return (
    <Container>
      <div className="page-common" ref={printPageRefs[0]}>
        <main className="main cover">
          <img src={images.index_cover} alt="" className="cover-img" />
          <div className="user-info-list row-group">
            <div className="user-info-item col-group">
              <div className="item-title">이름</div>
              <div className="item-txt">{resultPageData.name}</div>
            </div>
            <div className="user-info-item col-group">
              <div className="item-title">생년월일</div>
              <div className="item-txt">{resultPageData.birth}</div>
            </div>
            <div className="user-info-item col-group">
              <div className="item-title">검사 완료일</div>
              <div className="item-txt">{resultPageData.complete_date}</div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[1]}>
        <main className="list">
          <div className="col-group">
            <h2 className="list-title green">목차</h2>
            <div className="list-wrap row-group">
              <div className="list-group row-group gap32">
                <div className="list-group-title col-group gap32">
                  <div className="num">Ⅰ.</div>
                  <div className="txt">마인드 인사이트</div>
                </div>
                <div className="list-sub-group row-group gap16">
                  <div className="list-sub-item col-group gap32">
                    <div className="page">05</div>
                    <div className="title">소개</div>
                  </div>
                </div>
              </div>
              <div className="list-group row-group gap32">
                <div className="list-group-title col-group gap32">
                  <div className="num">Ⅱ.</div>
                  <div className="txt">감정상태 - 내 몸과 마음의 연결</div>
                </div>
                <div className="list-sub-group row-group gap16">
                  <div className="list-sub-item col-group gap32">
                    <div className="page">06</div>
                    <div className="title">불안 / 우울 / 분노 지수</div>
                  </div>
                  <div className="list-sub-item col-group gap32">
                    <div className="page">12</div>
                    <div className="title">
                      디지털 의존도 / 스트레스 정도 / 번아웃 증후군
                    </div>
                  </div>
                </div>
              </div>
              <div className="list-group row-group gap32">
                <div className="list-group-title col-group gap32">
                  <div className="num">Ⅲ.</div>
                  <div className="txt">융합역량지능</div>
                </div>
                <div className="list-sub-group row-group gap16">
                  <div className="list-sub-item col-group gap32">
                    <div className="page">15</div>
                    <div className="title">강점 영역 / 약점 보완 영역</div>
                  </div>
                  <div className="list-sub-item col-group gap32">
                    <div className="page">17</div>
                    <div className="title">
                      다각형 분석 / 전체 비율 분포 분석
                    </div>
                  </div>
                </div>
              </div>
              <div className="list-group row-group gap32">
                <div className="list-group-title col-group gap32">
                  <div className="num">Ⅳ.</div>
                  <div className="txt">에너지 역량 및 방향</div>
                </div>
                <div className="list-sub-group row-group gap16">
                  <div className="list-sub-item col-group gap32">
                    <div className="page">14</div>
                    <div className="title">
                      에너지 역량 / 에너지의 균형 분포
                    </div>
                  </div>
                  <div className="list-sub-item col-group gap32">
                    <div className="page">19</div>
                    <div className="title">에너지 유형</div>
                  </div>
                  <div className="list-sub-item col-group gap32">
                    <div className="page">21</div>
                    <div className="title">균형분포 정도</div>
                  </div>
                  <div className="list-sub-item col-group gap32">
                    <div className="page">22</div>
                    <div className="title">
                      에너지 방향에 따른 유형과 기질적 특성
                    </div>
                  </div>
                </div>
              </div>
              <div className="list-group row-group gap32">
                <div className="list-group-title col-group gap32">
                  <div className="num">Ⅴ.</div>
                  <div className="txt">힘의 균형</div>
                </div>
                <div className="list-sub-group row-group gap16">
                  <div className="list-sub-item col-group gap32">
                    <div className="page">24</div>
                    <div className="title">
                      힘의 균형 결과 / 성장을 위한 질문
                    </div>
                  </div>
                  <div className="list-sub-item col-group gap32">
                    <div className="page">26</div>
                    <div className="title">
                      유형별 관계 전략 / 성장을 위한 처방
                    </div>
                  </div>
                  <div className="list-sub-item col-group gap32">
                    <div className="page">27</div>
                    <div className="title">힘의 균형과 날개 & 화살</div>
                  </div>
                </div>
              </div>
              <div className="list-group row-group gap32">
                <div className="list-group-title col-group gap32">
                  <div className="num">Ⅵ.</div>
                  <div className="txt">관계와 소통 분석</div>
                </div>
                <div className="list-sub-group row-group gap16">
                  <div className="list-sub-item col-group gap32">
                    <div className="page">29</div>
                    <div className="title">자아상태 결과</div>
                  </div>
                  <div className="list-sub-item col-group gap32">
                    <div className="page">30</div>
                    <div className="title">의사소통 방식과 리더십</div>
                  </div>
                </div>
              </div>
              <div className="list-group row-group gap32">
                <div className="list-group-title col-group gap32">
                  <div className="num">Ⅶ.</div>
                  <div className="txt">인생 점수</div>
                </div>
                <div className="list-sub-group row-group gap16">
                  <div className="list-sub-item col-group gap32">
                    <div className="page">32</div>
                    <div className="title">인생 점수 결과</div>
                  </div>
                  <div className="list-sub-item col-group gap32">
                    <div className="page">33</div>
                    <div className="title">
                      5R : Remember / Reinterpretation / Refresh / Reformation /
                      Reconstruction
                    </div>
                  </div>
                </div>
              </div>
              <div className="list-group row-group gap32">
                <div className="list-group-title col-group gap32">
                  <div className="num">Ⅷ.</div>
                  <div className="txt">융합역량지수 Meta-Q</div>
                </div>
                <div className="list-sub-group row-group gap16">
                  <div className="list-sub-item col-group gap32">
                    <div className="page">35</div>
                    <div className="title">Meta-Q 지수 결과</div>
                  </div>
                  <div className="list-sub-item col-group gap32">
                    <div className="page">37</div>
                    <div className="title">성장을 위한 전략</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common page-summary" ref={printPageRefs[2]}>
        <main className="main">
          <p className="page-num">3</p>

          <div className="section">
            <div className="title-wrap">
              <span className="main-title dan">한눈에 보기</span>
              <p className="name">
                {resultPageData.name}님의 마인드 인사이트 결과
              </p>
            </div>
          </div>
          <div className="section contents-wrap">
            <div className="heading-wrap">
              <p className="name">감정상태</p>
              <div className="sub-wrap">
                <strong className="tit">당신의 몸과 마음의 연결 상태는?</strong>
                <p className="page">6페이지로 가기</p>
              </div>
            </div>
            <ul className="score-list-container">
              <li className="score-list-item">
                <div className={`result-score-wrap ${Ref3unrestScoreType}`}>
                  <div className="result-score-title-wrap col-group sp-bt">
                    <p className="result-score-title">나의 불안검사 결과</p>
                    <div className="result-score-num">
                      <strong>{resultPageData.unrest.score}</strong>점
                    </div>
                  </div>
                  <div className="result-score">
                    <div className="result-score-gauge-wrap">
                      <div
                        className="result-score-gauge"
                        style={{ width: `${resultPageData.unrest.score}%` }}
                      ></div>
                    </div>
                    <div
                      className="line percent-30"
                      style={{ left: "30%" }}
                    ></div>
                    <div
                      className="line percent-40"
                      style={{ left: "40%" }}
                    ></div>
                    <div
                      className="line percent-50"
                      style={{ left: "50%" }}
                    ></div>
                  </div>
                  <div className="result-score-list row-group">
                    {Ref3unrestScoreType === "type1" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">0점 - 30점</p>
                          <p className="txt">
                            정상적인 수준의 불안을 경험하고 있는 상태
                          </p>
                        </div>
                      </div>
                    )}
                    {Ref3unrestScoreType === "type2" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">31점 - 40점</p>
                          <p className="txt">
                            가벼운 정도의 불안을 경험하고 있는 상태
                          </p>
                        </div>
                      </div>
                    )}
                    {Ref3unrestScoreType === "type3" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">41점 - 50점</p>
                          <p className="txt">
                            상당한 정도의 불안을 경험하고 있는 상태
                          </p>
                        </div>
                      </div>
                    )}
                    {Ref3unrestScoreType === "type4" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">51점 - 100점</p>
                          <p className="txt">심한 불안을 경험하고 있는 상태</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </li>
              <li className="score-list-item">
                <div className={`result-score-wrap ${Ref4ScoreType}`}>
                  <div className="result-score-title-wrap col-group sp-bt">
                    <p className="result-score-title">나의 우울검사 결과</p>
                    <div className="result-score-num">
                      <strong>{resultPageData.melancholy}</strong>점
                    </div>
                  </div>
                  <div className="result-score">
                    <div className="result-score-gauge-wrap">
                      <div
                        className="result-score-gauge"
                        style={{ width: `${resultPageData.melancholy}%` }}
                      ></div>
                    </div>
                    <div
                      className="line percent-30"
                      style={{ left: "30%" }}
                    ></div>
                    <div
                      className="line percent-40"
                      style={{ left: "40%" }}
                    ></div>
                    <div
                      className="line percent-50"
                      style={{ left: "50%" }}
                    ></div>
                  </div>
                  <div className="result-score-list row-group">
                    {Ref4ScoreType === "type1" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">0점 - 30점</p>
                          <p className="txt">
                            정상적인 수준의 우울을 경험하고 있는 상태
                          </p>
                        </div>
                      </div>
                    )}
                    {Ref4ScoreType === "type2" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">31점 - 40점</p>
                          <p className="txt">
                            가벼운 정도의 우울을 경험하고 있는 상태
                          </p>
                        </div>
                      </div>
                    )}
                    {Ref4ScoreType === "type3" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">41점 - 50점</p>
                          <p className="txt">
                            상당한 정도의 우울을 경험하고 있는 상태
                          </p>
                        </div>
                      </div>
                    )}
                    {Ref4ScoreType === "type4" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">51점 - 100점</p>
                          <p className="txt">심한 우울을 경험하고 있는 상태</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </li>
              <li className="score-list-item">
                <div className={`result-score-wrap ${Ref7ScoreType}`}>
                  <div className="result-score-title-wrap col-group sp-bt">
                    <p className="result-score-title">나의 분노검사 결과</p>
                    <div className="result-score-num">
                      <strong>{resultPageData.anger.score}</strong>점
                    </div>
                  </div>
                  <div className="result-score">
                    <div className="result-score-gauge-wrap">
                      <div
                        className="result-score-gauge"
                        style={{ width: `${resultPageData.anger.score}%` }}
                      ></div>
                    </div>
                    <div
                      className="line percent-30"
                      style={{ left: "30%" }}
                    ></div>
                    <div
                      className="line percent-40"
                      style={{ left: "40%" }}
                    ></div>
                    <div
                      className="line percent-50"
                      style={{ left: "50%" }}
                    ></div>
                  </div>
                  <div className="result-score-list row-group">
                    {Ref7ScoreType === "type1" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">0점 - 30점</p>
                          <p className="txt">
                            정상적인 수준의 분노를 경험하고 있는 상태
                          </p>
                        </div>
                      </div>
                    )}
                    {Ref7ScoreType === "type2" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">31점 - 40점</p>
                          <p className="txt">
                            가벼운 정도의 분노를 경험하고 있는 상태
                          </p>
                        </div>
                      </div>
                    )}
                    {Ref7ScoreType === "type3" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">41점 - 50점</p>
                          <p className="txt">
                            상당한 정도의 분노를 경험하고 있는 상태
                          </p>
                        </div>
                      </div>
                    )}
                    {Ref7ScoreType === "type4" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">51점 - 100점</p>
                          <p className="txt">심한 분노를 경험하고 있는 상태</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </li>
              <li className="score-list-item">
                <div className={`result-score-wrap ${Ref9ScoreType}`}>
                  <div className="result-score-title-wrap col-group sp-bt">
                    <p className="result-score-title">디지털 의존도 결과</p>
                    <div className="result-score-num">
                      <strong>{resultPageData.digital}</strong>점
                    </div>
                  </div>
                  <div className="result-score">
                    <div className="result-score-gauge-wrap">
                      <div
                        className="result-score-gauge"
                        style={{ width: `${resultPageData.digital}%` }}
                      ></div>
                    </div>
                    <div
                      className="line percent-25"
                      style={{ left: "25%" }}
                    ></div>
                    <div
                      className="line percent-50"
                      style={{ left: "50%" }}
                    ></div>
                  </div>
                  <div className="result-score-list row-group">
                    {Ref9ScoreType === "type1" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">0점 - 30점</p>
                          <p className="txt">
                            일반적인 수준의 디지털 의존도를 보이고 있는 상태
                          </p>
                        </div>
                      </div>
                    )}
                    {Ref9ScoreType === "type2" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">26점 - 50점</p>
                          <p className="txt">
                            잠재적 위험군 수준의 디지털 의존도를 보이고 있는
                            상태
                          </p>
                        </div>
                      </div>
                    )}
                    {Ref9ScoreType === "type3" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">51점 이상</p>
                          <p className="txt">
                            고위험군 수준의 디지털 의존도를 보이고 있는 상태
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </li>
              <li className="score-list-item">
                <div className={`result-score-wrap ${Ref9StressScoreType}`}>
                  <div className="result-score-title-wrap col-group sp-bt">
                    <p className="result-score-title">스트레스 정도 결과</p>
                    <div className="result-score-num">
                      <strong>{resultPageData.stress}</strong>점
                    </div>
                  </div>
                  <div className="result-score">
                    <div className="result-score-gauge-wrap">
                      <div
                        className="result-score-gauge"
                        style={{ width: `${resultPageData.stress}%` }}
                      ></div>
                    </div>
                    <div
                      className="line percent-35"
                      style={{ left: "35%" }}
                    ></div>
                    <div
                      className="line percent-50"
                      style={{ left: "50%" }}
                    ></div>
                    <div
                      className="line percent-70"
                      style={{ left: "70%" }}
                    ></div>
                  </div>
                  <div className="result-score-list row-group">
                    {Ref9StressScoreType === "type1" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">0점 - 35점</p>
                          <p className="txt">
                            일반적인 수준의 스트레스를 경험하고 있는 상태
                          </p>
                        </div>
                      </div>
                    )}
                    {Ref9StressScoreType === "type2" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">36점 - 50점</p>
                          <p className="txt">
                            초기단계 수준의 스트레스를 경험하고 있는 상태
                          </p>
                        </div>
                      </div>
                    )}
                    {Ref9StressScoreType === "type3" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">51점 - 70점</p>
                          <p className="txt">
                            중증 수준의 스트레스를 경험하고 있는 상태
                          </p>
                        </div>
                      </div>
                    )}
                    {Ref9StressScoreType === "type4" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">71점 이상</p>
                          <p className="txt">
                            매우 심각한 수준의 스트레스를 경험하고 있는 상태
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </li>
              <li className="score-list-item">
                <div className={`result-score-wrap ${Ref10ScoreType}`}>
                  <div className="result-score-title-wrap col-group sp-bt">
                    <p className="result-score-title">번아웃 증후군 결과</p>
                    <div className="result-score-num">
                      <strong>{resultPageData.burnout}</strong>점
                    </div>
                  </div>
                  <div className="result-score">
                    <div className="result-score-gauge-wrap">
                      <div
                        className="result-score-gauge"
                        style={{ width: `${resultPageData.burnout}%` }}
                      ></div>
                    </div>
                    <div
                      className="line percent-15"
                      style={{ left: "15%" }}
                    ></div>
                    <div
                      className="line percent-35"
                      style={{ left: "35%" }}
                    ></div>
                    <div
                      className="line percent-60"
                      style={{ left: "60%" }}
                    ></div>
                  </div>
                  <div className="result-score-list row-group">
                    {Ref10ScoreType === "type1" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">0점 - 15점</p>
                          <p className="txt">
                            일반적인 수준의 업무집중도를 보이고 있는 상태
                          </p>
                        </div>
                      </div>
                    )}
                    {Ref10ScoreType === "type2" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">16점 - 35점</p>
                          <p className="txt">
                            초기단계 수준의 번아웃 증후군을 보이고 있는 상태
                          </p>
                        </div>
                      </div>
                    )}
                    {Ref10ScoreType === "type3" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">36점 - 60점</p>
                          <p className="txt">
                            중증 수준의 번아웃 증후군을 보이고 있는 상태
                          </p>
                        </div>
                      </div>
                    )}
                    {Ref10ScoreType === "type4" && (
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">61점 이상</p>
                          <p className="txt">
                            매우 심각한 수준의 번아웃 증후군을 보이고 있는 상태
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <div className="section contents-wrap mt-60">
            <div className="heading-wrap">
              <p className="name">융합역량지능</p>
              <div className="sub-wrap">
                <strong className="tit">당신의 잠재적 역량은?</strong>
                <p className="page">15페이지로 가기</p>
              </div>
            </div>
            <div className="grid chart-wrap">
              <div>
                <div className="row-group gap16">
                  <p className="default-title green">강점 영역</p>
                  <div className="step-item-list border">
                    {ref12TopThree.map((item, index) => (
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">0{index + 1}</div>
                        <div className="step-item-txt-wrap row-group gap8">
                          <p className="step-item-sub-title"> {item.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="row-group gap16 mt-24">
                  <p className="default-title red">약점 보완 영역</p>
                  <div className="step-item-list border red">
                    {ref12BottomTwo.map((item, index) => (
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">0{index + 1}</div>
                        <div className="step-item-txt-wrap row-group gap8">
                          <p className="step-item-sub-title">{item.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="meta-q-chart">
                <Radar data={data} options={options} />
              </div>
            </div>
          </div>
          <div className="section contents-wrap mt-60">
            <div className="heading-wrap">
              <p className="name">에너지 유형</p>
              <div className="sub-wrap">
                <strong className="tit">
                  당신의 에너지 방향에 따른 유형과 특징은?
                </strong>
                <p className="page">19페이지로 가기</p>
              </div>
            </div>
            <div className="energy-result-top-wrap">
              <div className="energy-result-title-wrap">
                <p className="default-title">
                  {resultPageData.name}님의 에너지 유형
                </p>
                <div className="energy-result-type">{energyTypeResult}</div>
              </div>
              <div className="energy-result-top-list row-group">
                <div
                  className={`energy-result-wrap ${
                    resultPageData.D > resultPageData.W
                      ? "right"
                      : resultPageData.D < resultPageData.W
                      ? "left"
                      : "right left"
                  }`}
                >
                  {" "}
                  {/* d의 점수가 높으면 right / w의 점수가 높으면 left */}
                  <div className="energy-result-top">
                    <div className="energy-result-top-title left">
                      <div className="num">{resultPageData.W}</div>
                      <p className="title">폭넓은(w)</p>
                    </div>
                    <div className="energy-guage-wrap">
                      <div className="energy-guage left">
                        <div
                          className="energy-guage-bar"
                          style={{ width: `${resultPageData.W}%` }}
                        ></div>
                      </div>
                      <div className="energy-guage right">
                        <div
                          className="energy-guage-bar"
                          style={{ width: `${resultPageData.D}%` }}
                        ></div>
                      </div>
                      <div className="energy-guage-num-group">
                        <p className="energy-guage-num">100</p>
                        <p className="energy-guage-num">50</p>
                        <p className="energy-guage-num">0</p>
                        <p className="energy-guage-num">50</p>
                        <p className="energy-guage-num">100</p>
                      </div>
                    </div>
                    <div className="energy-result-top-title right">
                      <p className="title">깊이 있는(d)</p>
                      <div className="num">{resultPageData.D}</div>
                    </div>
                  </div>
                </div>
                <div
                  className={`energy-result-wrap ${
                    resultPageData.I > resultPageData.P
                      ? "right"
                      : resultPageData.I < resultPageData.P
                      ? "left"
                      : "right left"
                  }`}
                >
                  {" "}
                  {/* i의 점수가 높으면 right / p의 점수가 높으면 left */}
                  <div className="energy-result-top">
                    <div className="energy-result-top-title left">
                      <div className="num">{resultPageData.P}</div>
                      <p className="title">실용적(P)</p>
                    </div>
                    <div className="energy-guage-wrap">
                      <div className="energy-guage left">
                        <div
                          className="energy-guage-bar"
                          style={{ width: `${resultPageData.P}%` }}
                        ></div>
                      </div>
                      <div className="energy-guage right">
                        <div
                          className="energy-guage-bar"
                          style={{ width: `${resultPageData.I}%` }}
                        ></div>
                      </div>
                      <div className="energy-guage-num-group">
                        <p className="energy-guage-num">100</p>
                        <p className="energy-guage-num">50</p>
                        <p className="energy-guage-num">0</p>
                        <p className="energy-guage-num">50</p>
                        <p className="energy-guage-num">100</p>
                      </div>
                    </div>
                    <div className="energy-result-top-title right">
                      <p className="title">통찰적(I)</p>
                      <div className="num">{resultPageData.I}</div>
                    </div>
                  </div>
                </div>
                <div
                  className={`energy-result-wrap ${
                    resultPageData.M > resultPageData.L
                      ? "right"
                      : resultPageData.M < resultPageData.L
                      ? "left"
                      : "right left"
                  }`}
                >
                  {" "}
                  {/* m의 점수가 높으면 right / l의 점수가 높으면 left */}
                  <div className="energy-result-top">
                    <div className="energy-result-top-title left">
                      <div className="num">{resultPageData.L}</div>
                      <p className="title">논리 중심(L)</p>
                    </div>
                    <div className="energy-guage-wrap">
                      <div className="energy-guage left">
                        <div
                          className="energy-guage-bar"
                          style={{ width: `${resultPageData.L}%` }}
                        ></div>
                      </div>
                      <div className="energy-guage right">
                        <div
                          className="energy-guage-bar"
                          style={{ width: `${resultPageData.M}%` }}
                        ></div>
                      </div>
                      <div className="energy-guage-num-group">
                        <p className="energy-guage-num">100</p>
                        <p className="energy-guage-num">50</p>
                        <p className="energy-guage-num">0</p>
                        <p className="energy-guage-num">50</p>
                        <p className="energy-guage-num">100</p>
                      </div>
                    </div>
                    <div className="energy-result-top-title right">
                      <p className="title">의미 중심(M)</p>
                      <div className="num">{resultPageData.M}</div>
                    </div>
                  </div>
                </div>
                <div
                  className={`energy-result-wrap ${
                    resultPageData.C > resultPageData.S
                      ? "right"
                      : resultPageData.C < resultPageData.S
                      ? "left"
                      : "right left"
                  }`}
                >
                  {" "}
                  {/* c의 점수가 높으면 right / s의 점수가 높으면 left */}
                  <div className="energy-result-top">
                    <div className="energy-result-top-title left">
                      <div className="num">{resultPageData.S}</div>
                      <p className="title">체계적(S)</p>
                    </div>
                    <div className="energy-guage-wrap">
                      <div className="energy-guage left">
                        <div
                          className="energy-guage-bar"
                          style={{ width: `${resultPageData.S}%` }}
                        ></div>
                      </div>
                      <div className="energy-guage right">
                        <div
                          className="energy-guage-bar"
                          style={{ width: `${resultPageData.C}%` }}
                        ></div>
                      </div>
                      <div className="energy-guage-num-group">
                        <p className="energy-guage-num">100</p>
                        <p className="energy-guage-num">50</p>
                        <p className="energy-guage-num">0</p>
                        <p className="energy-guage-num">50</p>
                        <p className="energy-guage-num">100</p>
                      </div>
                    </div>
                    <div className="energy-result-top-title right">
                      <p className="title">포용적(C)</p>
                      <div className="num">{resultPageData.C}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <div className="page-common page-summary" ref={printPageRefs[3]}>
        <main className="main">
          <p className="page-num">4</p>

          <div className="section contents-wrap">
            <div className="heading-wrap">
              <p className="name">힘의 균형</p>
              <div className="sub-wrap">
                <strong className="tit">
                  {resultPageData.name}님은 {resultPageData.power_num}.{" "}
                  {powerBalanceDesc[resultPageData.power_num]}
                </strong>
                <p className="page">24페이지로 가기</p>
              </div>
            </div>
            <div className="balance-of-power-img-wrap">
              <div className="text-wrap">
                {balanceOfPower
                  .slice(4, 9)
                  .reverse()
                  .map((item, index) => (
                    <div
                      className={`text-item item-${item.number} ${
                        resultPageData.power_num === item.number ? "active" : ""
                      }`}
                      key={index}
                    >
                      <p className="tit">
                        <span className="number">{item.number}</span>{" "}
                        {item.title}
                      </p>
                      {item.desc.map((descItem, descIndex) => (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(descItem),
                          }}
                          key={descIndex}
                          className="ul-list"
                        />
                      ))}
                    </div>
                  ))}
              </div>
              <div className={`after-wrap num_${resultPageData.power_num}`}>
                <img
                  className="balance-of-power-img"
                  src={images.glance_custom_balance_of_power}
                  alt=""
                />
              </div>
              <div className="text-wrap">
                {balanceOfPower.slice(0, 4).map((item, index) => (
                  <div
                    className={`text-item item-${item.number} ${
                      resultPageData.power_num === item.number ? "active" : ""
                    }`}
                    key={index}
                  >
                    <p className="tit">
                      <span className="number">{item.number}</span> {item.title}
                    </p>
                    {item.desc.map((descItem, descIndex) => (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(descItem),
                        }}
                        key={descIndex}
                        className="ul-list"
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* 힘의균형 차트 */}
            <div style={{ marginTop: "48px" }} className="">
              <ul
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "26px",
                  paddingLeft: "120px",
                  paddingRight: "172px",
                }}
                className="label-wrap"
              >
                <li>2</li>
                <li>3</li>
                <li>4</li>
                <li>5</li>
                <li>6</li>
                <li>7</li>
                <li>8</li>
                <li>9</li>
                <li>1</li>
              </ul>

              {/* 차트 영역 */}
              <div style={{ display: "flex" }}>
                <div
                  style={{
                    width: "100%",
                    height: "350px",
                  }}
                >
                  <Line
                    data={overviewBalanceOfPowerChartData}
                    options={overviewBalanceOfPowerChartOptions}
                    plugins={[
                      addMiddleGridLines,
                      overviewBalanceOfPowerBackgroundPlugin,
                    ]}
                  />
                </div>
                <ul
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "52px",
                    fontSize: "25px",
                    paddingTop: "8px",
                  }}
                >
                  <li>매우높음</li>
                  <li>평균이상</li>
                  <li>평&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;균</li>
                  <li>평균이하</li>
                  <li>매우낮음</li>
                </ul>
              </div>

              {/* 하단 label */}
              <ul
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  paddingLeft: "43px",
                  paddingRight: "99px",
                  marginTop: "-17px",
                }}
              >
                <li className="overview-balance-of-power-label text-center">
                  가슴&#40;감정&#41;중심
                </li>
                <li className="overview-balance-of-power-label text-center">
                  머리&#40;사고&#41;중심
                </li>
                <li className="overview-balance-of-power-label text-center">
                  장&#40;본능&#41;중심
                </li>
              </ul>
            </div>
          </div>

          <div className="section contents-wrap mt-60">
            <div className="heading-wrap">
              <p className="name">자아상태</p>
              <div className="sub-wrap">
                <strong className="tit">
                  당신의 소통 능력을 향상시키기 위해서는
                </strong>
                <p className="page">29페이지로 가기</p>
              </div>
            </div>
            <div className="row-group gap32">
              <div className="ego-result-chart-wrap col-group gap40">
                <div className="ego-result-chart-group">
                  <div className="ego-result-title-group col-group">
                    <p className="default-txt">비판적</p>
                    <p className="default-txt">보호적</p>
                    <p className="default-txt">현실적</p>
                    <p className="default-txt">자율적</p>
                    <p className="default-txt">의존적</p>
                  </div>
                  <div className="ego-result-chart">
                    <div style={{ width: "100%", height: "290px" }}>
                      <Line
                        data={chartData}
                        options={chartOptions}
                        plugins={[backgroundPlugin]}
                      />
                    </div>
                  </div>
                  <div className="ego-result-txt-group col-group">
                    <p className="default-txt default-txt-s">
                      JC <br />
                      (판단&통제)
                    </p>
                    <p className="default-txt default-txt-s">
                      EC <br />
                      (허용&보호)
                    </p>
                    <p className="default-txt default-txt-s">
                      MR <br />
                      (이성&합리)
                    </p>
                    <p className="default-txt default-txt-s">
                      FE <br />
                      (자율&표현)
                    </p>
                    <p className="default-txt default-txt-s">
                      AE <br />
                      (순응&의존)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="section contents-wrap col-2 grid gap40 mt-60">
            <div>
              <div className="heading-wrap">
                <p className="name">인생점수</p>
                <div className="sub-wrap align-right">
                  <p className="page">32페이지로 가기</p>
                </div>
              </div>
              <div
                className={`section life-score-section 
                     ${
                       (resultPageData.age < 10 && "age0") ||
                       (resultPageData.age < 20 && "age1") ||
                       (resultPageData.age < 30 && "age2") ||
                       (resultPageData.age < 40 && "age3") ||
                       (resultPageData.age < 50 && "age4") ||
                       (resultPageData.age < 60 && "age5") ||
                       (resultPageData.age < 70 && "age6") ||
                       (resultPageData.age < 80 && "age6")
                     }`}
              >
                <div className="life-score-result-wrap col-group gap56">
                  <div className="row-group gap40">
                    <div className="title-wrap col-group gap40">
                      <div className="icon"></div>
                      <div className="row-group gap-8">
                        <div className="txt-group col-group gap32">
                          <p className="default-txt default-txt-bold">
                            {resultPageData.name}
                          </p>
                          <p className="default-txt">{resultPageData.age}세</p>
                        </div>
                        <div className="score">
                          평균 {resultPageData.life_average}점
                        </div>
                      </div>
                    </div>
                    <div className="row-group gap32">
                      <p className="default-txt">
                        본 인생점수는, 지나온 각자의 시간들에 대해 스스로 평가한
                        만족도 지표입니다.
                      </p>
                      <p className="default-txt default-txt-l default-txt-bold green">
                        {(ref29ScoreResult &&
                          ref29ScoreResult[ref29ScoreType] &&
                          ref29ScoreResult[ref29ScoreType].review) ||
                          "review가 없습니다"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="heading-wrap">
                <p className="name">융합역량지수</p>
                <div className="sub-wrap align-right">
                  <p className="page">35페이지로 가기</p>
                </div>
              </div>
              <div className="result-score-wrap type2">
                <div className="result-score-title-wrap col-group sp-bt">
                  <p className="result-score-title">나의 Meta-Q검사 결과</p>
                  <div className="result-score-num">
                    <strong>{resultPageData.metaQ}</strong>점
                  </div>
                </div>
                <div className="result-score number">
                  <div className="result-score-gauge-wrap">
                    <div
                      className="result-score-gauge"
                      style={{
                        width: `${(resultPageData.metaQ / 300) * 100}%`,
                      }}
                    ></div>
                  </div>
                  <div className="line" style={{ left: "25%" }}></div>
                  <div className="line" style={{ left: "50%" }}></div>
                  <div className="line" style={{ left: "75%" }}></div>
                  <div className="result-score-num-wrap col-group">
                    <p className="num">0</p>
                    <p className="num">75</p>
                    <p className="num">150</p>
                    <p className="num">225</p>
                    <p className="num">300</p>
                  </div>
                </div>

                <div className="result-score-list row-group">
                  <div className="result-score-item col-group gap24">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(selectedItem.score),
                      }}
                      className="num"
                    />
                    <div className="txt-group row-group gap10">
                      <p className="title">
                        {selectedItem.title} {selectedItem.enTitle}
                      </p>
                      <p className="txt font-medium">{selectedItem.keywords}</p>
                      <p className="txt">{selectedItem.desc}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <div className="page-common" ref={printPageRefs[4]}>
        <main className="main bg-bottom">
          <p className="page-num">5</p>
          <div className="main-title-wrap col-group">
            <div className="num dan green">01</div>
            <div className="main-title-group row-group">
              <h2 className="main-title">마인드 인사이트</h2>
              <p className="main-sub-title">01. 마인드 인사이트 소개</p>
            </div>
          </div>

          <div className="section-top">
            <img src={images.section_top} alt="" className="img" />
            <p className="txt">
              몸과 마음의 중심은 가장 아픈 곳입니다. 아픈 곳은 성장과 변화가
              있는 곳입니다. <br />
              사람의 아픈 곳과 세상의 아픈 곳을 치유하며, 더불어 행복한 세상을
              향해 나아갑니다.
            </p>
          </div>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">1</div>
                <h3 className="section-title">마인드 인사이트란?</h3>
              </div>
              <p className="default-txt">
                ‘마인드 인사이트’는, 인간의 깊숙한 내면으로 들어가 마음을
                점검하고 분석하며 치유하는 과정입니다. <br />
                인사이트(insight)는 '통찰', 곧 본질을 꿰뚫어 본다는 것을
                의미합니다. 사람들은 나를 잘 모르며, 나조차 나를 설명하기
                어렵습니다. <br />
                내가 내 마음대로 되질 않습니다. 마인드 인사이트는 내 안의 ‘진짜
                나’를 알아가고 발견하는 과정을 담은 ‘나’에 관한 진단과 분석,
                해석과 상담, 치유와 회복의 과정에 함께 합니다. 나를 바르게
                분석하고 알고 있어야 인생의 위기를 피해가고, 삶의 위기를 극복할
                수 있습니다.
              </p>
            </div>
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">2</div>
                <h3 className="section-title">진행과정</h3>
              </div>
              <div className="step-item-wrap col-group gap40">
                <div className="step-item-list row-group gap16">
                  <div className="step-item border col-group gap24">
                    <div className="step-item-num">01</div>
                    <div className="step-item-txt-wrap row-group gap16">
                      <div className="step-item-title-wrap col-group gap16">
                        <p className="step-item-title">Mind Check</p>
                        <p className="step-item-txt">마음진단</p>
                      </div>
                      <p className="step-item-sub-title">
                        개인의 정서상태를 체크합니다.
                      </p>
                      <p className="step-item-txt">
                        불안, 우울, 분노, 디지털 의존도, 번아웃증후군, 공황장애,
                        스트레스 지수 등 체크하여 내 몸과 마음의 상관관계를
                        파악합니다.
                      </p>
                    </div>
                  </div>
                  <div className="step-item border col-group gap24">
                    <div className="step-item-num">02</div>
                    <div className="step-item-txt-wrap row-group gap16">
                      <div className="step-item-title-wrap col-group gap16">
                        <p className="step-item-title">Mind Analysis</p>
                        <p className="step-item-txt">마음분석</p>
                      </div>
                      <p className="step-item-sub-title">
                        나의 방식과 역량을 분석합니다.
                      </p>
                      <p className="step-item-txt">
                        내 안에 숨겨진 역량을 분석하고, 내가 사용하는 에너지의
                        방향과 힘의 균형을 파악합니다. 또, 내가 자주 사용하는
                        자아상태를 파악하여 타인과 의사소통하고 관계 맺는 방식을
                        분석합니다.
                      </p>
                    </div>
                  </div>
                  <div className="step-item border col-group gap24">
                    <div className="step-item-num">03</div>
                    <div className="step-item-txt-wrap row-group gap16">
                      <div className="step-item-title-wrap col-group gap16">
                        <p className="step-item-title">Mind Report</p>
                        <p className="step-item-txt">마음 리포트</p>
                      </div>
                      <p className="step-item-sub-title">
                        나에 대한 보고서를 출력합니다.
                      </p>
                      <p className="step-item-txt">
                        ‘건강한 미래의 나를 위한 처방전’과 같은 나의 인생보고서
                        일 수 있습니다. 개인의 인생점수(Life Cycle Score)에
                        정서지수와 다중지능지수, 그리고 에너지 방향과 의사소통
                        방식 등에 따른 기능지수를 통합적으로 분석한{" "}
                        <strong>Meta-Q</strong>
                        (융합역량지수)가 표시되며 이를 근거로 갈등회피 전략,
                        관계 전략, 셀프코칭 질문 등의 성장비전을 제시합니다.
                      </p>
                    </div>
                  </div>
                  <div className="step-item border col-group gap24">
                    <div className="step-item-num">04</div>
                    <div className="step-item-txt-wrap row-group gap16">
                      <div className="step-item-title-wrap col-group gap16">
                        <p className="step-item-title">Mind Therapy</p>
                        <p className="step-item-txt">마음훈련</p>
                      </div>
                      <p className="step-item-sub-title">
                        자기객관화를 진행하고 치유합니다.
                      </p>
                      <p className="step-item-txt">
                        선택에 따라 현재의 나를 이해하는 해석 상담을 받을 수
                        있고 마인드테라피 훈련과정을 통해, 미래의 나를 위한
                        변화의 처방전, ‘상담코칭 서비스’를 제공받습니다.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="row-group sp-bt">
                  <p className="default-txt">
                    마인드 인사이트는 Mind Check (진단), Mind Analysis (분석),
                    Mind Report (리포트) , Mind Therapy (훈련)과정을 통해 과거의
                    나를 돌아보며 자기객관화를 진행합니다. <br />
                    <br />
                    선택에 따라 현재의 나를 이해하는 해석 상담을 받을 수 있고
                    마인드테라피 훈련과정을 통해, 미래의 나를 위한 변화의
                    처방전, ‘상담코칭 서비스’를 제공받습니다.
                  </p>
                  <div className="row-group gap40">
                    <p className="default-title green">
                      우리 모두 당신을 응원하고 지지합니다.
                    </p>
                    <p className="default-txt">
                      우리는 타인과의 관계 속에서 상처 입고, 아파하면서
                      성장합니다. 부모의 언어와 사고체계가 나에게 많은 영향을
                      주어 물들고, 사회적 정의로 포장된 수많은 구호들이 나의
                      선택을 왜곡합니다. 이 과정에서 입게 되는 상처는 나
                      혼자만의 문제가 아니며 혼자서는 해결하기도 힘듭니다.{" "}
                      <br />
                      <br />
                      마인드 인사이트는 일상에서 넘어지고 상처받고 주저앉아
                      울면서도, 포기하지 않고 주어진 인생을 살아가는 ‘나’를
                      응원하고 지지합니다. 무엇인가에 가려져 보여지는 ‘나’가
                      아니라, 침묵 가운데서 조용하고 고요한 곳에서 속삭이고 있는{" "}
                      <strong className="orange">
                        ‘진짜 나’를 발견하여, 자신만의 길을 자신 있게 걸어나가는
                        것이 우리 프로그램의 존재이유 입니다. 우리 모두 당신을
                        응원하고 지지합니다.
                      </strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[5]}>
        <main className="main">
          <p className="page-num">6</p>
          <div className="main-title-wrap col-group">
            <div className="num dan green">02</div>
            <div className="main-title-group row-group">
              <h2 className="main-title">감정상태 - 내 몸과 마음의 연결</h2>
              <p className="main-sub-title">01. 불안 / 우울 / 분노 지수</p>
            </div>
          </div>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">1</div>
                <h3 className="section-title">불안검사 결과</h3>
              </div>
              <div className="result-score-container col-group gap40">
                <div className={`result-score-wrap ${Ref3unrestScoreType}`}>
                  <div className="result-score-title-wrap col-group sp-bt">
                    <p className="result-score-title">나의 불안검사 결과</p>
                    <div className="result-score-num">
                      <strong>{resultPageData.unrest.score}</strong>점
                    </div>
                  </div>
                  <div className="result-score">
                    <div className="result-score-gauge-wrap">
                      <div
                        className="result-score-gauge"
                        style={{ width: `${resultPageData.unrest.score}%` }}
                      ></div>
                    </div>
                    <div className="line" style={{ left: "30%" }}></div>
                    <div className="line" style={{ left: "40%" }}></div>
                    <div className="line" style={{ left: "50%" }}></div>
                  </div>
                  <div className="result-score-list row-group">
                    <div className="result-score-item col-group gap24">
                      <div className="icon"></div>
                      <div className="txt-group row-group gap8">
                        <p className="title">0점 - 30점</p>
                        <p className="txt">
                          정상적인 수준의 불안을 경험하고 있는 상태
                        </p>
                      </div>
                    </div>
                    <div className="result-score-item col-group gap24">
                      <div className="icon"></div>
                      <div className="txt-group row-group gap8">
                        <p className="title">31점 - 40점</p>
                        <p className="txt">
                          가벼운 정도의 불안을 경험하고 있는 상태
                        </p>
                      </div>
                    </div>
                    <div className="result-score-item col-group gap24">
                      <div className="icon"></div>
                      <div className="txt-group row-group gap8">
                        <p className="title">41점 - 50점</p>
                        <p className="txt">
                          상당한 정도의 불안을 경험하고 있는 상태
                        </p>
                      </div>
                    </div>
                    <div className="result-score-item col-group gap24">
                      <div className="icon"></div>
                      <div className="txt-group row-group gap8">
                        <p className="title">51점 - 100점</p>
                        <p className="txt">심한 불안을 경험하고 있는 상태</p>
                      </div>
                    </div>
                  </div>
                </div>
                <ResultScoreTextWrap
                  scoreType={Ref3unrestScoreType}
                  DetailText={Ref3selectDetailText}
                  isDigital={false}
                />
              </div>
            </div>
            {resultPageData.unrest.addition && (
              <SubInfoText subText={Ref3PagesubText} />
            )}
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">2</div>
                <h3 className="section-title">불안과 공포의 개념</h3>
              </div>
              <div className="row-group gap40">
                <p className="default-txt">
                  불안과 공포는 일상생활에서는 혼동해서 쓰는 단어이지만 구분되는
                  개념입니다. <br />
                  마음이 불편하고 조마조마한 상태가 불안과 공포에서 모두
                  나타납니다.
                </p>
                <div className="col-group gap40 relative">
                  <div className="icon-arrow-wrap">
                    <div className="icon-arrow-both">
                      <img
                        src={images.icon_arrow_bold_down}
                        className="icon-arrow-left"
                      />
                      <img
                        src={images.icon_arrow_bold_down}
                        className="icon-arrow-right"
                      />
                    </div>
                    <div className="icon-arrow-both">
                      <img
                        src={images.icon_arrow_bold_down}
                        className="icon-arrow-left"
                      />
                      <img
                        src={images.icon_arrow_bold_down}
                        className="icon-arrow-right"
                      />
                    </div>
                  </div>
                  <div className="green-border-box row-group gap32">
                    <p className="default-title green">불안</p>
                    <p className="default-txt default-txt-bold">
                      스트레스, 위험, 고통, 공포 등이 예상되는 상황이나,
                      예상하지 못한 일이 발생한 상황에서의 감정입니다.
                    </p>
                    <p className="default-txt default-txt-s">
                      신체적인 반응으로 숨 가쁨, 가슴 두근거림, 근육의 긴장,
                      식은땀, 두통, 소화불량 등이 나타나며, 정서적 반응으로
                      불쾌하고 모호한 두려움, 긴장, 불면, 우울감 등이
                      나타납니다.
                    </p>
                  </div>
                  <div className="green-border-box row-group gap32">
                    <p className="default-title green">공포</p>
                    <p className="default-txt default-txt-bold">
                      특별한 이유 없이 경험하는 불안과 다르게 대상이 명확하고
                      구체적인 경우에 발생하는 감정입니다.
                    </p>
                    <p className="default-txt default-txt-s">
                      예를 들어 길에서 갑자기 사나운 개를 만났다거나 산길을
                      걷다가 뱀과 마주쳤을 때 경험하는 감정을 공포라고 할 수
                      있습니다.
                    </p>
                  </div>
                  <div className="green-border-box row-group gap32">
                    <p className="default-title green">공황</p>
                    <p className="default-txt default-txt-bold">
                      응급상황으로 느껴지는 극심한 불안 증상이 발작(Attack)처럼
                      경험되는 상태를 말합니다.
                    </p>
                    <p className="default-txt default-txt-s">
                      갑작스럽게 호흡곤란이나 심장마비, 질식과 같이 뚜렷한 신체
                      증상이 동반되는 것이 특징이며 그 증상은 보통 갑작스럽게
                      발생하여 10분 안에 최고조에 이릅니다.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">3</div>
                <h3 className="section-title">정상적인 불안과 병적인 불안</h3>
              </div>
              <p className="default-txt">
                정상적인 불안 반응은 앞으로 일어날 일들을 대비하고, 공포 반응은
                위협적인 상황에서 자신을 지키기 위해 필요한 정서적 반응입니다.
                그러나{" "}
                <strong>
                  불안한 감정이 통제할 수가 없이 지나치게 나타나고, 불안
                  증상으로 인해 업무수행이 어렵거나 일상생활을 유지하는 것이
                  어렵다면 병적인 불안
                </strong>
                일 수 있습니다. 불안장애로 정확히 진단하기 위해서는 정신건강의학
                전문가의 도움이 필요합니다.
              </p>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[6]}>
        <main className="main">
          <p className="page-num">7</p>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">4</div>
                <h3 className="section-title">우울검사 결과</h3>
              </div>
              <div className="result-score-container col-group gap40">
                <div className={`result-score-wrap ${Ref4ScoreType}`}>
                  <div className="result-score-title-wrap col-group sp-bt">
                    <p className="result-score-title">나의 우울검사 결과</p>
                    <div className="result-score-num">
                      <strong>{resultPageData.melancholy}</strong>점
                    </div>
                  </div>
                  <div className="result-score">
                    <div className="result-score-gauge-wrap">
                      <div
                        className="result-score-gauge"
                        style={{ width: `${resultPageData.melancholy}%` }}
                      ></div>
                    </div>
                    <div className="line" style={{ left: "30%" }}></div>
                    <div className="line" style={{ left: "40%" }}></div>
                    <div className="line" style={{ left: "50%" }}></div>
                  </div>
                  <div className="result-score-list row-group">
                    <div className="result-score-item col-group gap24">
                      <div className="icon"></div>
                      <div className="txt-group row-group gap8">
                        <p className="title">0점 - 30점</p>
                        <p className="txt">
                          정상적인 수준의 우울을 경험하고 있는 상태
                        </p>
                      </div>
                    </div>
                    <div className="result-score-item col-group gap24">
                      <div className="icon"></div>
                      <div className="txt-group row-group gap8">
                        <p className="title">31점 - 40점</p>
                        <p className="txt">
                          가벼운 정도의 우울을 경험하고 있는 상태
                        </p>
                      </div>
                    </div>
                    <div className="result-score-item col-group gap24">
                      <div className="icon"></div>
                      <div className="txt-group row-group gap8">
                        <p className="title">41점 - 50점</p>
                        <p className="txt">
                          상당한 정도의 우울을 경험하고 있는 상태
                        </p>
                      </div>
                    </div>
                    <div className="result-score-item col-group gap24">
                      <div className="icon"></div>
                      <div className="txt-group row-group gap8">
                        <p className="title">51점 - 100점</p>
                        <p className="txt">심한 우울을 경험하고 있는 상태</p>
                      </div>
                    </div>
                  </div>
                </div>
                <ResultScoreTextWrap
                  scoreType={Ref4ScoreType}
                  DetailText={Ref4selectDetailText}
                  isDigital={false}
                />
              </div>
            </div>
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">5</div>
                <h3 className="section-title">우울한 기분과 우울증</h3>
              </div>
              <div className="row-group gap32">
                <div className="green-border-box row-group gap32 depression-1">
                  <img src={images.depression_01} alt="" className="img" />
                  <p className="default-title green">우울한 기분</p>
                  <p className="default-txt default-txt-bold">
                    누구나 경험할 수 있는 정서적 반응 중의 하나이며 <br />
                    보통, 우울감은 시간이 지나면 서서히 회복됩니다.
                  </p>
                  <p className="default-txt">
                    살아가면서 누구나 위기, 실패, 소외, 상실 등을 경험하고
                    이때에는 좌절감을 느끼고 <br />
                    자신감이 떨어지는 등의 감정변화를 경험합니다. <br />
                    <br />
                    그러나 일반적인 수준의 우울감을 넘어 우울증을 겪는 사람들은
                    대부분 의욕이 없고 <br />
                    무기력, 무감동하며 자신을 무능하고 무가치하게 느낍니다.
                  </p>
                </div>
                <div className="green-border-box row-group gap32 depression-2">
                  <img src={images.depression_02} alt="" className="img" />
                  <p className="default-title green">우울증</p>
                  <p className="default-txt default-txt-bold">
                    우울증은 ‘불안’에 이어 두 번째로 일반적인 정신건강
                    장애입니다. <br />
                    우울감과 삶에 대한 흥미와 관심을 잃는 것이 우울증의 핵심
                    증상입니다.
                  </p>
                  <p className="default-txt">
                    1차 진료 의료인을 찾는 사람의 약 30%가 우울증 증상을
                    경험하지만, <br />
                    주요 우울증이 있는 사람은 이 중 10% 미만입니다. <br />
                    <br />
                    우울증의 가장 심각한 증상은 자살 사고로, 우울증 환자의 60%
                    이상이 자살을 생각하고 10~15%가 실제로 자살을 시행합니다.
                    일부 우울증 환자는 자신이 우울증인 것을 인지하지 못하고
                    일상생활 기능이 떨어질 때까지도 자신의 상태에 대해 호소하지
                    않습니다. <br />
                    <br />
                    대부분의 우울증 환자는 삶에 대한 에너지 상실을 호소하는데
                    과업을 끝까지 마치는 데에 상당한 어려움을 느끼고, 학업 및
                    직장에서 정상적인 업무수행에 장애를 느끼고 새로운 과업을
                    실행할 동기를 찾지 못합니다. 우울장애로 정확히 진단하기
                    위해서는 정신건강의학 전문가의 도움이 필요합니다.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-5 page-common" ref={printPageRefs[7]}>
        <main className="main">
          <p className="page-num">8</p>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">6</div>
                <h3 className="section-title">
                  긴장 완화, 불면 해소에 도움 되는 안정화 기법
                </h3>
              </div>
              <div className="section-wrap row-group">
                <div className="row-group gap32">
                  <p className="default-title">
                    <span className="green">복식호흡</span>
                  </p>
                  <p className="default-txt">
                    복식호흡은 배 근육과 횡격막을 사용하는 호흡방식으로, 긴장을
                    완화시키고 몸과 마음을 편안하게 합니다. <br />
                    또한 불면, 스트레스를 해소하고 집중력을 높이는데 효과가
                    있습니다.
                  </p>
                  <div className="tip-wrap col-group gap40">
                    <div className="row-group gap40">
                      <img src={images.relax_tip_01} alt="" className="img-1" />
                      <div className="tip-item-wrap col-group gap24">
                        <div className="icon">TIP</div>
                        <div className="tip-item-group row-group">
                          <div className="tip-item">
                            스트레스를 받거나 화가 날 때, 불안할 때 등 호흡이
                            얕고 빨라지는 경우에 실시하면 효과가 좋습니다.
                          </div>
                          <div className="tip-item">
                            숨을 들이마시는 것보다 천천히 내쉬도록 합니다.
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="step-item-list border">
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">01</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            편안한 자세를 취하고 몸을 이완시킵니다. 한 손은
                            가슴에, 다른 손은 배 위에 놓고 가슴과 배의 움직임을
                            느낍니다.
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">02</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            코로 천천히 숨을 들이마시며, 배가 점점 부풀어오르는
                            것을 느낍니다.
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">03</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            숨을 다 들이마시고 잠시 숨을 멈춥니다.
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">04</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            입으로 천천히 숨을 내쉬며, 배가 홀쭉해지는 것을
                            느낍니다.
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">05</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            처음에는 짧게 시작하고, 점차 시간을 늘려가면서
                            연습해보세요.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row-group gap32">
                  <p className="default-title">
                    <span className="green">점진적 근육이완</span>
                  </p>
                  <p className="default-txt">
                    점진적 근육이완은 신체의 근육을 긴장시키고 이완하는
                    기법으로, 스트레스, 불안 등을 감소시키고 긴장을 완화하는데
                    효과가 있습니다.
                  </p>
                  <div className="tip-wrap col-group gap40">
                    <div className="row-group gap40">
                      <img src={images.relax_tip_02} alt="" className="img" />
                      <div className="tip-item-wrap col-group gap24">
                        <div className="icon">TIP</div>
                        <div className="tip-item-group row-group">
                          <div className="tip-item">
                            편안하고 조용한 장소에서 합니다. 불편한 자세나 옷
                            등은 피합니다.
                          </div>
                          <div className="tip-item">
                            근육에 과도하게 힘을 주거나 갑자기 힘을 풀지 마세요.
                            불편하지 않도록 천천히 부드럽게 하는 것이 좋습니다.
                          </div>
                          <div className="tip-item">
                            근육이 긴장하고 이완하는 감각과 그 차이를
                            느껴보세요. 꾸준히 연습하면 그 감각이 더 좋아집니다.
                          </div>
                          <div className="tip-item">
                            근육·관절에 통증이나 부상, 심혈관 질환이나 고혈압이
                            있는 경우는 주의해주세요.
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="step-item-list border">
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">01</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            다리를 쭉 뻗고 발끝을 몸쪽으로 당겨{" "}
                            <span className="green">종아리</span>에 힘을 줍니다.
                            그 다음 천천히 힘을 풀며 종아리를 이완합니다.
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">02</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            <span className="green">배</span>를 집어넣으며 힘을
                            줍니다. 그 다음 천천히 힘을 풀며 배를 이완합니다.
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">03</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            깊게 숨을 들이마십니다.{" "}
                            <span className="green">가슴</span>이 부풀어오르는
                            것을 느낍니다. 그 다음 숨을 천천히 내뱉으며 가슴을
                            이완합니다.
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">04</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            주먹을 쥐고 손과 팔에 힘을 줍니다. 그 다음 천천히
                            힘을 풀며 <span className="green">손</span>을
                            이완합니다.
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">05</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            <span className="green">팔</span>을 굽히며 팔뚝에
                            힘을 줍니다. 그 다음 천천히 힘을 풀며 팔을
                            이완합니다.
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">06</div>
                        <div className="step-item-txt-wrap">
                          <div className="row-group gap8">
                            <p className="step-item-sub-title">
                              양 <span className="green">어깨</span>를 귀쪽으로
                              들어올립니다. 그 다음 천천히 어깨를 내리며
                              이완합니다.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">07</div>
                        <div className="step-item-txt-wrap">
                          <div className="row-group gap8">
                            <p className="step-item-sub-title">
                              <span className="green">턱</span>을 가슴쪽으로
                              붙이며 목 뒤를 최대한 당깁니다. 그 다음 천천히
                              목을 이완합니다.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">08</div>
                        <div className="step-item-txt-wrap">
                          <div className="row-group gap8">
                            <p className="step-item-sub-title">
                              입술을 다물고 눈을 감으며{" "}
                              <span className="green">얼굴</span> 전체에 힘을
                              줍니다. 그 다음 천천히 얼굴을 이완합니다.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[8]}>
        <main className="main">
          <p className="page-num">9</p>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">6</div>
                <h3 className="section-title">
                  긴장 완화, 불면 해소에 도움 되는 안정화 기법
                </h3>
              </div>
              <div className="col-group gap40">
                <div className="section-wrap row-group half-div">
                  <div className="row-group gap40">
                    <div className="relax-title-wrap col-group gap40">
                      <div className="relax-title-group row-group gap32">
                        <p className="default-title green">나비포옹법</p>
                        <p className="default-txt">
                          이 기법은 괴로운 기억이 반복해서 떠오를 때 시행하면
                          효과적으로 심리적 외상으로 차단된 좌뇌, 우뇌의
                          정보처리 과정을 촉진합니다.
                        </p>
                      </div>
                      <img src={images.relax_item_01} alt="" className="img" />
                    </div>
                    <div className="step-item-list border">
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">01</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            양손을 X자로 교차시켜서 가슴 위에 올려둡니다.
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">02</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            좌우로 번갈아가며 자신의 팔뚝을 토닥여줍니다.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row-group gap40">
                    <div className="relax-title-wrap col-group gap40">
                      <div className="relax-title-group row-group gap32">
                        <p className="default-title green">착지연습</p>
                        <p className="default-txt">
                          착지연습은 재난 경험자가 해리나 침습의 영향에서 스스로
                          벗어날 수 있도록 돕는 기법으로 위험한 과거가 아닌
                          안전한 현재에 있음을 인지하도록 돕는 역할을 합니다.
                        </p>
                      </div>
                      <img src={images.relax_item_02} alt="" className="img" />
                    </div>
                    <div className="step-item-list border">
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">01</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            먼저 심호흡을 하고, 앉거나 서있는 상태에서 발이
                            바닥에 닿아있는 느낌에 집중하며 느껴봅니다.
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">02</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            발가락을 움직여보기도 하고, <br />
                            발뒤꿈치를 들었다가 쿵! 내려놓으세요.
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">03</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            뒤꿈치에 힘을 주면서 바닥을 느껴봅니다.
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">04</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            지금 내 주변에 무엇이 있는지 관찰해보고 <br />
                            만져보면서 그 감각을 느껴봅니다.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row-group gap40">
                    <div className="relax-title-wrap col-group gap40">
                      <div className="relax-title-group row-group gap32">
                        <p className="default-title green">안전지대연습</p>
                        <p className="default-txt">
                          편안하고 안전한 장소를 구체적으로 <br />
                          상상하며 몸과 마음을 이완하는 기법입니다. <br />
                          마음이 불편해질 때 시행하면 마음을
                          <br />
                          안정시킬 수 있습니다.
                        </p>
                      </div>
                      <img src={images.relax_item_03} alt="" className="img" />
                    </div>
                    <div className="step-item-list border">
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">01</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            눈을 감고 가장 편안하고 안전하다고 느끼는 장소(실제
                            가본 곳, 가상의 장소)를 떠올립니다.
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">02</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            풍경, 소리, 냄새, 몸에서 느껴지는 감각들을 생생하게
                            느끼며 머물러 봅니다.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="section-wrap row-group half-div">
                  <div className="row-group gap40">
                    <div className="relax-title-wrap col-group gap40">
                      <div className="relax-title-group row-group gap32">
                        <p className="default-title green">봉인연습</p>
                        <p className="default-txt">
                          봉인연습은 심리적 불편감을 조절하는 능력을 기르는
                          기법으로 안정화 단계에서 치료자가 가르쳐야 할 가장
                          기본적이고 중요한 기술 중 하나입니다.
                        </p>
                      </div>
                      <img src={images.relax_item_04} alt="" className="img" />
                    </div>
                    <div className="step-item-list border">
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">01</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            나를 힘들게 하는 기억이나 생각, 감정을 떠올리고 한
                            장의 사진으로 만들어 봅니다.
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">02</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            기억을 담아둘 아주 단단하고 새지 않는 상자(금고,
                            철제상자 등)를 떠올려봅니다.
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">03</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            이 상자를 봉인할 방법(자물쇠, 열쇠, 쇠사슬 등)을
                            정해봅니다.
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">04</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            힘든 기억, 생각, 감정을 상자에 넣고 입구를
                            봉인합니다. 심호흡 후 현재 기분, 느낌, 감각을
                            느껴보세요.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row-group gap40">
                    <div className="relax-title-wrap col-group gap40">
                      <div className="relax-title-group row-group gap32">
                        <p className="default-title green">정서자유기법</p>
                        <p className="default-txt">
                          정서자유기법은 해결하고 싶은 문제나 감정을 신체 부위를
                          두드려 해소하는 기법입니다.
                        </p>
                      </div>
                      <img src={images.relax_item_05} alt="" className="img" />
                    </div>
                    <div className="step-item-list border">
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">01</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            해결하고 싶은 문제나 감정을 구체적으로 떠올려봅니다.
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">02</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            정수리를 시작으로 눈썹 위, 눈 측면, 눈밑 뼈, 인중,
                            턱, 쇄골 아래, 가슴 측면을 손가락으로 5~10회
                            부드럽게 두드리며 내려옵니다. 마지막에는 두 손목
                            안쪽을 서로 맞대고 두드립니다.
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">03</div>
                        <div className="step-item-txt-wrap">
                          <p className="step-item-sub-title">
                            두드리면서 “나는 ＿＿＿ 이지만, 나 자신을 온전히
                            사랑하고 받아들입니다”라고 말합니다.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[9]}>
        <main className="main">
          <p className="page-num">10</p>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">7</div>
                <h3 className="section-title">분노검사 결과</h3>
              </div>
              <div className="result-score-container col-group gap40">
                <div className={`result-score-wrap ${Ref7ScoreType}`}>
                  <div className="result-score-title-wrap col-group sp-bt">
                    <p className="result-score-title">나의 분노검사 결과</p>
                    <div className="result-score-num">
                      <strong>{resultPageData.anger.score}</strong>점
                    </div>
                  </div>
                  <div className="result-score">
                    <div className="result-score-gauge-wrap">
                      <div
                        className="result-score-gauge"
                        style={{ width: `${resultPageData.anger.score}%` }}
                      ></div>
                    </div>
                    <div className="line" style={{ left: "30%" }}></div>
                    <div className="line" style={{ left: "40%" }}></div>
                    <div className="line" style={{ left: "50%" }}></div>
                  </div>
                  <div className="result-score-list row-group">
                    <div className="result-score-item col-group gap24">
                      <div className="icon"></div>
                      <div className="txt-group row-group gap8">
                        <p className="title">0점 - 30점</p>
                        <p className="txt">
                          정상적인 수준의 분노를 경험하고 있는 상태
                        </p>
                      </div>
                    </div>
                    <div className="result-score-item col-group gap24">
                      <div className="icon"></div>
                      <div className="txt-group row-group gap8">
                        <p className="title">31점 - 40점</p>
                        <p className="txt">
                          가벼운 정도의 분노를 경험하고 있는 상태
                        </p>
                      </div>
                    </div>
                    <div className="result-score-item col-group gap24">
                      <div className="icon"></div>
                      <div className="txt-group row-group gap8">
                        <p className="title">41점 - 50점</p>
                        <p className="txt">
                          상당한 정도의 분노를 경험하고 있는 상태
                        </p>
                      </div>
                    </div>
                    <div className="result-score-item col-group gap24">
                      <div className="icon"></div>
                      <div className="txt-group row-group gap8">
                        <p className="title">51점 - 100점</p>
                        <p className="txt">심한 분노를 경험하고 있는 상태</p>
                      </div>
                    </div>
                  </div>
                </div>
                <ResultScoreTextWrap
                  scoreType={Ref7ScoreType}
                  DetailText={Ref7selectDetailText}
                  isDigital={false}
                />
              </div>
            </div>
            {resultPageData.anger.addition && (
              <SubInfoText subText={Ref7PagesubText} />
            )}
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">8</div>
                <h3 className="section-title">화에 대처하는 방법</h3>
              </div>
              <div className="step-item-list border relative">
                <img src={images.anger} alt="" className="anger-img" />
                <div className="step-item border-dash col-group gap24">
                  <div className="step-item-num">01</div>
                  <div className="step-item-txt-wrap row-group gap8">
                    <p className="step-item-sub-title">
                      자기 자신과 대화 나누며 나의 생각을 살펴보는 연습하기
                    </p>
                    <p className="step-item-txt">
                      ‘내가 기대한 것은 무엇이었나’, ‘나의 기대는 정당한
                      것이었나’ 등을 생각해보기
                    </p>
                  </div>
                </div>
                <div className="step-item border-dash col-group gap24">
                  <div className="step-item-num">02</div>
                  <div className="step-item-txt-wrap">
                    <p className="step-item-sub-title">
                      상황을 객관적으로 생각하고, 구체적으로 표현하기
                    </p>
                  </div>
                </div>
                <div className="step-item border-dash col-group gap24">
                  <div className="step-item-num">03</div>
                  <div className="step-item-txt-wrap">
                    <p className="step-item-sub-title">
                      생각한 뒤에 말하거나, 진정된 후에 표현하기
                    </p>
                  </div>
                </div>
                <div className="step-item border-dash col-group gap24">
                  <div className="step-item-num">04</div>
                  <div className="step-item-txt-wrap row-group gap8">
                    <p className="step-item-sub-title">
                      화내는 모습을 거울로 들여다 보거나, 화 내는 말투와
                      목소리를 녹음해서 들어보기
                    </p>
                    <p className="step-item-txt">
                      모습과 목소리를 관찰할 수 있도록, 자주 갈등하는 상대에게
                      부탁을 해도 좋습니다.
                    </p>
                  </div>
                </div>
                <div className="step-item border-dash col-group gap24">
                  <div className="step-item-num">04</div>
                  <div className="step-item-txt-wrap row-group gap8">
                    <p className="step-item-sub-title">
                      분노에 반응하는 자신만의 자동반응체계 바꾸기
                    </p>
                    <p className="step-item-txt green">
                      (자신만의 반응체계를 구체적으로 떠올리며 적어봅니다. 본
                      결과지의 인생점수 결과 가운데 &lt;4. Reformation
                      개선하기&gt; 부분 참고)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[10]}>
        <main className="main">
          <p className="page-num">11</p>
          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">9</div>
                <h3 className="section-title">폭발 순간 긴급조치</h3>
              </div>
              <div className="row-group gap40">
                <div className="row-group gap8">
                  <p className="default-txt default-txt-bold">
                    1 Step - Stop 전략
                  </p>
                  <p className="default-title green">
                    Time-out 일단 그 상황에서 벗어나기
                  </p>
                </div>
                <div className="col-group gap40">
                  <div className="row-group gap24">
                    <img src={images.measure_item_01} alt="" className="img" />
                    <div className="step-item col-group gap24">
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap">
                        <p className="step-item-sub-title">물리적 거리두기</p>
                        <p className="step-item-txt">
                          다른 방, 혹은 밖으로 나가 그 상황에서 벗어나세요.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="row-group gap24">
                    <img src={images.measure_item_02} alt="" className="img" />
                    <div className="step-item col-group gap24">
                      <div className="step-item-num">02</div>
                      <div className="step-item-txt-wrap">
                        <p className="step-item-sub-title">심리적 시간 벌기</p>
                        <p className="step-item-txt">
                          숫자를 세거나 심호흡을 해 보세요. 물을 마시는 방법도
                          좋습니다.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="section">
              <div className="row-group gap40">
                <div className="row-group gap8">
                  <p className="default-txt default-txt-bold">
                    2 Step - Think 전략
                  </p>
                  <p className="default-title green">감정 정리하고 이성 찾기</p>
                </div>
                <div className="col-group gap40">
                  <div className="row-group gap24">
                    <img src={images.measure_item_03} alt="" className="img" />
                    <div className="step-item col-group gap24">
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap">
                        <p className="step-item-sub-title">
                          자신과의 공감 대화
                        </p>
                        <p className="step-item-txt">
                          왜 화가 나는지, 내가 바라는 것은 무엇인지 생각해
                          보세요.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="row-group gap24">
                    <img src={images.measure_item_04} alt="" className="img" />
                    <div className="step-item col-group gap24">
                      <div className="step-item-num">02</div>
                      <div className="step-item-txt-wrap">
                        <p className="step-item-sub-title">상대와 공감</p>
                        <p className="step-item-txt">
                          상대방의 상태나 왜 그렇게 행동하는지 생각해 보세요.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="section">
              <div className="row-group gap40">
                <div className="row-group gap8">
                  <p className="default-txt default-txt-bold">
                    3 Step - Choose 전략
                  </p>
                  <p className="default-title green">
                    어떻게 해야 할까? 여러 가지 대안 찾기
                  </p>
                </div>
                <div className="row-group gap24">
                  <img src={images.measure_item_05} alt="" className="img" />
                  <div className="col-group sp-bt">
                    <div className="step-item col-group gap24">
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap">
                        <p className="step-item-sub-title">
                          객관적이고 구체적으로 말하기
                        </p>
                      </div>
                    </div>
                    <div className="step-item col-group gap24">
                      <div className="step-item-num">02</div>
                      <div className="step-item-txt-wrap">
                        <p className="step-item-sub-title">
                          천천히 솔직하게 표현하기
                        </p>
                      </div>
                    </div>
                    <div className="step-item col-group gap24">
                      <div className="step-item-num">03</div>
                      <div className="step-item-txt-wrap">
                        <p className="step-item-sub-title">
                          원하는 것을 직접 말로 부탁하기
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[11]}>
        <main className="main">
          <p className="page-num">12</p>
          <div className="main-title-wrap col-group">
            <div className="num dan green">02</div>
            <div className="main-title-group row-group">
              <h2 className="main-title">감정상태 - 내 몸과 마음의 연결</h2>
              <p className="main-sub-title">
                02. 디지털 의존도 / 스트레스 정도 / 번아웃 증후군
              </p>
            </div>
          </div>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">1</div>
                <h3 className="section-title">디지털 의존도</h3>
              </div>
              <div className="result-score-container col-group gap40">
                <div className={`result-score-wrap ${Ref9ScoreType}`}>
                  <div className="result-score-title-wrap col-group sp-bt">
                    <p className="result-score-title">디지털 의존도 결과</p>
                    <div className="result-score-num">
                      <strong>{resultPageData.digital}</strong>점
                    </div>
                  </div>
                  <div className="result-score">
                    <div className="result-score-gauge-wrap">
                      <div
                        className="result-score-gauge"
                        style={{ width: `${resultPageData.digital}%` }}
                      ></div>
                    </div>
                    <div className="line" style={{ left: "25%" }}></div>
                    <div className="line" style={{ left: "50%" }}></div>
                  </div>
                  <div className="result-score-list row-group">
                    <div className="result-score-item col-group gap24">
                      <div className="icon"></div>
                      <div className="txt-group row-group gap8">
                        <p className="title">0점 - 30점</p>
                        <p className="txt">
                          일반적인 수준의 디지털 의존도를 보이고 있는 상태
                        </p>
                      </div>
                    </div>
                    <div className="result-score-item col-group gap24">
                      <div className="icon"></div>
                      <div className="txt-group row-group gap8">
                        <p className="title">26점 - 50점</p>
                        <p className="txt">
                          잠재적 위험군 수준의 디지털 의존도를 보이고 있는 상태
                        </p>
                      </div>
                    </div>
                    <div className="result-score-item col-group gap24">
                      <div className="icon"></div>
                      <div className="txt-group row-group gap8">
                        <p className="title">51점 이상</p>
                        <p className="txt">
                          고위험군 수준의 디지털 의존도를 보이고 있는 상태
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <ResultScoreTextWrap
                  scoreType={Ref9ScoreType}
                  DetailText={Ref9selectDetailText}
                  isDigital={true}
                />
              </div>
            </div>
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">2</div>
                <h3 className="section-title">스트레스</h3>
              </div>
              <div className="result-score-container col-group gap40">
                <div className={`result-score-wrap ${Ref9StressScoreType}`}>
                  <div className="result-score-title-wrap col-group sp-bt">
                    <p className="result-score-title">스트레스 정도 결과</p>
                    <div className="result-score-num">
                      <strong>{resultPageData.stress}</strong>점
                    </div>
                  </div>
                  <div className="result-score">
                    <div className="result-score-gauge-wrap">
                      <div
                        className="result-score-gauge"
                        style={{ width: `${resultPageData.stress}%` }}
                      ></div>
                    </div>
                    <div className="line" style={{ left: "35%" }}></div>
                    <div className="line" style={{ left: "50%" }}></div>
                    <div className="line" style={{ left: "70%" }}></div>
                  </div>
                  <div className="result-score-list row-group">
                    <div className="result-score-item col-group gap24">
                      <div className="icon"></div>
                      <div className="txt-group row-group gap8">
                        <p className="title">0점 - 35점</p>
                        <p className="txt">
                          일반적인 수준의 스트레스를 경험하고 있는 상태
                        </p>
                      </div>
                    </div>
                    <div className="result-score-item col-group gap24">
                      <div className="icon"></div>
                      <div className="txt-group row-group gap8">
                        <p className="title">36점 - 50점</p>
                        <p className="txt">
                          초기단계 수준의 스트레스를 경험하고 있는 상태
                        </p>
                      </div>
                    </div>
                    <div className="result-score-item col-group gap24">
                      <div className="icon"></div>
                      <div className="txt-group row-group gap8">
                        <p className="title">51점 - 70점</p>
                        <p className="txt">
                          중증 수준의 스트레스를 경험하고 있는 상태
                        </p>
                      </div>
                    </div>
                    <div className="result-score-item col-group gap24">
                      <div className="icon"></div>
                      <div className="txt-group row-group gap8">
                        <p className="title">71점 이상</p>
                        <p className="txt">
                          매우 심각한 수준의 스트레스를 경험하고 있는 상태
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <ResultScoreTextWrap
                  scoreType={Ref9StressScoreType}
                  DetailText={Ref9StressSelectDetailText}
                  isDigital={true}
                />
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[12]}>
        <main className="main">
          <p className="page-num">13</p>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">3</div>
                <h3 className="section-title">번아웃 증후군</h3>
              </div>
              <div className="row-group gap56">
                <div className="row-group gap32">
                  <p className="default-title">
                    <span className="green">번아웃 증후군</span>이란,
                  </p>
                  <p className="default-txt">
                    <strong>'번아웃(burnout: 극도의 피로, 연료 소진)'</strong>과{" "}
                    <strong>'증후군(Syndrome)'</strong>의 합성어로, <br />
                    어떤 일에 몰두하여 모든 에너지를 쏟은 사람이 극도의 피로를
                    느끼며 일상생활에서 무기력한 상태가 지속되는 것을 말합니다.
                  </p>
                </div>
                <div className="result-score-container col-group gap40">
                  <div className={`result-score-wrap ${Ref10ScoreType}`}>
                    <div className="result-score-title-wrap col-group sp-bt">
                      <p className="result-score-title">번아웃 증후군 결과</p>
                      <div className="result-score-num">
                        <strong>{resultPageData.burnout}</strong>점
                      </div>
                    </div>
                    <div className="result-score">
                      <div className="result-score-gauge-wrap">
                        <div
                          className="result-score-gauge"
                          style={{ width: `${resultPageData.burnout}%` }}
                        ></div>
                      </div>
                      <div className="line" style={{ left: "15%" }}></div>
                      <div className="line" style={{ left: "35%" }}></div>
                      <div className="line" style={{ left: "60%" }}></div>
                    </div>
                    <div className="result-score-list row-group">
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">0점 - 15점</p>
                          <p className="txt">
                            일반적인 수준의 업무집중도를 보이고 있는 상태
                          </p>
                        </div>
                      </div>
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">16점 - 35점</p>
                          <p className="txt">
                            초기단계 수준의 번아웃 증후군을 보이고 있는 상태
                          </p>
                        </div>
                      </div>
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">36점 - 60점</p>
                          <p className="txt">
                            중증 수준의 번아웃 증후군을 보이고 있는 상태
                          </p>
                        </div>
                      </div>
                      <div className="result-score-item col-group gap24">
                        <div className="icon"></div>
                        <div className="txt-group row-group gap8">
                          <p className="title">61점 이상</p>
                          <p className="txt">
                            매우 심각한 수준의 번아웃 증후군을 보이고 있는 상태
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <ResultScoreTextWrap
                    scoreType={Ref10ScoreType}
                    DetailText={Ref10StressSelectDetailText}
                    isDigital={false}
                  />
                </div>
              </div>
            </div>
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">4</div>
                <h3 className="section-title">
                  일반적 스트레스와 번아웃 증후군의 차이
                </h3>
              </div>
              <div className="green-border-box col-group gap40">
                <div className="compare-group row-group gap40">
                  <div className="compare-title">
                    <p className="default-title green">스트레스 (Stress)</p>
                    <img
                      src={images.compare_title_item_01}
                      alt=""
                      className="img"
                    />
                  </div>
                  <div className="step-item-list">
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap row-group gap8">
                        <p className="step-item-sub-title">
                          지나치게 일에 몰입해서 생긴다.
                        </p>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">02</div>
                      <div className="step-item-txt-wrap">
                        <p className="step-item-sub-title">
                          감정 반응이 격해진다.
                        </p>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">03</div>
                      <div className="step-item-txt-wrap">
                        <p className="step-item-sub-title">
                          행동이 많아지거나 급해진다.
                        </p>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">04</div>
                      <div className="step-item-txt-wrap row-group gap8">
                        <p className="step-item-sub-title">
                          에너지가 점차 소진된다.
                        </p>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">05</div>
                      <div className="step-item-txt-wrap row-group gap8">
                        <p className="step-item-sub-title">
                          불안에 이를 가능성이 높다.
                        </p>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">06</div>
                      <div className="step-item-txt-wrap row-group gap8">
                        <p className="step-item-sub-title">
                          신체 증상(두통, 소화불량 등)이 심해진다.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="compare-group row-group gap40">
                  <div className="compare-title">
                    <p className="default-title green">번아웃(Burnout)</p>
                    <img
                      src={images.compare_title_item_02}
                      alt=""
                      className="img"
                    />
                  </div>
                  <div className="step-item-list">
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap row-group gap8">
                        <p className="step-item-sub-title">
                          일에 몰입할 수가 없다.
                        </p>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">02</div>
                      <div className="step-item-txt-wrap">
                        <p className="step-item-sub-title">
                          감정 반응이 둔해진다.
                        </p>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">03</div>
                      <div className="step-item-txt-wrap">
                        <p className="step-item-sub-title">
                          좌절감이나 무기력으로 행동이 줄어든다.
                        </p>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">04</div>
                      <div className="step-item-txt-wrap row-group gap8">
                        <p className="step-item-sub-title">
                          의욕, 희망, 생각이 사라진다.
                        </p>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">05</div>
                      <div className="step-item-txt-wrap row-group gap8">
                        <p className="step-item-sub-title">
                          혼자라는 생각이나 우울감에 빠지기 쉽다.
                        </p>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">06</div>
                      <div className="step-item-txt-wrap row-group gap8">
                        <p className="step-item-sub-title">
                          정서적 증상(우울감, 무기력 등)이 심해진다.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[13]}>
        <main className="main">
          <p className="page-num">14</p>
          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">5</div>
                <h3 className="section-title">번아웃 증후군 대처법</h3>
              </div>
              <div className="row-group gap40">
                <p className="default-title">
                  1. 혼자 고민하지 말고 주변 사람에게 상담하기
                </p>
                <div className="row-group gap32">
                  <img src={images.measure_item_06} alt="" className="img" />
                  <p className="default-txt default-txt-l">
                    해결하기 위해서는 자신의 현재 상태를 제대로 인지하는 것이
                    중요합니다. 이미 우울감, 자기혐오에 빠져있는 상태이기 때문에
                    혼자 해결하려고 하기보다는 친구나 가족, 회사 사람들에게
                    솔직하게 털어놓고 상담하는 것이 좋습니다.
                  </p>
                </div>
              </div>
            </div>
            <div className="section">
              <div className="row-group gap40">
                <p className="default-title">
                  2. 실현 가능한 목표를 세우고 일의 강도 줄이기
                </p>
                <div className="row-group gap32">
                  <img src={images.measure_item_07} alt="" className="img" />
                  <p className="default-txt default-txt-l">
                    과한 목표를 세우거나, 할 수 없는 부분까지 일을 맡기 보다는
                    실현 가능한 목표를 세우고 일의 강도를 줄이면서 여유를 가지는
                    것이 좋습니다. 일에 대한 집착과 완벽주의를 버리고 우선순위를
                    정해 중요한 것부터 해결해나가도록 해야 합니다.
                  </p>
                </div>
              </div>
            </div>
            <div className="section">
              <div className="row-group gap40">
                <p className="default-title">
                  3. 운동, 취미생활 등 휴식 시간 갖기
                </p>
                <div className="row-group gap32">
                  <img src={images.measure_item_08} alt="" className="img" />
                  <p className="default-txt default-txt-l">
                    스트레스를 풀 수 있는 방법을 찾아 충분한 휴식을 하는 것이
                    중요합니다. 일주일에 하루 이상은 업무에서 완전히 벗어나
                    자신만의 시간을 갖는 것을 습관화 하는 것이 좋습니다. 또,
                    자신을 재정비 할 수 있도록 휴가를 내는 것도 중요합니다. 휴가
                    시간을 통해 일과 직장 사람들과의 거리를 두고 세상을 바라보며
                    주의를 환기하고 스스로의 마음을 돌아보는 시간을 갖는 것이
                    좋습니다.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[14]}>
        <main className="main">
          <p className="page-num">15</p>
          <div className="main-title-wrap col-group">
            <div className="num dan green">03</div>
            <div className="main-title-group row-group">
              <h2 className="main-title">융합역량지능</h2>
              <p className="main-sub-title">01. 강점 영역 / 약점 보완 영역</p>
            </div>
          </div>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">1</div>
                <h3 className="section-title">융합역량지능 결과</h3>
              </div>
              <div className="row-group gap40">
                <p className="default-txt">
                  하워드 가드너(Howard Gardner) 박사의 다중지능 이론을 근거로
                  하여 개인의 다차원적 지능을 측정하고, 새로운 가치를 창조적으로
                  발견하는 잠재적 역량을 파악합니다. 이 지능은, 본 검사의
                  최종결과수치인 융합역량지수(Meta-Q)의 분석지표 가운데 하나에
                  해당합니다.
                </p>
                <div className="meta-q-guide col-group">
                  <div className="meta-q-guide-item row-group gap8">
                    <p className="title">0점 - 10점</p>
                    <p className="txt">매우 낮음</p>
                  </div>
                  <div className="meta-q-guide-item row-group gap8">
                    <p className="title">11점 - 30점</p>
                    <p className="txt">낮음</p>
                  </div>
                  <div className="meta-q-guide-item row-group gap8">
                    <p className="title">31점 - 50점</p>
                    <p className="txt">보통</p>
                  </div>
                  <div className="meta-q-guide-item row-group gap8">
                    <p className="title">51점 - 80점</p>
                    <p className="txt">약간 높음</p>
                  </div>
                  <div className="meta-q-guide-item row-group gap8">
                    <p className="title">81점 - 90점</p>
                    <p className="txt">높음</p>
                  </div>
                  <div className="meta-q-guide-item row-group gap8">
                    <p className="title">91점 이상</p>
                    <p className="txt">매우 높음</p>
                  </div>
                </div>
                <div className="meta-q-chart-wrap">
                  <div className="chart-num-wrap row-group">
                    <p className="chart-num">100</p>
                    <p className="chart-num">50</p>
                    <p className="chart-num">0</p>
                  </div>
                  <div className="chart-graph-wrap">
                    <div
                      className={`
                                   chart-graph-item 
                                   ${
                                     ref12TopThree.some(
                                       (item) => item.name === "언어"
                                     )
                                       ? "green"
                                       : ""
                                   }
                                   ${
                                     ref12BottomTwo.some(
                                       (item) => item.name === "언어"
                                     )
                                       ? "red"
                                       : ""
                                   }
                                `}
                    >
                      <div className="chart-gauge">
                        <div
                          className="chart-gauge-core"
                          style={{ height: `${resultPageData.language}%` }}
                        >
                          <div className="chart-gauge-num">
                            {resultPageData.language}
                          </div>
                        </div>
                      </div>
                      <div className="chart-txt-group row-group gap8">
                        <div className="meta-q-icon lang">
                          <img
                            src={
                              isLangTopThree
                                ? images.icon_lang_green.default
                                : isLangBottomTwo
                                ? images.icon_lang_red.default
                                : images.icon_lang.default
                            }
                            alt=""
                          />
                        </div>
                        <p className="txt">언어</p>
                      </div>
                    </div>
                    <div
                      className={`
                                   chart-graph-item 
                                   ${
                                     ref12TopThree.some(
                                       (item) => item.name === "논리수학"
                                     )
                                       ? "green"
                                       : ""
                                   }
                                   ${
                                     ref12BottomTwo.some(
                                       (item) => item.name === "논리수학"
                                     )
                                       ? "red"
                                       : ""
                                   }
                                `}
                    >
                      <div className="chart-gauge">
                        <div
                          className="chart-gauge-core"
                          style={{ height: `${resultPageData.math}%` }}
                        >
                          <div className="chart-gauge-num">
                            {resultPageData.math}
                          </div>
                        </div>
                      </div>
                      <div className="chart-txt-group row-group gap8">
                        <div className="meta-q-icon logic">
                          <img
                            src={
                              isMathTopThree
                                ? images.icon_logic_green.default
                                : isMathBottomTwo
                                ? images.icon_logic_red.default
                                : images.icon_logic.default
                            }
                            alt=""
                          />
                        </div>
                        <p className="txt">논리수학</p>
                      </div>
                    </div>
                    <div
                      className={`
                                   chart-graph-item 
                                   ${
                                     ref12TopThree.some(
                                       (item) => item.name === "시간·공간"
                                     )
                                       ? "green"
                                       : ""
                                   }
                                   ${
                                     ref12BottomTwo.some(
                                       (item) => item.name === "시간·공간"
                                     )
                                       ? "red"
                                       : ""
                                   }
                                `}
                    >
                      <div className="chart-gauge">
                        <div
                          className="chart-gauge-core"
                          style={{ height: `${resultPageData.time}%` }}
                        >
                          <div className="chart-gauge-num">
                            {resultPageData.time}
                          </div>
                        </div>
                      </div>
                      <div className="chart-txt-group row-group gap8">
                        <div className="meta-q-icon space">
                          <img
                            src={
                              isTimeTopThree
                                ? images.icon_space_green.default
                                : isTimeBottomTwo
                                ? images.icon_space_red.default
                                : images.icon_space.default
                            }
                            alt=""
                          />
                        </div>
                        <p className="txt">시간·공간</p>
                      </div>
                    </div>
                    <div
                      className={`
                                   chart-graph-item 
                                   ${
                                     ref12TopThree.some(
                                       (item) => item.name === "신체운동감각"
                                     )
                                       ? "green"
                                       : ""
                                   }
                                   ${
                                     ref12BottomTwo.some(
                                       (item) => item.name === "신체운동감각"
                                     )
                                       ? "red"
                                       : ""
                                   }
                                `}
                    >
                      <div className="chart-gauge">
                        <div
                          className="chart-gauge-core"
                          style={{ height: `${resultPageData.body}%` }}
                        >
                          <div className="chart-gauge-num">
                            {resultPageData.body}
                          </div>
                        </div>
                      </div>
                      <div className="chart-txt-group row-group gap8">
                        <div className="meta-q-icon physical">
                          <img
                            src={
                              isBodyTopThree
                                ? images.icon_physical_green.default
                                : isBodyBottomTwo
                                ? images.icon_physical_red.default
                                : images.icon_physical.default
                            }
                            alt=""
                          />
                        </div>
                        <p className="txt">신체운동감각</p>
                      </div>
                    </div>
                    <div
                      className={`
                                   chart-graph-item 
                                    ${
                                      ref12TopThree.some(
                                        (item) => item.name === "음악"
                                      )
                                        ? "green"
                                        : ""
                                    }
                                    ${
                                      ref12BottomTwo.some(
                                        (item) => item.name === "음악"
                                      )
                                        ? "red"
                                        : ""
                                    }
                                `}
                    >
                      <div className="chart-gauge">
                        <div
                          className="chart-gauge-core"
                          style={{ height: `${resultPageData.music}%` }}
                        >
                          <div className="chart-gauge-num">
                            {resultPageData.music}
                          </div>
                        </div>
                      </div>
                      <div className="chart-txt-group row-group gap8">
                        <div className="meta-q-icon music">
                          <img
                            src={
                              isMusicTopThree
                                ? images.icon_music_green.default
                                : isMusicBottomTwo
                                ? images.icon_music_red.default
                                : images.icon_music.default
                            }
                            alt=""
                          />
                        </div>
                        <p className="txt">음악</p>
                      </div>
                    </div>
                    <div
                      className={`
                                   chart-graph-item 
                                   ${
                                     ref12TopThree.some(
                                       (item) => item.name === "대인관계"
                                     )
                                       ? "green"
                                       : ""
                                   }
                                   ${
                                     ref12BottomTwo.some(
                                       (item) => item.name === "대인관계"
                                     )
                                       ? "red"
                                       : ""
                                   }
                                `}
                    >
                      <div className="chart-gauge">
                        <div
                          className="chart-gauge-core"
                          style={{ height: `${resultPageData.relationship}%` }}
                        >
                          <div className="chart-gauge-num">
                            {resultPageData.relationship}
                          </div>
                        </div>
                      </div>
                      <div className="chart-txt-group row-group gap8">
                        <div className="meta-q-icon friendly">
                          <img
                            src={
                              isRelationTopThree
                                ? images.icon_friendly_green.default
                                : isRelationBottomTwo
                                ? images.icon_friendly_red.default
                                : images.icon_friendly.default
                            }
                            alt=""
                          />
                        </div>
                        <p className="txt">대인관계</p>
                      </div>
                    </div>
                    <div
                      className={`
                                   chart-graph-item 
                                   ${
                                     ref12TopThree.some(
                                       (item) => item.name === "자기성찰"
                                     )
                                       ? "green"
                                       : ""
                                   }
                                   ${
                                     ref12BottomTwo.some(
                                       (item) => item.name === "자기성찰"
                                     )
                                       ? "red"
                                       : ""
                                   }
                                `}
                    >
                      <div className="chart-gauge">
                        <div
                          className="chart-gauge-core"
                          style={{ height: `${resultPageData.self}%` }}
                        >
                          <div className="chart-gauge-num">
                            {resultPageData.self}
                          </div>
                        </div>
                      </div>
                      <div className="chart-txt-group row-group gap8">
                        <div className="meta-q-icon personal">
                          <img
                            src={
                              isSelfTopThree
                                ? images.icon_personal_green.default
                                : isSelfBottomTwo
                                ? images.icon_personal_red.default
                                : images.icon_personal.default
                            }
                            alt=""
                          />
                        </div>
                        <p className="txt">자기성찰</p>
                      </div>
                    </div>
                    <div
                      className={`
                                   chart-graph-item 
                                   ${
                                     ref12TopThree.some(
                                       (item) => item.name === "자연"
                                     )
                                       ? "green"
                                       : ""
                                   }
                                   ${
                                     ref12BottomTwo.some(
                                       (item) => item.name === "자연"
                                     )
                                       ? "red"
                                       : ""
                                   }
                                `}
                    >
                      <div className="chart-gauge">
                        <div
                          className="chart-gauge-core"
                          style={{ height: `${resultPageData.nature}%` }}
                        >
                          <div className="chart-gauge-num">
                            {resultPageData.nature}
                          </div>
                        </div>
                      </div>
                      <div className="chart-txt-group row-group gap8">
                        <div className="meta-q-icon nature">
                          <img
                            src={
                              isNatureTopThree
                                ? images.icon_nature_green.default
                                : isNatureBottomTwo
                                ? images.icon_nature_red.default
                                : images.icon_nature.default
                            }
                            alt=""
                          />
                        </div>
                        <p className="txt">자연</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="section">
              <div className="meta-q-result-group col-group gap40">
                <div className="row-group gap32">
                  <p className="default-title green">강점 영역</p>
                  <p className="default-txt">
                    자신이 잘 수행하고 자신있어 하는 영역이므로 이를 정확히
                    인지하고 꾸준하게 성장, 발달 시키는 것이 좋습니다.
                  </p>
                  <div className="step-item-list border">
                    {ref12TopThree.map((item, index) => (
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">0{index + 1}</div>
                        <div className="step-item-txt-wrap row-group gap8">
                          <p className="step-item-sub-title"> {item.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="row-group gap32">
                  <p className="default-title red">약점 보완 영역</p>
                  <p className="default-txt">
                    자신이 어려워하고 그 능력개발에 있어서도 소홀했을 가능성이
                    높은 영역이므로 이 부분을 보완하는 것이 필요합니다. 특히,
                    그동안 신경쓰지 않았던 약점영역을 새롭게 개발하여 강점영역과
                    함께 기능할 수 있도록 노력한다면 보다 통합적인 균형발달을
                    이룰 수 있고, 융합지능 상승에도 영향을 줄 수 있습니다.
                  </p>
                  <div className="step-item-list border red">
                    {ref12BottomTwo.map((item, index) => (
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">0{index + 1}</div>
                        <div className="step-item-txt-wrap row-group gap8">
                          <p className="step-item-sub-title">{item.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[15]}>
        <main className="main">
          <p className="page-num">16</p>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">2</div>
                <h3 className="section-title">강점영역</h3>
              </div>
              <div className="row-group gap24">
                {ref12TopThree.map((item, index) => (
                  <div className="green-border-box row-group gap32" key={index}>
                    <p className="default-title green col-group gap24 al-ce">
                      <img
                        data-test={item.img}
                        src={images[item.img + "_green"].default}
                        alt=""
                        className="icon"
                      />
                      {item.name}
                    </p>
                    <p
                      className="default-txt"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          ref13Data[item.dataName].desc
                        ),
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">3</div>
                <h3 className="section-title">약점 보완 영역</h3>
              </div>
              <div className="row-group gap24">
                {ref12BottomTwo.map((item, index) => (
                  <div className="red-border-box row-group gap32" key={index}>
                    <p className="default-title red col-group gap24 al-ce">
                      <img
                        src={images[item.img + "_red"].default}
                        alt=""
                        className="icon"
                      />
                      {item.name}
                    </p>
                    <p
                      className="default-txt"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          ref13Data[item.dataName].desc
                        ),
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common has-chart" ref={printPageRefs[16]}>
        <main className="main">
          <p className="page-num">17</p>
          <div className="main-title-wrap col-group">
            <div className="num dan green">03</div>
            <div className="main-title-group row-group">
              <h2 className="main-title">융합역량지능</h2>
              <p className="main-sub-title">
                02. 다각형 분석 / 03. 전체비율 분포 분석
              </p>
            </div>
          </div>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">1</div>
                <h3 className="section-title">다각형 분석</h3>
              </div>
              <div className="row-group gap40">
                <p className="default-txt">
                  자신의 각 지능이 영역별로 어떻게 발달되어 있는지를 다각형으로
                  표시하여 그 분포 정도를 파악해봅니다. 다각형 분포도는 각
                  영역에 대한 절대적인 평가뿐만 아니라, 개인 내적으로 영역별
                  상대적 분포를 평가할 수 있습니다.
                </p>
                <div className="step-item-list border">
                  <div className="step-item border-dash col-group gap24">
                    <div className="step-item-num">01</div>
                    <div className="step-item-txt-wrap row-group gap16">
                      <p className="default-title green">다각형이 고른 형태</p>
                      <p className="default-txt">
                        전 영역의 점수가 비슷하게 형성되어 고른 발달 분포를
                        보이는 형태입니다. 전체적으로 넓은 형태로서 각각의
                        점수가 높으면, 우수한 발달 역량을 보유하고 있는 경우일
                        수도 있으나 상대적으로 두각을 나타내는 강점역량이
                        형성되지 않은 경우일 수도 있습니다. 점수를 매기는 기준은
                        개인마다 차이가 있으므로 다각형이 넓고 고르게 분포되어
                        있다고 하여 다른 사람들에 비해 평균적으로 우수하다는
                        의미는 아니라는 점을 기억해야 합니다.
                      </p>
                    </div>
                  </div>
                  <div className="step-item border-dash col-group gap24">
                    <div className="step-item-num">02</div>
                    <div className="step-item-txt-wrap row-group gap16">
                      <p className="default-title green">다각형이 작은 형태</p>
                      <p className="default-txt">
                        전체적으로 좁은 형태로서 상대적으로 강점역량과
                        약점역량이 뚜렷하게 구분되지 않는 경우로 각 영역이 고른
                        발달을 이루지 못한 경우일 수 있습니다. 점수를 매기는
                        기준은 개인마다 차이가 있으므로 다각형이 좁게 분포되어
                        있다고 하여 다른 사람들에 비해 평균적으로 미숙하다는
                        의미는 아니라는 점을 기억해야 합니다.
                      </p>
                    </div>
                  </div>
                  <div className="step-item border-dash col-group gap24">
                    <div className="step-item-num">03</div>
                    <div className="step-item-txt-wrap row-group gap16">
                      <p className="default-title green">
                        다각형이 한쪽 방향으로 쏠려있거나, 양쪽 방향으로
                        퍼져있는 형태
                      </p>
                      <p className="default-txt">
                        특정 영역에서 우수한 발달 역량을 보유하고 있는 경우일 수
                        있습니다.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">2</div>
                <h3 className="section-title">전체비율 분포 분석</h3>
              </div>
              <div className="row-group gap40">
                <p className="default-txt">
                  전체 영역을 상대적 비율로 나타낸 표입니다. 자신의 각 지능이
                  영역별로 어떻게 발달되어 있는지를 영역비율로 확인해볼 수
                  있습니다.
                </p>
                <div className="col-group gap40">
                  <div className="row-group gap24 half-div">
                    <p className="default-title green">전체 비율 분석 그래프</p>
                    <div className="green-border-box">
                      <div className="meta-q-chart">
                        <Radar data={data} options={options} />
                      </div>
                    </div>
                  </div>
                  <div className="row-group gap24 half-div">
                    <p className="default-title green">전체 분포 그래프</p>
                    <div className="green-border-box">
                      <div id="chart_2" className="circle-chart">
                        <svg ref={chartRef} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[17]}>
        <main className="main">
          <p className="page-num">18</p>
          <div className="main-title-wrap col-group">
            <div className="num dan green">04</div>
            <div className="main-title-group row-group">
              <h2 className="main-title">에너지 역량 및 방향</h2>
              <p className="main-sub-title">
                01. 에너지 역량 / 에너지의 균형 분포
              </p>
            </div>
          </div>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">1</div>
                <h3 className="section-title">에너지 역량</h3>
              </div>
              <div className="row-group gap40">
                <p className="default-txt">
                  당신은 살아가면서 수많은 사람을 만나고 다양한 사건을
                  경험하면서 고유한 존재 방식을 만들어 냈습니다. 이렇게 형성된
                  성격(personality)은 당신이 한 명의 고유한 개인으로서 어떤
                  존재인지를 설명해줍니다. 성격은 당신이 하는 말, 자신에 대한
                  감정, 행동 양식 및 의사결정 방식에 반영됩니다. 성격적 특성을
                  잘 이해하면 자신의 행동, 감정 및 타인과의 관계에 대해 더 잘
                  이해하고 지혜롭게 살아갈 수 있도록 도와줍니다. <br />
                  <br />
                  사람들은 주로 다음의 4가지 차원들 가운데 한 가지 측면을 더욱
                  선호합니다. <br />
                  <br />
                  무엇을 선호하는지 지표를 파악하는 것은 당신의 성격 특성을
                  분석해 나가는 과정에서 중요한 이해의 시작입니다. 하나의 측면
                  또는 다른 측면을 선호하는 것이 옳거나 그른 것 또는 좋거나 나쁜
                  것이 아니라는 점을 기억하세요. 오히려 이러한 선호도는 자신에게
                  가장 자연스러운 행동 방식임을 나타냅니다. 우리는 서로 다른
                  차원의 힘이 융합되는 것을 지향합니다. 유년기, 청소년기,
                  청년기를 지나며 선택했던 심리적 선호를 이제는 반대편의
                  에너지와 통합해 나가는 것이 마인드 인사이트의 지향이며, 당신의
                  성장 비결이기 때문입니다.
                </p>
                <p className="default-title green">성격유형의 4가지 차원</p>
                <div className="step-item-list per-type border">
                  <div className="col-group border-dash">
                    <div className="step-item col-group gap24">
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title">
                          <span className="green">폭넓은 W</span>ide
                        </p>
                        <p className="default-txt default-txt-s">
                          에너지의 방향이 외부의 사람과 사물을 향하며 관계의
                          폭이 넓고 다양하다.
                        </p>
                      </div>
                    </div>
                    <div className="col-group gap32 al-ce">
                      <i className="icon-arrow"></i>
                      <p className="default-txt center">
                        <strong>에너지</strong>의 방향이 <br />
                        어디를 향하는가?
                      </p>
                      <i className="icon-arrow"></i>
                    </div>
                    <div className="step-item col-group gap24">
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title">
                          <span className="green">깊이있는 D</span>epth
                        </p>
                        <p className="default-txt default-txt-s">
                          에너지의 방향이 내부를 향하며 사람과 사물을 깊이 있게
                          대하고 평온하다.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-group border-dash">
                    <div className="step-item col-group gap24">
                      <div className="step-item-num">02</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title">
                          <span className="green">실용적 P</span>ractical
                        </p>
                        <p className="default-txt default-txt-s">
                          정보를 있는 그대로 정확하고 상세하게 처리하며
                          실용적이고 세부적으로 인식한다.
                        </p>
                      </div>
                    </div>
                    <div className="col-group gap32 al-ce">
                      <i className="icon-arrow"></i>
                      <p className="default-txt center">
                        <strong>정보를</strong> 어떻게 <br />
                        인식하는가?
                      </p>
                      <i className="icon-arrow"></i>
                    </div>
                    <div className="step-item col-group gap24">
                      <div className="step-item-num">02</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title">
                          <span className="green">통찰적 I</span>nsight
                        </p>
                        <p className="default-txt default-txt-s">
                          정보를 상징적이고 비판적으로 처리하며 예리하게
                          관찰하여 꿰뚫어 인식한다.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-group border-dash">
                    <div className="step-item col-group gap24">
                      <div className="step-item-num">03</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title">
                          <span className="green">논리중심 L</span>ogical
                        </p>
                        <p className="default-txt default-txt-s">
                          논리와 합리성을 따지며 명백한 사실을 중심으로 객관적
                          의사결정을 추구한다.
                        </p>
                      </div>
                    </div>
                    <div className="col-group gap32 al-ce">
                      <i className="icon-arrow"></i>
                      <p className="default-txt center">
                        어떤 방식으로 <br />
                        <strong>의사결정</strong> 하는가?
                      </p>
                      <i className="icon-arrow"></i>
                    </div>
                    <div className="step-item col-group gap24">
                      <div className="step-item-num">03</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title">
                          <span className="green">의미중심 M</span>eaning
                        </p>
                        <p className="default-txt default-txt-s">
                          정서적 공감과 가치공유를 중심으로 감정적 의사결정을
                          추구한다.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-group border-dash">
                    <div className="step-item col-group gap24">
                      <div className="step-item-num">04</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title">
                          <span className="green">체계적 S</span>ystematic
                        </p>
                        <p className="default-txt default-txt-s">
                          계획적이고 규칙적이며 효율을 중심으로 체계적인
                          생활양식을 선호한다.
                        </p>
                      </div>
                    </div>
                    <div className="col-group gap32 al-ce">
                      <i className="icon-arrow"></i>
                      <p className="default-txt center">
                        어떤 <strong>생활양식</strong>을 <br />
                        선호하는가?
                      </p>
                      <i className="icon-arrow"></i>
                    </div>
                    <div className="step-item col-group gap24">
                      <div className="step-item-num">04</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title">
                          <span className="green">포용적</span> in
                          <span className="green">C</span>lusive
                        </p>
                        <p className="default-txt default-txt-s">
                          유연하고 개방적이며 새로움에 열린 태도로 포용적인
                          생활양식을 선호한다.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">2</div>
                <h3 className="section-title">에너지 균형 분포</h3>
              </div>
              <p className="default-txt">
                에너지의 방향은 개인의 선호도에 따라 어느 한쪽으로 쏠리거나 균형
                잡힌 상태일 수 있습니다. 어느 한 측면을 선호하는 것이 맞거나
                틀림, 좋거나 나쁨, 또는 우월하거나 열등한 것이 아니라는 점을
                기억해야 합니다. <br />
                <br />
                그러나 자신이 사용하는 에너지의 배분을 파악하는 일은, 통합적이고
                균형 잡힌 삶을 위한 체크 포인트가 될 수 있습니다.
              </p>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[18]}>
        <main className="main">
          <p className="page-num">19</p>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">3</div>
                <h3 className="section-title">
                  {resultPageData.name}님의 에너지 유형
                </h3>
              </div>
              <div className="energy-result-top-wrap">
                <div className="energy-result-title-wrap">
                  <p className="default-title">
                    {resultPageData.name}님의 에너지 유형
                  </p>
                  <div className="energy-result-type">{energyTypeResult}</div>
                </div>
                <div className="energy-result-top-list row-group">
                  <div
                    className={`energy-result-wrap ${
                      resultPageData.D > resultPageData.W
                        ? "right"
                        : resultPageData.D < resultPageData.W
                        ? "left"
                        : "right left"
                    }`}
                  >
                    {" "}
                    {/* d의 점수가 높으면 right / w의 점수가 높으면 left */}
                    <div className="energy-result-top">
                      <div className="energy-result-top-title left">
                        <div className="num">{resultPageData.W}</div>
                        <p className="title">폭넓은(w)</p>
                      </div>
                      <div className="energy-guage-wrap">
                        <div className="energy-guage left">
                          <div
                            className="energy-guage-bar"
                            style={{ width: `${resultPageData.W}%` }}
                          ></div>
                        </div>
                        <div className="energy-guage right">
                          <div
                            className="energy-guage-bar"
                            style={{ width: `${resultPageData.D}%` }}
                          ></div>
                        </div>
                        <div className="energy-guage-num-group">
                          <p className="energy-guage-num">100</p>
                          <p className="energy-guage-num">50</p>
                          <p className="energy-guage-num">0</p>
                          <p className="energy-guage-num">50</p>
                          <p className="energy-guage-num">100</p>
                        </div>
                      </div>
                      <div className="energy-result-top-title right">
                        <p className="title">깊이 있는(d)</p>
                        <div className="num">{resultPageData.D}</div>
                      </div>
                    </div>
                  </div>
                  <div
                    className={`energy-result-wrap ${
                      resultPageData.I > resultPageData.P
                        ? "right"
                        : resultPageData.I < resultPageData.P
                        ? "left"
                        : "right left"
                    }`}
                  >
                    {" "}
                    {/* i의 점수가 높으면 right / p의 점수가 높으면 left */}
                    <div className="energy-result-top">
                      <div className="energy-result-top-title left">
                        <div className="num">{resultPageData.P}</div>
                        <p className="title">실용적(P)</p>
                      </div>
                      <div className="energy-guage-wrap">
                        <div className="energy-guage left">
                          <div
                            className="energy-guage-bar"
                            style={{ width: `${resultPageData.P}%` }}
                          ></div>
                        </div>
                        <div className="energy-guage right">
                          <div
                            className="energy-guage-bar"
                            style={{ width: `${resultPageData.I}%` }}
                          ></div>
                        </div>
                        <div className="energy-guage-num-group">
                          <p className="energy-guage-num">100</p>
                          <p className="energy-guage-num">50</p>
                          <p className="energy-guage-num">0</p>
                          <p className="energy-guage-num">50</p>
                          <p className="energy-guage-num">100</p>
                        </div>
                      </div>
                      <div className="energy-result-top-title right">
                        <p className="title">통찰적(I)</p>
                        <div className="num">{resultPageData.I}</div>
                      </div>
                    </div>
                  </div>
                  <div
                    className={`energy-result-wrap ${
                      resultPageData.M > resultPageData.L
                        ? "right"
                        : resultPageData.M < resultPageData.L
                        ? "left"
                        : "right left"
                    }`}
                  >
                    {" "}
                    {/* m의 점수가 높으면 right / l의 점수가 높으면 left */}
                    <div className="energy-result-top">
                      <div className="energy-result-top-title left">
                        <div className="num">{resultPageData.L}</div>
                        <p className="title">논리 중심(L)</p>
                      </div>
                      <div className="energy-guage-wrap">
                        <div className="energy-guage left">
                          <div
                            className="energy-guage-bar"
                            style={{ width: `${resultPageData.L}%` }}
                          ></div>
                        </div>
                        <div className="energy-guage right">
                          <div
                            className="energy-guage-bar"
                            style={{ width: `${resultPageData.M}%` }}
                          ></div>
                        </div>
                        <div className="energy-guage-num-group">
                          <p className="energy-guage-num">100</p>
                          <p className="energy-guage-num">50</p>
                          <p className="energy-guage-num">0</p>
                          <p className="energy-guage-num">50</p>
                          <p className="energy-guage-num">100</p>
                        </div>
                      </div>
                      <div className="energy-result-top-title right">
                        <p className="title">의미 중심(M)</p>
                        <div className="num">{resultPageData.M}</div>
                      </div>
                    </div>
                  </div>
                  <div
                    className={`energy-result-wrap ${
                      resultPageData.C > resultPageData.S
                        ? "right"
                        : resultPageData.C < resultPageData.S
                        ? "left"
                        : "right left"
                    }`}
                  >
                    {" "}
                    {/* c의 점수가 높으면 right / s의 점수가 높으면 left */}
                    <div className="energy-result-top">
                      <div className="energy-result-top-title left">
                        <div className="num">{resultPageData.S}</div>
                        <p className="title">체계적(S)</p>
                      </div>
                      <div className="energy-guage-wrap">
                        <div className="energy-guage left">
                          <div
                            className="energy-guage-bar"
                            style={{ width: `${resultPageData.S}%` }}
                          ></div>
                        </div>
                        <div className="energy-guage right">
                          <div
                            className="energy-guage-bar"
                            style={{ width: `${resultPageData.C}%` }}
                          ></div>
                        </div>
                        <div className="energy-guage-num-group">
                          <p className="energy-guage-num">100</p>
                          <p className="energy-guage-num">50</p>
                          <p className="energy-guage-num">0</p>
                          <p className="energy-guage-num">50</p>
                          <p className="energy-guage-num">100</p>
                        </div>
                      </div>
                      <div className="energy-result-top-title right">
                        <p className="title">포용적(C)</p>
                        <div className="num">{resultPageData.C}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="section">
              <div class="personality-type-wrap">
                <img src={images.icon_energy_type_line} alt="" class="line" />
                <div class="personality-type-group col-group">
                  {selectedEnergyTypeArr.map((selectedItem, index) => (
                    <div class="personality-type-item" key={index}>
                      <div class="txt-group row-group">
                        <p class="txt title">
                          <strong>{energyTypeInfo[selectedItem].name}</strong>
                        </p>
                        <p
                          class="txt"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              energyTypeInfo[selectedItem].desc
                            ),
                          }}
                        />
                      </div>
                      <img
                        src={images[energyTypeInfo[selectedItem].img]}
                        alt=""
                        class="img"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="section">
              <div className="row-group gap40">
                <p className="default-title green">에너지 방향</p>
                <div
                  className={`energy-result-wrap ${
                    resultPageData.D > resultPageData.W
                      ? "right"
                      : resultPageData.D < resultPageData.W
                      ? "left"
                      : "right left"
                  }`}
                >
                  {" "}
                  {/* d의 점수가 높으면 right / w의 점수가 높으면 left */}
                  <div className="energy-result-top">
                    <div className="energy-result-top-title left">
                      <div className="num">{resultPageData.W}</div>
                      <p className="title">폭넓은(w)</p>
                    </div>
                    <div className="energy-guage-wrap">
                      <div className="energy-guage left">
                        <div
                          className="energy-guage-bar"
                          style={{ width: `${resultPageData.W}%` }}
                        ></div>
                      </div>
                      <div className="energy-guage right">
                        <div
                          className="energy-guage-bar"
                          style={{ width: `${resultPageData.D}%` }}
                        ></div>
                      </div>
                      <div className="energy-guage-num-group">
                        <p className="energy-guage-num">100</p>
                        <p className="energy-guage-num">50</p>
                        <p className="energy-guage-num">0</p>
                        <p className="energy-guage-num">50</p>
                        <p className="energy-guage-num">100</p>
                      </div>
                    </div>
                    <div className="energy-result-top-title right">
                      <p className="title">깊이 있는(d)</p>
                      <div className="num">{resultPageData.D}</div>
                    </div>
                  </div>
                  <div className="col-group gap40">
                    <div className="step-item border col-group gap24 half-div left">
                      <div className="step-item-num">W</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title">폭넓은</p>
                        <div className="row-group gap8">
                          <p className="default-txt dot">
                            외부의 사람, 사물, 사건에 흥미가 높고 민감하다.
                          </p>
                          <p className="default-txt dot">
                            사교적이고 활동적이다.
                          </p>
                          <p className="default-txt dot">
                            다양한 사람들과 넓은 대인관계를 맺는다.
                          </p>
                          <p className="default-txt dot">
                            여러 사람들과의 상호작용을 즐긴다.
                          </p>
                          <p className="default-txt dot">
                            개방적이고 대담하며 자신을 드러내고 싶어한다.
                          </p>
                          <p className="default-txt dot">
                            깊이 생각하기 보다는 적극적으로 참여한다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border col-group gap24 half-div right">
                      <div className="step-item-num">D</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title">깊이 있는</p>
                        <div className="row-group gap8">
                          <p className="default-txt dot">
                            자신의 생각, 내면세계에 집중한다.
                          </p>
                          <p className="default-txt dot">조용하고 신중하다.</p>
                          <p className="default-txt dot">
                            소수의 사람들과 친밀한 관계를 맺는다.
                          </p>
                          <p className="default-txt dot">
                            자신의 생각이나 감정을 잘 드러내지 않는다.
                          </p>
                          <p className="default-txt dot">
                            자신의 생각을 드러내기 전에 충분히 숙고한다.
                          </p>
                          <p className="default-txt dot">
                            편안한 곳을 좋아하며, 자신을 돌아보는 시간을 갖는다.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[19]}>
        <main className="main">
          <p className="page-num">20</p>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="row-group gap40">
                <p className="default-title green">정보 인식 체계</p>
                <div
                  className={`energy-result-wrap ${
                    resultPageData.I > resultPageData.P
                      ? "right"
                      : resultPageData.I < resultPageData.P
                      ? "left"
                      : "right left"
                  }`}
                >
                  {" "}
                  {/* i의 점수가 높으면 right / p의 점수가 높으면 left */}
                  <div className="energy-result-top">
                    <div className="energy-result-top-title left">
                      <div className="num">{resultPageData.P}</div>
                      <p className="title">실용적(P)</p>
                    </div>
                    <div className="energy-guage-wrap">
                      <div className="energy-guage left">
                        <div
                          className="energy-guage-bar"
                          style={{ width: `${resultPageData.P}%` }}
                        ></div>
                      </div>
                      <div className="energy-guage right">
                        <div
                          className="energy-guage-bar"
                          style={{ width: `${resultPageData.I}%` }}
                        ></div>
                      </div>
                      <div className="energy-guage-num-group">
                        <p className="energy-guage-num">100</p>
                        <p className="energy-guage-num">50</p>
                        <p className="energy-guage-num">0</p>
                        <p className="energy-guage-num">50</p>
                        <p className="energy-guage-num">100</p>
                      </div>
                    </div>
                    <div className="energy-result-top-title right">
                      <p className="title">통찰적(I)</p>
                      <div className="num">{resultPageData.I}</div>
                    </div>
                  </div>
                  <div className="col-group gap40">
                    <div className="step-item border col-group gap24 half-div left">
                      <div className="step-item-num">p</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title">실용적</p>
                        <div className="row-group gap8">
                          <p className="default-txt dot">
                            실제 경험을 중시하며 현재에 집중한다.
                          </p>
                          <p className="default-txt dot">
                            구체적이며 정확하고 검증된 사실에 따라 일을
                            처리한다.
                          </p>
                          <p className="default-txt dot">
                            새로운 시도보다는 기존의 방식을 선호한다.
                          </p>
                          <p className="default-txt dot">
                            예측 가능하고 정해진 패턴을 따른다.
                          </p>
                          <p className="default-txt dot">
                            가능성 보다는 구체적인 목표에 따른다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border col-group gap24 half-div right">
                      <div className="step-item-num">i</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title">통찰적</p>
                        <div className="row-group gap8">
                          <p className="default-txt dot">
                            가능성과 의미를 추구한다.
                          </p>
                          <p className="default-txt dot">
                            혁신적인 아이디어를 고안해내고 적용하는 것을 즐긴다.
                          </p>
                          <p className="default-txt dot">
                            독창적이고 영감을 중요시하며, 미래지향적이다.
                          </p>
                          <p className="default-txt dot">
                            변화에 민감하고 빠르게 적응한다.
                          </p>
                          <p className="default-txt dot">
                            새로운 시도를 즐긴다.
                          </p>
                          <p className="default-txt dot">
                            심도 깊고 포괄적인 주제에 관심을 갖는다.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="section">
              <div className="row-group gap40">
                <p className="default-title green">의사결정 방식</p>
                <div
                  className={`energy-result-wrap ${
                    resultPageData.M > resultPageData.L
                      ? "right"
                      : resultPageData.M < resultPageData.L
                      ? "left"
                      : "right left"
                  }`}
                >
                  {" "}
                  {/* m의 점수가 높으면 right / l의 점수가 높으면 left */}
                  <div className="energy-result-top">
                    <div className="energy-result-top-title left">
                      <div className="num">{resultPageData.L}</div>
                      <p className="title">논리 중심(L)</p>
                    </div>
                    <div className="energy-guage-wrap">
                      <div className="energy-guage left">
                        <div
                          className="energy-guage-bar"
                          style={{ width: `${resultPageData.L}%` }}
                        ></div>
                      </div>
                      <div className="energy-guage right">
                        <div
                          className="energy-guage-bar"
                          style={{ width: `${resultPageData.M}%` }}
                        ></div>
                      </div>
                      <div className="energy-guage-num-group">
                        <p className="energy-guage-num">100</p>
                        <p className="energy-guage-num">50</p>
                        <p className="energy-guage-num">0</p>
                        <p className="energy-guage-num">50</p>
                        <p className="energy-guage-num">100</p>
                      </div>
                    </div>
                    <div className="energy-result-top-title right">
                      <p className="title">의미 중심(M)</p>
                      <div className="num">{resultPageData.M}</div>
                    </div>
                  </div>
                  <div className="col-group gap40">
                    <div className="step-item border col-group gap24 half-div left">
                      <div className="step-item-num">L</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title">논리 중심</p>
                        <div className="row-group gap8">
                          <p className="default-txt dot">
                            이성적이고 논리적이며 객관적인 자료를 근거로 판단
                            한다.
                          </p>
                          <p className="default-txt dot">
                            원리와 원칙을 중시하며 상대를 공정한 태도로 대한다.
                          </p>
                          <p className="default-txt dot">
                            독립적이고 객관적이며, 다른 사람들에게 영향을 잘
                            받지 않는다.
                          </p>
                          <p className="default-txt dot">
                            사람들과 토론을 즐기며, 비판적 사고를 한다.
                          </p>
                          <p className="default-txt dot">
                            의사결정에서 개인적인 요인을 제거한다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border col-group gap24 half-div right">
                      <div className="step-item-num">m</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title">의미 중심</p>
                        <div className="row-group gap8">
                          <p className="default-txt dot">
                            관계중심적이며, 상대에게 미칠 영향을 고려하면서
                            판단한다.
                          </p>
                          <p className="default-txt dot">
                            이성적인 판단보다는 공감을 먼저 한다.
                          </p>
                          <p className="default-txt dot">
                            과업보다는 관계에 더 비중을 둔다.
                          </p>
                          <p className="default-txt dot">
                            상대의 생각과 감정에 집중하며, 조화로운 관계를
                            추구한다.
                          </p>
                          <p className="default-txt dot">
                            의사결정에 개인적인 요인이 영향을 미친다.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="section">
              <div className="row-group gap40">
                <p className="default-title green">생활양식</p>
                <div
                  className={`energy-result-wrap ${
                    resultPageData.C > resultPageData.S
                      ? "right"
                      : resultPageData.C < resultPageData.S
                      ? "left"
                      : "right left"
                  }`}
                >
                  {" "}
                  {/* c의 점수가 높으면 right / s의 점수가 높으면 left */}
                  <div className="energy-result-top">
                    <div className="energy-result-top-title left">
                      <div className="num">{resultPageData.S}</div>
                      <p className="title">체계적(S)</p>
                    </div>
                    <div className="energy-guage-wrap">
                      <div className="energy-guage left">
                        <div
                          className="energy-guage-bar"
                          style={{ width: `${resultPageData.S}%` }}
                        ></div>
                      </div>
                      <div className="energy-guage right">
                        <div
                          className="energy-guage-bar"
                          style={{ width: `${resultPageData.C}%` }}
                        ></div>
                      </div>
                      <div className="energy-guage-num-group">
                        <p className="energy-guage-num">100</p>
                        <p className="energy-guage-num">50</p>
                        <p className="energy-guage-num">0</p>
                        <p className="energy-guage-num">50</p>
                        <p className="energy-guage-num">100</p>
                      </div>
                    </div>
                    <div className="energy-result-top-title right">
                      <p className="title">포용적(C)</p>
                      <div className="num">{resultPageData.C}</div>
                    </div>
                  </div>
                  <div className="col-group gap40">
                    <div className="step-item border col-group gap24 half-div left">
                      <div className="step-item-num">S</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title">체계적</p>
                        <div className="row-group gap8">
                          <p className="default-txt dot">
                            계획을 세우고 계획대로 하는 것을 선호한다.
                          </p>
                          <p className="default-txt dot">
                            시간과 규칙을 잘 지키고 책임감 있다.
                          </p>
                          <p className="default-txt dot">
                            꼼꼼하고 신중하며 안정적인 환경을 좋아한다.
                          </p>
                          <p className="default-txt dot">
                            충동적으로 행동하지 않으며 행동하기 전에
                            심사숙고한다.
                          </p>
                          <p className="default-txt dot">
                            구조화되고 체계적인 접근방식을 선호한다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border col-group gap24 half-div right">
                      <div className="step-item-num">C</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title">포용적</p>
                        <div className="row-group gap8">
                          <p className="default-txt dot">
                            융통성이 있으며 임기응변에 능하다.
                          </p>
                          <p className="default-txt dot">
                            상황에 따라 유연하게 일을 처리한다.
                          </p>
                          <p className="default-txt dot">
                            여러 가지 일을 동시에 하며, 뒷심이 있다.
                          </p>
                          <p className="default-txt dot">
                            자유분방하며 자신만의 규칙과 목표를 만들어간다.
                          </p>
                          <p className="default-txt dot">
                            더 많은 정보를 수집할 때까지 의사결정을 미룬다.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[20]}>
        <main className="main">
          <p className="page-num">21</p>
          <div className="main-title-wrap col-group">
            <div className="num dan green">04</div>
            <div className="main-title-group row-group">
              <h2 className="main-title">에너지 역량 및 방향</h2>
              <p className="main-sub-title">02. 에너지 유형 / 균형분포 정도</p>
            </div>
          </div>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">1</div>
                <h3 className="section-title">성격 유형적 특징</h3>
              </div>
              <div className="row-group gap32">
                <p className="default-title green">
                  {resultPageData.name}님의 성격 유형적 특징
                </p>
                <p className="default-txt">
                  {PersonalityTypeText[PersonalityTypeValue[0]]}
                </p>
              </div>
            </div>
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">2</div>
                <h3 className="section-title">균형분포 정도</h3>
              </div>
              <div className="row-group gap32">
                <p className="default-title green">
                  {resultPageData.name}님의 균형분포 정도
                </p>
                <div className="row-group gap40">
                  <div className="col-group gap40">
                    <div className="green-border-box half-div">
                      <div className="balance-chart-wrap">
                        <div
                          className={`balance-chart-bar ${
                            isWDSame
                              ? "center"
                              : resultPageData.W > resultPageData.D
                              ? "left"
                              : resultPageData.W < resultPageData.D
                              ? "right"
                              : ""
                          }`}
                        >
                          {/* 오른쪽 점수가 더 높으면 right/왼쪽 점수가 높으면 left/10점 내 차이일 경우 center */}
                        </div>
                        <img
                          src={images.balance_core}
                          alt=""
                          className="balance-chart-core"
                        />
                        <div className="balance-chart-group col-group">
                          <div
                            className={`balance-chart ${
                              resultPageData.W > resultPageData.D || isWDSame
                                ? "active"
                                : ""
                            }`}
                          >
                            <div
                              className="balance-chart-gauge"
                              style={{
                                background: `conic-gradient(#eeeeee 0% calc( 100% - ${resultPageData.W}% ), transparent calc( 100% - ${resultPageData.W}% ) 100% )`,
                              }}
                            />
                            <div className="balance-chart-circle">
                              {resultPageData.W}
                            </div>
                            <p className="title">폭넓은</p>
                          </div>
                          <div
                            className={`balance-chart ${
                              resultPageData.W < resultPageData.D || isWDSame
                                ? "active"
                                : ""
                            }`}
                          >
                            {" "}
                            {/* 점수가 더 높은 쪽에 active */}
                            <div
                              className="balance-chart-gauge"
                              style={{
                                background: `conic-gradient(#eeeeee 0% calc( 100% - ${resultPageData.D}% ), transparent calc( 100%  - ${resultPageData.D}% ) 100%)`,
                              }}
                            />
                            <div className="balance-chart-circle">
                              {resultPageData.D}
                            </div>
                            <p className="title">깊이 있는</p>
                          </div>
                        </div>
                      </div>
                      <div className="balance-chart-txt-wrap row-group gap16">
                        <p className="default-title">
                          에너지 방향 :{" "}
                          {resultPageData.W > resultPageData.D
                            ? "폭넓은"
                            : "깊이있는"}
                        </p>
                        {isWDSame ? (
                          <p className="default-txt default-txt-s">
                            {resultPageData.name}님은, 에너지의 방향이{" "}
                            <strong className="green">
                              외부의 사람과 사물을 향하며 관계의 폭이 넓고
                              다양한 측면과 에너지의 방향이 내부를 향하며 사람과
                              사물을 깊이 있게 대하고 평온한 측면이 비교적
                              균등하게 균형잡혀
                            </strong>{" "}
                            있습니다. 이처럼, 자신이 사용하는 에너지의 배분을
                            통합적으로 잘 유지하기 바랍니다.
                          </p>
                        ) : resultPageData.W > resultPageData.D ? (
                          <p className="default-txt default-txt-s">
                            {resultPageData.name}님은, 에너지의 방향이{" "}
                            <strong className="green">
                              외부의 사람과 사물을 향하며 관계의 폭이 넓고
                              다양한 측면으로 {resultPageData.W}% 치우쳐져
                              있습니다.
                            </strong>{" "}
                            ‘깊이있는’ 형태로의 의식적인 노력이 필요합니다.
                          </p>
                        ) : (
                          <p className="default-txt default-txt-s">
                            {resultPageData.name}님은, 에너지의 방향이{" "}
                            <strong className="green">
                              내부를 향하며 사람과 사물을 깊이 있게 대하고
                              평온한 측면으로 {resultPageData.D}% 치우쳐져
                              있습니다.
                            </strong>{" "}
                            ‘폭넓은’ 형태로의 의식적인 노력이 필요합니다.
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="green-border-box half-div">
                      <div className="balance-chart-wrap">
                        <div
                          className={`balance-chart-bar ${
                            isPISame
                              ? "center"
                              : resultPageData.P > resultPageData.I
                              ? "left"
                              : resultPageData.P < resultPageData.I
                              ? "right"
                              : ""
                          }`}
                        >
                          {/* 오른쪽 점수가 더 높으면 right/왼쪽 점수가 높으면 left/10점 내 차이일 경우 center */}
                        </div>
                        <img
                          src={images.balance_core}
                          alt=""
                          className="balance-chart-core"
                        />
                        <div className="balance-chart-group col-group">
                          <div
                            className={`balance-chart ${
                              resultPageData.P > resultPageData.I || isPISame
                                ? "active"
                                : ""
                            }`}
                          >
                            {" "}
                            {/* 점수가 더 높은 쪽에 active */}
                            <div
                              className="balance-chart-gauge"
                              style={{
                                background: `conic-gradient(#eeeeee 0% calc( 100% - ${resultPageData.P}% ), transparent calc( 100%  - ${resultPageData.P}% ) 100%)`,
                              }}
                            />
                            <div className="balance-chart-circle">
                              {resultPageData.P}
                            </div>
                            <p className="title">실용적</p>
                          </div>
                          <div
                            className={`balance-chart ${
                              resultPageData.P < resultPageData.I || isPISame
                                ? "active"
                                : ""
                            }`}
                          >
                            <div
                              className="balance-chart-gauge"
                              style={{
                                background: `conic-gradient(#eeeeee 0% calc( 100% - ${resultPageData.I}% ), transparent calc( 100%  - ${resultPageData.I}% ) 100%)`,
                              }}
                            />
                            <div className="balance-chart-circle">
                              {resultPageData.I}
                            </div>
                            <p className="title">통찰적</p>
                          </div>
                        </div>
                      </div>
                      <div className="balance-chart-txt-wrap row-group gap16">
                        <p className="default-title">
                          정보 인식 체계 :{" "}
                          {resultPageData.P > resultPageData.I
                            ? "실용적"
                            : "통찰적"}
                        </p>
                        {isPISame ? (
                          <p className="default-txt default-txt-s">
                            {resultPageData.name}님은, 정보의 인식체계가{" "}
                            <strong className="green">
                              정보를 있는 그대로 정확하고 상세하게 처리하며
                              실용적이고 세부적으로 인식하는 측면과 정보를
                              상징적이고 비판적으로 처리하며 예리하게 관찰하여
                              꿰뚫어 인식하는 측면이 비교적 균등하게 균형 잡혀
                            </strong>{" "}
                            있습니다. 이처럼, 자신이 사용하는 에너지의 배분을
                            통합적으로 잘 유지하기 바랍니다.
                          </p>
                        ) : resultPageData.P > resultPageData.I ? (
                          <p className="default-txt default-txt-s">
                            {resultPageData.name}님은, 정보의 인식체계가{" "}
                            <strong className="green">
                              정보를 있는 그대로 정확하고 상세하게 처리하며
                              실용적이고 세부적으로 인식하는 측면으로{" "}
                              {resultPageData.P}% 치우쳐져 있습니다.
                            </strong>{" "}
                            ‘통찰적’인 형태로의 의식적인 노력이 필요합니다.
                          </p>
                        ) : (
                          <p className="default-txt default-txt-s">
                            {resultPageData.name}님은, 정보의 인식체계가{" "}
                            <strong className="green">
                              정보를 상징적이고 비판적으로 처리하며 예리하게
                              관찰하여 꿰뚫어 인식하는 측면으로{" "}
                              {resultPageData.I}% 치우쳐져 있습니다.
                            </strong>{" "}
                            ‘실용적’인 형태로의 의식적인 노력이 필요합니다.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-group gap40">
                    <div className="green-border-box half-div">
                      <div className="balance-chart-wrap">
                        <div
                          className={`balance-chart-bar ${
                            isLMSame
                              ? "center"
                              : resultPageData.L > resultPageData.M
                              ? "left"
                              : resultPageData.L < resultPageData.M
                              ? "right"
                              : ""
                          }`}
                        >
                          {/* 오른쪽 점수가 더 높으면 right/왼쪽 점수가 높으면 left/10점 내 차이일 경우 center */}
                        </div>
                        <img
                          src={images.balance_core}
                          alt=""
                          className="balance-chart-core"
                        />
                        <div className="balance-chart-group col-group">
                          <div
                            className={`balance-chart ${
                              resultPageData.L > resultPageData.M || isLMSame
                                ? "active"
                                : ""
                            }`}
                          >
                            <div
                              className="balance-chart-gauge"
                              style={{
                                background: `conic-gradient(#eeeeee 0% calc( 100% - ${resultPageData.L}% ), transparent calc( 100%  - ${resultPageData.L}% ) 100%)`,
                              }}
                            />
                            <div className="balance-chart-circle">
                              {resultPageData.L}
                            </div>
                            <p className="title">논리중심</p>
                          </div>
                          <div
                            className={`balance-chart ${
                              resultPageData.L < resultPageData.M || isLMSame
                                ? "active"
                                : ""
                            }`}
                          >
                            {" "}
                            {/* 점수가 더 높은 쪽에 active */}
                            <div
                              className="balance-chart-gauge"
                              style={{
                                background: `conic-gradient(#eeeeee 0% calc( 100% - ${resultPageData.M}% ), transparent calc( 100%  - ${resultPageData.M}% ) 100%)`,
                              }}
                            />
                            <div className="balance-chart-circle">
                              {resultPageData.M}
                            </div>
                            <p className="title">의미중심</p>
                          </div>
                        </div>
                      </div>
                      <div className="balance-chart-txt-wrap row-group gap16">
                        <p className="default-title">
                          의사결정 방식 :{" "}
                          {resultPageData.L > resultPageData.M
                            ? "논리 중심"
                            : "의미 중심"}
                        </p>
                        {isLMSame ? (
                          <p className="default-txt default-txt-s">
                            {resultPageData.name}님의 의사결정 방식은 의사결정
                            방식은{" "}
                            <strong className="green">
                              논리와 합리성을 따지며 명백한 사실을 중심으로
                              객관적 의사결정을 추구하는 측면과 정서적 공감과
                              가치공유를 중심으로 감정적 의사결정을 추구하는
                              측면이 비교적 균등하게 균형잡혀
                            </strong>{" "}
                            있습니다. 이처럼, 자신이 사용하는 에너지의 배분을
                            통합적으로 잘 유지하기 바랍니다.
                          </p>
                        ) : resultPageData.L > resultPageData.M ? (
                          <p className="default-txt default-txt-s">
                            {resultPageData.name}님의 의사결정 방식은{" "}
                            <strong className="green">
                              논리와 합리성을 따지며 명백한 사실을 중심으로
                              객관적 의사결정을 추구하는 측면으로{" "}
                              {resultPageData.L}% 치우쳐져 있습니다.
                            </strong>{" "}
                            ‘의미 중심’ 형태로의 의식적인 노력이 필요합니다.
                          </p>
                        ) : (
                          <p className="default-txt default-txt-s">
                            {resultPageData.name}님의 의사결정 방식은{" "}
                            <strong className="green">
                              정서적 공감과 가치공유를 중심으로 감정적
                              의사결정을 추구하는 측면으로 {resultPageData.M}%
                              치우쳐져 있습니다.
                            </strong>{" "}
                            ‘논리 중심’ 형태로의 의식적인 노력이 필요합니다.
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="green-border-box half-div">
                      <div className="balance-chart-wrap">
                        <div
                          className={`balance-chart-bar ${
                            isSCSame
                              ? "center"
                              : resultPageData.S > resultPageData.C
                              ? "left"
                              : resultPageData.S < resultPageData.C
                              ? "right"
                              : ""
                          }`}
                        >
                          {/* 오른쪽 점수가 더 높으면 right/왼쪽 점수가 높으면 left/10점 내 차이일 경우 center */}
                        </div>
                        <img
                          src={images.balance_core}
                          alt=""
                          className="balance-chart-core"
                        />
                        <div className="balance-chart-group col-group">
                          <div
                            className={`balance-chart ${
                              resultPageData.S > resultPageData.C || isSCSame
                                ? "active"
                                : ""
                            }`}
                          >
                            {" "}
                            {/* 점수가 더 높은 쪽에 active */}
                            <div
                              className="balance-chart-gauge"
                              style={{
                                background: `conic-gradient(#eeeeee 0% calc( 100% - ${resultPageData.S}% ), transparent calc( 100%  - ${resultPageData.S}% ) 100%)`,
                              }}
                            />
                            <div className="balance-chart-circle">
                              {resultPageData.S}
                            </div>
                            <p className="title">체계적</p>
                          </div>
                          <div
                            className={`balance-chart ${
                              resultPageData.S < resultPageData.C || isSCSame
                                ? "active"
                                : ""
                            }`}
                          >
                            <div
                              className="balance-chart-gauge"
                              style={{
                                background: `conic-gradient(#eeeeee 0% calc( 100% - ${resultPageData.C}% ), transparent calc( 100%  - ${resultPageData.C}% ) 100%)`,
                              }}
                            />
                            <div className="balance-chart-circle">
                              {resultPageData.C}
                            </div>
                            <p className="title">포용적</p>
                          </div>
                        </div>
                      </div>
                      <div className="balance-chart-txt-wrap row-group gap16">
                        <p className="default-title">
                          생활 양식 :{" "}
                          {resultPageData.S > resultPageData.C
                            ? "체계적"
                            : "포용적"}
                        </p>
                        {isSCSame ? (
                          <p className="default-txt default-txt-s">
                            {resultPageData.name}님의 생활양식은{" "}
                            <strong className="green">
                              계획적이고 규칙적이며 효율을 중심으로 체계적인
                              측면과 유연하고 개방적이며 새로움에 열린 태도로
                              포용적인 측면이 비교적 균등하게 균형잡혀
                            </strong>{" "}
                            있습니다. 이처럼, 자신이 사용하는 에너지의 배분을
                            통합적으로 잘 유지하기 바랍니다.
                          </p>
                        ) : resultPageData.S > resultPageData.C ? (
                          <p className="default-txt default-txt-s">
                            {resultPageData.name}님의 생활양식은{" "}
                            <strong className="green">
                              계획적이고 규칙적이며 효율을 중심으로 체계적인
                              측면으로 {resultPageData.S}% 치우쳐져 있습니다.
                            </strong>{" "}
                            ‘포용적’인 형태로의 의식적인 노력이 필요합니다.
                          </p>
                        ) : (
                          <p className="default-txt default-txt-s">
                            {resultPageData.name}님의 생활양식은{" "}
                            <strong className="green">
                              유연하고 개방적이며 새로움에 열린 태도로 포용적인
                              측면으로 {resultPageData.C}% 치우쳐져 있습니다.
                            </strong>{" "}
                            ‘체계적’인 형태로의 의식적인 노력이 필요합니다.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common page-19" ref={printPageRefs[21]}>
        <main className="main">
          <p className="page-num">22</p>
          <div className="main-title-wrap col-group">
            <div className="num dan green">04</div>
            <div className="main-title-group row-group">
              <h2 className="main-title">에너지 역량 및 방향</h2>
              <p className="main-sub-title">
                03. 에너지 방향에 따른 유형과 기질적 특성
              </p>
            </div>
          </div>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">1</div>
                <h3 className="section-title">
                  에너지 방향에 따른 유형과 기질적 특성
                </h3>
              </div>
              <div className="col-group gap40">
                <div className="half-div row-group gap32">
                  <p className="default-title green">
                    실용적이면서 포용력 있는 PC
                  </p>
                  <div className="step-item-list border">
                    <div
                      className={`step-item border-dash col-group gap24 ${
                        PersonalityTypeValue.some((item) => item === "DPLC")
                          ? "active"
                          : ""
                      }`}
                    >
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">DPLC 체험형</p>
                        <div className="row-group gap4">
                          <p className="default-txt dot">
                            조용하며 상황을 예리하게 관찰한다.
                          </p>
                          <p className="default-txt dot">
                            차가운 인상이지만 알고 나면 따뜻하다.
                          </p>
                          <p className="default-txt dot">
                            상황판단이 빠르고 논리적이다.
                          </p>
                          <p className="default-txt dot">
                            계획을 잘 세우지만 결과물에 연연하지 않는다.
                          </p>
                          <p className="default-txt dot">
                            방법과 이유에 관심이 많다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`step-item border-dash col-group gap24 ${
                        PersonalityTypeValue.some((item) => item === "DPMC")
                          ? "active"
                          : ""
                      }`}
                    >
                      <div className="step-item-num">02</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">DPMC 인내형</p>
                        <div className="row-group gap4">
                          <p className="default-txt dot">
                            섬세하고 예술적 감각이 발달했다.
                          </p>
                          <p className="default-txt dot">
                            자유롭고 모험적이다.
                          </p>
                          <p className="default-txt dot">
                            주변에 사람은 많지만 쉽게 외로움과 고독을 느낀다.
                          </p>
                          <p className="default-txt dot">
                            생각은 많지만 추진력이 부족하다.
                          </p>
                          <p className="default-txt dot">
                            아군과 적군을 구분하지 못한다.
                          </p>
                          <p className="default-txt dot">
                            타인을 잘 이해하는 편이지만 분위기에 휩쓸리는 경향이
                            있다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`step-item border-dash col-group gap24 ${
                        PersonalityTypeValue.some((item) => item === "WPLC")
                          ? "active"
                          : ""
                      }`}
                    >
                      <div className="step-item-num">03</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">WPLC 열정형</p>
                        <div className="row-group gap4">
                          <p className="default-txt dot">
                            남과 다르게 표현한다.
                          </p>
                          <p className="default-txt dot">
                            다양한 분야에 관심을 가진다.
                          </p>
                          <p className="default-txt dot">
                            모임이나 회의를 할 때 주도한다.
                          </p>
                          <p className="default-txt dot">
                            구속되기 싫어하고 자유로운 것을 좋아하며 미래
                            지향적이다.
                          </p>
                          <p className="default-txt dot">
                            매사에 밝고 긍정적이며 표정이 밝다.
                          </p>
                          <p className="default-txt dot">
                            감성이 풍부하고 열정적이다.
                          </p>
                          <p className="default-txt dot">
                            자극적인 일을 즐긴다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`step-item border-dash col-group gap24 ${
                        PersonalityTypeValue.some((item) => item === "WPMC")
                          ? "active"
                          : ""
                      }`}
                    >
                      <div className="step-item-num">04</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">WPMC 낙천형</p>
                        <div className="row-group gap4">
                          <p className="default-txt dot">
                            작은 일에도 감동한다.
                          </p>
                          <p className="default-txt dot">
                            삶 자체를 즐기고 행복하며 낙천적이다.
                          </p>
                          <p className="default-txt dot">
                            다른 사람을 잘 도와준다.
                          </p>
                          <p className="default-txt dot">
                            일을 끝까지 미루지만 중요한 일은 즉시 처리한다.
                          </p>
                          <p className="default-txt dot">
                            주목받는 것을 즐긴다.
                          </p>
                          <p className="default-txt dot">
                            쉽게 흥분하고 감동하며 열정이 넘친다.
                          </p>
                          <p className="default-txt dot">
                            털털하다는 소리를 많이 듣고, 쉽게 거절하지 못한다.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="half-div row-group gap32">
                  <p className="default-title green">
                    의미를 생각하며 통찰하는 IM
                  </p>
                  <div className="step-item-list border">
                    <div
                      className={`step-item border-dash col-group gap24 ${
                        PersonalityTypeValue.some((item) => item === "DIMC")
                          ? "active"
                          : ""
                      }`}
                    >
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">DIMC 개성형</p>
                        <div className="row-group gap4">
                          <p className="default-txt dot">
                            조화로운 관계를 중시한다.
                          </p>
                          <p className="default-txt dot">
                            내면세계에 관심이 많다.
                          </p>
                          <p className="default-txt dot">
                            조용하며 성실하고 책임감이 강하다.
                          </p>
                          <p className="default-txt dot">
                            지향하는 바에 대한 신념이 강하다.
                          </p>
                          <p className="default-txt dot">
                            대체로 관대하고 개방적이다.
                          </p>
                          <p className="default-txt dot">
                            자신과 타인의 감정을 잘 파악하지만 속마음을 잘
                            표현하지 않는다.
                          </p>
                          <p className="default-txt dot">
                            반복되는 일에는 쉽게 지루함을 느낀다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`step-item border-dash col-group gap24 ${
                        PersonalityTypeValue.some((item) => item === "DIMS")
                          ? "active"
                          : ""
                      }`}
                    >
                      <div className="step-item-num">02</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">DIMS 고뇌형</p>
                        <div className="row-group gap4">
                          <p className="default-txt dot">
                            자기 안에 갈등이 많고 복잡하다.
                          </p>
                          <p className="default-txt dot">
                            완벽주의 성향이 있어서 종종 예민하다.
                          </p>
                          <p className="default-txt dot">
                            행동하기 전에 시뮬레이션을 반복한다.
                          </p>
                          <p className="default-txt dot">
                            관심받는 것은 좋아하지만 나서는 것을 싫어한다.
                          </p>
                          <p className="default-txt dot">
                            비유로 표현하고 추상적으로 생각한다.
                          </p>
                          <p className="default-txt dot">
                            사람의 실존에 대해서 고민하며, 때론 현실감각이
                            부족하고 이상을 추구한다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`step-item border-dash col-group gap24 ${
                        PersonalityTypeValue.some((item) => item === "WIMC")
                          ? "active"
                          : ""
                      }`}
                    >
                      <div className="step-item-num">03</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">WIMC 재미형</p>
                        <div className="row-group gap4">
                          <p className="default-txt dot">
                            계획을 세우기보다 자신의 방식대로 일을 처리한다.
                          </p>
                          <p className="default-txt dot">
                            창의적이고 기발한 아이디어를 잘 낸다.
                          </p>
                          <p className="default-txt dot">
                            돌발상황에 대처하는 능력이 강하다.
                          </p>
                          <p className="default-txt dot">
                            상처받은 사람을 위로한다.
                          </p>
                          <p className="default-txt dot">
                            감정기복이 겉으로 드러난다.
                          </p>
                          <p className="default-txt dot">
                            정서적, 인지적 공감능력이 뛰어나다.
                          </p>
                          <p className="default-txt dot">
                            느긋하게 기다려 주기보다, 먼저 말을 하거나 주도해
                            나가는 편이다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`step-item border-dash col-group gap24 ${
                        PersonalityTypeValue.some((item) => item === "WIMS")
                          ? "active"
                          : ""
                      }`}
                    >
                      <div className="step-item-num">04</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">WIMS 격려형</p>
                        <div className="row-group gap4">
                          <p className="default-txt dot">
                            비전을 제시하고, 책임감이 있다.
                          </p>
                          <p className="default-txt dot">
                            제안은 많이하지만 실천이 약하고 현실적인 부분에
                            약하다.
                          </p>
                          <p className="default-txt dot">
                            사람을 좋아하고 다정하다.
                          </p>
                          <p className="default-txt dot">
                            신념이 강하고, 상황을 통찰하는 능력이 있다.
                          </p>
                          <p className="default-txt dot">
                            가치중심적인 일을 좋아하며 좋은 영향력을 행사하고
                            싶어한다.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common page-20" ref={printPageRefs[22]}>
        <main className="main">
          <p className="page-num">23</p>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="col-group gap40">
                <div className="half-div row-group gap32">
                  <p className="default-title green">
                    체계적이면서 현실적인 PS
                  </p>
                  <div className="step-item-list border">
                    <div
                      className={`step-item border-dash col-group gap24 ${
                        PersonalityTypeValue.some((item) => item === "WPLS")
                          ? "active"
                          : ""
                      }`}
                    >
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">WPLS 성과형</p>
                        <div className="row-group gap4">
                          <p className="default-txt dot">
                            주로 행정, 총무, 회계업무를 맡는다.
                          </p>
                          <p className="default-txt dot">
                            주변 사람들에게 입바른 소리를 한다.
                          </p>
                          <p className="default-txt dot">
                            대인관계가 폭넓고 오지랖이 넓다.
                          </p>
                          <p className="default-txt dot">
                            계획 후 실행하며 치밀하고 정확하다.
                          </p>
                          <p className="default-txt dot">
                            솔직하고 직설적이며 뒤끝이 없다.
                          </p>
                          <p className="default-txt dot">
                            규칙적이고 결단력이 있다.
                          </p>
                          <p className="default-txt dot">
                            남들을 잘 챙기고 보살펴준다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`step-item border-dash col-group gap24 ${
                        PersonalityTypeValue.some((item) => item === "WPMS")
                          ? "active"
                          : ""
                      }`}
                    >
                      <div className="step-item-num">02</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">WPMS 사교형</p>
                        <div className="row-group gap4">
                          <p className="default-txt dot">
                            성격이 활발하며 말하는 것을 좋아한다.
                          </p>
                          <p className="default-txt dot">
                            자신감이 넘치고, 문제를 스스로 해결하려 한다.
                          </p>
                          <p className="default-txt dot">
                            관계는 다양하고 원만하지만, 깊은 속마음을 털어놓는
                            상대는 극소수다.
                          </p>
                          <p className="default-txt dot">
                            주변 환경의 영향을 많이 받는다.
                          </p>
                          <p className="default-txt dot">
                            헌신적으로 남을 도우면서 인정받는 것을 좋아한다.
                          </p>
                          <p className="default-txt dot">
                            옳고 그름을 명확하게 구분하고 전통적인 가치관을
                            선호한다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`step-item border-dash col-group gap24 ${
                        PersonalityTypeValue.some((item) => item === "DPLS")
                          ? "active"
                          : ""
                      }`}
                    >
                      <div className="step-item-num">03</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">DPLS 안정형</p>
                        <div className="row-group gap4">
                          <p className="default-txt dot">
                            성실하고,근면하며 책임감이 강하다.
                          </p>
                          <p className="default-txt dot">
                            진지하고 전통적이며 조직을 지킨다.
                          </p>
                          <p className="default-txt dot">
                            과업 중심적이며 허튼소리를 않는다.
                          </p>
                          <p className="default-txt dot">
                            약속을 잘 지키며 최선을 다한다.
                          </p>
                          <p className="default-txt dot">
                            정서적 표현을 많이 하지 않는다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`step-item border-dash col-group gap24 ${
                        PersonalityTypeValue.some((item) => item === "DPMS")
                          ? "active"
                          : ""
                      }`}
                    >
                      <div className="step-item-num">04</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">DPMS 양육형</p>
                        <div className="row-group gap4">
                          <p className="default-txt dot">
                            배려심이 많아 대인관계가 원만하다.
                          </p>
                          <p className="default-txt dot">
                            생각이 많고 신중하다.
                          </p>
                          <p className="default-txt dot">
                            겸손과 예의를 중시한다.
                          </p>
                          <p className="default-txt dot">
                            유행을 따르지 않는다.
                          </p>
                          <p className="default-txt dot">
                            참을성이 강하며 뒷심이 강하다.
                          </p>
                          <p className="default-txt dot">
                            싫은 소리를 잘 못한다.
                          </p>
                          <p className="default-txt dot">
                            결정을 잘 못하지만 주관은 뚜렷하다.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="half-div row-group gap32">
                  <p className="default-title green">
                    유능하면서 통찰력있는 IL
                  </p>
                  <div className="step-item-list border">
                    <div
                      className={`step-item border-dash col-group gap24 ${
                        PersonalityTypeValue.some((item) => item === "WILC")
                          ? "active"
                          : ""
                      }`}
                    >
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">WILC 표현형</p>
                        <div className="row-group gap4">
                          <p className="default-txt dot">
                            감정에 솔직하고 표현을 잘한다.
                          </p>
                          <p className="default-txt dot">
                            에너지가 넘치고 활동 범위가 넓다.
                          </p>
                          <p className="default-txt dot">
                            두뇌회전이 빠르고, 사람이나 조직의 문제점을 쉽게
                            해결한다.
                          </p>
                          <p className="default-txt dot">
                            처음 보는 사람과도 쉽게 대화한다.
                          </p>
                          <p className="default-txt dot">
                            추진력과 실행력이 높고, 임기응변에 강해서 일의 성공
                            가능성도 높다.
                          </p>
                          <p className="default-txt dot">
                            위험을 감수하며 새로운 시도를 한다.
                          </p>
                          <p className="default-txt dot">
                            직설적이며, 이유를 잘 묻는다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`step-item border-dash col-group gap24 ${
                        PersonalityTypeValue.some((item) => item === "WILS")
                          ? "active"
                          : ""
                      }`}
                    >
                      <div className="step-item-num">02</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">WILS 체계형</p>
                        <div className="row-group gap4">
                          <p className="default-txt dot">
                            관계의 폭이 넓고 리더 역할을 한다.
                          </p>
                          <p className="default-txt dot">
                            객관적 사실 중심으로 대안을 제시한다.
                          </p>
                          <p className="default-txt dot">
                            열정적이고 완벽을 추구한다.
                          </p>
                          <p className="default-txt dot">
                            갈등 상황에서 감정보다는 논리적으로 해결한다.
                          </p>
                          <p className="default-txt dot">
                            스스로 해냈을 때 성취감이 높다.
                          </p>
                          <p className="default-txt dot">
                            체계적으로 계획하고 단계적으로 성취한다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`step-item border-dash col-group gap24 ${
                        PersonalityTypeValue.some((item) => item === "DILC")
                          ? "active"
                          : ""
                      }`}
                    >
                      <div className="step-item-num">03</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">DILC 독립형</p>
                        <div className="row-group gap4">
                          <p className="default-txt dot">
                            아이디어가 많아서 머릿속이 복잡하다.
                          </p>
                          <p className="default-txt dot">
                            관심사가 매우 분명하다.
                          </p>
                          <p className="default-txt dot">
                            논리중심적이며 주관이 뚜렷하다.
                          </p>
                          <p className="default-txt dot">
                            최소한의 노력으로 최대의 이익을 추구하므로 일의
                            효율성이 높다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`step-item border-dash col-group gap24 ${
                        PersonalityTypeValue.some((item) => item === "DILS")
                          ? "active"
                          : ""
                      }`}
                    >
                      <div className="step-item-num">04</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">DILS 효율형</p>
                        <div className="row-group gap4">
                          <p className="default-txt dot">
                            책임감이 강하고 모범생이라는 평가를 받는다.
                          </p>
                          <p className="default-txt dot">
                            헌신적이며 신뢰를 받는다.
                          </p>
                          <p className="default-txt dot">
                            전략적인 사고와 냉철한 논리력으로 문제를 해결한다.
                          </p>
                          <p className="default-txt dot">
                            일을 체계적이고 효율적으로 처리한다.
                          </p>
                          <p className="default-txt dot">
                            논리중심적인 사고에 익숙해 정서적 공감이 어렵다.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[23]}>
        <main className="main">
          <p className="page-num">24</p>
          <div className="main-title-wrap col-group">
            <div className="num dan green">05</div>
            <div className="main-title-group row-group">
              <h2 className="main-title">힘의 균형과 날개</h2>
              <p className="main-sub-title">
                01. 힘의 균형 결과 / 성장을 위한 질문
              </p>
            </div>
          </div>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">1</div>
                <h3 className="section-title">힘의 균형</h3>
              </div>
              <p className="default-txt default-txt-l">
                <strong className="green">
                  우리가 삶을 살아나가는데 필요한 에너지를 얻는 원천
                </strong>
                을 말합니다. 힘의 중심은 우리의 신체 기관 가운데 대표적으로{" "}
                <strong className="green">‘머리’, ‘가슴(심장)’, ‘장’</strong>과
                깊은 관련이 있으며{" "}
                <strong className="green">
                  어디를 중심에 두느냐에 따라 문제를 해결하는 방식이 달라집니다.
                </strong>{" "}
                <br />
                보편적으로 머리형은 ‘사고’에, 가슴(심장)형은 ‘감정’에, 장형은
                ‘본능’에 기초하여 그 기능을 주로 사용합니다. <br />
                자신의 에너지가 시작되는 지점을 알게 된다는 것은 인생을
                살아가면서 만나게 되는 수많은 문제 상황에서 그 해결방식의 흐름을
                파악하는 실마리가 될 수 있습니다.
              </p>
            </div>
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">2</div>
                <h3 className="section-title">3가지 힘의 중심</h3>
              </div>
              <div className="row-group gap56">
                <div className="balance-of-power-img-wrap">
                  <div className="text-wrap">
                    {balanceOfPower.map((item, index) => (
                      <div
                        className={`text-item item${index + 1} ${
                          resultPageData.power_num === index + 1 ? "active" : ""
                        }`}
                        key={index}
                      >
                        <p className="tit">
                          {index + 1}. {item.title}
                        </p>
                        {item.desc.map((descItem, descIndex) => (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(descItem),
                            }}
                            key={descIndex}
                            className="ul-list"
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className={`after-wrap num_${resultPageData.power_num}`}>
                    <img
                      className="balance-of-power-img"
                      src={images.custom_balance_of_power}
                      alt=""
                    />
                  </div>
                </div>
                <div className="col-group gap40 relative">
                  <div className="icon-arrow-wrap">
                    <div className="icon-arrow-both">
                      <img
                        src={images.icon_arrow_bold_down}
                        alt=""
                        className="icon-arrow-left"
                      />
                      <img
                        src={images.icon_arrow_bold_down}
                        alt=""
                        className="icon-arrow-right"
                      />
                    </div>
                    <div className="icon-arrow-both">
                      <img
                        src={images.icon_arrow_bold_down}
                        alt=""
                        className="icon-arrow-left"
                      />
                      <img
                        src={images.icon_arrow_bold_down}
                        alt=""
                        className="icon-arrow-right"
                      />
                    </div>
                  </div>
                  <div className="green-border-box row-group gap24">
                    <p className="default-title green">
                      머리 중심(5, 6 ,7번 유형)
                    </p>
                    <div className="row-group gap4">
                      <p className="default-txt default-txt-s dot">
                        힘의 원천 : 사고(Thinking)
                      </p>
                      <p className="default-txt default-txt-s dot">
                        내부감정 : 두려움
                      </p>
                      <p className="default-txt default-txt-s dot">
                        관계양식 : 일정한 거리를 유지함
                      </p>
                      <p className="default-txt default-txt-s dot">
                        관심과 의사결정 : 논리, 정보, 근거 등에 의한 객관적
                        의사결정
                      </p>
                      <p className="default-txt default-txt-s dot">
                        관심주제 : 상황파악, 신념과 전략
                      </p>
                      <p className="default-txt default-txt-s dot">
                        발달기관 : 시각
                      </p>
                      <p className="default-txt default-txt-s dot">
                        선호하는 일 : 연구
                      </p>
                      <p className="default-txt default-txt-s dot">
                        추구 : 안전
                      </p>
                    </div>
                  </div>
                  <div className="green-border-box row-group gap24">
                    <p className="default-title green">
                      가슴 중심(2, 3, 4번 유형)
                    </p>
                    <div className="row-group gap4">
                      <p className="default-txt default-txt-s dot">
                        힘의 원천 : 감정(emotion)
                      </p>
                      <p className="default-txt default-txt-s dot">
                        내부감정 : 수치심, 불안
                      </p>
                      <p className="default-txt default-txt-s dot">
                        관계양식 : 친밀감을 확인, 표현함
                      </p>
                      <p className="default-txt default-txt-s dot">
                        관심과 의사결정 : 정서적인 관계에 따라 의사결정
                      </p>
                      <p className="default-txt default-txt-s dot">
                        관심주제 : 사람, 자아와 사랑
                      </p>
                      <p className="default-txt default-txt-s dot">
                        발달기관 : 촉각, 미각
                      </p>
                      <p className="default-txt default-txt-s dot">
                        선호하는 일 : 봉사, 서비스
                      </p>
                      <p className="default-txt default-txt-s dot">
                        추구 : 이해와 공감
                      </p>
                    </div>
                  </div>
                  <div className="green-border-box row-group gap24">
                    <p className="default-title green">
                      장 중심(8, 9, 1번 유형)
                    </p>
                    <div className="row-group gap4">
                      <p className="default-txt default-txt-s dot">
                        힘의 원천 : 행동(behavior)
                      </p>
                      <p className="default-txt default-txt-s dot">
                        내부감정 : 분노
                      </p>
                      <p className="default-txt default-txt-s dot">
                        관계양식 : 책임과 의무, 의리를 중시
                      </p>
                      <p className="default-txt default-txt-s dot">
                        관심과 의사결정 : 규율과 정도에 맞는 도덕적 의사결정
                      </p>
                      <p className="default-txt default-txt-s dot">
                        관심주제 : 의지와 힘, 저항과 통제
                      </p>
                      <p className="default-txt default-txt-s dot">
                        발달기관 : 청각, 후각
                      </p>
                      <p className="default-txt default-txt-s dot">
                        선호하는 일 : 지도, 리더
                      </p>
                      <p className="default-txt default-txt-s dot">
                        추구 : 독립성
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[24]}>
        <main className="main">
          <p className="page-num">25</p>

          <div className="section-wrap row-group">
            {resultPageData.power_num === 1 ? (
              <div className="section">
                <div className="section-title-wrap col-group">
                  <div className="num">3</div>
                  <h3 className="section-title">
                    {resultPageData.name}님은 [{resultPageData.power_num}.
                    완벽을 추구하는 올곧은 사람], 장(본능) 중심입니다.
                  </h3>
                </div>
                <div className="row-group gap32">
                  <div className="step-item-list border">
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">특징</p>
                        <div className="row-group gap32">
                          <p className="default-txt default-txt-s">
                            매사에 완벽함을 추구하며 완전무결하게 해내기 위해
                            노력하고, 주위에도 그렇게 하기를 요구하고
                            기대합니다. 자신이 수행한 행동에 대해 완벽함의
                            정도를 평가하는 내적 기준을 가지고 맞추려
                            노력합니다. 다른 사람에 대해 매우 비판적이며 자신의
                            주장대로 고집하려는 성향이 있습니다. 근면하고 성실한
                            자세로 일하고 일을 정확히 처리하는 편입니다. 시간을
                            건설적으로 이용합니다. 인상이 깔끔하고 항상 말과
                            행동을 자제하며 '~해야 한다'는 말을 자주 합니다.
                            자신은 '올바른 길을 걷고 있다', '매사를 정확하게
                            파악하고 있다'는 생각에 만족감을 느낍니다.
                          </p>
                        </div>
                        <div className="col-group gap40">
                          <div className="half-div row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              이상적이고 원칙적입니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              정리정돈을 잘하고 부지런합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              근면 성실하고 일을 정확하게 처리하여 신뢰를
                              얻습니다.
                            </p>
                          </div>
                          <div className="half-div row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              말과 행동에 일관성이 있는 정직한 사람입니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              양심적이고 공정하며, 개인적인 이득 때문에 일하지
                              않습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              세상에 대한 개선의 의지가 강하며 윤리
                              도덕적입니다.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-dash col-group gap40">
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">02</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">원동력</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              사회 정의 문제를 위한 투쟁
                            </p>
                            <p className="default-txt default-txt-s dot">
                              자원봉사와 소외된 이웃 돕기
                            </p>
                            <p className="default-txt default-txt-s dot">
                              문제에 대한 올바른 해결 방안 제시
                            </p>
                            <p className="default-txt default-txt-s dot">
                              타인을 포함한 개인의 성장
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">03</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">적성</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s">
                              가르치거나 분석하는 일 또는 정확성이 요구되는 분야
                              - 교사, 의사, 성직자, 품질관리, 반도체, 회계사,
                              전문직 종사자
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-dash col-group gap40">
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">04</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">장점</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              매우 높은 도덕적 성향과 정직함을 지니고 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              이상주의적이고 낙관적인 세계관을 가집니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              개인적인 가치에 대해 깊은 동기 부여를 합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              커뮤니티에 연결하고 돌볼 수 있는 능력이 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              개선하고 변화시키는 방법을 찾는 능력이 있습니다.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">05</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">단점</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              자신과 타인 모두에게 비판적인 성향이 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              완벽주의 성향이 타인을 힘들게 할 수 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              어려운 현실을 쉽게 받아들이지 못합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              강박관념에 사로잡히거나 독선적일 가능성이
                              있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              타인의 무능력에 쉽게 화가 납니다.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">06</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          회피하는 것 - 분노
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s">
                            분노는 숨겨져 있고 억제되어 있습니다. 이 유형이
                            스스로 만들어 놓은 이상화 된 이미지는 ‘나는 항상
                            모범을 보여야 한다’, ‘나는 공정하다’, ‘나는
                            착하다’입니다. 이로 인해 자신을 화를 내서는 안
                            된다고 감정을 억제합니다. 만약, 분노를 억제할 수
                            없다면 그 분노를 합리화하여 당시 상황은 자신이 화를
                            낼 수밖에 없는 상황이었음을 증명하려고 합니다.
                            분노의 책임이 자신에게 돌아오는 것을 회피합니다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">07</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          성장의 포인트 (전환 방법)
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s">
                            전환해야 할 부분은 비판적이고 일방적인 습관들입니다.
                            살아가는 동안 우리는 완벽할 수 없으며 어떤 것도
                            완벽할 수 없음을 깨닫고 내가 처한 현실과 나 자신을
                            인정해야 합니다. 자신의 경계를 늦추고 발전을 위한
                            끊임없는 욕망을 쉬게 해줄 필요가 있습니다.
                            무엇보다도 나 자신을 용납하고 완벽에 대한 집착을
                            인정하며 판단하고 비평하는 삶의 자세를 변화시킬
                            필요가 있습니다. 동시에, 항상 틀린 점에 주목하는
                            나의 태도는 어떤 상황에서도 발전을 꾀할 수 있는
                            직관적인 본능과 정확하고 예민한 비판력으로 발전될 수
                            있음을 기억하는 것이 좋습니다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">08</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          성장을 위한 질문 던지기
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            완벽을 추구하며 올곧게 살아온 당신. 열심히
                            노력하느라 내면의 목소리에는 소홀하지 않았나요? 지금
                            당신의 내면에 귀 기울여보세요. 어떤 목소리가
                            들려오고 있나요?
                          </p>
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            지금까지 걸어온 길을 되돌아보며, 스스로에게 어떤
                            말을 해주고 싶은가요?
                          </p>
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            당신이 정해놓은 기준과 규칙은 무엇인가요? 그 기준을
                            벗어난다면 어떤 일이 벌어질까요?
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : resultPageData.power_num === 2 ? (
              <div className="section">
                <div className="section-title-wrap col-group">
                  <div className="num">3</div>
                  <h3 className="section-title">
                    {resultPageData.name}님은 [{resultPageData.power_num}.
                    친절한 조력가], 가슴(감정) 중심입니다.
                  </h3>
                </div>
                <div className="row-group gap32">
                  <div className="step-item-list border">
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">특징</p>
                        <div className="row-group gap32">
                          <p className="default-txt default-txt-s">
                            정이 많고 어려움에 처한 사람들에게 도움의 손길을
                            내밀며 주변 사람들에게 도움이 되는 일을 마다하지
                            않습니다. 타인이 필요로 하는 것에 몰두하지만 타인의
                            도움을 필요로 하고 있는 자신에 대해서는 자각하지
                            못합니다. 상대의 욕구를 충족시켜 주는 것 자체가
                            본래의 목적은 아니며 타인으로부터 애정과 호감을 받길
                            원합니다. 이러한 행동의 바탕에는 타인이 자신을
                            인정해 주기를 바라는 마음과 보답을 받고 싶은 욕구가
                            있습니다. 예리한 직감을 갖고 있고 주위 사람들의
                            기분을 이해하기 때문에 적응력이 뛰어납니다. 또한,
                            다양한 자기 모습을 갖고 있어 상대방에 따라 다른
                            모습을 연출할 수 있습니다.
                          </p>
                        </div>
                        <div className="col-group gap40">
                          <div className="half-div row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              사람과의 정서적인 교류를 잘하고 자기를 희생할 줄
                              압니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              공감을 잘하고 모성적인 성향이 강하여 타인의 기분을
                              맞추려고 노력합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              부탁을 받으면 거절하지 못하고 부탁하지 않은 일도
                              앞장서 도와줍니다.
                            </p>
                          </div>
                          <div className="half-div row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              베푼 만큼 그것에 상응하는 감사를 받고 싶어합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              타인중심이며 감정이 과할 때가 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              모든 친구들에게 특별히 중요한 사람이 되기를
                              바랍니다.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-dash col-group gap40">
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">02</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">원동력</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              사랑하고 사랑받는 일
                            </p>
                            <p className="default-txt default-txt-s dot">
                              감정표현
                            </p>
                            <p className="default-txt default-txt-s dot">
                              감사와 보상
                            </p>
                            <p className="default-txt default-txt-s dot">
                              반응과 인정
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">03</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">적성</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s">
                              공감하고 도움을 주는 일 또는 정서적 지지가
                              요구되는 분야 간호사, 사회복지사, 상담가, 가이드
                              등
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-dash col-group gap40">
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">04</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">장점</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              따뜻한 도움의 손길을 자주 내밉니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              공감을 잘하고 눈치가 빠릅니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              사람들 사이를 연결하는 다리 역할을 합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              친절하고 상냥합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              칭찬과 격려를 잘합니다.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">05</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">단점</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              남을 잘 보살피지만 소유하려는 경향이 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              감정이 과하며 신경질적인 면이 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              시선이 주로 타인과 외부를 향해 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              은근히 불평하고 요구가 많습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              대항해서 맞서지는 않으나 간섭합니다.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">06</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          회피하는 것 - 나에게 필요한 점에 대한 인식
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s">
                            남들에게 필요한 것에 대해 매우 잘 알고 있지만, 정작
                            자신을 도와달라고 요청하지 못하며 도움을 받는데에
                            불편함을 느낍니다. 내가 주는 사랑은 철저하게
                            일방적입니다. 나에게 필요한 것을 간접적으로
                            표현하거나 내가 말하기 전에 남들이 알아서
                            걱정해주기를 바랍니다. 심하면 일종의 메시아
                            콤플렉스를 가진 사람이 됩니다. 사람들에게 나의
                            도움이 필요하지만, 내가 줄 수 있는 도움에는 한계가
                            있다는 점을 인정하지 못할 수 있습니다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">07</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          성장의 포인트 (전환 방법)
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s">
                            내가 주는 사랑과 도움이 얼마나 일방적인지 그것이
                            얼마나 잘못되어 있는 것인지 인식하는 데서
                            출발합니다. 나는 남들의 도움을 거리낌 없이 받을 수
                            있고 그것에 대해 불편함을 느끼지 않아야 합니다.
                            남에게 도움을 줌으로 인해 느꼈던 자부심에서부터
                            벗어나야 하고 나도 도움이 필요한 부분이 있음을
                            인정해야 합니다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">08</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          성장을 위한 질문 던지기
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            정이 많고 따뜻한 마음을 가진 당신. 다른 사람을
                            돌보느라 정작 자신에게 소홀하지는 않은가요? 앞으로
                            누구보다 먼저 당신 자신을 잘 돌보기 위해서는 무엇을
                            해야 할까요?
                          </p>
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            당신에게 다른 사람의 애정과 인정이 중요한 이유는
                            무엇인가요?
                          </p>
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            정말 상대방을 위한 일은 어떤 것일까요? 구체적으로
                            떠올려보세요.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : resultPageData.power_num === 3 ? (
              <div className="section">
                <div className="section-title-wrap col-group">
                  <div className="num">3</div>
                  <h3 className="section-title">
                    {resultPageData.name}님은 [{resultPageData.power_num}.
                    성공을 향해가는 열정적 야심가], 가슴(감정) 중심입니다.
                  </h3>
                </div>
                <div className="row-group gap32">
                  <div className="step-item-list border">
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">특징</p>
                        <div className="row-group gap32">
                          <p className="default-txt default-txt-s">
                            효율을 중시하며 성공을 향해 매진하고, 침체에 빠지는
                            것을 두려워 합니다. 자부심이 강하면서도 꺾이기 쉬운
                            면이 있습니다. 실패를 극도로 두려워해 실패할
                            가능성이 있는 일에는 관여하려 들지 않습니다.
                            실적이나 지위를 잃어버리면 자존심이 무너지기 쉬워
                            성공에 걸림돌이 되는 무능력한 사람이나 비생산적인
                            사람을 싫어합니다. 또한, 팀원이나 주위 사람들을 자기
                            뜻대로 움직이려 합니다.
                          </p>
                        </div>
                        <div className="col-group gap40">
                          <div className="half-div row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              부지런하고 낙관적이며 상황에 따라 유연하게
                              대처합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              카리스마 있고 문제를 쉽게 해결하며 훌륭한
                              동기유발자 역할을 합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              열정적이고 유능하며 경쟁에 강한 모습을 보입니다.
                            </p>
                          </div>
                          <div className="half-div row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              목표를 가지고 성취하는 것을 좋아하며, 효율성을
                              강조하는 경향이 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              자기 확신이 있고, 자기 관리를 잘하며 기회를 잘
                              포착합니다.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-dash col-group gap40">
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">02</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">원동력</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              성공, 인정받고 있다는 느낌
                            </p>
                            <p className="default-txt default-txt-s dot">
                              실패에 대한 두려움
                            </p>
                            <p className="default-txt default-txt-s dot">
                              개인 개발과 결과적 보상
                            </p>
                            <p className="default-txt default-txt-s dot">
                              목표 설정과 달성을 통한 만족감
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">03</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">적성</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s">
                              달성목표와 성과가 구체적으로 드러나는 분야 - 배우,
                              홈쇼핑, 영업, 대중강연 등
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-dash col-group gap40">
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">04</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">장점</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              성취를 위한 추진 능력이 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              주변 사람들을 향한 격려와 동기부여가 탁월합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              타인을 이해하며 소통하는 능력이 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              자신감 넘치는 성향으로 카리스마가 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              실용적으로 생각하며 효율적으로 업무를 처리합니다.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">05</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">단점</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              타인이 자신을 보는 이미지에 지나치게 집중합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              목표를 달성하지 못할 때 받아들이기 어려워합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              지나친 경쟁 심리를 보일 때가 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              감정을 무시하고 과하게 활동할 때가 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              자기 과시적이며 정치적으로 보일 때가 있습니다.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">06</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          회피하는 것 - 실패
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s">
                            실패의 경험이 있으면 그것을 숨기고, 부인하는 경향이
                            있습니다. 자신이 하는 일에 따라 스스로를 강하게
                            정의했다는 것은 어떤 일에서의 실패를 ‘나의 실패’로
                            인식한다는 의미를 지닙니다. 자신에게는 성공 외에는
                            선택이 없기 때문에 과거의 성공은 기억하지만 실패의
                            기억은 거의 지니지 않습니다. 어떠한 실패라도
                            부분적으로는 성공이라고 기억합니다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">07</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          성장의 포인트 (전환 방법)
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s">
                            전환은 자신이 스스로에게 주어진 좋은 것들을 어떤
                            방법으로 왜곡했는지 구체적으로 파악하는 것에서
                            시작합니다. 우선, 내가 이상화해 놓은 나의 모습은
                            모두 실제 나의 모습이 아니라는 점을 인식해야 합니다.
                            이를 위해서는 자신이 스스로에 대해 만들어 놓은
                            이미지보다 실제 자신은 더 좋은 사람이 될 수 있는
                            잠재력이 있음을 아는 것이 중요합니다. 이는 바로
                            자신의 실패들을 인정하는 것으로, 어떤 방법을
                            사용해서라도 성공하려는 목마름으로부터의 탈출을
                            의미합니다. 가장 중요한 점은, 나는 내가 하는 일
                            때문에 사랑받는 것이 아니라 ‘내가 바로 나 자신이기
                            때문에’ 사랑받는다는 점을 기억하는 것입니다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">08</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          성장을 위한 질문 던지기
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            성공을 향해 열정적으로 달리는 당신. 당신에게
                            성공이란 어떤 의미인가요?
                          </p>
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            당신은 실패를 무척 두려워합니다. 만약, 어떤 일에
                            실패한다면 자신이 어떻게 느껴질 것 같은가요?
                          </p>
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            다른 사람이 보는 자신의 이미지가 아닌, 당신은 자신을
                            어떤 사람이라고 생각하나요?
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : resultPageData.power_num === 4 ? (
              <div className="section">
                <div className="section-title-wrap col-group">
                  <div className="num">3</div>
                  <h3 className="section-title">
                    {resultPageData.name}님은 [{resultPageData.power_num}.
                    특별함을 지향하는 예술가], 가슴(감정) 중심입니다.
                  </h3>
                </div>
                <div className="row-group gap32">
                  <div className="step-item-list border">
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">특징</p>
                        <div className="row-group gap32">
                          <p className="default-txt default-txt-s">
                            자신의 개성을 표현하고자 합니다. 아름다운 세상을
                            창출하려고 하며, 어떤 감정이나 느낌을 유지하려고
                            합니다. 자신의 이미지를 보호하기 위해 현실에서
                            물러나 있고 자신의 감정적 욕구를 먼저 다루고자 하는
                            경향이 있습니다. 자신은 특별한 사람이라고 자부하고
                            있으며 무엇보다도 감동을 중시하고 평범함을
                            싫어합니다. 다른 사람들보다 슬픔이나 고독 등도
                            진하게 느끼며 타인에 대한 이해심이 많고 사람들을
                            격려하는 것을 좋아합니다. 겉으로 드러난 것보다는 그
                            속에 숨어있는 의미를 탐색하여 관심 분야에 지극히
                            몰두하는 경향이 있습니다.
                          </p>
                        </div>
                        <div className="col-group gap40">
                          <div className="half-div row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              자신의 감정을 예술로 표현합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              민감하고 직관적이며 독창적입니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              즉흥적이며 감정이 들쑥날쑥하여 지나치게 예민할
                              때가 있습니다.
                            </p>
                          </div>
                          <div className="half-div row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              관심을 요구하며 소유욕이 강합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              자신의 감정의 깊이를 과장하려는 경향이 있습니다.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-dash col-group gap40">
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">02</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">원동력</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              자신의 개성, 감정, 느낌을 유지하고 표현
                            </p>
                            <p className="default-txt default-txt-s dot">
                              아름다운 세상 만들기
                            </p>
                            <p className="default-txt default-txt-s dot">
                              창의성
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">03</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">적성</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s">
                              예술적이거나 직관력을 필요로 하는 분야 - 배우,
                              작가, 디자이너, 트레이너, 비평가, 심리학자, 상담사
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-dash col-group gap40">
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">04</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">장점</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              매우 솔직하고 정직합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              상상력이 풍부하고 창의적으로 깊이 생각합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              공감능력이 뛰어나고 감정적으로 사람들과 잘
                              소통하는 방법을 알고 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              예술적 능력 뛰어납니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              평범한 것을 독특한 것으로 바꾸는 능력이 있습니다.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">05</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">단점</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              자신에게 없는 것에 집착하는 경향이 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              자신에게 너무 집중하는 경향이 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              다른 사람이 시키는 일에 저항감을 느낍니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              정체성이 부족하고 성취감을 못 느낄 때 자존감이
                              낮아지는 경향이 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              획일적이고 얽매이는 공동체 생활을 힘들어합니다.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">06</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          회피하는 것 - 눈물과 웃음을 주는 평범한 일상
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s">
                            ‘평범한 감정들의 평탄함’과 ‘평범한 사람들’에 대해
                            불만족스러워합니다. 자신은 모든 것을 강렬하게 해야
                            하며 드라마틱하고 과장되게 하려고 합니다. 격렬하지
                            않으면 감정은 현실적이지 않고 진실하지 못하다고
                            여기며 남들과 똑같아지는 것에 공포를 느끼기도
                            합니다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">07</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          성장의 포인트 (전환 방법)
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s">
                            자신과 주위의 모든 평범함은 인생의 다양한
                            경험만큼이나 현실적이고 중요한 부분이라는 사실을
                            인정함으로써 전환은 시작됩니다. 진정한 자아를 찾는
                            유일한 방법은, 스스로 잘나 보인다고 생각하는
                            부분뿐만 아니라 그것을 포함한 나의 모든 부분을
                            포용하는 방법밖에 없다는 것을 배워야 합니다.
                            특별해지기 위해 리허설 하거나 힘들게 노력하지 않아도
                            됩니다. 남들과 비교하는 행위를 멈추고 자신을 있는
                            그대로 내버려 두는 일부터 시작합니다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">08</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          성장을 위한 질문 던지기
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            창의적이고 남들과 다른 개성을 지닌 당신. 때로는
                            자신에게 지나치게 몰입합니다. 이러한 몰입에서 벗어나
                            외부에 관심을 둔다면 가장 끌리는 분야는 무엇인가요?
                          </p>
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            당신에게 없다고 생각하는 것은 무엇인가요? 그것이
                            없는 당신의 삶이 어떻게 느껴지나요?
                          </p>
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            당신이 갖고 있는 능력, 자원은 무엇인가요? 이러한
                            것들이 당신의 삶을 어떻게 만들고 있나요?
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : resultPageData.power_num === 5 ? (
              <div className="section">
                <div className="section-title-wrap col-group">
                  <div className="num">3</div>
                  <h3 className="section-title">
                    {resultPageData.name}님은 [{resultPageData.power_num}.
                    관찰력이 뛰어난 연구가], 머리(사고) 중심입니다.
                  </h3>
                </div>
                <div className="row-group gap32">
                  <div className="step-item-list border">
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">특징</p>
                        <div className="row-group gap32">
                          <p className="default-txt default-txt-s">
                            독립적이고 논리적이며, 현실을 파악하는 관찰력이
                            뛰어납니다. 지적 호기심이 많고 분석에 능해 독창적인
                            이론을 세우기도 합니다. 충분히 관찰하고 정보를
                            수집하여 상황을 정확하게 파악하고 판단하려고 합니다.
                            공정함과 정의를 바탕으로 한 정직하고 신뢰할 수 있는
                            성품이며, 자신의 윤리관에 자신을 갖고 있습니다.
                            사려가 깊으며 말이 많지 않고 태도가 조심스럽습니다.
                            고독을 즐기는 편이며, 자신만의 시간과 공간을 갖는
                            것을 매우 중요하게 여깁니다. ‘현명한 사람’이나
                            ‘지혜로운 사람’이라는 평가를 받을 때 만족합니다.
                          </p>
                        </div>
                        <div className="col-group gap40">
                          <div className="half-div row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              혁신적이고 독창적이며, 종종 시대를 앞서갑니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              완전히 새로운 방식으로 세상을 보는 편입니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              호기심이 많아 지식과 정보 수집을 끊임없이 합니다.
                            </p>
                          </div>
                          <div className="half-div row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              감정에 방해받지 않고 집중력을 발휘합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              직접 체험하지 않고 관찰력으로 습득할 수 있습니다.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-dash col-group gap40">
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">02</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">원동력</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              지식 습득과 주변 환경 이해
                            </p>
                            <p className="default-txt default-txt-s dot">
                              세상에 대한 새로운 발견
                            </p>
                            <p className="default-txt default-txt-s dot">
                              혼자 생각하는 시간
                            </p>
                            <p className="default-txt default-txt-s dot">
                              능력 있고 인정받는 느낌
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">03</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">적성</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s">
                              지속적인 탐구가 가능하고 지적 호기심을 유발할 수
                              있는 분야 - 컴퓨터 전문가, 작가, 애널리스트, 학자,
                              기술자
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-dash col-group gap40">
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">04</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">장점</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              신뢰할 수 있으며 비밀유지에 능숙합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              서로의 경계를 존중하는데 문제가 없습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              위기상황에 침착하며 객관적인 태도를 유지합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              복잡한 문제를 풀어내는 것에 수월합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              생각이 많으며 깊고 관찰적입니다.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">05</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">단점</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              지식을 습득하고 저장하는데 능숙하지만 실제로
                              행동하는 것은 어려워합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              타인에게 오만하게 보일 때가 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              차갑고 완고하여 감정 소통이 어려울 때가 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              사람들로부터 스스로를 고립시키곤 합니다.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">06</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          회피하는 것 - 공허함
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s">
                            스스로 가장 두려워하고 피하고 싶어 하는 것은
                            공허함입니다. 인생의 모든 면을 이해해야 하는데
                            그렇게 하지 못 할까 봐 두려움을 느낍니다. 이점
                            때문에 자신을 항상 새로운 지식과 통찰력으로 채워
                            넣으려 하고 이미 가지고 있는 지식을 놓치지 않으려
                            합니다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">07</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          성장의 포인트 (전환 방법)
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s">
                            우선, 무엇보다도 자신 내면에 있는 공허함을 있는
                            그대로 경험하고, 그것을 없애려 했던 모든 행위들이
                            오히려 스스로에게 해가 됨을 경험하는 것이
                            중요합니다. 자신의 두려움을 직시하는 것은, 내가
                            느끼는 공허함이 지식으로 채워질 수 있는 것이 아니라
                            오직 삶 안에서 타인과의 활발한 교류를 통해 채워질 수
                            있는 것임을 깨닫게 해줍니다. 내가 갈망하는 지혜는
                            이처럼 삶 안에 있는 것임을 배워야 합니다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">08</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          성장을 위한 질문 던지기
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            분석적이고 관찰력이 뛰어난 당신. 당신의 능력이
                            세상을 위해 쓰인다면, 어떤 식으로 쓸 수 있을까요?
                          </p>
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            지식을 습득하고 저장하는 것뿐만 아니라, 행동으로
                            실천하기 위해서는 무엇을 해야 할까요?
                          </p>
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            다른 사람과 갈등이 생겼을 때 어떤 식으로 갈등을
                            풀어나갈 수 있을까요?
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : resultPageData.power_num === 6 ? (
              <div className="section">
                <div className="section-title-wrap col-group">
                  <div className="num">3</div>
                  <h3 className="section-title">
                    {resultPageData.name}님은 [{resultPageData.power_num}.
                    충직한 보안관], 머리(사고) 중심입니다.
                  </h3>
                </div>
                <div className="row-group gap32">
                  <div className="step-item-list border">
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">특징</p>
                        <div className="row-group gap32">
                          <p className="default-txt default-txt-s">
                            책임감이 강하며 충직하고 신뢰할 수 있습니다.
                            안전함과 확실함을 추구하며 체계적이고 조직적인 일에
                            능합니다. 의무와 규칙을 잘 지키고 자신이 신뢰하는
                            조직이나 사람에 충실합니다. 다른 사람의 명예와
                            권위를 존중하고 조직 안에서 자신을 드러내지 않고
                            일합니다. 자신의 울타리 안에 있는 사람들을 돌보며,
                            그 외의 사람들에게는 조심스러운 태도를 보입니다.
                            안전을 추구하여 발생 가능한 문제들을 예측하고
                            준비하며, 신중하게 행동합니다. 분별력이 있고
                            성실하게 노력하는 사람입니다. 삶을 안정되게 유지하는
                            것을 매우 중요하게 생각합니다.
                          </p>
                        </div>
                        <div className="col-group gap40">
                          <div className="half-div row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              안전을 추구하며 안정감을 얻기 위해 노력합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              다른 사람들의 지지를 원합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              다른 사람들이 자신을 어떻게 생각하는지 관심이
                              많습니다.
                            </p>
                          </div>
                          <div className="half-div row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              꾸준하고 성실하게 노력하며 항상 준비되어 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              남을 존중하고 배려하며 용감합니다.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-dash col-group gap40">
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">02</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">원동력</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              일관되고 신뢰할 수 있는 관계
                            </p>
                            <p className="default-txt default-txt-s dot">
                              다른 사람을 돕는 일
                            </p>
                            <p className="default-txt default-txt-s dot">
                              사랑하는 사람들과 함께 보내는 시간
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">03</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">적성</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s">
                              명확한 지침이 있고 분명한 결과를 도출할 수 있는
                              분야 - 교사, 세무사, 공무원, 과학자, 건축기사
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-dash col-group gap40">
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">04</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">장점</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              책임감이 있으며 실용적인 선택을 할 수 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              계획, 약속을 존중하고 지키려고 합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              타인을 잘 보호하고 돌봅니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              다른 관점에서 바라보고 생각하는 능력이 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              논리적이고 감정적인 모습을 균형있게 지니고
                              있습니다.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">05</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">단점</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              불안감이 들기 시작하면 생각을 통제하기 어렵습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              최악의 상황을 생각하거나 비관적으로 생각하는
                              경향이 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              중요한 순간 결정 내리는 것을 두려워합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              우유부단한 면이 있으며 자기 의심과 불안이
                              높습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              소심하고 초조해하며 경계심이 많습니다.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">06</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          회피하는 것 - 권력에 대한 반항심, 두려움
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s">
                            외부에서 해답을 찾을 수 없는 의심과 질문들, 그리고
                            두려움에 노출되는 것을 피하기 위해 무슨 행동이든
                            합니다. 그러나 나의 이런 행동들은 나를 더 완고하게
                            만듭니다. 불확실성, 특별함, 단체에서의 일탈을 피하며
                            조직이나 틀, 사람들 안에 있을 때 안정감을 느낍니다.
                            상황이 달라졌음에도 이미 한 약속은 지켜야 하고,
                            인적이 없는 한밤중에도 횡단보도의 신호를 지키는 것이
                            당연합니다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">07</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          성장의 포인트 (전환 방법)
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s">
                            우선, 나의 의심과 공포를 직시하고 자신에게 한 발자국
                            다가서서 자신의 내면에 귀를 기울이고 믿음을 가지는
                            것이 중요합니다. 외부의 지원과 정의에 의존하기보다는
                            나 스스로 책임을 지기 시작할 필요가 있습니다. 내가
                            가진 의심과 공포들을 인정하기 시작할 때 비로소
                            자신을 더 믿을 수 있게 되며 감추어진 내면의 힘을
                            인지할 수 있습니다. 또, 나의 두려움과 의심을 소리
                            내어 이야기할 수 있게 되면 자신의 위치에 대해 더
                            확실한 믿음을 가질 수 있게 되며 내가 속한 집단이나
                            팀에서 중요한 역할을 할 수 있게 될 것입니다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">08</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          성장을 위한 질문 던지기
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            충직하고 안정감을 추구하는 당신. 안정감을 위해
                            당신이 포기했던 새로운 도전이나 모험에는 어떤 것들이
                            있었나요?
                          </p>
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            그 새로운 도전이나 모험을 위해 당신이 가진 능력 중
                            어떤 능력을 쓸 수 있나요?
                          </p>
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            당신은 걱정을 하고 최악의 상황을 상상하는데 에너지를
                            쓰고 있습니다. 이 에너지를 긍정적인 방향으로 쓴다면
                            어떻게 쓸 수 있을까요?
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : resultPageData.power_num === 7 ? (
              <div className="section">
                <div className="section-title-wrap col-group">
                  <div className="num">3</div>
                  <h3 className="section-title">
                    {resultPageData.name}님은 [{resultPageData.power_num}.
                    즐거운 모험가], 머리(사고) 중심입니다.
                  </h3>
                </div>
                <div className="row-group gap32">
                  <div className="step-item-list border">
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">특징</p>
                        <div className="row-group gap32">
                          <p className="default-txt default-txt-s">
                            새롭고 재미있는 일을 생각하거나 실행하기를
                            좋아합니다. 모든 일에서 긍정적인 면을 잘 발견하며
                            기발한 아이디어를 잘 생각해냅니다. 새로운 상황에도
                            잘 적응하고 문제해결 능력이 뛰어납니다. 활기차고
                            재치가 넘치며 낙관적입니다. 이러한 모습들로 주위
                            사람들의 의욕을 높이는 역할을 합니다. 다양한 분야에
                            관심이 많고 폭넓은 대인관계를 맺습니다. 짧게 집중할
                            수 있는 단기 프로젝트를 선호하는 반면, 단조롭고
                            변화가 없는 반복적인 일을 어려워합니다.
                          </p>
                        </div>
                        <div className="col-group gap40">
                          <div className="half-div row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              낙관적이고 우호적이며 모임을 좋아합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              상상력이 풍부하고 열정적입니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              충동적이고 자기중심적일 때가 있습니다.
                            </p>
                          </div>
                          <div className="half-div row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              삶이 즐겁고 기쁨으로 채워져야 합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              고통에서 해방되고 싶어 합니다.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-dash col-group gap40">
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">02</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">원동력</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              새로운 아이디어들을 떠올리고 실행하는 일
                            </p>
                            <p className="default-txt default-txt-s dot">
                              창의적이거나 트렌디한 것
                            </p>
                            <p className="default-txt default-txt-s dot">
                              다양한 선택지가 주어졌을 때
                            </p>
                            <p className="default-txt default-txt-s dot">
                              새로운 사람들을 만나고 알아가는 일
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">03</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">적성</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s">
                              빠른 전환과 순발력이 요구되는 분야 - 이벤트
                              플래너, 분장전문가, 개그맨, 광고기획자
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-dash col-group gap40">
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">04</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">장점</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              카리스마 있으며 인기가 많습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              빠른 생각과 창의적 능력이 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              많은 옵션과 문제 해결책을 떠올리는 능력이
                              있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              변화에 쉽게 적응하거나 대응합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              새로운 기술을 빠르게 습득합니다.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">05</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">단점</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              미리 계획을 세우는 일에 약합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              쉽게 지루함을 느낍니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              자신에게 중요하지 않다고 생각되는 일에는 게으름을
                              피웁니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              충동적이며 성급하게 결정을 내리곤 합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              무책임하고 산만하며 일관성 없는 모습을 보일 때가
                              있습니다.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">06</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          회피하는 것 - 고통과 시련
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s">
                            고통과 시련을 주는, 즐겁지 않은 일과는 관계 맺고
                            싶지 않습니다. 이러한 일들을 삶에서 없애기 위해
                            노력하며 알고 싶어 하지도 않습니다. 언제나 유쾌하고
                            다른 이들을 행복하게 해주려고 노력합니다. 다른
                            이들이 슬프거나 우울해할 때 불편한 감정을 느끼며
                            농담을 던지거나 다른 이야기를 함으로써 분위기를
                            가볍게 만들려고 합니다. 이처럼 부정적인 감정이나
                            분노를 회피하면서 나도 모르게 나오는 표현들이 오해를
                            불러일으키곤 합니다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">07</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          성장의 포인트 (전환 방법)
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s">
                            나 자신, 그리고 다른 이들의 아픔을 피하지 않는
                            것에서부터 시작됩니다. 불편한 감정이 올라올 때 애써
                            누그러뜨리거나 어영부영 넘어가려 하지 않고 있는
                            그대로 인정하면 나는 고통과 시련이 삶의 한 부분임을
                            알게 됩니다. 인생은 항상 웃음으로만 가득 차 있을 수
                            없습니다. 내가 좋아하는 부분만 선택적으로 즐길 수
                            없으며 그래서는 성장이 있을 수 없다는 사실을 배워야
                            합니다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">08</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          성장을 위한 질문 던지기
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            삶을 즐기며 기쁘게 살아가는 당신. 즐거움과 기쁨에
                            집중하느라 당신의 삶에서 무언가 놓치고 있다면 그것은
                            무엇일까요?
                          </p>
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            앞으로 삶을 즐겁게 살려면 지금 당신에게서 무엇을
                            보완해야 할까요?
                          </p>
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            당신은 종종 성급하게 결정을 합니다. 어떤 결정을
                            하는데 있어서 충분히 심사숙고하려면 어떻게 해야
                            할까요?
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : resultPageData.power_num === 8 ? (
              <div className="section">
                <div className="section-title-wrap col-group">
                  <div className="num">3</div>
                  <h3 className="section-title">
                    {resultPageData.name}님은 [{resultPageData.power_num}.
                    자신감 넘치는 도전가], 장(본능) 중심입니다.
                  </h3>
                </div>
                <div className="row-group gap32">
                  <div className="step-item-list border">
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">특징</p>
                        <div className="row-group gap32">
                          <p className="default-txt default-txt-s">
                            힘 있는 사람이 되고자 합니다. 대범하고 결단력이
                            있으며, 리더 성향을 타고 났습니다. 자신감이 넘치고
                            모험과 역경을 두려워하지 않고 도전합니다. 자신의
                            의견을 주저 없이 표현하며 주도권을 갖고 일을
                            추진합니다. 자기 확신이 강하여 주위 사람들에게
                            영향력이 있는 사람입니다. 솔직하고 관대하며 어떠한
                            어려움도 이겨낼 수 있는 용기를 지니고 있습니다.
                            직관에 따라 경정하는 경향이 있으며 위험을 감수하려
                            합니다. 다른 사람들에게 통제당하는 상황을 어려워하며
                            스스로 강하고 존경받는 사람이 되기를 원합니다.
                          </p>
                        </div>
                        <div className="col-group gap40">
                          <div className="half-div row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              자신감 넘치고 정직하며 솔직합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              단호하며 공정하고 관대합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              강한 의지와 활동력이 있습니다.
                            </p>
                          </div>
                          <div className="half-div row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              정열적이고 현실적이며 리더십이 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              결단력이 있고 약자를 끝까지 보살핍니다.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-dash col-group gap40">
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">02</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">원동력</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              자신의 약점을 이겨내고 강점을 증명하는 상황
                            </p>
                            <p className="default-txt default-txt-s dot">
                              책임지고 다른 사람을 이끌기
                            </p>
                            <p className="default-txt default-txt-s dot">
                              다른 사람들에게 도움이 되는 결정 내리기
                            </p>
                            <p className="default-txt default-txt-s dot">
                              논리적이고 실용적인 아이디어와 솔루션
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">03</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">적성</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s">
                              조직체를 통솔하거나 자율적인 권한이 주어지는 분야
                              - 감독, 스턴트맨, 농장경영자, 노동조합 지도자
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-dash col-group gap40">
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">04</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">장점</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              강한 의지력을 갖추어 마음먹은 것은 해냅니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              자신의 능력에 대한 확고한 자신감이 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              조직을 이끄는 리더 자질이 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              자급자족하는 능력과 독립적 기질이 강합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              자신의 울타리 안에 있는 사람들을 적극적으로
                              보호합니다.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">05</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">단점</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              의견 대립 시 거칠어 보일 수 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              단순한 규칙은 중요시하지 않고 현실성을
                              중요시합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              강한 자신감이 허세로 보일 수 있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              감성적 공감이 어려울 때가 있습니다.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">06</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          회피하는 것 - 나약함
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s">
                            남들이 나를 이용할까 봐 언제나 경계를 하며 나의
                            나약함과 남들의 순수함을 부정합니다. 나의 나약함을
                            인정하지 않기 때문에 힘이 없고 나약한 사람들을
                            경멸하고 무시합니다. 내게는 나약함이나 자비, 동정심
                            등이 자리 잡을 곳이 없다고 생각하므로 그런 감정들을
                            유발할 수 있는 상황들을 어떻게든 피하려고 합니다.
                            이러한 이유로, ‘부드러움 감정과 그 필요성’,
                            ‘남들에게 다가가기’, ‘따뜻함이나 호감, 친절 베풀기’
                            등에 어려움을 느끼는데 이러한 것들이 나약함을
                            보여준다고 믿기 때문입니다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">07</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          성장의 포인트 (전환 방법)
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s">
                            자신 스스로 안에서 또는 타인에게서, 자신이 가장
                            두려워하는 감정들을 인식하고 받아들이는 행동이
                            필요합니다. 의식적으로 내 자신과 남들에게 더 친절한
                            태도를 보이고 체화함으로써 자신의 진짜 힘을 찾아가며
                            진정으로 자신에게 주어진 힘은, 자비와 친절 그리고
                            동정심에 있다는 사실을 깨달아갑니다. 솔직함을
                            추구하는 나의 의지는 그대로 간직한 채로, 타인과
                            교류할 때 그의 입장이 되어 생각해보고 내가 그들과
                            어떻게 교류하고 있는지를 객관적으로 바라볼 수 있도록
                            노력합니다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">08</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          성장을 위한 질문 던지기
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            대범하고 도전 정신이 있는 당신은 다른 사람들을
                            이끌어가는 리더십을 발휘합니다. 반대로 당신이 다른
                            사람을 의지하고 그 의견에 따른다면 어떨 것 같은가요?
                          </p>
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            당신이 생각하는 약점은 무엇인가요? 그 약점을
                            받아들인다면 당신에게 어떤 변화가 있을까요?
                          </p>
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            당신이 다른 사람의 의견을 수용하고 협업하기 위해서는
                            무엇이 필요할까요?
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="section">
                <div className="section-title-wrap col-group">
                  <div className="num">3</div>
                  <h3 className="section-title">
                    {resultPageData.name}님은 [{resultPageData.power_num}.
                    조화를 이루는 평화주의자], 장(본능) 중심입니다.
                  </h3>
                </div>
                <div className="row-group gap32">
                  <div className="step-item-list border">
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">특징</p>
                        <div className="row-group gap32">
                          <p className="default-txt default-txt-s">
                            사람들과의 조화를 중시합니다. 어떠한 상황에서도 좋은
                            점을 찾아내 화합하고 일치시킵니다. 다른 사람들의
                            말에 귀 기울이고 이해하는 능력이 있습니다. 상대를
                            넓게 받아들이는 수용적이고 포용적인 사람입니다.
                            그러나 고집이 세고 완고한 면이 있어서 동의되지 않는
                            일에는 겉으로 드러내고 반대하지 않으면서도 결국은
                            자신이 원하는 대로 하는 경향이 있습니다. 대체로 다른
                            사람에게 상냥하고 위안을 주어 다른 사람들이 안정과
                            편안함을 느낄 수 있도록 합니다. 인내심이 강하고
                            겸손하며 스스로 만족할 줄 아는 사람입니다.
                          </p>
                        </div>
                        <div className="col-group gap40">
                          <div className="half-div row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              평화적이고 공정한 중재자 역할을 합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              침착하고 편안하며 겸손합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              폭넓게 받아들이는 수용성을 지니고 있습니다.
                            </p>
                          </div>
                          <div className="half-div row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              누구에게나 위안을 줍니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              어떤 상황에서도 좋은 점을 찾아내어 화합하고
                              일치시킵니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              인내심이 강하고 온순하며 스스로 만족합니다.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-dash col-group gap40">
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">02</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">원동력</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              분열된 팀을 하나로 모으는 일
                            </p>
                            <p className="default-txt default-txt-s dot">
                              사람들 사이의 오래된 상처를 중재하거나 치유하는 일
                            </p>
                            <p className="default-txt default-txt-s dot">
                              일관되고 안정감 있는 생활
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">03</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">적성</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s">
                              조직 간의 조화나 중재, 수용이 필요한 분야 -
                              사회복지사, 성직자, 외교관, 행정관료, 상담자
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-dash col-group gap40">
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">04</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">장점</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              타인의 갈등 조절에 탁월합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              도움이 필요한 상황에서 적극적으로 격려하고
                              지원합니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              다양한 관점의 시각으로 문제를 바라보는 능력이
                              있습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              뛰어난 적응력과 융통성이 있습니다.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="step-item col-group gap24 half-div">
                        <div className="step-item-num">05</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">단점</p>
                          <div className="row-group gap4">
                            <p className="default-txt default-txt-s dot">
                              본인 스스로 다른 사람과의 갈등에 직면하는 게
                              어렵습니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              문제를 자꾸 덮어 최소화 하려는 모습을 보입니다.
                            </p>
                            <p className="default-txt default-txt-s dot">
                              모든 사람을 조화롭게 하려 애씁니다.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">06</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          회피하는 것 - 분쟁
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s">
                            문제가 생길 것 같은 모든 상황을 빠르게 파악합니다.
                            이는 분쟁이나 논쟁, 불쾌함을 줄 수 있는 모든 경우를
                            말하며 만약 미리 방지하지 못했다면 자신이 그 일과
                            연관이 없음을 확실히 합니다. 심지어 그 일이 벌어지지
                            않은 것처럼 행동하거나 다른 일에 심취한 듯
                            행동합니다. 정신 내·외적으로 충격을 방지하는 일이
                            무엇보다 중요하기 때문에 자신의 감정들을 절제하고
                            억제합니다. 자신을 분쟁, 소동, 혼란으로부터 방어하며
                            둔감하게 하고 그런 일이 생기더라도 침착하게
                            행동합니다. 이와 같은 완고함으로 자신을 보호하고
                            상황을 조정하고자 합니다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">07</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          성장의 포인트 (전환 방법)
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s">
                            전환은 자신이 스스로에게 주어진 좋은 것들을 어떤
                            방법으로 왜곡했는지 구체적으로 파악하는 것에서
                            시작합니다. 특히, 현재의 습관들이 어떻게
                            형성되었는지, 나의 생활 방식이 어떤 과정으로
                            설계되었는지 알아보는 것이 중요합니다. 평화롭게
                            살고자 하는 내 욕망은 평화를 만드는 원동력이 될 수
                            있습니다. 그러나 동시에, 강박적으로 다툼을
                            피하려하는 자신을 인식해야 합니다. 세상에 존재하는
                            각기 다른 입장들끼리 서로의 의견을 듣게 할 필요가
                            있음을 인정하며 나의 인내심으로 이 과정을 이끌어 낼
                            수 있다는 것을 믿는 것이 중요합니다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">08</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          성장을 위한 질문 던지기
                        </p>
                        <div className="row-group gap4">
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            사람들과의 화합을 중요시하는 당신. 모든 사람의
                            화합을 위해 노력하느라 당신이 놓치고 있는 것은
                            무엇인가요?
                          </p>
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            당신이 갈등을 피함으로써 얻는 것과 잃는 것은
                            무엇인가요?
                          </p>
                          <p className="default-txt default-txt-s green default-txt-bold dot">
                            지금 당신에게 필요한 것은 무엇인가요?
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[25]}>
        <main className="main">
          <p className="page-num">26</p>
          <div className="main-title-wrap col-group">
            <div className="num dan green">05</div>
            <div className="main-title-group row-group">
              <h2 className="main-title">힘의 균형과 날개</h2>
              <p className="main-sub-title">
                02. 유형별 관계 전략 / 성장을 위한 처방
              </p>
            </div>
          </div>

          <div className="section-wrap row-group">
            <BalanceOfPowerWings
              data={BalanceOfPowerWingsData[resultPageData.power_num]}
            />
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">2</div>
                <h3 className="section-title">성장을 위한 처방</h3>
              </div>
              <div className="green-border-box bg-green row-group gap4 prescription">
                {ref12Text[energyTypeResult].desc.map((item, index) => (
                  <p
                    className="default-txt default-txt-l default-txt-bold dot"
                    key={index}
                  >
                    {item}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[26]}>
        <main className="main">
          <p className="page-num">27</p>
          <div className="main-title-wrap col-group">
            <div className="num dan green">05</div>
            <div className="main-title-group row-group">
              <h2 className="main-title">힘의 균형과 날개</h2>
              <p className="main-sub-title">03. 힘의 균형과 날개 & 화살</p>
            </div>
          </div>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">1</div>
                <h3 className="section-title">힘의 균형, 그 날개와 화살</h3>
              </div>
              <div className="row-group gap32">
                <p className="default-txt-l">
                  당신이 어떤 유형이든 우리는 원래 우리가 가지고 태어난 것의
                  한계가 있고 넘지 못할 선이 있으며 이미 왜곡된 무언가로 만들어
                  버린 경우들도 있습니다. 우리 자신은 완전함을 위해 만들어졌지만
                  인생을 통한 경험들은 우리를 수비적으로 만들거나 거친 행동을
                  하게 압박을 줍니다. <br />
                  <br />
                  우리가 사용하는 힘의 중심은 자신이 그동안 어떤 길을 선택해
                  왔는지 자각하려고 하는 사람들에게 지도를 제시해 주며, 앞으로
                  더욱 완전한 존재가 되기 위해 택해야 하는 전환의 길을 보게 해
                  줍니다.
                </p>
                <div className="green-bg-box row-group gap24">
                  <p className="default-txt">
                    ‘날개’가, 자신에게 주어진 상황과 환경에 적응하기 위한 내면
                    에너지의 활용이라면, ‘화살표’ 방향은 저항을 극복하는
                    움직임을 표현합니다. 우리는 날개와 화살표를 이해하고
                    내면에서 일어나는 저항을 극복하여 자신의 삶을 보다 성숙하게
                    통합하는 방향으로 나아갈 수 있어야 합니다.
                  </p>
                  <div className="col-group gap40">
                    <div className="half-div row-group gap8">
                      <p className="default-txt default-txt-bold green">
                        통합방향
                      </p>
                      <p className="default-txt">
                        내면의 의식을 통합하는 지점으로 의식적인 성찰과 올바른
                        자아 인식에서 비롯됩니다. 이는 긍정적인 노력의 결과로
                        다양함을 포용할 수 있습니다.
                      </p>
                    </div>
                    <div className="half-div row-group gap8">
                      <p className="default-txt default-txt-bold green">
                        분열방향
                      </p>
                      <p className="default-txt">
                        내면의 의식을 돌보지 않거나 저항과 집착이 강할 때 이
                        같은 성격의 틀이 강화됩니다. 자신과 타인에게 부정적이며
                        충동적이고 퇴행적일 수 있습니다.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-group gap40">
                  <div className="row-group gap24 half-div">
                    <p className="default-title green">날개(wing) 그래프</p>
                    <img src={images.graph_wing} alt="" />
                    <p className="default-txt">
                      날개란, 자기 유형의 양 옆에 위치한 유형을 말하며 성격을
                      균형 있게 발전하도록 돕는 역할을 합니다. 잠재력을 개발할
                      수 있는 가능성의 영역이기도 합니다.
                    </p>
                  </div>
                  <div className="row-group gap24 half-div">
                    <p className="default-title green">
                      화살(arrow) 그래프 : 통합방향
                    </p>
                    <img src={images.graph_arrow} alt="" />
                    <p className="default-txt">
                      화살표의 각 유형은 통합의 방향과 분열의 방향으로
                      나아갑니다. 통합의 방향은 나의 힘과 에너지를 더욱 강하게
                      만들 수 있는 협력자 유형이며, 분열의 방향은 나의 힘과
                      에너지를 소진시키는 소진형 유형입니다. 우리는 살아가면서
                      나의 통합 유형의 에너지를 발견해야 할 것이며 분열의 유형은
                      잘 피해가야 할 것입니다.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common page-25" ref={printPageRefs[27]}>
        <main className="main">
          <p className="page-num">28</p>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="row-group gap56">
                <div className="col-group gap40">
                  <div className="half-div row-group gap32">
                    <p className="default-title green">
                      {wingsTypeData[resultPageData.power_num][0].name}
                    </p>
                    <div className="step-item-list border">
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">01</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">특징</p>
                          <p className="default-txt">
                            {wingsTypeData[resultPageData.power_num][0].desc}
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">02</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">장점</p>
                          <div className="row-group gap4">
                            {wingsTypeData[
                              resultPageData.power_num
                            ][0].advantage.map((item, index) => (
                              <p className="default-txt dot" key={index}>
                                {item}
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">03</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">성찰할 점</p>
                          <div className="row-group gap4">
                            {wingsTypeData[
                              resultPageData.power_num
                            ][0].reflection.map((item, index) => (
                              <p className="default-txt dot" key={index}>
                                {item}
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="half-div row-group gap32">
                    <p className="default-title green">
                      {wingsTypeData[resultPageData.power_num][1].name}
                    </p>
                    <div className="step-item-list border">
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">01</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">특징</p>
                          <p className="default-txt">
                            {wingsTypeData[resultPageData.power_num][1].desc}
                          </p>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">02</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">장점</p>
                          <div className="row-group gap4">
                            {wingsTypeData[
                              resultPageData.power_num
                            ][1].advantage.map((item, index) => (
                              <p className="default-txt dot" key={index}>
                                {item}
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="step-item border-dash col-group gap24">
                        <div className="step-item-num">03</div>
                        <div className="step-item-txt-wrap row-group gap16">
                          <p className="default-title green">성찰할 점</p>
                          <div className="row-group gap4">
                            {wingsTypeData[
                              resultPageData.power_num
                            ][1].reflection.map((item, index) => (
                              <p className="default-txt dot" key={index}>
                                {item}
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="wing-type-section">
                  <div className="row-group gap24">
                    <p className="default-title green">날개 유형</p>
                    <div className="wing-type-wrap">
                      <div className="wing-type-group col-group">
                        <div className="wing-type-item row-group gap8">
                          <p className="default-txt default-txt-bold green">
                            1W9
                          </p>
                          <p className="default-txt default-txt-s">
                            스트레스 상황에 처하거나 시비가 붙으면 <br />
                            도망치려 하거나 움츠러드는 경향이 있습니다. <br />
                            차분하지만 수동적인 면이 강합니다.
                          </p>
                        </div>
                        <div className="num">1번</div>
                        <div className="wing-type-item row-group gap8">
                          <p className="default-txt default-txt-bold green">
                            1W2
                          </p>
                          <p className="default-txt default-txt-s">
                            더욱 외향적인 성향을 지녔으며, 남들이 자신에게서{" "}
                            <br />
                            어떤 영향을 받는지에 민감하게 반응합니다.
                          </p>
                        </div>
                      </div>
                      <div className="wing-type-group col-group">
                        <div className="wing-type-item row-group gap8">
                          <p className="default-txt default-txt-bold green">
                            2W1
                          </p>
                          <p className="default-txt default-txt-s">
                            완벽주의 기질이 있습니다.
                          </p>
                        </div>
                        <div className="num">2번</div>
                        <div className="wing-type-item row-group gap8">
                          <p className="default-txt default-txt-bold green">
                            2W3
                          </p>
                          <p className="default-txt default-txt-s">
                            효율적이며 생산적이고 싶어합니다.
                          </p>
                        </div>
                      </div>
                      <div className="wing-type-group col-group">
                        <div className="wing-type-item row-group gap8">
                          <p className="default-txt default-txt-bold green">
                            3W2
                          </p>
                          <p className="default-txt default-txt-s">
                            남들에게 일이 주는 압박이 어떤 영향을 미치는지
                            살피며, <br />
                            동시에 그들에게 필요한 것에 관심을 기울입니다.
                          </p>
                        </div>
                        <div className="num">3번</div>
                        <div className="wing-type-item row-group gap8">
                          <p className="default-txt default-txt-bold green">
                            3W4
                          </p>
                          <p className="default-txt default-txt-s">
                            3번이 4번의 독창성을 가지면 일에 창의성이 생겨나{" "}
                            <br />
                            더욱 효과적인 결과를 낼 수 있습니다.
                          </p>
                        </div>
                      </div>
                      <div className="wing-type-group col-group">
                        <div className="wing-type-item row-group gap8">
                          <p className="default-txt default-txt-bold green">
                            4W3
                          </p>
                          <p className="default-txt default-txt-s">
                            자심감이 높고 이벤트에 능합니다.
                          </p>
                        </div>
                        <div className="num">4번</div>
                        <div className="wing-type-item row-group gap8">
                          <p className="default-txt default-txt-bold green">
                            4W5
                          </p>
                          <p className="default-txt default-txt-s">
                            사려 깊은 면모가 돋보이지만, 자긍심이 낮습니다.
                          </p>
                        </div>
                      </div>
                      <div className="wing-type-group col-group">
                        <div className="wing-type-item row-group gap8">
                          <p className="default-txt default-txt-bold green">
                            5W4
                          </p>
                          <p className="default-txt default-txt-s">
                            생각에 창의적인 표현을 더해 효과를 높이는 경향이
                            있습니다.
                          </p>
                        </div>
                        <div className="num">5번</div>
                        <div className="wing-type-item row-group gap8">
                          <p className="default-txt default-txt-bold green">
                            5W6
                          </p>
                          <p className="default-txt default-txt-s">
                            더 조심스럽고 안전에 신경을 많이 씁니다.
                          </p>
                        </div>
                      </div>
                      <div className="wing-type-group col-group">
                        <div className="wing-type-item row-group gap8">
                          <p className="default-txt default-txt-bold green">
                            6W5
                          </p>
                          <p className="default-txt default-txt-s">
                            더욱 조심스럽습니다.
                          </p>
                        </div>
                        <div className="num">6번</div>
                        <div className="wing-type-item row-group gap8">
                          <p className="default-txt default-txt-bold green">
                            6W7
                          </p>
                          <p className="default-txt default-txt-s">
                            더 재미있고 낙관적입니다.
                          </p>
                        </div>
                      </div>
                      <div className="wing-type-group col-group">
                        <div className="wing-type-item row-group gap8">
                          <p className="default-txt default-txt-bold green">
                            7W6
                          </p>
                          <p className="default-txt default-txt-s">
                            더욱 믿음직스럽고 충성스럽습니다.
                          </p>
                        </div>
                        <div className="num">7번</div>
                        <div className="wing-type-item row-group gap8">
                          <p className="default-txt default-txt-bold green">
                            7W8
                          </p>
                          <p className="default-txt default-txt-s">
                            더 권위적이고 더욱 강압적으로 될 수도 있습니다.
                          </p>
                        </div>
                      </div>
                      <div className="wing-type-group col-group">
                        <div className="wing-type-item row-group gap8">
                          <p className="default-txt default-txt-bold green">
                            8W7
                          </p>
                          <p className="default-txt default-txt-s">
                            장난기가 심하고, 모든 관심을 독차지 하려고 합니다.
                          </p>
                        </div>
                        <div className="num">8번</div>
                        <div className="wing-type-item row-group gap8">
                          <p className="default-txt default-txt-bold green">
                            8W9
                          </p>
                          <p className="default-txt default-txt-s">
                            느긋하고 차분하며 세상이 흘러가는 대로 지켜보는 면이
                            있습니다.
                          </p>
                        </div>
                      </div>
                      <div className="wing-type-group col-group">
                        <div className="wing-type-item row-group gap8">
                          <p className="default-txt default-txt-bold green">
                            9W8
                          </p>
                          <p className="default-txt default-txt-s">
                            화가 나거나 짜증이 날 때 더 거칠어집니다. <br />
                            양쪽 날개 모두의 개입을 유도합니다.
                          </p>
                        </div>
                        <div className="num">9번</div>
                        <div className="wing-type-item row-group gap8">
                          <p className="default-txt default-txt-bold green">
                            9W1
                          </p>
                          <p className="default-txt default-txt-s">
                            더 비판적이며 양쪽 날개 모두의 개입을 유도합니다.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[28]}>
        <main className="main">
          <p className="page-num">29</p>
          <div className="main-title-wrap col-group">
            <div className="num dan green">06</div>
            <div className="main-title-group row-group">
              <h2 className="main-title">관계와 소통 분석</h2>
              <p className="main-sub-title">01. 자아상태 결과</p>
            </div>
          </div>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">1</div>
                <h3 className="section-title">관계와 소통 분석</h3>
              </div>
              <div className="row-group gap32">
                <div className="ego-result-group col-group gap24">
                  <div className="ego-result-item col-group gap16">
                    <img src={images.icon_ego_jc} alt="" className="icon" />
                    <div className="row-group">
                      <p className="default-txt default-txt-bold">
                        {resultPageData.JC}
                      </p>
                      <p className="default-txt default-txt-s">JC(판단&통제)</p>
                    </div>
                  </div>
                  <div className="ego-result-item col-group gap16">
                    <img src={images.icon_ego_ec} alt="" className="icon" />
                    <div className="row-group">
                      <p className="default-txt default-txt-bold">
                        {resultPageData.EC}
                      </p>
                      <p className="default-txt default-txt-s">EC(허용&보호)</p>
                    </div>
                  </div>
                  <div className="ego-result-item col-group gap16">
                    <img src={images.icon_ego_mr} alt="" className="icon" />
                    <div className="row-group">
                      <p className="default-txt default-txt-bold">
                        {resultPageData.MR}
                      </p>
                      <p className="default-txt default-txt-s">MR(이성&합리)</p>
                    </div>
                  </div>
                  <div className="ego-result-item col-group gap16">
                    <img src={images.icon_ego_fe} alt="" className="icon" />
                    <div className="row-group">
                      <p className="default-txt default-txt-bold">
                        {resultPageData.FE}
                      </p>
                      <p className="default-txt default-txt-s">FE(자율&표현)</p>
                    </div>
                  </div>
                  <div className="ego-result-item col-group gap16">
                    <img src={images.icon_ego_ae} alt="" className="icon" />
                    <div className="row-group">
                      <p className="default-txt default-txt-bold">
                        {resultPageData.AE}
                      </p>
                      <p className="default-txt default-txt-s">AE(순응&의존)</p>
                    </div>
                  </div>
                </div>
                <div className="ego-result-chart-wrap col-group gap40">
                  <div className="ego-result-chart-group">
                    <div className="ego-result-title-group col-group">
                      <p className="default-txt">비판적</p>
                      <p className="default-txt">보호적</p>
                      <p className="default-txt">현실적</p>
                      <p className="default-txt">자율적</p>
                      <p className="default-txt">의존적</p>
                    </div>
                    <div className="ego-result-chart">
                      {/* <Line data={chartData} options={chartOptions} plugins={[backgroundPlugin]} /> */}
                      <div style={{ width: "100%", height: "290px" }}>
                        <Line
                          data={chartData}
                          options={chartOptions}
                          plugins={[backgroundPlugin]}
                        />
                      </div>
                    </div>
                    <div className="ego-result-txt-group col-group">
                      <p className="default-txt default-txt-s">
                        JC <br />
                        (판단&통제)
                      </p>
                      <p className="default-txt default-txt-s">
                        EC <br />
                        (허용&보호)
                      </p>
                      <p className="default-txt default-txt-s">
                        MR <br />
                        (이성&합리)
                      </p>
                      <p className="default-txt default-txt-s">
                        FE <br />
                        (자율&표현)
                      </p>
                      <p className="default-txt default-txt-s">
                        AE <br />
                        (순응&의존)
                      </p>
                    </div>
                  </div>

                  <div className="ego-result-score-wrap">
                    <div className="ego-result-score-item row-group gap8">
                      <p className="title">0점 - 19점</p>
                      <p className="txt">낮은 유형</p>
                    </div>
                    <div className="ego-result-score-item row-group gap8">
                      <p className="title">20점 - 35점</p>
                      <p className="txt">적당한 유형</p>
                    </div>
                    <div className="ego-result-score-item row-group gap8">
                      <p className="title">36점 - 50점</p>
                      <p className="txt">높은 유형</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="section">
              <div className="row-group gap40">
                <div className="row-group gap16">
                  <p className="default-title green">양육자</p>
                  <div className="step-item-list border">
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          판단(Judgment) & 통제(Control)
                        </p>
                        <p className="default-txt default-txt-s">
                          JC 자아는 자신의 가치관과 사고방식이 옳다고 생각하면서
                          법, 도덕, 윤리, 규칙 등을 준수하는 역할을 합니다. 이
                          자아는 생활에 적합한 규칙과 합리적인 사고를 나타내며,
                          동시에 교류 속에서 비판과 비난의 역할을 합니다. 이
                          자아 유형만 너무 높다면, 지배적인 태도와 명령조의
                          말투를 사용하지 않는지 돌아보아야 합니다.
                        </p>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">02</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          허용(Embrace) & 보호(Care)
                        </p>
                        <p className="default-txt default-txt-s">
                          EC 자아는 아이의 성장을 지켜봐주는 부모 같은 자아로서
                          애정, 보호, 헌신, 공감, 배려, 친절, 위로 등 포용적인
                          언어로 설명할 수 있는 자아입니다. 후배, 동생, 아이 등
                          손아랫사람을 포용하고 칭찬하는 면이 강하며, 부드럽고
                          상냥한 양육태도를 지니고 있습니다. EC가 높은 양육자와
                          자란 아이는 비슷한 양육 태도와 자아상태를 갖게 되지만,
                          지나치게 높을 경우 아이의 주체적인 의사결정을 방해할
                          수 있습니다.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row-group gap16">
                  <p className="default-title green">성인</p>
                  <div className="step-item-list border">
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          이성(Mature) & 합리(Rational)
                        </p>
                        <p className="default-txt default-txt-s">
                          MR 자아는 성인으로서 가지는 모든 능력을 활용하여
                          사고하는 자아상태를 말합니다. ‘이성’을 사용하며 정보를
                          통한 판단, 냉철한 계산, 분석적 사고 등을 통해 외부의
                          사실을 객관적으로 처리합니다. 즉, 수집한 정보를
                          수용하거나 평가, 수정합니다. 감정적이기보다
                          합리적이지만 이것이 이상적인 삶의 방식을 의미하지는
                          않습니다. MR은 성인으로서 주변과 상호작용하는 관계
                          안에서 합리적인 성향을 드러내는 것을 뜻합니다.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row-group gap16">
                  <p className="default-title green">아이</p>
                  <div className="step-item-list border">
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">01</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          자율(Free) & 표현(Express)
                        </p>
                        <p className="default-txt default-txt-s">
                          FE 자아는 모든 자아 중에서 가장 본능적인 부분입니다.
                          천진난만함, 직관력, 충동적, 호기심 등의 역할을 합니다.
                          즉, 어떤 것에도 구속되지 않는 자발성, 창조성의
                          원인이며, 인간 본연의 감정을 솔직하게 표현합니다. 이
                          자아가 활성화 되면 예술적 소질과 직관력 등이 발휘되며,
                          일반적으로 밝고 유머 있는 모습을 보입니다. 그러나
                          지나치게 높을 경우, 철없고 실없는 행동을 하는 사람처럼
                          보일 수 있습니다.
                        </p>
                      </div>
                    </div>
                    <div className="step-item border-dash col-group gap24">
                      <div className="step-item-num">02</div>
                      <div className="step-item-txt-wrap row-group gap16">
                        <p className="default-title green">
                          순응(Adapt) & 의존(dEpend)
                        </p>
                        <p className="default-txt default-txt-s">
                          AE 자아는 착한 아이 역할을 하는 어린이 자아입니다.
                          감정 억제, 타협, 인내, 신중함, 기대 부응능력 등 본연의
                          자기 기질을 억제하고 부모, 교사, 친구 등의 기대에
                          부응하려고 노력합니다. 그래서 부모의 영향력이 중요하게
                          작용합니다. 자기주장을 못 하고 타협하거나, 타인에게
                          의존하면서 자발성이 결여된 모습을 보일 수 있습니다. 이
                          경우 침울, 격노, 반항 등의 모습으로 나타날 수
                          있습니다. 한편, 감정절제를 잘하고 성숙한 행동을 하는
                          어른스러운 모습을 보이기도 합니다.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[29]}>
        <main className="main">
          <p className="page-num">30</p>
          <div className="main-title-wrap col-group">
            <div className="num dan green">06</div>
            <div className="main-title-group row-group">
              <h2 className="main-title">관계와 소통 분석</h2>
              <p className="main-sub-title">02. 의사소통 방식과 리더십</p>
            </div>
          </div>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">1</div>
                <h3 className="section-title section-title-s">
                  {resultPageData.name}님의 교류분석 결과{" "}
                  <span className="green">
                    가장 두드러지는 특징은&nbsp;
                    {RelationCommuniHighItems.map((item) => item.name).join(
                      ", "
                    )}
                    {RelationCommuniHighItems.length > 0 &&
                      `이(가) 높${
                        RelationCommuniRowItems.length > 0 ? "고 " : "은 "
                      }`}
                    {RelationCommuniRowItems.map((item) => item.name).join(
                      ", "
                    )}
                    {RelationCommuniRowItems.length > 0 && "이(가) 낮은 "}
                    {RelationCommuniValues.every(
                      (value) =>
                        value.result !== "낮음" && value.result !== "높음"
                    ) && "적당한 "}
                    형태
                  </span>
                  라는 점입니다.
                </h3>
              </div>
              <div className="step-item-list border">
                <div className="step-item border-dash col-group gap24">
                  <div className="step-item-num">01</div>
                  <div className="step-item-txt-wrap row-group gap16">
                    <p className="default-title green">
                      {jcDetail[aboutJC].name}
                    </p>
                    <p
                      className="default-txt"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(jcDetail[aboutJC].desc),
                      }}
                    >
                      {/* {jcDetail[aboutJC].desc} */}
                    </p>
                  </div>
                </div>
                <div className="step-item border-dash col-group gap24">
                  <div className="step-item-num">02</div>
                  <div className="step-item-txt-wrap row-group gap16">
                    <p className="default-title green">
                      {ecDetail[aboutEC].name}
                    </p>

                    <p
                      className="default-txt"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(ecDetail[aboutEC].desc),
                      }}
                    ></p>
                  </div>
                </div>
                <div className="step-item border-dash col-group gap24">
                  <div className="step-item-num">03</div>
                  <div className="step-item-txt-wrap row-group gap16">
                    <p className="default-title green">
                      {mrDetail[aboutMR].name}
                    </p>
                    <p
                      className="default-txt"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(mrDetail[aboutMR].desc),
                      }}
                    ></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[30]}>
        <main className="main">
          <p className="page-num">31</p>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="step-item-list border">
                <div className="step-item border-dash col-group gap24">
                  <div className="step-item-num">04</div>
                  <div className="step-item-txt-wrap row-group gap16">
                    <p className="default-title green">
                      {feDetail[aboutFE].name}
                    </p>
                    <p
                      className="default-txt"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(feDetail[aboutFE].desc),
                      }}
                    ></p>
                  </div>
                </div>
                <div className="step-item border-dash col-group gap24">
                  <div className="step-item-num">05</div>
                  <div className="step-item-txt-wrap row-group gap16">
                    <p className="default-title green">
                      {aeDetail[aboutAE].name}
                    </p>
                    <p
                      className="default-txt"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(aeDetail[aboutAE].desc),
                      }}
                    ></p>
                  </div>
                </div>
              </div>
            </div>
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">2</div>
                <h3 className="section-title">낮은 포인트를 높이는 방법</h3>
              </div>
              <div className="row-group gap32">
                <p className="default-txt">
                  낮은 부분을 높이면, 반대로 높았던 부분이 저절로 낮아지게
                  됩니다.
                </p>
                <div className="step-item-list border">
                  <div className="step-item border-dash col-group gap24">
                    <div className="step-item-num">01</div>
                    <div className="step-item-txt-wrap row-group gap16">
                      <p className="default-title green">JC(판단&통제)</p>
                      <p className="default-txt default-txt-s">
                        시간이나 금전 관리에 조금 더 신경쓰고 철저하게
                        행동합니다. 일상생활에서 시간표를 만들어 생활의 리듬을
                        찾고 그것을 지킵니다. 타인의 말과 행동에 조금 더 주의를
                        기울입니다.
                      </p>
                    </div>
                  </div>
                  <div className="step-item border-dash col-group gap24">
                    <div className="step-item-num">02</div>
                    <div className="step-item-txt-wrap row-group gap16">
                      <p className="default-title green">EC(허용&보호)</p>
                      <p className="default-txt default-txt-s">
                        어린 아이를 대할 때는 따뜻한 시선과 부드러운 손짓으로
                        쓰다듬듯 행동합니다. 비록 잘하지 못하더라도 요리를
                        만들어 다른 사람에게 대접하거나 손편지로 마음을
                        전해봅니다. 타인을 배려하고 친절한 말과 태도를 유지할
                        것을 항상 명심합니다.
                      </p>
                    </div>
                  </div>
                  <div className="step-item border-dash col-group gap24">
                    <div className="step-item-num">03</div>
                    <div className="step-item-txt-wrap row-group gap16">
                      <p className="default-title green">MR(이성&합리)</p>
                      <p className="default-txt default-txt-s">
                        뉴스나 시사에 관심을 기울이고, 어렵더라도 끝까지
                        시청해봅니다. 간단한 메모 형태라도 하루의 일과를
                        기록합니다. 독서 모임이나 체육활동 등의 친목 모임에
                        가입하고 또래들과 소통에 주의를 기울이며 참석합니다.
                        관심 있는 분야의 다큐멘터리를 찾아 집중하는 시간을
                        갖습니다.
                      </p>
                    </div>
                  </div>
                  <div className="step-item border-dash col-group gap24">
                    <div className="step-item-num">04</div>
                    <div className="step-item-txt-wrap row-group gap16">
                      <p className="default-title green">FE(자율&표현)</p>
                      <p className="default-txt default-txt-s">
                        평소 유쾌하고 발랄한 사람들과 어울리려 노력합니다.
                        유머감각이 있어서 인기가 많은 사람들을 유심히 관찰하고
                        그들의 특징을 파악해봅니다. 누군가 자신의 의견을 물어올
                        때 생각이 정리되지 않았더라도 우선 자신의 의견이나
                        생각을 입 밖으로 말하는 연습을 합니다.
                      </p>
                    </div>
                  </div>
                  <div className="step-item border-dash col-group gap24">
                    <div className="step-item-num">05</div>
                    <div className="step-item-txt-wrap row-group gap16">
                      <p className="default-title green">AE(순응&의존)</p>
                      <p className="default-txt default-txt-s">
                        주변 사람들의 이야기에 귀 기울이며 그들의 입장에서
                        생각해봅니다. 누군가 제안하는 영화를 함께 보거나
                        제안하는 활동에 기꺼이 함께합니다. 드라마나 영화, 예능
                        프로그램을 시청하면서 주인공의 상황에 감정 이입하고
                        기뻐하거나 슬퍼하고 화내는 등의 표현을 함께 해봅니다.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[31]}>
        <main className="main">
          <p className="page-num">32</p>
          <div className="main-title-wrap col-group">
            <div className="num dan green">07</div>
            <div className="main-title-group row-group">
              <h2 className="main-title">인생점수</h2>
              <p className="main-sub-title">
                나는 내 인생에 왜 지금과 같은 점수를 주었는지 마인드 인사이트를
                통해 <br />
                체계적으로 점검, 분석해 보시기를 추천드립니다.
              </p>
            </div>
          </div>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="age-wrap col-group gap16">
                <div className="age-item row-group gap48">
                  <div className="col-group gap24 al-ce">
                    <img src={images.age_icon_0_on} alt="" className="icon" />
                    <div className="row-group gap8">
                      <p className="default-txt default-txt-ss">10세 이전</p>
                      <p className="default-txt default-txt-bold">영유아기</p>
                    </div>
                  </div>
                  <div className="row-group gap4">
                    <div className="age-score-wrap col-group">
                      <div
                        className={`age-score ${
                          resultPageData[0] >= 10 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[0] >= 20 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[0] >= 30 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[0] >= 40 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[0] >= 50 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[0] >= 60 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[0] >= 70 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[0] >= 80 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[0] >= 90 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[0] >= 100 ? "active" : ""
                        }`}
                      ></div>
                    </div>
                    <p className="default-txt default-txt-ss right">
                      {resultPageData[0]}점
                    </p>
                  </div>
                </div>
                <div className="age-item row-group gap48">
                  <div className="col-group gap24 al-ce">
                    <img
                      src={
                        resultPageData.age > 10
                          ? images.age_icon_1_on
                          : images.age_icon_1
                      }
                      alt=""
                      className="icon"
                    />
                    <div className="row-group gap8">
                      <p className="default-txt default-txt-ss">10~20세</p>
                      <p className="default-txt default-txt-bold">10대</p>
                    </div>
                  </div>
                  <div className="row-group gap4">
                    <div className="age-score-wrap col-group">
                      <div
                        className={`age-score ${
                          resultPageData[10] >= 10 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[10] >= 20 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[10] >= 30 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[10] >= 40 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[10] >= 50 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[10] >= 60 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[10] >= 70 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[10] >= 80 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[10] >= 90 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[10] >= 100 ? "active" : ""
                        }`}
                      ></div>
                    </div>
                    <p className="default-txt default-txt-ss right">
                      {resultPageData[10]}점
                    </p>
                  </div>
                </div>
                <div className="age-item row-group gap48">
                  <div className="col-group gap24 al-ce">
                    <img
                      src={
                        resultPageData.age > 20
                          ? images.age_icon_2_on
                          : images.age_icon_2
                      }
                      alt=""
                      className="icon"
                    />
                    <div className="row-group gap8">
                      <p className="default-txt default-txt-ss">21~30세</p>
                      <p className="default-txt default-txt-bold">20대</p>
                    </div>
                  </div>
                  <div className="row-group gap4">
                    <div className="age-score-wrap col-group">
                      <div
                        className={`age-score ${
                          resultPageData[20] >= 10 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[20] >= 20 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[20] >= 30 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[20] >= 40 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[20] >= 50 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[20] >= 60 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[20] >= 70 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[20] >= 80 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[20] >= 90 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[20] >= 100 ? "active" : ""
                        }`}
                      ></div>
                    </div>
                    <p className="default-txt default-txt-ss right">
                      {resultPageData[20]}점
                    </p>
                  </div>
                </div>
                <div className="age-item row-group gap48">
                  <div className="col-group gap24 al-ce">
                    <img
                      src={
                        resultPageData.age > 30
                          ? images.age_icon_3_on
                          : images.age_icon_3
                      }
                      alt=""
                      className="icon"
                    />
                    <div className="row-group gap8">
                      <p className="default-txt default-txt-ss">31~40세</p>
                      <p className="default-txt default-txt-bold">30대</p>
                    </div>
                  </div>
                  <div className="row-group gap4">
                    <div className="age-score-wrap col-group">
                      <div
                        className={`age-score ${
                          resultPageData[30] >= 10 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[30] >= 20 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[30] >= 30 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[30] >= 40 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[30] >= 50 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[30] >= 60 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[30] >= 70 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[30] >= 80 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[30] >= 90 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[30] >= 100 ? "active" : ""
                        }`}
                      ></div>
                    </div>
                    <p className="default-txt default-txt-ss right">
                      {resultPageData[30]}점
                    </p>
                  </div>
                </div>
                <div className="age-item row-group gap48">
                  <div className="col-group gap24 al-ce">
                    <img
                      src={
                        resultPageData.age > 40
                          ? images.age_icon_4_on
                          : images.age_icon_4
                      }
                      alt=""
                      className="icon"
                    />
                    <div className="row-group gap8">
                      <p className="default-txt default-txt-ss">41~50세</p>
                      <p className="default-txt default-txt-bold">40대</p>
                    </div>
                  </div>
                  <div className="row-group gap4">
                    <div className="age-score-wrap col-group">
                      <div
                        className={`age-score ${
                          resultPageData[40] >= 10 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[40] >= 20 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[40] >= 30 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[40] >= 40 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[40] >= 50 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[40] >= 60 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[40] >= 70 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[40] >= 80 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[40] >= 90 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[40] >= 100 ? "active" : ""
                        }`}
                      ></div>
                    </div>
                    <p className="default-txt default-txt-ss right">
                      {resultPageData[40]}점
                    </p>
                  </div>
                </div>
                <div className="age-item row-group gap48">
                  <div className="col-group gap24 al-ce">
                    <img
                      src={
                        resultPageData.age > 50
                          ? images.age_icon_5_on
                          : images.age_icon_5
                      }
                      alt=""
                      className="icon"
                    />
                    <div className="row-group gap8">
                      <p className="default-txt default-txt-ss">51~60세</p>
                      <p className="default-txt default-txt-bold">50대</p>
                    </div>
                  </div>
                  <div className="row-group gap4">
                    <div className="age-score-wrap col-group">
                      <div
                        className={`age-score ${
                          resultPageData[50] >= 10 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[50] >= 20 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[50] >= 30 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[50] >= 40 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[50] >= 50 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[50] >= 60 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[50] >= 70 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[50] >= 80 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[50] >= 90 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[50] >= 100 ? "active" : ""
                        }`}
                      ></div>
                    </div>
                    <p className="default-txt default-txt-ss right">
                      {resultPageData[50]}점
                    </p>
                  </div>
                </div>
                <div className="age-item row-group gap48">
                  <div className="col-group gap24 al-ce">
                    <img
                      src={
                        resultPageData.age > 60
                          ? images.age_icon_6_on
                          : images.age_icon_6
                      }
                      alt=""
                      className="icon"
                    />
                    <div className="row-group gap8">
                      <p className="default-txt default-txt-ss">61~70세</p>
                      <p className="default-txt default-txt-bold">60대</p>
                    </div>
                  </div>
                  <div className="row-group gap4">
                    <div className="age-score-wrap col-group">
                      <div
                        className={`age-score ${
                          resultPageData[60] >= 10 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[60] >= 20 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[60] >= 30 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[60] >= 40 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[60] >= 50 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[60] >= 60 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[60] >= 70 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[60] >= 80 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[60] >= 90 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[60] >= 100 ? "active" : ""
                        }`}
                      ></div>
                    </div>
                    <p className="default-txt default-txt-ss right">
                      {resultPageData[60]}점
                    </p>
                  </div>
                </div>
                <div className="age-item row-group gap48">
                  <div className="col-group gap24 al-ce">
                    <img
                      src={
                        resultPageData.age > 70
                          ? images.age_icon_7_on
                          : images.age_icon_7
                      }
                      alt=""
                      className="icon"
                    />
                    <div className="row-group gap8">
                      <p className="default-txt default-txt-ss">71~80세</p>
                      <p className="default-txt default-txt-bold">70대</p>
                    </div>
                  </div>
                  <div className="row-group gap4">
                    <div className="age-score-wrap col-group">
                      <div
                        className={`age-score ${
                          resultPageData[70] >= 10 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[70] >= 20 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[70] >= 30 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[70] >= 40 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[70] >= 50 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[70] >= 60 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[70] >= 70 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[70] >= 80 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[70] >= 90 ? "active" : ""
                        }`}
                      ></div>
                      <div
                        className={`age-score ${
                          resultPageData[70] >= 100 ? "active" : ""
                        }`}
                      ></div>
                    </div>
                    <p className="default-txt default-txt-ss right">
                      {resultPageData[70]}점
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div
              className={`section life-score-section ${
                (resultPageData.age < 10 && "age0") ||
                (resultPageData.age < 20 && "age1") ||
                (resultPageData.age < 30 && "age2") ||
                (resultPageData.age < 40 && "age3") ||
                (resultPageData.age < 50 && "age4") ||
                (resultPageData.age < 60 && "age5") ||
                (resultPageData.age < 70 && "age6") ||
                (resultPageData.age < 80 && "age6")
              }`}
            >
              <div className="life-score-title-wrap col-group gap16">
                <img src={images.icon_life_score} alt="" className="img" />
                <p style={{ marginBottom: "0px" }} className="life-score-title">
                  <span className="green">인생점수</span> 결과
                </p>
              </div>
              <div className="life-score-result-wrap col-group gap56">
                <div className="row-group gap56">
                  <div className="title-wrap col-group gap40">
                    <div className="icon"></div>
                    <div className="row-group gap24">
                      <div className="txt-group col-group gap32">
                        <p className="default-txt default-txt-bold">
                          {resultPageData.name}
                        </p>
                        <p className="default-txt">{resultPageData.age}세</p>
                      </div>
                      <div className="score">
                        평균 {resultPageData.life_average}점
                      </div>
                    </div>
                  </div>
                  <div className="row-group gap32">
                    <p className="default-txt">
                      본 인생점수는, 지나온 각자의 시간들에 대해 스스로 평가한{" "}
                      <br />
                      만족도 지표입니다.
                    </p>
                    <p className="default-txt default-txt-l default-txt-bold green">
                      {(ref29ScoreResult &&
                        ref29ScoreResult[ref29ScoreType] &&
                        ref29ScoreResult[ref29ScoreType].review) ||
                        "review가 없습니다"}
                    </p>
                  </div>
                </div>
                <div className="chart">
                  <div className="chart-txt">
                    {resultPageData.life_average}점
                  </div>
                  <Doughnut data={chartData2} options={chartOptions2} />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[32]}>
        <main className="main">
          <p className="page-num">33</p>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">1</div>
                <h3 className="section-title">
                  <span className="green">R</span>emember 기억하기
                </h3>
              </div>
              <div className="row-group gap24">
                {/* <p className="default-txt default-txt-bold">
                           시대는 빠르게 변화하고 있습니다. 내가 틀릴 수도 있다라는 생각으로 변화를 시작해야합니다.
                        </p> */}
                <p
                  className="default-txt"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      ref29ScoreResult[ref29ScoreType].remember
                    ),
                  }}
                />
              </div>
            </div>
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">2</div>
                <h3 className="section-title">
                  <span className="green">R</span>einterpretation 재해석하기
                </h3>
              </div>
              <div className="row-group gap24">
                {/* <p className="default-txt default-txt-bold">
                           몸을 새로운 마음에 길들여 조화를 이루게 하는 무의식 재학습 프로그램
                        </p> */}
                <p
                  className="default-txt"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      ref29ScoreResult[ref29ScoreType].reinterpretation
                    ),
                  }}
                />
              </div>
            </div>
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">3</div>
                <h3 className="section-title">
                  <span className="green">R</span>efresh 새롭게 하기
                </h3>
              </div>
              <div className="row-group gap24">
                {/* <p className="default-txt default-txt-bold green">
                           '지금여기'에 좋은 사람과 좋은 시간을 보내는 것이 방법입니다.
                        </p> */}
                <p
                  className="default-txt"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      ref29ScoreResult[ref29ScoreType].refresh
                    ),
                  }}
                />
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[33]}>
        <main className="main">
          <p className="page-num">34</p>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">4</div>
                <h3 className="section-title">
                  <span className="green">R</span>eformation 개선하기
                </h3>
              </div>
              <div className="row-group gap24">
                <p
                  className="default-txt"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      ref29ScoreResult[ref29ScoreType].reformation_desc[0]
                    ),
                  }}
                />
                <div className="col-group gap24">
                  {ref29ScoreResult[ref29ScoreType].reformation.map(
                    (item, index) => (
                      <div
                        className="green-border-box row-group gap24 center"
                        key={index}
                      >
                        <p className="default-txt default-txt-bold green">
                          {item.name}
                        </p>
                        <p className="default-txt default-txt-s">
                          {item.content}
                        </p>
                        <img
                          src={images.icon_arrow_bold_down}
                          alt=""
                          className="icon-arrow-down"
                        />
                        <p
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(item.result),
                          }}
                          className="default-txt default-txt-s default-txt-bold"
                        />
                      </div>
                    )
                  )}
                </div>
                <div className="col-group gap24">
                  {ref29ScoreResult[ref29ScoreType].reformation.map(
                    (item, index) =>
                      item.tip && (
                        <div
                          className="green-tip-box row-group gap16 center"
                          key={index}
                        >
                          <div className="icon">TIP</div>
                          <p
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(item.tip),
                            }}
                            className="default-txt default-txt-bold"
                          />
                        </div>
                      )
                  )}
                </div>
                <p
                  className="default-txt"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      ref29ScoreResult[ref29ScoreType].reformation_desc[1]
                    ),
                  }}
                />
              </div>
            </div>
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">5</div>
                <h3 className="section-title">
                  <span className="green">R</span>econstruction 새로 세우기
                </h3>
              </div>
              <div className="col-group gap56">
                <img
                  src={images.Reconstruction}
                  alt=""
                  className="recons-img"
                />
                <p
                  className="default-txt"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      ref29ScoreResult[ref29ScoreType].reconstruction
                    ),
                  }}
                />
              </div>
            </div>
          </div>
        </main>
      </div>

      <div className="page-common" ref={printPageRefs[34]}>
        <main className="main">
          <p className="page-num">35</p>
          <div className="main-title-wrap col-group">
            <div className="num dan green">08</div>
            <div className="main-title-group row-group">
              <h2 className="main-title">융합역량지수 Meta-Q</h2>
              <p className="main-sub-title">01. 융합역량지수 Meta-Q</p>
            </div>
          </div>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">1</div>
                <h3 className="section-title">융합역량지수 Meta-Q</h3>
              </div>
              <div className="result-score-container col-group gap40">
                <div className="result-score-wrap type4">
                  <div className="result-score-title-wrap col-group sp-bt">
                    <p className="result-score-title">나의 Meta-Q검사 결과</p>
                    <div className="result-score-num">
                      <strong>{resultPageData.metaQ}</strong>점
                    </div>
                  </div>
                  <div className="result-score number">
                    <div className="result-score-gauge-wrap">
                      <div
                        className="result-score-gauge"
                        style={{
                          width: `${(resultPageData.metaQ / 300) * 100}%`,
                        }}
                      ></div>
                    </div>
                    <div className="line" style={{ left: "25%" }}></div>
                    <div className="line" style={{ left: "50%" }}></div>
                    <div className="line" style={{ left: "75%" }}></div>
                    <div className="result-score-num-wrap col-group">
                      <p className="num">0</p>
                      <p className="num">75</p>
                      <p className="num">150</p>
                      <p className="num">225</p>
                      <p className="num">300</p>
                    </div>
                  </div>

                  <div className="result-score-list row-group">
                    {nextItem !== null && (
                      <div className="result-score-item col-group gap24">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(nextItem.score),
                          }}
                          className="num"
                        />
                        <div className="txt-group row-group gap10">
                          <p className="title">
                            {nextItem.title} {selectedItem.enTitle}
                          </p>
                          <p className="txt font-medium">{nextItem.keywords}</p>
                          <p className="txt">{nextItem.desc}</p>
                        </div>
                      </div>
                    )}
                    <div className="result-score-item current-score col-group gap24">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(selectedItem.score),
                        }}
                        className="num"
                      />
                      <div className="txt-group row-group gap10">
                        <p className="title">
                          {selectedItem.title} {selectedItem.enTitle}
                        </p>
                        <p className="txt font-medium">
                          {selectedItem.keywords}
                        </p>
                        <p className="txt">{selectedItem.desc}</p>
                      </div>
                    </div>
                    {previousItem !== null && (
                      <div className="result-score-item col-group gap24">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(previousItem.score),
                          }}
                          className="num"
                        />
                        <div className="txt-group row-group gap10">
                          <p className="title">
                            {previousItem.title} {selectedItem.enTitle}
                          </p>
                          <p className="txt font-medium">
                            {previousItem.keywords}
                          </p>
                          <p className="txt">{previousItem.desc}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="result-score-txt-wrap row-group gap40">
                  <div className="result-score-txt type4">
                    당신의 융합역량은 <br />
                    <span className="color">{selectedItem.title}</span>입니다.
                    <img
                      className="result-score-txt-img"
                      data-src={images[selectedItem.img]}
                      src={images[selectedItem.img]}
                      alt=""
                    />
                  </div>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(selectedItem.detail),
                    }}
                    className="default-txt"
                  />
                </div>
              </div>
            </div>
            <div className="section">
              <div className="row-group gap32">
                <div className="step-item-list border">
                  <div className="step-item border-dash col-group gap24">
                    <div className="step-item-num">01</div>
                    <div className="step-item-txt-wrap row-group gap28">
                      <p className="default-title green">
                        1. 나와의 관계 - {selectedItem.detailText_1.title}
                      </p>
                      <p
                        className="default-txt"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            selectedItem.detailText_1.content
                          ),
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <div className="page-common" ref={printPageRefs[35]}>
        <main className="main">
          <p className="page-num">36</p>
          <div className="main-title-wrap col-group">
            <div className="num dan green">08</div>
            <div className="main-title-group row-group">
              <h2 className="main-title">융합역량지수 Meta-Q</h2>
              <p className="main-sub-title">02. 성장을 위한 전략</p>
            </div>
          </div>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="row-group gap32">
                <div className="step-item-list border">
                  <div className="step-item border-dash col-group gap24">
                    <div className="step-item-num">02</div>
                    <div className="step-item-txt-wrap row-group gap28">
                      <p className="default-title green">
                        타인과의 관계 - {selectedItem.detailText_2.title}
                      </p>
                      <p
                        className="default-txt"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            selectedItem.detailText_2.content
                          ),
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ marginTop: "20px" }} className="row-group gap32">
                <div className="step-item-list border">
                  <div className="step-item border-dash col-group gap24">
                    <div className="step-item-num">03</div>
                    <div className="step-item-txt-wrap row-group gap28">
                      <p className="default-title green">
                        3. 사회 속의 나 - {selectedItem.detailText_3.title}
                      </p>
                      <p
                        className="default-txt"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            selectedItem.detailText_3.content
                          ),
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <div className="page-common" ref={printPageRefs[36]}>
        <main className="main">
          <p className="page-num">37</p>
          <div className="main-title-wrap col-group">
            <div className="num dan green">08</div>
            <div className="main-title-group row-group">
              <h2 className="main-title">융합역량지수 Meta-Q</h2>
              <p className="main-sub-title">02. 성장을 위한 전략</p>
            </div>
          </div>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">2</div>
                <h3 className="section-title">성장을 위한 전략</h3>
              </div>
              <div className="row-group gap32">
                <div className="green-border-box row-group gap24 bg-green strategy-1">
                  <p className="default-title green">“나는 내려놓습니다”</p>
                  <div className="row-group gap4">
                    {ref34ToGrowth[resultPageData.power_num][0].items.map(
                      (item, index) => (
                        <p
                          className="default-txt default-txt-bold dot"
                          key={index}
                        >
                          {item}
                        </p>
                      )
                    )}
                  </div>
                </div>
                <div className="green-border-box row-group gap24 strategy-2">
                  <p className="default-title green">“나는 받아들입니다”</p>
                  <div className="row-group gap4">
                    {ref34ToGrowth[resultPageData.power_num][1].items.map(
                      (item, index) => (
                        <p className="default-txt dot" key={index}>
                          {item}
                        </p>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[37]}>
        <main className="main">
          <p className="page-num">38</p>
          <div className="main-title-wrap col-group">
            <div className="num dan green">08</div>
            <div className="main-title-group row-group">
              <h2 className="main-title">융합역량지수 Meta-Q</h2>
              <p className="main-sub-title">03. 나의 강점과 핵심가치</p>
            </div>
          </div>

          <div className="section-top section-top-08">
            <img src={images.section_top_02} alt="" className="img" />
            <p className="txt">
              나는 지금 어디를 향해 가고 있나요? 내가 가고 있는 길에서 중요한
              것은 무엇일까요? <br />
              무엇이 나를 살게 하고, 무엇이 나를 힘들게 하나요? 내가 정말 원하는
              것은 무엇일까요?
            </p>
          </div>

          <div className="section-wrap row-group">
            <div className="section">
              <div className="section-title-wrap col-group">
                <div className="num">1</div>
                <h3 className="default-title">
                  마인드인사이트를 통해 발견한 나의 강점과 핵심가치 확인하기
                </h3>
              </div>
              <div className="row-group gap32">
                <p className="default-title green">
                  Step1 : 나만의 강점은 무엇인가요?
                </p>
                <p className="default-txt">
                  마인드 인사이트 결과지를 모두 살펴본 후 당신은, 당신을 얼마나
                  더 잘 이해하게 되었나요? <br />
                  이미 알고 있었던 면과 새롭게 발견한 면, 인정하는 부분과
                  받아들이기 어려운 부분까지 그 모든 면이 합쳐져 나를 이루고
                  있습니다. <br />
                  <br />
                  이제 스스로 각자의 강점을 직접 적어봅니다. 누군가와 비교하거나
                  자체검열로 주저하지 말고 솔직하게 떠오르는 것들을 그대로
                  적어봅니다. 개인이 가진 강점의 많은 부분은 어린 시절에
                  만들어집니다. 지금 당장 떠오르는 강점이 없다면, 과거를
                  떠올리며 적어봅니다.
                </p>
              </div>
            </div>
            <div className="section">
              <div className="letter-wrap">
                <div className="letter-title-wrap row-group gap16">
                  <p className="letter-sub-title">My Strength</p>
                  <p className="letter-title">내가 생각하는 내 강점</p>
                </div>
                <div className="row-group gap56 letter-group">
                  <div className="letter-item col-group">
                    <p className="default-txt">나에게는</p>
                    <p className="default-txt">한 특별함이 있습니다.</p>
                  </div>
                  <div className="letter-item col-group">
                    <p className="default-txt">나는</p>
                    <p className="default-txt">을 참 잘합니다.</p>
                  </div>
                  <div className="letter-item col-group">
                    <p className="default-txt">나는</p>
                    <p className="default-txt">
                      을 할 때 시간 가는 줄 모르고 집중합니다.
                    </p>
                  </div>
                  <div className="letter-item col-group">
                    <p className="default-txt">나는</p>
                    <p className="default-txt">
                      을 할 때 나의 존재감을 느낍니다.
                    </p>
                  </div>
                  <div className="letter-item col-group">
                    <p className="default-txt">나는</p>
                    <p className="default-txt">을 하고 싶고, 할 것입니다.</p>
                  </div>
                  <div className="letter-item col-group">
                    <p className="default-txt">나는</p>
                    <p className="default-txt">
                      을 통해 내가 속한 공동체에 기여하고 있습니다.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[38]}>
        <main className="main">
          <p className="page-num">39</p>

          <div className="section">
            <div className="row-group gap32">
              <div className="row-group gap40">
                <p className="default-title green">Step2 : 강점리스트 만들기</p>
                <p className="default-txt">
                  강점은 사전적으로 ‘남보다 우세하거나 더 뛰어난 점’이라고
                  정의하고 있습니다. 능력과 성과를 중시하는 현대 사회에서는
                  다양한 분야에서 서로 다른 의미로 사용되고 있기도 한 이 강점은,
                  단순히 남보다 우세하거나 더 뛰어나다고 해서 쓰이는 말이
                  아닙니다. <br />
                  <br />
                  자신이 가진 것과 자신에게 없는 것을 있는 그대로 인정하고, 가진
                  것을 잘 활용하여 좋은 결과를 낼 수 있도록 끊임없이 노력하는 이
                  성장의 과정이 모두 강점을 만드는 요인입니다. 그러기 위해서는
                  먼저 자신이 무엇을 가진 사람인지 파악하는 것이 중요합니다.
                </p>
              </div>
              <div className="check-list col-group">
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">자기 주도적인</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">겸손한</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">인내심이 강한</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">긍정적인</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">원칙을 지키는</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">민감한</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">배움을 즐기는</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">독창적인</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">성실한</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">계획적인</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">성취감이 높은</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">숙련된</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">승부욕 강한</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">추진력 있는</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">심사숙고하는</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">소통을 잘 하는</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">신념이 강한</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">유연한</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">전략적인</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">관계를 중요시하는</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">정리정돈을 잘하는</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">존재감 있는</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">목표 지향적인</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">자기 성찰적인</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">문제해결을 잘하는</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">책임감이 있는</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">성장을 돕는</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">강점에 집중하는</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">이야기를 즐기는</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">수용하는</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">이타적인</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">중재하는</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">활기찬</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">단호한</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">사교적인</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">공감을 잘하는</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">솔직한</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">협조적인</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">독립적인</p>
                </div>
                <div className="check-item col-group gap16">
                  <div className="check-icon"></div>
                  <p className="check-txt">신뢰가 가는</p>
                </div>
              </div>
              <div className="input-box">
                <i className="icon"></i>
              </div>
              <p className="default-txt">
                단어들을 천천히 살펴보면서 나에 해당 되는 부분에 동그라미로 표시
                해 보세요. <br />
                제시된 단어 가운데 나의 강점을 표현 하는 용어가 없다면 빈 칸에
                스스로 적어봅니다. <br />
                <br />
                <strong>
                  모두 찾았다면, 선택한 제시어 가운데 몇 개를 골라 예시와 같은
                  문장으로 만들어 봅니다.
                </strong>
              </p>
              <div className="example-box">
                <div className="label">예시</div>
                <p className="default-txt">
                  나는 공감을 잘하고 강점에 집중하는 사람입니다. 이와 같은 나의
                  강점은 어려운 사람들의 이야기를 잘 들어주고 지지해줌으로써
                  그들이 어려움을 헤쳐나갈 수 있도록 돕습니다.
                </p>
              </div>
              <div className="row-group gap56">
                <div className="step-item col-group gap24">
                  <div className="step-item-num">01</div>
                  <div className="step-item-txt-wrap">
                    <div className="letter-list row-group gap24">
                      <div className="letter-item col-group min">
                        <p className="default-txt">나는</p>
                        <p className="default-txt">하고,</p>
                        <p className="default-txt">하는 사람입니다.</p>
                      </div>
                      <div className="letter-item col-group">
                        <p className="default-txt">이와 같은 나의 강점은,</p>
                        <p className="default-txt">합니다.</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="step-item col-group gap24">
                  <div className="step-item-num">02</div>
                  <div className="step-item-txt-wrap">
                    <div className="letter-list row-group gap24">
                      <div className="letter-item col-group min">
                        <p className="default-txt">나는</p>
                        <p className="default-txt">하고,</p>
                        <p className="default-txt">하는 사람입니다.</p>
                      </div>
                      <div className="letter-item col-group">
                        <p className="default-txt">이와 같은 나의 강점은,</p>
                        <p className="default-txt">합니다.</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="step-item col-group gap24">
                  <div className="step-item-num">03</div>
                  <div className="step-item-txt-wrap">
                    <div className="letter-list row-group gap24">
                      <div className="letter-item col-group min">
                        <p className="default-txt">나는</p>
                        <p className="default-txt">하고,</p>
                        <p className="default-txt">하는 사람입니다.</p>
                      </div>
                      <div className="letter-item col-group">
                        <p className="default-txt">이와 같은 나의 강점은,</p>
                        <p className="default-txt">합니다.</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="green-tip-box col-group gap24 w-full-size">
                  <div className="icon">TIP</div>
                  <p className="default-txt default-txt-bold">
                    변화는, 있는 그대로의 나를 인정하고 깊이 들여다보는 일에서
                    시작됩니다.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[39]}>
        <main className="main">
          <p className="page-num">40</p>

          <div className="section">
            <div className="row-group gap32">
              <div className="row-group gap40">
                <p className="default-title green">
                  Step3 : 핵심가치 리스트 만들기
                </p>
                <div className="check-list-wrap col-group">
                  <div className="green-border-box row-group gap32">
                    <div className="check-list-title">사랑</div>
                    <div className="check-list col-group">
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">영적인 삶</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">의미있는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">환대하는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">포용적인</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">이타적인</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">의지적인</p>
                      </div>
                    </div>
                  </div>
                  <div className="green-border-box row-group gap32">
                    <div className="check-list-title">도전</div>
                    <div className="check-list col-group">
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">열정적인</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">모험하는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">용기있는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">성취하는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">대담한</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">자신감 있는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">경험하는</p>
                      </div>
                    </div>
                  </div>
                  <div className="green-border-box row-group gap32">
                    <div className="check-list-title">자유</div>
                    <div className="check-list col-group">
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">창의적인</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">독립적인</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">목표의식</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">주도적인</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">재미있는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">유연한</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">개방적인</p>
                      </div>
                    </div>
                  </div>
                  <div className="green-border-box row-group gap32">
                    <div className="check-list-title">사회</div>
                    <div className="check-list col-group">
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">영향력 있는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">인정 받는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">리더십 있는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">성실한</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">추진력 있는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">결단력 있는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">유능한</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">정직한</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">완벽한</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">협력하는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">소속된</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">함께하는</p>
                      </div>
                    </div>
                  </div>
                  <div className="green-border-box row-group gap32">
                    <div className="check-list-title">성장</div>
                    <div className="check-list col-group">
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">지혜로운</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">통찰력 있는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">깨닫는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">깊이 있는</p>
                      </div>
                      <div className="check-item col-group gap16 wide">
                        <div className="check-icon"></div>
                        <p className="check-txt">자기성찰적인</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">탐색하는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">아는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">의식적인</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">공부하는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">통합하는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">멈추지 않는</p>
                      </div>
                    </div>
                  </div>
                  <div className="green-border-box row-group gap32">
                    <div className="check-list-title">관계</div>
                    <div className="check-list col-group">
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">공감적인</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">진정성 있는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">사려 깊은</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">열린 마음</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">관대한</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">협동적인</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">도움 주는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">존중하는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">나누는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">배려하는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">연결된</p>
                      </div>
                    </div>
                  </div>
                  <div className="green-border-box row-group gap32">
                    <div className="check-list-title">평화</div>
                    <div className="check-list col-group">
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">조화로운</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">만족하는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">평온한</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">감사하는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">헌신하는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">안정적인</p>
                      </div>
                    </div>
                  </div>
                  <div className="green-border-box row-group gap32">
                    <div className="check-list-title">즐거움</div>
                    <div className="check-list col-group">
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">유연한</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">유머있는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">자극적인</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">활동적인</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">열정적인</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">가벼운</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">쉬운</p>
                      </div>
                    </div>
                  </div>
                  <div className="green-border-box row-group gap32">
                    <div className="check-list-title">지향</div>
                    <div className="check-list col-group">
                      <div className="check-item col-group gap16 wide">
                        <div className="check-icon"></div>
                        <p className="check-txt">현재에 집중하는</p>
                      </div>
                      <div className="check-item col-group gap16 wide">
                        <div className="check-icon"></div>
                        <p className="check-txt">미래 지향적인</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">이상적인</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">현실적인</p>
                      </div>
                    </div>
                  </div>
                  <div className="green-border-box row-group gap32">
                    <div className="check-list-title">정의</div>
                    <div className="check-list col-group">
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">합리적인</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">상식적인</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">공평한</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">평등한</p>
                      </div>
                      <div className="check-item col-group gap16 wide">
                        <div className="check-icon"></div>
                        <p className="check-txt">배제하지 않는</p>
                      </div>
                      <div className="check-item col-group gap16 wide">
                        <div className="check-icon"></div>
                        <p className="check-txt">타협하지 않는</p>
                      </div>
                    </div>
                  </div>
                  <div className="green-border-box row-group gap32">
                    <div className="check-list-title">아름다움</div>
                    <div className="check-list col-group">
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">사랑스러운</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">빛이 나는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">눈에 띄는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">세련된</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">우아한</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">교양있는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">자연스러운</p>
                      </div>
                    </div>
                  </div>
                  <div className="green-border-box row-group gap32">
                    <div className="check-list-title">성공</div>
                    <div className="check-list col-group">
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">도달하는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">성취하는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">승리하는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">지배하는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">능력있는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">달성하는</p>
                      </div>
                      <div className="check-item col-group gap16">
                        <div className="check-icon"></div>
                        <p className="check-txt">앞서는</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="input-box">
                <i className="icon"></i>
              </div>
              <p className="default-txt">
                단어들을 천천히 살펴보면서 나에 해당 되는 부분에 동그라미로 표시
                해 보세요. <br />
                제시된 단어 가운데 나의 가치를 표현 하는 용어가 없다면 빈 칸에
                스스로 적어봅니다. <br />
                <br />
                <strong>
                  모두 찾았다면, 선택한 제시어 가운데 세 개를 골라 예시와 같은
                  문장으로 만들어 봅니다.
                </strong>
              </p>
              <div className="example-box">
                <div className="label">예시</div>
                <p className="default-txt">
                  나에게는 영적인 삶이 매우 중요합니다. 이를 위해 의식적으로
                  항상 깨어있으려 하고 <br />
                  나에게 주어진 삶을 수용적으로 받아들이며 영성을 깨닫기 위해
                  노력하는 것이 중요합니다.
                </p>
              </div>
              <div className="row-group gap56">
                <div className="step-item col-group gap24 al-en">
                  <div className="step-item-num">01</div>
                  <div className="step-item-txt-wrap">
                    <div className="letter-item"></div>
                  </div>
                </div>
                <div className="step-item col-group gap24 al-en">
                  <div className="step-item-num">02</div>
                  <div className="step-item-txt-wrap">
                    <div className="letter-item"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="page-common" ref={printPageRefs[40]}>
        <main className="main">
          <p className="page-num">41</p>

          <div className="section">
            <div className="row-group gap40">
              <p className="default-title green">
                Step4 : 강점과 신념 활용 셀프코칭
              </p>
              <div className="row-group gap56">
                <div className="step-item-list border">
                  <div className="step-item border-dash col-group gap24">
                    <div className="step-item-num">01</div>
                    <div className="step-item-txt-wrap row-group gap32">
                      <p className="default-txt default-txt-bold">
                        위에서 찾은 강점 가운데 스스로 가장 마음에 드는 것은
                        무엇이며 나는 그 강점을 주로 어떻게 사용하나요?
                      </p>
                      <div className="empty-box-wrap col-group">
                        <img
                          src={images.icon_arrow_bold_down}
                          alt=""
                          className="icon-arrow-down icon-arrow-right"
                        />
                        <div className="empty-box"></div>
                      </div>
                    </div>
                  </div>
                  <div className="step-item border-dash col-group gap24">
                    <div className="step-item-num">02</div>
                    <div className="step-item-txt-wrap row-group gap32">
                      <p className="default-txt default-txt-bold">
                        나를 기쁘게 하고 움직이게 하는 강점은 무엇인가요? 나는
                        그 강점을 얼마나 자주 사용하나요?
                      </p>
                      <div className="empty-box-wrap col-group">
                        <img
                          src={images.icon_arrow_bold_down}
                          alt=""
                          className="icon-arrow-down icon-arrow-right"
                        />
                        <div className="empty-box"></div>
                      </div>
                    </div>
                  </div>
                  <div className="step-item border-dash col-group gap24">
                    <div className="step-item-num">03</div>
                    <div className="step-item-txt-wrap row-group gap32">
                      <p className="default-txt default-txt-bold">
                        나의 강점으로 선택하지는 못했지만, 갖고 싶은 강점은
                        무엇인가요? 그 강점이 있다면 나의 무엇이 달라질까요?
                      </p>
                      <div className="empty-box-wrap col-group">
                        <img
                          src={images.icon_arrow_bold_down}
                          alt=""
                          className="icon-arrow-down icon-arrow-right"
                        />
                        <div className="empty-box"></div>
                      </div>
                    </div>
                  </div>
                  <div className="step-item border-dash col-group gap24">
                    <div className="step-item-num">04</div>
                    <div className="step-item-txt-wrap row-group gap32">
                      <p className="default-txt default-txt-bold">
                        나는 나의 모든 강점을 얼마나 잘 활용하고 있나요?
                        구체적으로 적어봅니다.
                      </p>
                      <div className="empty-box-wrap col-group">
                        <img
                          src={images.icon_arrow_bold_down}
                          alt=""
                          className="icon-arrow-down icon-arrow-right"
                        />
                        <div className="empty-box"></div>
                      </div>
                    </div>
                  </div>
                  <div className="step-item border-dash col-group gap24">
                    <div className="step-item-num">05</div>
                    <div className="step-item-txt-wrap row-group gap32">
                      <p className="default-txt default-txt-bold">
                        위에서 찾은 핵심가치들은 나의 신념에 어떤 영향을
                        주었을까요? 핵심가치에 의해 만들어진 신념을 찾아
                        적어봅니다.
                      </p>
                      <div className="empty-box-wrap col-group">
                        <img
                          src={images.icon_arrow_bold_down}
                          alt=""
                          className="icon-arrow-down icon-arrow-right"
                        />
                        <div className="empty-box"></div>
                      </div>
                    </div>
                  </div>
                  <div className="step-item border-dash col-group gap24">
                    <div className="step-item-num">06</div>
                    <div className="step-item-txt-wrap row-group gap32">
                      <p className="default-txt default-txt-bold">
                        살아가면서 나를 힘들게 했던 신념이 있다면 무엇인가요?
                        그리고 그 신념은 왜, 어떻게 만들어 졌나요?
                      </p>
                      <div className="empty-box-wrap col-group">
                        <img
                          src={images.icon_arrow_bold_down}
                          alt=""
                          className="icon-arrow-down icon-arrow-right"
                        />
                        <div className="empty-box"></div>
                      </div>
                    </div>
                  </div>
                  <div className="step-item border-dash col-group gap24">
                    <div className="step-item-num">07</div>
                    <div className="step-item-txt-wrap row-group gap32">
                      <p className="default-txt default-txt-bold">
                        Step3에서 표시한 핵심가치 가운데, 내 인생에 꼭
                        필요하다고 생각하는 가치들에만 더 굵게 표시해봅니다.{" "}
                        <br />
                        그리고 이 초핵심가치들로 나의 신념을 새롭게
                        정의해봅니다.
                      </p>
                      <div className="empty-box-wrap col-group">
                        <img
                          src={images.icon_arrow_bold_down}
                          alt=""
                          className="icon-arrow-down icon-arrow-right"
                        />
                        <div className="empty-box"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </Container>
  );
}

export default PageContainer;
